import React, { useState, useMemo, createContext, useContext, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabase.js";

// ─────────────────────────────────────────────────────────────
// EMAIL HELPER — sends via /api/email (Resend) server-side
// ─────────────────────────────────────────────────────────────
async function sendInviteEmail(email, firmName, role) {
  const appUrl = window.location.origin;
  const html = `
    <div style="font-family:'Helvetica Neue',sans-serif;max-width:560px;margin:0 auto;padding:32px;background:#F4F7FC;border-radius:12px;">
      <div style="background:#1E3A5F;padding:24px;border-radius:8px 8px 0 0;text-align:center;">
        <h1 style="color:#fff;margin:0;font-size:22px;font-weight:700;">Financial Advisor OS</h1>
      </div>
      <div style="background:#fff;padding:32px;border-radius:0 0 8px 8px;border:1px solid #E2E8F0;">
        <h2 style="color:#1E3A5F;font-size:18px;margin-top:0;">You've been invited</h2>
        <p style="color:#475569;font-size:14px;line-height:1.6;">
          You've been invited to join <strong>${firmName}</strong> as a <strong>${role}</strong> on Financial Advisor OS.
        </p>
        <p style="color:#475569;font-size:14px;line-height:1.6;">
          Click the button below to set up your account and get started.
        </p>
        <div style="text-align:center;margin:28px 0;">
          <a href="${appUrl}" style="background:#1E3A5F;color:#fff;padding:12px 28px;border-radius:8px;text-decoration:none;font-size:14px;font-weight:700;display:inline-block;">
            Accept Invitation →
          </a>
        </div>
        <p style="color:#94A3B8;font-size:12px;text-align:center;margin-bottom:0;">
          If you weren't expecting this invitation, you can ignore this email.
        </p>
      </div>
    </div>
  `;

  try {
    const resp = await fetch("/api/email", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        to:      email,
        subject: `You've been invited to join ${firmName} on Financial Advisor OS`,
        html,
      }),
    });
    const data = await resp.json();
    if (!resp.ok) throw new Error(data.error || "Email failed");
    return { success: true };
  } catch(e) {
    console.warn("Email send failed:", e.message);
    return { success: false, error: e.message };
  }
}

// ─────────────────────────────────────────────────────────────
// CLIENT PERSISTENCE HELPERS
// All client reads/writes go through Supabase clients table
// ─────────────────────────────────────────────────────────────
const clientsCache = { data: null, firmId: null };

async function sbGetClients(firmId) {
  if (clientsCache.firmId === firmId && clientsCache.data) return clientsCache.data;
  const { data, error } = await supabase.from("clients").select("*").eq("firm_id", firmId).order("created_at", { ascending: false });
  if (error) { console.warn("Failed to load clients:", error.message); return []; }
  clientsCache.data = (data || []).map(r => ({
    ...r.data,
    _sbRowId: r.id,  // keep the Supabase row UUID internally
    // id comes from r.data.id (the original client_XXXXX id)
  }));
  clientsCache.firmId = firmId;
  return clientsCache.data;
}

function invalidateClientsCache() { clientsCache.data = null; }

async function sbSaveClient(client, firmId, advisorId) {
  invalidateClientsCache();

  // Check if client already exists by its data id
  const { data: existing } = await supabase
    .from("clients")
    .select("id")
    .eq("data->>id", client.id)
    .single();

  if (existing) {
    // Update existing row
    const { data, error } = await supabase
      .from("clients")
      .update({ data: client, updated_at: new Date().toISOString() })
      .eq("id", existing.id)
      .select()
      .single();
    if (error) { console.warn("Failed to update client:", error.message); return null; }
    return data;
  } else {
    // Insert new row
    const { data, error } = await supabase
      .from("clients")
      .insert({ firm_id: firmId, advisor_id: advisorId, data: client, updated_at: new Date().toISOString() })
      .select()
      .single();
    if (error) { console.warn("Failed to insert client:", error.message); return null; }
    return data;
  }
}

async function sbDeleteClient(clientId, firmId) {
  invalidateClientsCache();
  const query = supabase.from("clients").delete().eq("data->>id", clientId);
  const { error } = firmId ? await query.eq("firm_id", firmId) : await query;
  if (error) console.warn("Failed to delete client:", error.message);
}

async function sbUpdateClient(clientId, updates, firmId) {
  invalidateClientsCache();
  const query = supabase.from("clients").select("*").eq("data->>id", clientId);
  const { data: existing } = firmId ? await query.eq("firm_id", firmId).single() : await query.single();
  if (!existing) return;
  const newData = { ...existing.data, ...updates };
  await supabase.from("clients").update({ data: newData, updated_at: new Date().toISOString() }).eq("data->>id", clientId);
  return newData;
}
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

// ── Shared style constants ──
const sLabel    = { fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:4 };
const inp       = { border:"1px solid #E2E8F0", borderRadius:8, padding:"8px 12px", fontSize:13, fontFamily:"'Outfit',sans-serif", outline:"none", background:"#fff" };
const smallInp  = (overrides={}) => ({ border:"1px solid #E2E8F0", borderRadius:6, padding:"5px 8px", fontSize:12, fontFamily:"'Outfit',sans-serif", outline:"none", background:"#fff", ...overrides });
const badge     = (color="#1E3A5F") => ({ display:"inline-block", padding:"2px 10px", borderRadius:20, fontSize:10, fontWeight:700, color, background:color+"15", border:`1px solid ${color}33` });
const card      = { background:"#fff", border:"1px solid #E2E8F0", borderRadius:14, overflow:"hidden", marginBottom:16 };
const cardTitle = { fontSize:12, fontWeight:700, color:"#1E3A5F", padding:"12px 16px", borderBottom:"1px solid #F1F5F9", letterSpacing:"0.2px" };
const ttStyle   = { background:"#1E3A5F", border:"none", borderRadius:8, color:"#fff", fontSize:11, fontFamily:"'Outfit',sans-serif", padding:"6px 10px" };

// ─────────────────────────────────────────────────────────────
// RESPONSIVE HOOK
// ─────────────────────────────────────────────────────────────
function useBreakpoint() {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const handler = () => setW(window.innerWidth);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return { isMobile: w < 640, isTablet: w < 900, w };
}

// ─────────────────────────────────────────────────────────────
// ENGINE CONFIG
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// PLATFORM LAYER — your company's super-admin layer
// Manages firm licenses, feature flags, and isolated firm data
// ─────────────────────────────────────────────────────────────

const PLATFORM_ACCESS_CODE = "WLTH-2025"; // change in production

const BASE_FEATURES = {
  analyzer:   true,
  allocator:  true,
  comparator: true,
  trading:    true,
  adminSuite: true,
};

// PRODUCTION: seed data should be loaded from your database on app init, not hardcoded
const DEFAULT_PLATFORM = {
  firms: [
    {
      id:            "firm_demo",
      firmName:      "Meridian Wealth Advisors",
      logoInitials:  "MW",
      brandColor:    "#1E3A5F",
      tier:          "base",
      features:      { analyzer:true, allocator:true, comparator:true, trading:true, adminSuite:true },
      maxSeats:      10,
      billingContact:"Jane Doe",
      billingEmail:  "billing@meridian.com",
      contractStart: "2025-01-01",
      contractEnd:   "2026-01-01",
      notes:         "Demo firm — pre-loaded with sample data",
      createdAt:     "2025-01-01",
      active:        true,
      engine:        null, // populated from DEFAULT_ENGINE on first load
    }
  ],
  // PRODUCTION: platform user credentials must be stored hashed server-side and verified via proper auth
  platformUsers: [
    { id:"pu_1", name:"Platform Admin", username:"platadmin", password:"platform123" }
  ],
  auditLog: [],
};

// ─────────────────────────────────────────────────────────────
// PII VAULT — AES-GCM encryption for client identity protection
// Real names & account numbers are tokenized before storage or API calls.
// CryptoKey lives in sessionStorage scope only — vault unreadable without it.
// ─────────────────────────────────────────────────────────────

async function _derivePIIKey(password, saltB64) {
  const enc    = new TextEncoder();
  const salt   = Uint8Array.from(atob(saltB64), c => c.charCodeAt(0));
  const keyMat = await crypto.subtle.importKey("raw", enc.encode(password), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name:"PBKDF2", salt, iterations:100000, hash:"SHA-256" },
    keyMat, { name:"AES-GCM", length:256 }, false, ["encrypt","decrypt"]
  );
}
function _genSalt() {
  const b = crypto.getRandomValues(new Uint8Array(16));
  return btoa(String.fromCharCode(...b));
}
async function _encryptVault(data, key) {
  const iv  = crypto.getRandomValues(new Uint8Array(12));
  const enc = new TextEncoder();
  const ct  = await crypto.subtle.encrypt({ name:"AES-GCM", iv }, key, enc.encode(JSON.stringify(data)));
  const out = new Uint8Array(iv.byteLength + ct.byteLength);
  out.set(iv); out.set(new Uint8Array(ct), iv.byteLength);
  return btoa(String.fromCharCode(...out));
}
async function _decryptVault(b64, key) {
  const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
  const plain = await crypto.subtle.decrypt({ name:"AES-GCM", iv:bytes.slice(0,12) }, key, bytes.slice(12));
  return JSON.parse(new TextDecoder().decode(plain));
}
async function _makeToken(value, prefix) {
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
  const hex  = Array.from(new Uint8Array(hash)).map(b=>b.toString(16).padStart(2,"0")).join("").slice(0,8).toUpperCase();
  return `${prefix}_${hex}`;
}

const PIIVaultCtx = createContext(null);

function PIIVaultProvider({ children, engine, setEngine, currentUser }) {
  const [vaultKey,   setVaultKey]   = useState(null);
  const [vaultMap,   setVaultMap]   = useState({});
  const [vaultReady, setVaultReady] = useState(false);

  useEffect(() => {
    if (!currentUser) { setVaultKey(null); setVaultMap({}); setVaultReady(false); return; }
    // PRODUCTION: key derivation should happen server-side; browser receives only a session token
    const pw = currentUser.password || currentUser.id;
    (async () => {
      let salt = engine.piiSalt;
      if (!salt) { salt = _genSalt(); setEngine(prev => ({...prev, piiSalt:salt})); }
      const key = await _derivePIIKey(pw, salt);
      setVaultKey(key);
      if (engine.piiVault) {
        try { setVaultMap(await _decryptVault(engine.piiVault, key)); }
        catch { setVaultMap({}); }
      } else { setVaultMap({}); }
      setVaultReady(true);
    })();
  }, [currentUser?.id]);

  const tokenizePII = async (name, acct) => {
    if (!vaultKey) return { nameToken: name||"UNKNOWN", acctToken: acct };
    const nameToken = name ? await _makeToken(name, "CLT") : null;
    const acctToken = acct ? await _makeToken(acct, "ACT") : null;
    const updated   = { ...vaultMap };
    if (nameToken) updated[nameToken] = { name, ...(acctToken?{accountNumber:acct}:{}) };
    if (acctToken) updated[acctToken] = { accountNumber:acct, ...(nameToken?{name}:{}) };
    setVaultMap(updated);
    const ct = await _encryptVault(updated, vaultKey);
    setEngine(prev => ({...prev, piiVault:ct}));
    return { nameToken: nameToken||name||"UNKNOWN", acctToken: acctToken||acct||null };
  };

  const resolveName = (token) => {
    if (!token) return "";
    if (/^CLT_[0-9A-F]{8}$/.test(token)) return vaultMap[token]?.name || token;
    // Also check ACT tokens which store name too
    if (/^ACT_[0-9A-F]{8}$/.test(token)) return vaultMap[token]?.name || token;
    return token; // not a token — return as-is (pre-vault clients)
  };
  const resolveAcct = (token) => {
    if (!token) return "";
    if (/^ACT_[0-9A-F]{8}$/.test(token)) return vaultMap[token]?.accountNumber || token;
    if (/^CLT_[0-9A-F]{8}$/.test(token)) return vaultMap[token]?.accountNumber || token;
    return token;
  };

  // PRODUCTION: token resolution should require a server round-trip — raw names should never persist in client memory
  return (
    <PIIVaultCtx.Provider value={{ tokenizePII, resolveName, resolveAcct, vaultReady }}>
      {children}
    </PIIVaultCtx.Provider>
  );
}

const usePIIVault = () => useContext(PIIVaultCtx) || {
  tokenizePII: async (n,a) => ({ nameToken:n||"UNKNOWN", acctToken:a }),
  resolveName: t => t || "",
  resolveAcct: t => t || "",
  vaultReady: false,
};

const DEFAULT_ENGINE = {
  firmName: "Financial Advisor OS",
  adminPassword: "committee2024",

  employees: [
    // PRODUCTION: passwords must be hashed (bcrypt/argon2) and verified server-side — never stored or compared in plaintext
    { id: "emp_1", name: "Alex Johnson", username: "ajohnson", password: "pass123", role: "admin",   email: "ajohnson@firm.com" },
    { id: "emp_2", name: "Maria Chen",   username: "mchen",    password: "pass123", role: "advisor", email: "mchen@firm.com"    },
    { id: "emp_3", name: "David Park",   username: "dpark",    password: "pass123", role: "advisor", email: "dpark@firm.com"    },
    { id: "emp_4", name: "Bob",          username: "bob",      password: "bobby",   role: "admin",   email: "bob@firm.com"      },
  ],

  // ── Sleeves ──
  sleeves: {
    opportunistic: {
      label: "Opportunistic",
      color: "#2563EB",
      assets: [
        { id: "us_eq",   label: "US Equity",                  color: "#2563EB", weight: 50 },
        { id: "intl_eq", label: "International Equity",       color: "#7C3AED", weight: 25 },
        { id: "em_eq",   label: "Emerging Markets",           color: "#DB2777", weight: 15 },
        { id: "opp_alt", label: "Opportunistic Alternatives", color: "#D97706", weight: 10 },
      ],
    },
    defensive: {
      label: "Defensive",
      color: "#059669",
      assets: [
        { id: "ig_inc",  label: "Investment Grade Income", color: "#059669", weight: 55 },
        { id: "hy_inc",  label: "High Yield Income",       color: "#0891B2", weight: 25 },
        { id: "def_alt", label: "Defensive Alternatives",  color: "#65A30D", weight: 20 },
      ],
    },
  },

  // ── Implementation styles — tickers keyed by asset id across both sleeves ──
  implementationStyles: {
    Flagship: {
      description: "Actively managed, high-conviction strategies",
      color: "#1E3A5F",
      tickers: {
        us_eq:   ["FCNTX","VWUSX","AGTHX"],
        intl_eq: ["FIVFX","ODVYX","NEWFX"],
        em_eq:   ["FEMKX","ODMAX","WGEMX"],
        opp_alt: ["QSPIX","AQMIX","ASFYX"],
        ig_inc:  ["FTBFX","PTTAX","MWTRX"],
        hy_inc:  ["FHIIX","PHIYX","MWHYX"],
        def_alt: ["FCSSX","GSGIX","RYMSX"],
      },
    },
    Systematic: {
      description: "Rules-based, factor-driven strategies",
      color: "#0891B2",
      tickers: {
        us_eq:   ["DFUS","AVUS","FNDX"],
        intl_eq: ["DFAI","AVDE","FNDF"],
        em_eq:   ["DFEM","AVEM","FNDE"],
        opp_alt: ["QMOM","VMOT","MTUM"],
        ig_inc:  ["DFCF","AVSD","FIPDX"],
        hy_inc:  ["HYDB","FALN","USHY"],
        def_alt: ["PDBC","DJP","COMB"],
      },
    },
    "Index Passive": {
      description: "Low-cost index tracking strategies",
      color: "#059669",
      tickers: {
        us_eq:   ["VTI","SCHB","ITOT"],
        intl_eq: ["VXUS","IXUS","SPDW"],
        em_eq:   ["VWO","IEMG","SPEM"],
        opp_alt: ["BTAL","MNA","DALI"],
        ig_inc:  ["BND","AGG","SCHZ"],
        hy_inc:  ["HYG","JNK","USHY"],
        def_alt: ["PDBC","DJP","GSG"],
      },
    },
  },

  // ── Models — investment committee model portfolios ──
  // Structure: models[styleName][sleeveName][assetId] = { primary:[{ticker,split}] }
  models: {
    Flagship: {
      opportunistic: {
        us_eq:   { primary:[{ticker:"FCNTX",split:60},{ticker:"VWUSX",split:40}] },
        intl_eq: { primary:[{ticker:"FIVFX",split:60},{ticker:"ODVYX",split:40}] },
        em_eq:   { primary:[{ticker:"FEMKX",split:100}] },
        opp_alt: { primary:[{ticker:"QSPIX",split:100}] },
      },
      defensive: {
        ig_inc:  { primary:[{ticker:"FTBFX",split:60},{ticker:"PTTAX",split:40}] },
        hy_inc:  { primary:[{ticker:"FHIIX",split:100}] },
        def_alt: { primary:[{ticker:"FCSSX",split:100}] },
      },
    },
    Systematic: {
      opportunistic: {
        us_eq:   { primary:[{ticker:"DFUS",split:50},{ticker:"AVUS",split:30},{ticker:"FNDX",split:20}] },
        intl_eq: { primary:[{ticker:"DFAI",split:60},{ticker:"AVDE",split:40}] },
        em_eq:   { primary:[{ticker:"DFEM",split:60},{ticker:"AVEM",split:40}] },
        opp_alt: { primary:[{ticker:"QMOM",split:50},{ticker:"MTUM",split:50}] },
      },
      defensive: {
        ig_inc:  { primary:[{ticker:"DFCF",split:60},{ticker:"AVSD",split:40}] },
        hy_inc:  { primary:[{ticker:"HYDB",split:60},{ticker:"FALN",split:40}] },
        def_alt: { primary:[{ticker:"PDBC",split:100}] },
      },
    },
    "Index Passive": {
      opportunistic: {
        us_eq:   { primary:[{ticker:"VTI",split:70},{ticker:"SCHB",split:30}] },
        intl_eq: { primary:[{ticker:"VXUS",split:70},{ticker:"IXUS",split:30}] },
        em_eq:   { primary:[{ticker:"VWO",split:60},{ticker:"IEMG",split:40}] },
        opp_alt: { primary:[{ticker:"BTAL",split:100}] },
      },
      defensive: {
        ig_inc:  { primary:[{ticker:"BND",split:60},{ticker:"AGG",split:40}] },
        hy_inc:  { primary:[{ticker:"HYG",split:60},{ticker:"JNK",split:40}] },
        def_alt: { primary:[{ticker:"PDBC",split:100}] },
      },
    },
  },

  // ── Audit log — append-only array of event objects ──
  auditLog: [],

  // ── DCA (Dollar Cost Averaging) configuration ──
  dcaConfig: {
    enabled:          true,
    maxPeriod:        12,
    maxUnit:          "months",   // "weeks" | "months" | "quarters" | "years"
    allowedIntervals: ["weekly","biweekly","monthly","quarterly","semiannual","annual"],
  },

  // ── Allocator settings ──
  allocatorSettings: {
    allowTickerEditing:   true,  // advisors can override model ticker selections
    allowBlendAdjust:     true,  // advisors can change Opp/Def blend ratio
    allowStyleChange:     true,  // advisors can switch implementation style
    allowClientDirected:  true,  // advisors can add client-directed holding overrides
  },

  benchmarks: {
    "60/40 Blend":   { us_eq:36, intl_eq:12, em_eq:0,  opp_alt:0, ig_inc:36, hy_inc:8,  def_alt:8  },
    "Global Market": { us_eq:25, intl_eq:20, em_eq:10, opp_alt:5, ig_inc:25, hy_inc:10, def_alt:5  },
    "All-Weather":   { us_eq:18, intl_eq:0,  em_eq:0,  opp_alt:12,ig_inc:35, hy_inc:0,  def_alt:35 },
  },

  // ── Approved Holdings — tickers committee allows advisors to keep for clients ──
  // approvedHoldings: [{ ticker, assetId }]  — assetId optional, blank = unassigned
  approvedHoldings: [
    {ticker:"VTI",  assetId:"us_eq"},
    {ticker:"SCHB", assetId:"us_eq"},
    {ticker:"BND",  assetId:"ig_inc"},
    {ticker:"AGG",  assetId:"ig_inc"},
    {ticker:"VWO",  assetId:"em_eq"},
    {ticker:"VXUS", assetId:"intl_eq"},
    {ticker:"IEMG", assetId:"em_eq"},
    {ticker:"HYG",  assetId:"hy_inc"},
    {ticker:"JNK",  assetId:"hy_inc"},
    {ticker:"ITOT", assetId:"us_eq"},
  ],

  // ── PII Vault — AES-GCM encrypted {token→{name,accountNumber}} map ──
  // Key is derived per-session from advisor credentials; vault is unreadable without it
  piiVault: null,   // base64 AES-GCM ciphertext, null until first client saved
  piiSalt:  null,   // base64 random salt, generated once on first login — PRODUCTION: generate server-side, store in DB, never send to client

  // ── Clients — saved from Portfolio Analyzer onboarding ──
  // { id, name, accountNumber, accountType, custodian, statementDate, totalPortfolioValue,
  //   cashBalance, holdings: [...], analysisResults: {}, advisorId, advisorName, savedAt }
  clients: [],

  // ── Ticker characteristics — keyed by ticker symbol ──
  // expenseRatio: annual % (e.g. 0.75 = 0.75%), yield: annual % (e.g. 2.10 = 2.10%)
  tickerData: {
    // Flagship — Opportunistic
    FCNTX:{expenseRatio:0.47,yield:0.10},              VWUSX:{expenseRatio:0.42,yield:0.20,minAUM:1000000},  AGTHX:{expenseRatio:0.63,yield:0.30,minAUM:5000000},
    FIVFX:{expenseRatio:0.99,yield:1.20},              ODVYX:{expenseRatio:0.90,yield:1.50,minAUM:1000000},  NEWFX:{expenseRatio:0.81,yield:0.60,minAUM:5000000},
    FEMKX:{expenseRatio:1.01,yield:0.80},              ODMAX:{expenseRatio:1.11,yield:1.40,minAUM:1000000},  WGEMX:{expenseRatio:1.25,yield:0.90,minAUM:5000000},
    QSPIX:{expenseRatio:0.49,yield:0.00},              AQMIX:{expenseRatio:1.29,yield:0.00,minAUM:1000000},  ASFYX:{expenseRatio:1.40,yield:0.00,minAUM:5000000},
    // Flagship — Defensive
    FTBFX:{expenseRatio:0.45,yield:3.80},              PTTAX:{expenseRatio:0.76,yield:3.90,minAUM:1000000},  MWTRX:{expenseRatio:0.67,yield:3.70,minAUM:5000000},
    FHIIX:{expenseRatio:0.72,yield:6.50},              PHIYX:{expenseRatio:0.64,yield:6.20,minAUM:1000000},  MWHYX:{expenseRatio:0.69,yield:6.40,minAUM:5000000},
    FCSSX:{expenseRatio:0.99,yield:0.00},              GSGIX:{expenseRatio:0.59,yield:0.00,minAUM:1000000},  RYMSX:{expenseRatio:1.44,yield:0.00,minAUM:5000000},
    // Systematic — Opportunistic
    DFUS:{expenseRatio:0.09,yield:1.40},  AVUS:{expenseRatio:0.15,yield:1.30},  FNDX:{expenseRatio:0.25,yield:1.60},
    DFAI:{expenseRatio:0.18,yield:2.80},  AVDE:{expenseRatio:0.23,yield:3.10},  FNDF:{expenseRatio:0.25,yield:2.90},
    DFEM:{expenseRatio:0.35,yield:2.50},  AVEM:{expenseRatio:0.33,yield:2.30},  FNDE:{expenseRatio:0.39,yield:2.60},
    QMOM:{expenseRatio:0.29,yield:0.00},  VMOT:{expenseRatio:0.29,yield:0.00},  MTUM:{expenseRatio:0.15,yield:0.40},
    // Systematic — Defensive
    DFCF:{expenseRatio:0.22,yield:4.10},  AVSD:{expenseRatio:0.15,yield:3.90},  FIPDX:{expenseRatio:0.10,yield:4.00},
    HYDB:{expenseRatio:0.35,yield:6.80},  FALN:{expenseRatio:0.25,yield:6.10},  USHY:{expenseRatio:0.15,yield:6.50},
    PDBC:{expenseRatio:0.59,yield:0.00},  DJP:{expenseRatio:0.85,yield:0.00},   COMB:{expenseRatio:0.29,yield:0.00},
    // Index Passive — Opportunistic
    VTI:{expenseRatio:0.03,yield:1.30},   SCHB:{expenseRatio:0.03,yield:1.40},  ITOT:{expenseRatio:0.03,yield:1.30},
    VXUS:{expenseRatio:0.07,yield:3.00},  IXUS:{expenseRatio:0.07,yield:2.80},  SPDW:{expenseRatio:0.04,yield:2.90},
    VWO:{expenseRatio:0.08,yield:2.80},   IEMG:{expenseRatio:0.09,yield:2.50},  SPEM:{expenseRatio:0.11,yield:2.60},
    BTAL:{expenseRatio:1.30,yield:0.00},  MNA:{expenseRatio:0.76,yield:0.00},   DALI:{expenseRatio:0.64,yield:0.00},
    // Index Passive — Defensive
    BND:{expenseRatio:0.03,yield:3.80},   AGG:{expenseRatio:0.03,yield:3.70},   SCHZ:{expenseRatio:0.03,yield:3.80},
    HYG:{expenseRatio:0.48,yield:6.30},   JNK:{expenseRatio:0.40,yield:6.50},
    GSG:{expenseRatio:0.75,yield:0.00},
  },
  // ── AI Configuration — version-controlled prompts & model ──
  aiConfig: {
    model: "claude-sonnet-4-5",
    extractPrompt: `You are an expert financial data extractor. This statement may contain one or more accounts. Extract ALL accounts and their holdings and return ONLY valid JSON — no markdown, no explanation.

IMPORTANT: If the document contains multiple accounts (e.g. a brokerage account AND an IRA), return each as a separate entry in the accounts array. Do NOT merge holdings from different accounts.

Schema:
{
  "clientName": "string or null",
  "statementDate": "string or null",
  "accounts": [
    {
      "accountNumber": "string or null",
      "accountType": "Brokerage|IRA|401k|Roth IRA|529|HSA|Mutual Fund|Unknown",
      "accountLabel": "string or null (nickname/label shown on statement, e.g. 'Joint Taxable', 'Rollover IRA')",
      "custodian": "string or null",
      "totalValue": number,
      "cashBalance": number,
      "holdings": [
        {
          "id": "unique string",
          "ticker": "string",
          "name": "string",
          "assetClass": "US Equity|International Equity|Fixed Income|Alternative|Cash|Real Estate|Other",
          "shares": number,
          "currentPrice": number,
          "marketValue": number,
          "costBasis": number,
          "gainLoss": number,
          "gainLossPct": number,
          "holdingPeriod": "Short-term|Long-term|Unknown",
          "purchaseDate": "string or null"
        }
      ]
    }
  ]
}

Rules:
- If only one account exists, still return it inside the accounts array.
- Set null for any field not visible in the document.
- Extract every holding visible. Do not omit any positions.
- Identify account type from labels like "IRA", "Roth", "401(k)", "Brokerage", "Individual", "Joint" etc.`,
    analysisPrompt: `You are a senior financial advisor analyst. Given these holdings, run the requested analysis and return ONLY valid JSON with ONLY that single key.`,
    promptVersion: "1.0.0",
    lastUpdatedBy: null,
    lastUpdatedAt: null,
  },
};

// ─────────────────────────────────────────────────────────────
// CONTEXTS
// ─────────────────────────────────────────────────────────────
const EngineCtx  = createContext(null);
const SessionCtx = createContext(null);
const useEngine  = () => useContext(EngineCtx);
const useSession = () => useContext(SessionCtx);

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────
const fmtSecs = s => { const m = Math.floor(s/60); return m > 0 ? `${m}m ${s%60}s` : `${s}s`; };
const fmtDate = d => new Date(d).toLocaleString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"});
const pct     = n => `${Math.round(n*10)/10}%`;

// Given engine + implStyle + oppPct(0-100), compute final flat allocation
function computeAllocation(engine, oppPct, styleName) {
  const defPct = 100 - oppPct;
  const result = {};
  const opp = engine.sleeves.opportunistic;
  const def = engine.sleeves.defensive;
  // Use style-specific weights if configured, else fall back to global sleeve weights
  const styleWeights = styleName ? engine.implementationStyles[styleName]?.sleeveWeights : null;
  opp.assets.forEach(a => {
    const w = styleWeights?.opportunistic?.[a.id] ?? a.weight;
    result[a.id] = (w / 100) * oppPct;
  });
  def.assets.forEach(a => {
    const w = styleWeights?.defensive?.[a.id] ?? a.weight;
    result[a.id] = (w / 100) * defPct;
  });
  return result;
}

// All assets flat
function allAssets(engine) {
  return [
    ...engine.sleeves.opportunistic.assets,
    ...engine.sleeves.defensive.assets,
  ];
}

const HELP_ARTICLES = [
  // ── SYSTEM LOGIC ──
  {
    id:"sl-01", category:"System Logic", tags:["Tiers","AUM","Tickers"],
    title:"How AUM-Based Ticker Filtering Works",
    summary:"Tickers auto-select based on total portfolio value when a client is loaded.",
    body:`When a client's total value is entered (or loaded from the Analyzer), the system automatically filters available tickers for the selected implementation style based on each ticker's minimum AUM threshold.

Minimum AUM is configured in Admin → Tickers (the Min AUM column). Tickers with no minimum are available at all portfolio sizes. Tickers with a minimum are excluded when the client's AUM falls below that value — remaining tickers absorb the split proportionally.

Advisors can still manually change tickers after auto-selection using the Edit Tickers control. A "Customized" badge replaces the auto-filtered badge to indicate divergence from defaults.`,
    note:"AUM filtering happens silently — no confirmation is required. If all tickers in an asset class are excluded, the system falls back to the full unfiltered list.",
  },
  {
    id:"sl-02", category:"System Logic", tags:["Tickers","Splits","Allocation"],
    title:"Default Splits & Split Validation",
    summary:"Each ticker in a style carries a default allocation percentage per asset class.",
    body:`Default splits determine what percentage of an asset class's allocation goes to each ticker. For example, if US Equity is 30% of the total portfolio and FCNTX has a 60% default split, FCNTX receives 18% of the overall allocation.

Splits are configured in Admin → Tickers. Each style column shows a checkbox (include) and a percentage input. Splits per style per asset must sum to 100% — a red ⚠ badge appears when they don't.`,
    note:"A split of 0% is treated as excluded. At least one ticker per asset class should have a non-zero split.",
  },
  {
    id:"sl-03", category:"System Logic", tags:["Kept Holdings","Eligibility","Non-taxable","Client Directed"],
    title:"Kept Holdings — Eligibility & Override Rules",
    summary:"What determines whether a position can be kept, and how client-directed overrides work.",
    body:`When a client is loaded in the Allocator, advisors can mark holdings to keep. A holding is eligible if it meets any one of:
1. The ticker appears on the firm's Approved Holdings list.
2. The unrealized gain exceeds 5% of total portfolio value.
3. The unrealized gain exceeds $50,000.

Non-Taxable Accounts: Holdings in tax-advantaged accounts (IRA, Roth, 401k, etc.) cannot be kept through the standard checkbox — they always appear in the sell list.

Client Directed Override: The Client Directed Holdings section lets advisors enter tickers the client has directed to retain. These bypass ALL eligibility rules including the non-taxable restriction. A "Client Directed" reason is required and every override is logged in the audit trail.`,
    note:"Kept holdings are incorporated at their current portfolio weight, not cost basis. Client directed overrides should be documented in writing outside the system.",
  },
  {
    id:"sl-04", category:"System Logic", tags:["Allocation","Blend","Sleeves"],
    title:"Sleeve Blend & Allocation Math",
    summary:"The Opportunistic/Defensive blend ratio drives all downstream allocation percentages.",
    body:`The allocator uses a two-sleeve model: Opportunistic and Defensive. The blend slider sets what percentage of the total portfolio goes to the Opportunistic sleeve — the remainder goes to Defensive.

Within each sleeve, assets are weighted by their configured percentages (Admin → Sleeve Weights). Final dollar amounts = Total Portfolio Value × Asset Weight %.

When kept holdings are present, the allocation table shows the kept position's current weight and the remaining room for model tickers.`,
    note:"Sleeve and asset weights must each sum to 100% within their respective groups.",
  },
  {
    id:"sl-05", category:"System Logic", tags:["Analyzer","AI","Native","Extraction"],
    title:"Portfolio Analyzer — Statement Extraction",
    summary:"How the two-pass extraction system works: native scan first, AI review on demand.",
    body:`The Analyzer uses a two-pass approach:

Pass 1 — Native Scan: PDFs are scanned using PDF.js text extraction and pattern matching. Instant, no AI credits used. Shows a confidence rating — high (3+ positions), low (1–2), or none.

Pass 2 — AI Review: The "🤖 AI Review" button is always available after a scan. Clicking sends the statement to Claude AI for full extraction including account types, client name, and complex formatting. Each AI call is logged in the audit trail.

Image files cannot be natively parsed and require AI review.`,
    note:"Always review extracted data before proceeding.",
  },
  {
    id:"sl-06", category:"System Logic", tags:["Multi-account","Tax","Account Types"],
    title:"Multi-Account Input & Tax Status",
    summary:"How multiple accounts are handled across all entry methods.",
    body:`All three input methods (AI extraction, Excel upload, manual entry) support multiple accounts in one session. Each source is assigned an account nickname and type before being committed to the pool.

Tax Status auto-flags: Traditional IRA, Roth IRA, 401(k), 403(b), 457(b), SEP IRA, SIMPLE IRA, Inherited IRA, Roth Inherited IRA, HSA, and Pension are automatically marked non-taxable. All other types default to taxable.

Non-taxable rows show a 🛡 Non-tax badge in the pool. This flows into the allocator where non-taxable positions are blocked from the keep checkbox.`,
    note:"Tax status can be overridden per-row in the step 2 holdings editor.",
  },
  {
    id:"sl-07", category:"System Logic", tags:["Trading","Transition","DCA","Cash Needs","Deviation"],
    title:"Transition Strategies — How the Math Works",
    summary:"How S1 (full liquidation), S2 (gradual transition), DCA, and cash needs are calculated.",
    body:`Strategy 1 — Full Liquidation: Sells all taxable positions immediately. Proceeds redeploy proportionally into the most underweight model buckets. Non-taxable (exempt) positions are held, creating residual deviation that the chart shows.

Strategy 2 — Gradual Transition: Sells year-by-year within an annual tax budget. Each year, freed proceeds go into underweight buckets. Cash needs (withdrawals) are reserved first before model deployment. The portfolio value shrinks each year by the withdrawal amount.

DCA: Available within either strategy when new cash is being added. Deploys the cash in equal installments across the selected number of periods into underweight buckets.

Model Deviation: The sum of absolute differences between current allocation and target across all asset classes. Both strategy charts show a dashed deviation line converging toward zero.`,
    note:"Over-allocated buckets (from kept positions) show amber hatching and contribute to deviation until the kept position is eventually sold.",
  },

  // ── ADMIN SETTINGS ──
  {
    id:"as-01", category:"Admin Settings", tags:["Tickers","Upload"],
    title:"Managing Tickers — Best Practices",
    summary:"How to add, configure, and remove tickers across implementation styles.",
    body:`Tickers are managed in Admin → Tickers. Configure ER %, Yield %, and style columns (include checkbox + split %) for each ticker. Splits must sum to 100% per style per asset class — a red ⚠ badge appears when they don't.

To add a ticker: use the inline "+" row. The ticker must first exist in the global pool — add new symbols via the "Add a new ticker" input at the bottom of the page.`,
    note:"Always set ER and Yield before making a ticker live.",
  },
  {
    id:"as-02", category:"Admin Settings", tags:["AUM","Tiers","Tickers"],
    title:"Configuring AUM-Based Ticker Access",
    summary:"How to restrict specific tickers to higher AUM portfolios.",
    body:`In Admin → Tickers, each row has a Min AUM column. Leave blank for all portfolio sizes. Set a dollar threshold (e.g. 1000000) to restrict the ticker to portfolios at or above that value.

At allocation time excluded tickers' splits are re-normalized across remaining eligible tickers. The combined total (existing + new money) is used for AUM matching.`,
    note:"Only use Min AUM for institutional share classes or strategies with genuine minimums.",
  },
  {
    id:"as-03", category:"Admin Settings", tags:["DCA","Trading Rules"],
    title:"Configuring DCA Rules — Trading Rules Tab",
    summary:"How admins control which DCA options advisors can access.",
    body:`In Admin → Trading Rules:

Enable/Disable: Toggling off removes the DCA panel from the Trading page entirely.

Maximum Deployment Period: Sets the hard cap on total DCA duration. Enter a number and select the unit (weeks, months, quarters, years).

Allowed Intervals: Select which frequencies advisors may use — Weekly, Bi-weekly, Monthly, Quarterly, Semi-annual, Annual. Unchecked options don't appear in the advisor's dropdown. The advisor sees a small "⚙ Admin limit" note.`,
    note:"Changes to Trading Rules take effect immediately for all advisors on that firm.",
  },
  {
    id:"as-04", category:"Admin Settings", tags:["Audit","AI","Overrides"],
    title:"Audit Log — AI Override Tracking",
    summary:"How to monitor AI statement review usage per advisor.",
    body:`Every "🤖 AI Review" click logs an ai_statement_review event with the advisor name, timestamp, file count, and native scan result count.

The Audit Log tab shows a 🤖 AI Statement Review Usage summary strip listing each advisor's total AI override count and date of last use.

All standard events (login, logout, client saved/deleted, allocation exported, engine changes) are also logged and filterable by type, user, and date range. Exportable to CSV.`,
    note:"PRODUCTION: The audit log must write to an immutable server-side log in production.",
  },

  // ── ADVISOR WORKFLOW ──
  {
    id:"aw-01", category:"Advisor Workflow", tags:["Analyzer","Holdings","Data Quality"],
    title:"Building the Holdings Pool — Data Quality Gate",
    summary:"How to satisfy the data quality checklist before running analysis.",
    body:`Step 1 combines holdings from AI extraction, Excel/CSV upload, and manual entry. Each source is assigned an account and type before being added to the pool.

Step 2 Data Quality Checklist:
1. 🔍 Lookup All — fetches name and asset class from Yahoo Finance for any missing values.
2. 📡 Pull Yield & ER — fetches yield and expense ratio for applicable positions (ETFs, funds, fixed income, real estate). Individual stocks are exempt.
3. ⚠ Required fields — ticker, name, asset class, market value, and cost basis must be present. Negative market values (short positions) are valid.

Steps are pre-satisfied when data is already complete. Missing data can also be filled directly in the inline editing table.`,
    note:"Cost basis is required because it drives gain/loss calculations and tax analysis. Zero is a valid entry.",
  },
  {
    id:"aw-02", category:"Advisor Workflow", tags:["Analyzer","Save","Load","Edit"],
    title:"Saving, Loading & Editing Client Records",
    summary:"How to save a client and re-open them for editing without starting over.",
    body:`Saving: The floating 💾 Save button appears as soon as holdings are in the pool. If the client was previously saved this session, saving will update the existing record.

Loading into Analyzer (My Clients tab):
• ✏️ Edit — loads the client's holdings back into the pool at step 1. Add/remove holdings, re-run lookups, re-run analyses. Save will update the existing record.
• 📊 Allocator — loads the client into the Allocator and navigates there.

The Back button in step 2 preserves the pool — advisors return to step 1 with all data intact.`,
    note:"The nav guard will prompt before navigating away with unsaved analysis work.",
  },
  {
    id:"aw-03", category:"Advisor Workflow", tags:["Allocator","Client","Additional Cash"],
    title:"Using the Allocator with an Existing Client",
    summary:"How to load a client, review kept holdings, and add new money.",
    body:`Load a saved client via the client selector. The holdings panel shows all positions with an Account column (nickname and 🛡/Taxable badge).

Kept Holdings: Check eligible positions. Non-taxable positions are blocked. Use the Client Directed Holdings section for any positions the client has directed to retain regardless of rules.

Additional Cash: Enter in the "Additional Dollars to Allocate" field. This auto-populates the DCA cash field on the Trading page.

Saving: 💾 Save Allocation saves to the client record. ⇄ Save & Compare saves and navigates to the Comparator.`,
    note:"The nav guard will prompt before leaving with an unsaved allocation.",
  },
  {
    id:"aw-04", category:"Advisor Workflow", tags:["Comparator","Trading","Transition","DCA","Cash Needs"],
    title:"Comparator & Trading — Review and Plan the Transition",
    summary:"How to compare current holdings to the model and build a transition plan.",
    body:`Comparator: Shows current holdings vs proposed model — asset class drift, ER delta, buy/sell/keep breakdown. The version picker switches between saved allocation versions.

Trading Page:
• Allocation Overview — target vs kept allocation and room to deploy
• Transition Strategies — select Full Liquidation (S1) or Gradual Transition (S2)
• Both strategies support: DCA toggle (for new cash), Cash Needs (regular withdrawals), and the Advisor Notes field at the bottom

Cash Needs: Enter per-withdrawal amount and frequency. Each year the withdrawal is reserved in the Cash bucket before model deployment. The allocation chart shows the portfolio shrinking alongside the transition.

DCA: Toggle "+ Add DCA" within S1 or S2. Pre-populated from the Additional Cash in the Allocator. Select interval and number of periods within admin limits.`,
    note:"The nav guard on the Trading page prompts if you try to leave with a strategy selected or notes entered.",
  },
  {
    id:"aw-05", category:"Advisor Workflow", tags:["Tickers","Customizing"],
    title:"Customizing Tickers Mid-Allocation",
    summary:"How to change tickers after the tier default has been applied.",
    body:`Click "Edit Tickers ›" on the allocation table to open the ticker modal. Check/uncheck tickers and adjust split percentages, then click Apply. A ✏ Customized badge appears when you've diverged from tier defaults.

Changing the implementation style or portfolio value significantly may re-apply tier defaults — re-open "Edit Tickers ›" to restore custom selections.`,
    note:"Custom ticker selections are saved when the allocation snapshot is saved to the client record.",
  },

  // ── GLOSSARY ──
  {
    id:"gl-01", category:"Glossary", tags:["Terms"],
    title:"Implementation Style",
    summary:"The investment approach used to select tickers for each asset class.",
    body:`Flagship — Actively managed mutual funds. Higher expense ratios; discretionary decisions.
Systematic — Rules-based, factor-driven strategies (DFA, Avantis). Lower cost than active.
Index Passive — Low-cost ETFs tracking broad market indices (Vanguard, Schwab). Lowest ER.`,
  },
  {
    id:"gl-02", category:"Glossary", tags:["Terms","Allocation"],
    title:"Opportunistic / Defensive Blend",
    summary:"The ratio controlling equity-oriented vs income-oriented sleeve allocations.",
    body:`Opportunistic — Growth-oriented. US Equity, International Equity, Emerging Markets, Opportunistic Alternatives. Higher volatility, higher expected return.
Defensive — Income-oriented. Investment Grade Income, High Yield Income, Defensive Alternatives. Lower volatility, capital preservation.
The blend slider goes from 0% (fully Defensive) to 100% (fully Opportunistic).`,
  },
  {
    id:"gl-03", category:"Glossary", tags:["Terms","Tickers"],
    title:"Default Split",
    summary:"The percentage of an asset class allocation directed to each ticker.",
    body:`A default split is the pre-configured percentage of an asset class's allocation that goes to a specific ticker. Example: If US Equity is 30% of the total portfolio and FCNTX has a 60% default split, FCNTX receives 18% overall.

Splits must sum to 100% per style per asset class.`,
  },
  {
    id:"gl-04", category:"Glossary", tags:["Terms","Tax"],
    title:"Tax-Loss Harvesting",
    summary:"Selling positions at a loss to offset capital gains elsewhere in the portfolio.",
    body:`Tax-loss harvesting realizes a capital loss to offset capital gains and reduce tax liability. The Analyzer's Tax-Loss Harvesting analysis identifies candidates with unrealized losses and estimates the potential savings.

The wash-sale rule (IRS) prohibits repurchasing a "substantially identical" security within 30 days before or after the sale.`,
  },
  {
    id:"gl-05", category:"Glossary", tags:["Terms","Risk"],
    title:"Concentration Risk",
    summary:"The risk of outsized exposure to a single security, sector, or asset class.",
    body:`The Analyzer flags positions exceeding 10% of portfolio value as noteworthy and those exceeding 20% as high concentration risk. Common sources include employer stock, inherited positions, and long-held appreciated securities.`,
  },
  {
    id:"gl-06", category:"Glossary", tags:["Terms","AUM"],
    title:"AUM — Assets Under Management",
    summary:"The total market value of assets the advisor manages on behalf of a client.",
    body:`AUM is the total current market value of all investments being managed. The platform uses the total portfolio value entered by the advisor (or loaded from a statement) as the AUM figure for tier matching. When additional dollars are being invested, the combined total is used.`,
  },
  {
    id:"gl-07", category:"Glossary", tags:["Terms","Trading","Deviation"],
    title:"Model Deviation",
    summary:"A score measuring how far the current portfolio is from the target model.",
    body:`Model deviation = sum of |current % − target %| across all asset classes. A score of 0% means the portfolio exactly matches the model. The transition strategy charts show a dashed deviation line converging toward zero.

Over-allocated buckets (kept positions exceeding target) contribute positively to deviation but require selling the kept position to resolve — buying into other buckets cannot fix them.`,
  },
  {
    id:"gl-08", category:"Glossary", tags:["Terms","Trading","DCA"],
    title:"Dollar Cost Averaging (DCA)",
    summary:"Deploying new cash into the portfolio gradually over a scheduled series of periods.",
    body:`DCA splits new cash into equal installments deployed at regular intervals. Each installment goes proportionally into the most underweight asset class buckets.

In this platform, DCA is embedded within Strategy 1 or Strategy 2 — it runs simultaneously with the transition. Admins control the maximum deployment period and which intervals are available.`,
  },
];

const HELP_CATEGORIES = ["System Logic", "Admin Settings", "Advisor Workflow", "Glossary"];
const HELP_CAT_COLORS = {
  "System Logic":    { bg:"#EFF6FF", border:"#BFDBFE", text:"#2563EB" },
  "Admin Settings":  { bg:"#F0FDF4", border:"#BBF7D0", text:"#16A34A" },
  "Advisor Workflow":{ bg:"#FEF3C7", border:"#FDE68A", text:"#D97706" },
  "Glossary":        { bg:"#F5F3FF", border:"#DDD6FE", text:"#7C3AED" },
};

// ─────────────────────────────────────────────────────────────
// HELP TOOLTIP — small contextual ? popover for advisor UI
// ─────────────────────────────────────────────────────────────
function HelpTip({ articleId, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const article = HELP_ARTICLES.find(a => a.id === articleId);
  const text = children || article?.summary || "";

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <span ref={ref} style={{position:"relative",display:"inline-flex",alignItems:"center"}}>
      <button onClick={e=>{e.stopPropagation();setOpen(o=>!o);}}
        style={{width:15,height:15,borderRadius:"50%",border:"1px solid #CBD5E1",background:open?"#EFF6FF":"#F8FAFC",color:open?"#2563EB":"#94A3B8",fontSize:9,fontWeight:800,cursor:"pointer",display:"inline-flex",alignItems:"center",justifyContent:"center",padding:0,lineHeight:1,fontFamily:"'Outfit',sans-serif",flexShrink:0,marginLeft:4}}>
        ?
      </button>
      {open && (
        <div style={{position:"absolute",top:"calc(100% + 6px)",left:"50%",transform:"translateX(-50%)",zIndex:200,width:240,background:"#fff",border:"1px solid #E2E8F0",borderRadius:10,boxShadow:"0 4px 20px rgba(0,0,0,0.12)",padding:"12px 14px"}}>
          <div style={{fontSize:11,fontWeight:700,color:"#1E3A5F",marginBottom:4}}>{article?.title || "Help"}</div>
          <div style={{fontSize:11,color:"#64748B",lineHeight:1.6}}>{text}</div>
          {article && (
            <div style={{marginTop:8,fontSize:10,color:"#94A3B8",fontStyle:"italic"}}>See Admin → Help for full details</div>
          )}
          <div style={{position:"absolute",top:-5,left:"50%",transform:"translateX(-50%)",width:8,height:8,background:"#fff",border:"1px solid #E2E8F0",borderTop:"none",borderRight:"none",transform:"translateX(-50%) rotate(135deg)"}}/>
        </div>
      )}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────
// ADVISOR HELP MODAL — floating ? button with walkthrough
// ─────────────────────────────────────────────────────────────
const ADVISOR_STEPS = [
  { icon:"📋", title:"Step 1 — Analyze a Statement", body:"Go to the Analyzer tab. Upload a brokerage statement (PDF or image) — the system first attempts a native scan, then offers AI review if needed. Or import via Excel/CSV, or enter holdings manually. Each source supports multiple accounts with individual account types (IRA, Roth, Taxable, Trust, etc.)." },
  { icon:"✅", title:"Step 2 — Enrich & Verify Holdings", body:"In the Review Holdings step, use '🔍 Lookup All' to auto-fill security names and asset classes, then '📡 Pull Yield & ER' to fetch yield and expense ratio data. Fix any flagged required fields inline. Cost basis is required — short positions with negative market values are supported." },
  { icon:"💾", title:"Step 3 — Save the Client", body:"Click '💾 Save Client' to store the holdings and analysis results. To edit an existing client's holdings later, go to 'My Clients', click '✏️ Edit' to load them back into the Analyzer pool, make changes, and re-save. The record updates rather than creating a duplicate." },
  { icon:"📊", title:"Step 4 — Build the Allocation", body:"On the Allocator tab, load an existing client or start fresh. Set the implementation style, Opportunistic/Defensive blend, and enter any additional new cash. Client-directed holdings can override all keep rules including non-taxable restrictions. Save your allocation with 💾 or jump to Compare with ⇄." },
  { icon:"🔒", title:"Step 5 — Kept Holdings & Overrides", body:"In the holdings panel, check eligible positions to keep. Non-taxable account positions (IRA, 401k, etc.) cannot be kept unless added as Client Directed Holdings — enter the ticker and select 'Client Directed' as the reason. This overrides all eligibility and tax-exempt rules." },
  { icon:"⇄", title:"Step 6 — Compare Current vs Proposed", body:"The Comparator shows current holdings vs proposed allocation side-by-side: asset class drift, expense ratio delta, and a buy/sell/keep breakdown. Use the version picker to compare against any previously saved allocation, not just the most recent." },
  { icon:"📈", title:"Step 7 — Build the Trading Plan", body:"The Trading page shows the transition plan across three sections: Allocation Overview (what to buy/sell/keep), Tax Overview, and Transition Strategies. Select Full Liquidation or Gradual Transition, configure a tax budget, set client cash needs for regular withdrawals, and optionally add Dollar Cost Averaging for new cash deployment." },
  { icon:"💰", title:"Step 8 — Cash Needs & DCA", body:"In Transition Strategies, set the client's regular withdrawal amount and frequency (monthly, quarterly, etc.) — proceeds are reserved for withdrawals before redeploying to the model, and the portfolio shrinks accordingly in the allocation chart. Toggle '+ Add DCA' to layer in gradual new cash deployment on top of any strategy." },
  { icon:"📄", title:"Step 9 — Notes & Save", body:"Add advisor notes at the bottom of the Trading page for any instructions, client preferences, or follow-up items. Save the allocation with the floating 💾 button — you'll be prompted if you try to navigate away with unsaved work. Notes, strategy selection, and DCA settings are preserved in-session." },
];

function AdvisorHelpModal({ onClose }) {
  const [tab,       setTab]       = useState("articles"); // "articles" | "guide"
  const [search,    setSearch]    = useState("");
  const [activecat, setActivecat] = useState("All");
  const [openId,    setOpenId]    = useState(null);
  const [step,      setStep]      = useState(0);
  const FF = "'Outfit',sans-serif";

  // Advisors see Advisor Workflow + Glossary articles only
  const ADVISOR_CATS = ["Advisor Workflow", "Glossary"];
  const articles = HELP_ARTICLES.filter(a => ADVISOR_CATS.includes(a.category));
  const cats = ["All", ...ADVISOR_CATS];

  const filtered = articles.filter(a => {
    const matchCat = activecat === "All" || a.category === activecat;
    const q = search.toLowerCase();
    const matchSearch = !q || a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q) || (a.tags||[]).some(t=>t.toLowerCase().includes(q));
    return matchCat && matchSearch;
  });

  const openArticle = openId ? articles.find(a => a.id === openId) : null;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",zIndex:300,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div style={{background:"#fff",borderRadius:16,width:"min(680px,98vw)",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 64px rgba(0,0,0,0.2)",overflow:"hidden"}} onClick={e=>e.stopPropagation()}>

        {/* Header */}
        <div style={{background:"#1E3A5F",padding:"16px 20px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontFamily:FF,fontSize:18,fontWeight:700,color:"#fff"}}>Help Center</div>
            <div style={{fontSize:11,color:"#93C5FD",marginTop:1}}>Guides and reference for advisors</div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            {/* Tab switcher */}
            <div style={{display:"flex",background:"rgba(255,255,255,0.1)",borderRadius:8,padding:3,gap:3}}>
              {[["articles","📚 Articles"],["guide","🗺 Quick Start"]].map(([id,label])=>(
                <button key={id} onClick={()=>setTab(id)}
                  style={{padding:"4px 12px",borderRadius:6,border:"none",background:tab===id?"#fff":"transparent",color:tab===id?"#1E3A5F":"#93C5FD",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
                  {label}
                </button>
              ))}
            </div>
            <button onClick={onClose} style={{background:"rgba(255,255,255,0.1)",border:"none",borderRadius:7,color:"#93C5FD",padding:"6px 10px",cursor:"pointer",fontSize:14,fontFamily:FF}}>✕</button>
          </div>
        </div>

        {/* ── ARTICLES TAB ── */}
        {tab === "articles" && !openArticle && (
          <div style={{display:"flex",flexDirection:"column",flex:1,overflow:"hidden"}}>
            {/* Search + category filter */}
            <div style={{padding:"12px 16px",borderBottom:"1px solid #F1F5F9",flexShrink:0}}>
              <input value={search} onChange={e=>setSearch(e.target.value)}
                placeholder="Search articles…"
                style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:8,padding:"8px 12px",fontSize:12,fontFamily:FF,outline:"none",background:"#F8FAFC",boxSizing:"border-box",marginBottom:10}}/>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {cats.map(cat=>{
                  const col = cat==="All"?{bg:"#F1F5F9",border:"#E2E8F0",text:"#64748B"}:HELP_CAT_COLORS[cat]||{bg:"#F1F5F9",border:"#E2E8F0",text:"#64748B"};
                  return (
                    <button key={cat} onClick={()=>setActivecat(cat)}
                      style={{padding:"3px 12px",borderRadius:20,border:`1px solid ${activecat===cat?col.border:"#E2E8F0"}`,background:activecat===cat?col.bg:"#fff",color:activecat===cat?col.text:"#94A3B8",fontSize:11,fontWeight:activecat===cat?700:500,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
                      {cat}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Article list */}
            <div style={{flex:1,overflowY:"auto",padding:"8px 16px 16px"}}>
              {filtered.length === 0 && (
                <div style={{textAlign:"center",padding:"40px 0",color:"#94A3B8",fontSize:13}}>No articles match your search.</div>
              )}
              {filtered.map(a => {
                const col = HELP_CAT_COLORS[a.category]||{bg:"#F1F5F9",border:"#E2E8F0",text:"#64748B"};
                return (
                  <div key={a.id} onClick={()=>setOpenId(a.id)}
                    style={{padding:"12px 14px",border:"1px solid #E2E8F0",borderRadius:10,marginBottom:8,cursor:"pointer",transition:"all 0.15s",background:"#fff"}}
                    onMouseEnter={e=>e.currentTarget.style.borderColor="#93C5FD"}
                    onMouseLeave={e=>e.currentTarget.style.borderColor="#E2E8F0"}>
                    <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                      <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,background:col.bg,color:col.text,border:`1px solid ${col.border}`}}>{a.category}</span>
                      {(a.tags||[]).slice(0,2).map(t=>(
                        <span key={t} style={{fontSize:9,color:"#94A3B8",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:4,padding:"1px 6px"}}>{t}</span>
                      ))}
                    </div>
                    <div style={{fontFamily:FF,fontSize:13,fontWeight:700,color:"#1E3A5F",marginBottom:3}}>{a.title}</div>
                    <div style={{fontSize:11,color:"#64748B",lineHeight:1.5}}>{a.summary}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── ARTICLE DETAIL ── */}
        {tab === "articles" && openArticle && (
          <div style={{flex:1,overflowY:"auto",padding:"16px 20px"}}>
            <button onClick={()=>setOpenId(null)}
              style={{background:"none",border:"none",color:"#2563EB",fontSize:12,cursor:"pointer",fontFamily:FF,padding:0,marginBottom:12,display:"flex",alignItems:"center",gap:4}}>
              ← Back to articles
            </button>
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              {(() => { const col=HELP_CAT_COLORS[openArticle.category]||{bg:"#F1F5F9",border:"#E2E8F0",text:"#64748B"}; return (
                <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,background:col.bg,color:col.text,border:`1px solid ${col.border}`}}>{openArticle.category}</span>
              ); })()}
            </div>
            <div style={{fontFamily:FF,fontSize:18,fontWeight:700,color:"#1E3A5F",marginBottom:6}}>{openArticle.title}</div>
            <div style={{fontSize:12,color:"#64748B",marginBottom:16,fontStyle:"italic"}}>{openArticle.summary}</div>
            <div style={{fontSize:13,color:"#334155",lineHeight:1.8,whiteSpace:"pre-wrap",borderTop:"1px solid #F1F5F9",paddingTop:16}}>{openArticle.body}</div>
            {openArticle.note && (
              <div style={{marginTop:16,padding:"10px 14px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,fontSize:12,color:"#2563EB",lineHeight:1.6}}>
                💡 {openArticle.note}
              </div>
            )}
          </div>
        )}

        {/* ── QUICK START GUIDE TAB ── */}
        {tab === "guide" && (
          <div style={{flex:1,display:"flex",flexDirection:"column",overflow:"hidden"}}>
            {/* Step dots */}
            <div style={{display:"flex",justifyContent:"center",gap:6,padding:"14px 0 0",flexShrink:0}}>
              {ADVISOR_STEPS.map((_,i) => (
                <button key={i} onClick={()=>setStep(i)}
                  style={{width:i===step?20:8,height:8,borderRadius:4,border:"none",background:i===step?"#2563EB":"#E2E8F0",cursor:"pointer",padding:0,transition:"all 0.2s"}}/>
              ))}
            </div>
            {/* Step content */}
            <div style={{flex:1,padding:"20px 28px 24px",overflowY:"auto"}}>
              <div style={{fontSize:32,marginBottom:10,textAlign:"center"}}>{ADVISOR_STEPS[step].icon}</div>
              <div style={{fontFamily:FF,fontSize:17,fontWeight:700,color:"#1E3A5F",marginBottom:10,textAlign:"center"}}>{ADVISOR_STEPS[step].title}</div>
              <div style={{fontSize:13,color:"#475569",lineHeight:1.8,textAlign:"center",maxWidth:440,margin:"0 auto"}}>{ADVISOR_STEPS[step].body}</div>
            </div>
            {/* Footer nav */}
            <div style={{padding:"12px 20px 18px",display:"flex",alignItems:"center",justifyContent:"space-between",borderTop:"1px solid #E2E8F0",flexShrink:0}}>
              <button onClick={()=>setStep(s=>Math.max(0,s-1))} disabled={step===0}
                style={{background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:step===0?"#CBD5E1":"#64748B",padding:"7px 16px",cursor:step===0?"not-allowed":"pointer",fontSize:12,fontFamily:FF}}>
                ← Back
              </button>
              <span style={{fontSize:11,color:"#94A3B8"}}>{step+1} of {ADVISOR_STEPS.length}</span>
              {step < ADVISOR_STEPS.length-1 ? (
                <button onClick={()=>setStep(s=>s+1)}
                  style={{background:"#1E3A5F",border:"none",borderRadius:7,color:"#fff",padding:"7px 16px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>
                  Next →
                </button>
              ) : (
                <button onClick={onClose}
                  style={{background:"#059669",border:"none",borderRadius:7,color:"#fff",padding:"7px 16px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:FF}}>
                  Got it ✓
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// PORTAL SCREEN  (landing — advisor or admin)
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// SHARE CLIENT MODAL
// ─────────────────────────────────────────────────────────────
// Resolves a PII token to the real name for display purposes
function ClientName({ token, fallback="—" }) {
  const { resolveName } = usePIIVault();
  return <span>{resolveName(token) || fallback}</span>;
}

function ShareClientModal({ client, engine, currentUser, onClose, onShare }) {
  const [mode,       setMode]       = useState("shared");   // "shared" | "transfer"
  const [targetId,   setTargetId]   = useState("");
  const [submitted,  setSubmitted]  = useState(false);

  // Advisors can only share with other advisors; admins can share with anyone
  const candidates = (engine.employees || []).filter(e =>
    e.id !== currentUser.id &&
    (currentUser.role === "admin" || e.role === "advisor")
  );
  const target = candidates.find(e => e.id === targetId);

  const handleShare = () => {
    if (!target) return;
    onShare(client, target, mode);
    setSubmitted(true);
    setTimeout(onClose, 1400);
  };

  const FF = "'Outfit',sans-serif";
  const btnBase = { border:"none", borderRadius:9, padding:"10px 0", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FF, width:"100%" };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.5)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(3px)"}}>
      <div style={{background:"#fff",borderRadius:16,width:"min(420px,96vw)",boxShadow:"0 20px 60px rgba(0,0,0,0.18)",overflow:"hidden"}}>

        {/* Header */}
        <div style={{background:"linear-gradient(135deg,#1E3A5F,#0F2240)",padding:"20px 24px"}}>
          <div style={{fontSize:17,fontWeight:700,color:"#fff",fontFamily:FF,marginBottom:3}}>Share Client</div>
          <div style={{fontSize:12,color:"#93C5FD",fontFamily:FF}}><ClientName token={client.name}/> · {fmt$(client.totalPortfolioValue)}</div>
        </div>

        {submitted ? (
          <div style={{padding:"40px 24px",textAlign:"center"}}>
            <div style={{fontSize:32,marginBottom:10}}>{mode==="transfer"?"🔄":"🤝"}</div>
            <div style={{fontWeight:700,fontSize:14,color:"#059669",fontFamily:FF}}>
              {mode==="transfer" ? `Transferred to ${target?.name}` : `Shared with ${target?.name}`}
            </div>
          </div>
        ) : (
          <div style={{padding:"24px"}}>

            {/* Mode picker */}
            <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:10,fontFamily:FF}}>Share type</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:20}}>
              {[
                { key:"shared",   icon:"🤝", title:"Shared Access", desc:"Both advisors can view and load this client" },
                { key:"transfer", icon:"🔄", title:"Transfer",      desc:"Move client to another advisor — removes your access" },
              ].map(opt => (
                <button key={opt.key} onClick={()=>setMode(opt.key)}
                  style={{padding:"12px 10px",borderRadius:10,border:`2px solid ${mode===opt.key?"#2563EB":"#E2E8F0"}`,background:mode===opt.key?"#EFF6FF":"#F8FAFC",cursor:"pointer",fontFamily:FF,textAlign:"left",transition:"all 0.15s"}}>
                  <div style={{fontSize:18,marginBottom:4}}>{opt.icon}</div>
                  <div style={{fontWeight:700,fontSize:12,color:mode===opt.key?"#2563EB":"#334155",marginBottom:2}}>{opt.title}</div>
                  <div style={{fontSize:10,color:"#94A3B8",lineHeight:1.4}}>{opt.desc}</div>
                </button>
              ))}
            </div>

            {/* Advisor picker */}
            <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:8,fontFamily:FF}}>
              {mode==="transfer"?"Transfer to":"Share with"}
            </div>
            {candidates.length === 0 ? (
              <div style={{fontSize:12,color:"#94A3B8",padding:"12px 0",fontFamily:FF}}>No other advisors available.</div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:20,maxHeight:180,overflowY:"auto"}}>
                {candidates.map(emp => (
                  <button key={emp.id} onClick={()=>setTargetId(emp.id)}
                    style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",borderRadius:9,border:`2px solid ${targetId===emp.id?"#2563EB":"#E2E8F0"}`,background:targetId===emp.id?"#EFF6FF":"#fff",cursor:"pointer",fontFamily:FF,textAlign:"left",transition:"all 0.12s"}}>
                    <div style={{width:30,height:30,borderRadius:"50%",background:targetId===emp.id?"#2563EB":"#E2E8F0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:targetId===emp.id?"#fff":"#64748B",flexShrink:0}}>
                      {emp.name.split(" ").map(n=>n[0]).join("")}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:700,fontSize:12,color:"#1E3A5F"}}>{emp.name}</div>
                      <div style={{fontSize:10,color:"#94A3B8",textTransform:"capitalize"}}>{emp.role}</div>
                    </div>
                    {targetId===emp.id && <span style={{color:"#2563EB",fontSize:14}}>✓</span>}
                  </button>
                ))}
              </div>
            )}

            {/* Warning for transfer */}
            {mode==="transfer" && targetId && (
              <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8,padding:"8px 12px",marginBottom:16,fontSize:11,color:"#92400E",fontFamily:FF}}>
                ⚠ You will lose access to this client after transfer.
              </div>
            )}

            {/* Actions */}
            <div style={{display:"flex",gap:10}}>
              <button onClick={onClose} style={{...btnBase,background:"#F1F5F9",color:"#64748B",fontWeight:600}}>Cancel</button>
              <button onClick={handleShare} disabled={!targetId}
                style={{...btnBase,background:targetId?"#1E3A5F":"#E2E8F0",color:targetId?"#fff":"#94A3B8",cursor:targetId?"pointer":"not-allowed"}}>
                {mode==="transfer"?"Transfer →":"Share →"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ADVISOR CLIENTS VIEW
// ─────────────────────────────────────────────────────────────
function AdvisorClientsView({ engine, setEngine, currentUser, sbClients=[], loadClients=()=>{}, onLoadClient, addAudit=()=>{} }) {
  const { resolveName, resolveAcct } = usePIIVault();
  const rn = t => resolveName(t) || t;
  const [search,    setSearch]    = useState("");
  const [sortBy,    setSortBy]    = useState("name");
  const [sortDir,   setSortDir]   = useState("asc");
  const [confirmId, setConfirmId] = useState(null);
  const [shareClient, setShareClient] = useState(null);  // client object being shared

  // Advisors see own + shared-with-them; sharedWith is array of advisorIds
  const myClients = (sbClients || []).filter(c =>
    c.advisorId === currentUser.id || (c.sharedWith || []).includes(currentUser.id)
  );

  const deleteClient = (id) => {
    const c = (sbClients||[]).find(x=>x.id===id);
    sbDeleteClient(id, currentUser?.firm_id).then(() => loadClients(currentUser?.firm_id));
    if (c) addAudit("client_deleted", `Client deleted: ${rn(c.name)} (${fmt$(c.totalPortfolioValue)})`);
    setConfirmId(null);
  };

  const handleShare = (client, target, mode) => {
    let updates;
    if (mode === "transfer") {
      addAudit("client_shared", `Client "${rn(client.name)}" transferred to ${target.name}`);
      updates = { advisorId: target.id, advisorName: target.name, sharedWith: [] };
    } else {
      const sw = [...new Set([...(client.sharedWith||[]), target.id])];
      addAudit("client_shared", `Client "${rn(client.name)}" shared with ${target.name}`);
      updates = { sharedWith: sw };
    }
    sbUpdateClient(client.id, updates, currentUser?.firm_id).then(() => loadClients(currentUser?.firm_id));
  };

  const removeSharedAccess = (clientId, advisorId) => {
    const client = sbClients.find(c => c.id === clientId);
    if (!client) return;
    const updates = { sharedWith: (client.sharedWith||[]).filter(id => id !== advisorId) };
    sbUpdateClient(clientId, updates, currentUser?.firm_id).then(() => loadClients(currentUser?.firm_id));
  };

  const filtered = myClients.filter(c =>
    !search || rn(c.name)?.toLowerCase().includes(search.toLowerCase()) || c.custodian?.toLowerCase().includes(search.toLowerCase()) || c.accountType?.toLowerCase().includes(search.toLowerCase())
  );

  const sorted = [...filtered].sort((a, b) => {
    let av, bv;
    if (sortBy === "value") { av = a.totalPortfolioValue||0; bv = b.totalPortfolioValue||0; }
    else if (sortBy === "date") { av = new Date(a.savedAt||0).getTime(); bv = new Date(b.savedAt||0).getTime(); }
    else { av = (a.name||"").toLowerCase(); bv = (b.name||"").toLowerCase(); }
    return sortDir === "asc" ? (av < bv ? -1 : av > bv ? 1 : 0) : (av > bv ? -1 : av < bv ? 1 : 0);
  });

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("asc"); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <span style={{color:"#CBD5E1",fontSize:9}}>⇅</span>;
    return <span style={{color:"#2563EB",fontSize:9}}>{sortDir==="asc"?"↑":"↓"}</span>;
  };

  const thS = { padding:"10px 16px", fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", background:"#F8FAFC", borderBottom:"2px solid #E2E8F0", textAlign:"left", whiteSpace:"nowrap", userSelect:"none" };
  const tdS = { padding:"12px 16px", borderBottom:"1px solid #F4F6F9", fontSize:12, verticalAlign:"middle" };

  const ownCount    = myClients.filter(c=>c.advisorId===currentUser.id).length;
  const sharedCount = myClients.filter(c=>c.advisorId!==currentUser.id).length;

  return (
    <div style={{maxWidth:1000,margin:"0 auto",padding:"24px 24px 56px"}}>
      {shareClient && (
        <ShareClientModal
          client={shareClient}
          engine={engine}
          currentUser={currentUser}
          onClose={()=>setShareClient(null)}
          onShare={handleShare}
        />
      )}

      {/* Header */}
      <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:24,flexWrap:"wrap"}}>
        <div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:700,color:"#1E3A5F"}}>My Clients</div>
          <div style={{fontSize:12,color:"#94A3B8",marginTop:2,display:"flex",gap:10}}>
            <span>{ownCount} owned</span>
            {sharedCount > 0 && <span style={{color:"#7C3AED"}}>· {sharedCount} shared with me</span>}
          </div>
        </div>
        <div style={{flex:1,minWidth:200}}>
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search by name, custodian, account type…"
            style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:9,padding:"9px 14px",fontSize:12,outline:"none",fontFamily:"'Outfit',sans-serif",color:"#1E293B",boxSizing:"border-box",background:"#fff"}}/>
        </div>
      </div>

      {myClients.length === 0 ? (
        <div style={{textAlign:"center",padding:"80px 24px",background:"#F8FAFC",borderRadius:14,border:"1px solid #E2E8F0"}}>
          <div style={{fontSize:32,marginBottom:12}}>👤</div>
          <div style={{fontWeight:700,fontSize:15,color:"#1E3A5F",marginBottom:6}}>No clients saved yet</div>
          <div style={{fontSize:13,color:"#64748B"}}>Run a Portfolio Analysis and save a client to see them here.</div>
        </div>
      ) : sorted.length === 0 ? (
        <div style={{textAlign:"center",padding:"60px 24px",color:"#94A3B8",fontSize:13}}>No clients match your search.</div>
      ) : (
        <div style={{border:"1px solid #E2E8F0",borderRadius:14,overflow:"hidden",background:"#fff"}}>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",minWidth:640}}>
              <thead>
                <tr>
                  <th style={{...thS,cursor:"pointer"}} onClick={()=>toggleSort("name")}>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>Client <SortIcon col="name"/></div>
                  </th>
                  <th style={thS}>Account</th>
                  <th style={{...thS,cursor:"pointer"}} onClick={()=>toggleSort("value")}>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>Portfolio Value <SortIcon col="value"/></div>
                  </th>
                  <th style={thS}>Positions</th>
                  <th style={thS}>Custodian</th>
                  <th style={{...thS,cursor:"pointer"}} onClick={()=>toggleSort("date")}>
                    <div style={{display:"flex",alignItems:"center",gap:5}}>Saved <SortIcon col="date"/></div>
                  </th>
                  <th style={{...thS,textAlign:"center"}}>Shared With</th>
                  <th style={{...thS,textAlign:"center"}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((c, ri) => {
                  const isOwner  = c.advisorId === currentUser.id;
                  const isSharedToMe = !isOwner;
                  const owner    = engine.employees?.find(e=>e.id===c.advisorId);
                  const sharedWith = (c.sharedWith||[]).map(id=>engine.employees?.find(e=>e.id===id)).filter(Boolean);
                  return (
                    <tr key={c.id} style={{background:ri%2===0?"#fff":"#FAFBFC",transition:"background 0.1s"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#EFF6FF"}
                      onMouseLeave={e=>e.currentTarget.style.background=ri%2===0?"#fff":"#FAFBFC"}>

                      <td style={tdS}>
                        <div style={{display:"flex",alignItems:"center",gap:10}}>
                          <div style={{width:34,height:34,borderRadius:"50%",background:isSharedToMe?"#F5F3FF":"#EFF6FF",border:`1px solid ${isSharedToMe?"#DDD6FE":"#BFDBFE"}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:800,color:isSharedToMe?"#7C3AED":"#2563EB",flexShrink:0}}>
                            {(rn(c.name)||"?")[0].toUpperCase()}
                          </div>
                          <div>
                            <div style={{fontWeight:700,color:"#1E3A5F",fontSize:13}}>{rn(c.name)||"Unnamed"}</div>
                            <div style={{display:"flex",gap:5,marginTop:2,alignItems:"center"}}>
                              {c.accountNumber && <span style={{fontSize:10,color:"#94A3B8"}}>Acct {resolveAcct(c.accountNumber)}</span>}
                              {(c.savedAllocations||[]).length>0&&(
                                <span style={{fontSize:9,padding:"1px 6px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:6,color:"#2563EB",fontWeight:700}}>📊 {c.savedAllocations.length}</span>
                              )}
                              {isSharedToMe && <span style={{fontSize:9,padding:"1px 6px",borderRadius:8,background:"#F5F3FF",color:"#7C3AED",fontWeight:700,border:"1px solid #DDD6FE"}}>shared by {owner?.name?.split(" ")[0]||"?"}</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td style={tdS}>
                        <span style={{fontSize:11,padding:"2px 8px",borderRadius:10,background:"#F1F5F9",color:"#475569",fontWeight:600}}>
                          {c.accountType||"—"}
                        </span>
                      </td>

                      <td style={{...tdS,fontWeight:700,color:"#1E3A5F"}}>{fmt$(c.totalPortfolioValue)}</td>
                      <td style={{...tdS,color:"#64748B"}}>{c.holdings?.length||0}</td>
                      <td style={{...tdS,color:"#64748B"}}>{c.custodian||"—"}</td>

                      <td style={{...tdS,color:"#94A3B8",fontSize:11}}>
                        {c.savedAt ? new Date(c.savedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"}) : "—"}
                      </td>

                      {/* Shared with pills */}
                      <td style={{...tdS,textAlign:"center"}}>
                        {isOwner && sharedWith.length > 0 ? (
                          <div style={{display:"flex",gap:4,flexWrap:"wrap",justifyContent:"center"}}>
                            {sharedWith.map(emp=>(
                              <span key={emp.id} style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:10,padding:"2px 7px",borderRadius:10,background:"#F5F3FF",border:"1px solid #DDD6FE",color:"#7C3AED",fontWeight:600}}>
                                {emp.name.split(" ")[0]}
                                <button onClick={()=>removeSharedAccess(c.id,emp.id)}
                                  style={{background:"none",border:"none",color:"#C4B5FD",cursor:"pointer",fontSize:11,lineHeight:1,padding:0,fontWeight:700}}
                                  title={`Remove ${emp.name}'s access`}
                                  onMouseEnter={e=>e.target.style.color="#7C3AED"}
                                  onMouseLeave={e=>e.target.style.color="#C4B5FD"}>×</button>
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span style={{fontSize:10,color:"#CBD5E1"}}>—</span>
                        )}
                      </td>

                      {/* Actions */}
                      <td style={{...tdS,textAlign:"center"}}>
                        {confirmId === c.id ? (
                          <div style={{display:"flex",gap:6,justifyContent:"center",alignItems:"center"}}>
                            <span style={{fontSize:11,color:"#DC2626",fontWeight:600,whiteSpace:"nowrap"}}>Delete?</span>
                            <button onClick={()=>deleteClient(c.id)}
                              style={{background:"#DC2626",border:"none",borderRadius:6,color:"#fff",padding:"4px 10px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Yes</button>
                            <button onClick={()=>setConfirmId(null)}
                              style={{background:"none",border:"1px solid #E2E8F0",borderRadius:6,color:"#64748B",padding:"4px 10px",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>No</button>
                          </div>
                        ) : (
                          <div style={{display:"flex",gap:5,justifyContent:"center"}}>
                            <button onClick={()=>onLoadClient(c)}
                              style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:7,color:"#2563EB",padding:"5px 11px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
                              Load →
                            </button>
                            {isOwner && (
                              <button onClick={()=>setShareClient(c)}
                                style={{background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:7,color:"#7C3AED",padding:"5px 9px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}
                                title="Share or transfer client">
                                ↗
                              </button>
                            )}
                            {isOwner && (
                              <button onClick={()=>setConfirmId(c.id)}
                                style={{background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:7,color:"#DC2626",padding:"5px 9px",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                                🗑
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer count */}
          <div style={{padding:"10px 16px",background:"#F8FAFC",borderTop:"1px solid #E2E8F0",fontSize:11,color:"#94A3B8",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span>Showing {sorted.length} of {myClients.length} client{myClients.length!==1?"s":""}</span>
            {search && <button onClick={()=>setSearch("")} style={{background:"none",border:"none",color:"#94A3B8",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Clear search</button>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SET PASSWORD SCREEN — shown to new invited users on first login
// ─────────────────────────────────────────────────────────────
function SetPasswordScreen({ currentUser, onComplete }) {
  const [password, setPassword] = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [loading,  setLoading]  = useState(false);
  const [err,      setErr]      = useState("");
  const FF = "'Outfit',sans-serif";

  const handleSubmit = async () => {
    if (password.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (password !== confirm)  { setErr("Passwords do not match."); return; }
    setLoading(true);
    setErr("");
    await onComplete(password);
    setLoading(false);
  };

  return (
    <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F4F7FC",fontFamily:FF}}>
      <div style={{width:"100%",maxWidth:420,padding:"0 16px"}}>
        <div style={{textAlign:"center",marginBottom:32}}>
          <div style={{fontSize:28,fontWeight:800,color:"#1E3A5F",letterSpacing:"-0.5px"}}>Welcome!</div>
          <div style={{fontSize:14,color:"#64748B",marginTop:8}}>Set a password to secure your account</div>
          <div style={{fontSize:12,color:"#94A3B8",marginTop:4}}>{currentUser?.email}</div>
        </div>
        <div style={{background:"#fff",borderRadius:16,padding:32,boxShadow:"0 4px 24px rgba(0,0,0,0.06)",border:"1px solid #E2E8F0"}}>
          <div style={{marginBottom:16}}>
            <label style={{display:"block",fontSize:11,fontWeight:700,color:"#64748B",letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:6}}>New Password</label>
            <input type="password" value={password} onChange={e=>setPassword(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
              placeholder="At least 8 characters"
              style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:9,padding:"12px 14px",fontSize:14,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
          </div>
          <div style={{marginBottom:20}}>
            <label style={{display:"block",fontSize:11,fontWeight:700,color:"#64748B",letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:6}}>Confirm Password</label>
            <input type="password" value={confirm} onChange={e=>setConfirm(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&handleSubmit()}
              placeholder="Repeat password"
              style={{width:"100%",border:`1px solid ${err?"#FCA5A5":"#E2E8F0"}`,borderRadius:9,padding:"12px 14px",fontSize:14,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
          </div>
          {err && <div style={{fontSize:12,color:"#DC2626",marginBottom:12,padding:"8px 12px",background:"#FFF5F5",borderRadius:7,border:"1px solid #FCA5A5"}}>{err}</div>}
          <button onClick={handleSubmit} disabled={loading||!password||!confirm}
            style={{width:"100%",background:loading||!password||!confirm?"#94A3B8":"#1E3A5F",border:"none",borderRadius:9,color:"#fff",padding:"13px 0",fontSize:14,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:FF}}>
            {loading ? "Setting password…" : "Set Password & Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoginForm({ title, subtitle, accentColor, onSubmit, requireAdmin }) {
  const [email,    setEmail]    = useState("");
  const [password, setPassword] = useState("");
  const [err,      setErr]      = useState("");
  const [loading,  setLoading]  = useState(false);
  const FF = "'Outfit',sans-serif";

  const submit = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (error) {
        setErr(error.message || "Invalid email or password.");
        setLoading(false);
        return;
      }

      // Read profile from user metadata (no RLS needed)
      const meta = data.user.user_metadata || {};
      let profile = null;

      if (meta.role) {
        // Profile data is in user metadata — use it directly
        // Platform admin has null firm_id which is valid
        profile = {
          id:       data.user.id,
          email:    data.user.email,
          name:     meta.name || data.user.email,
          role:     meta.role,
          firm_id:  meta.firm_id || null,
        };
      } else {
        // Fall back to profiles table query
        const { data: profileData, error: profileErr } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", data.user.id)
          .single();
        if (profileErr || !profileData) {
          setErr("Account not found. Contact your administrator.");
          await supabase.auth.signOut();
          setLoading(false);
          return;
        }
        profile = profileData;
      }

      if (!profile) {
        setErr("Account not found. Contact your administrator.");
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }
      if (requireAdmin && !["admin","platform"].includes(profile.role)) {
        setErr("This account does not have admin access.");
        await supabase.auth.signOut();
        setLoading(false);
        return;
      }
      onSubmit({ ...profile, email: data.user.email });
    } catch(e) {
      setErr("Login failed. Please try again.");
    }
    setLoading(false);
  };

  const fieldStyle = {
    width: "100%",
    boxSizing: "border-box",
    border: "1.5px solid #E2E8F0",
    borderRadius: 9,
    padding: "11px 14px",
    fontSize: 14,
    fontFamily: FF,
    color: "#1E3A5F",
    outline: "none",
    background: "#F8FAFC",
    transition: "border-color 0.15s, background 0.15s",
  };

  const errFieldStyle = { ...fieldStyle, borderColor: "#FCA5A5", background: "#FFF5F5" };

  return (
    <div style={{display:"flex",flexDirection:"column",gap:14,fontFamily:FF}}>

      {/* Email */}
      <div>
        <label style={{display:"block",fontSize:11,fontWeight:700,color:"#64748B",letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:6,fontFamily:FF}}>
          Email
        </label>
        <input
          type="email"
          value={email}
          onChange={e=>setEmail(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&submit()}
          placeholder="you@example.com"
          autoComplete="email"
          style={err ? errFieldStyle : fieldStyle}
          onFocus={e=>{e.target.style.borderColor=accentColor;e.target.style.background="#fff";}}
          onBlur={e=>{e.target.style.borderColor=err?"#FCA5A5":"#E2E8F0";e.target.style.background=err?"#FFF5F5":"#F8FAFC";}}
        />
      </div>

      {/* Password */}
      <div>
        <label style={{display:"block",fontSize:11,fontWeight:700,color:"#64748B",letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:6,fontFamily:FF}}>
          Password
        </label>
        <input
          type="password"
          value={password}
          onChange={e=>setPassword(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&submit()}
          placeholder="••••••••"
          autoComplete="current-password"
          style={err ? errFieldStyle : fieldStyle}
          onFocus={e=>{e.target.style.borderColor=accentColor;e.target.style.background="#fff";}}
          onBlur={e=>{e.target.style.borderColor=err?"#FCA5A5":"#E2E8F0";e.target.style.background=err?"#FFF5F5":"#F8FAFC";}}
        />
      </div>

      {/* Error message */}
      {err&&(
        <div style={{display:"flex",alignItems:"center",gap:6,padding:"8px 12px",background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:7}}>
          <span style={{fontSize:13}}>⚠</span>
          <span style={{fontSize:12,color:"#DC2626",fontWeight:600,fontFamily:FF}}>{err}</span>
        </div>
      )}

      {/* Submit */}
      <button
        onClick={submit}
        disabled={loading}
        style={{width:"100%",background:accentColor,border:"none",borderRadius:9,color:"#fff",padding:"12px 0",fontSize:14,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:FF,letterSpacing:"0.3px",transition:"opacity 0.15s",marginTop:2,opacity:loading?0.7:1}}
        onMouseEnter={e=>!loading&&(e.currentTarget.style.opacity="0.88")}
        onMouseLeave={e=>e.currentTarget.style.opacity=loading?"0.7":"1"}
      >
        {loading ? "Signing in…" : "Sign In →"}
      </button>
    </div>
  );
}

function PortalScreen({ engine, platform, activeFirmId, onFirmSelect, onAdvisorLogin, onAdminLogin, onPlatformLogin }) {
  const { isMobile } = useBreakpoint();
  const [role, setRole]             = useState("advisor");
  const [showPlatform, setShowPlatform] = useState(false);
  const [platCode, setPlatCode]     = useState("");
  const [platUser, setPlatUser]     = useState("");
  const [platPass, setPlatPass]     = useState("");
  const [platError, setPlatError]   = useState("");
  const FF = "'Outfit',sans-serif";

  const roles = [
    { key:"advisor", label:"Advisor",   icon:"📊", color:"#2563EB", desc:"Portfolio allocation & client tools" },
    { key:"admin",   label:"Admin",     icon:"⚙️", color:"#1E3A5F", desc:"Engine configuration & analytics"   },
  ];
  const active = roles.find(r=>r.key===role);

  const handlePlatformLogin = async () => {
    if (platCode !== PLATFORM_ACCESS_CODE) { setPlatError("Invalid access code"); return; }
    setPlatError("");
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: platUser.trim().toLowerCase(),
        password: platPass,
      });
      if (error) { setPlatError("Invalid credentials"); return; }
      const meta = data.user.user_metadata || {};
      if (meta.role !== "platform") { setPlatError("This account does not have platform access."); await supabase.auth.signOut(); return; }
      const profile = { id: data.user.id, email: data.user.email, name: meta.name || "Platform Admin", role: "platform", firm_id: null };
      onPlatformLogin(profile);
    } catch(e) {
      setPlatError("Login failed. Please try again.");
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(160deg,#0F2240 0%,#1E3A5F 55%,#1a3660 100%)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",fontFamily:FF,padding:isMobile?"20px":"32px"}}>
      <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet"/>

      {/* Single login card */}
      <div style={{width:"100%",maxWidth:420,background:"#fff",borderRadius:20,boxShadow:"0 24px 64px rgba(0,0,0,0.22)",overflow:"hidden",fontFamily:FF}}>

        {/* Card top band — firm branding */}
        <div style={{background:"linear-gradient(135deg,#1E3A5F,#0F2240)",padding:isMobile?"20px 20px 18px":"28px 32px 24px",textAlign:"center"}}>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:isMobile?24:28,fontWeight:700,color:"#fff",letterSpacing:"0.3px",lineHeight:1.2}}>
            {engine.firmName}
          </div>
          <div style={{fontSize:11,color:"#93C5FD",marginTop:6,letterSpacing:"1.5px",textTransform:"uppercase",fontWeight:500}}>
            Advisor Intelligence Platform
          </div>
          <div style={{width:36,height:2,background:"linear-gradient(to right,#2563EB,#7C3AED)",borderRadius:2,margin:"12px auto 0"}}/>
          {(platform?.firms||[]).filter(f=>f.active).length > 1 && (
            <select value={activeFirmId} onChange={e=>onFirmSelect(e.target.value)}
              style={{marginTop:14,background:"rgba(255,255,255,0.1)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:7,color:"#fff",padding:"6px 12px",fontSize:12,fontFamily:FF,outline:"none",cursor:"pointer",width:"100%"}}>
              {platform.firms.filter(f=>f.active).map(f=>(
                <option key={f.id} value={f.id} style={{background:"#1E3A5F"}}>{f.firmName}</option>
              ))}
            </select>
          )}
        </div>

        {/* Role selector */}
        <div style={{padding:isMobile?"16px 16px 0":"24px 32px 0"}}>
          <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"1px",marginBottom:10,fontFamily:FF}}>Sign in as</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:24,maxWidth:"100%"}}>
            {roles.map(r=>(
              <button key={r.key} onClick={()=>setRole(r.key)}
                style={{padding:"12px 10px",borderRadius:10,border:`2px solid ${role===r.key?r.color:"#E2E8F0"}`,background:role===r.key?r.color+"0C":"#F8FAFC",cursor:"pointer",fontFamily:FF,transition:"all 0.15s",textAlign:"center"}}>
                <div style={{fontSize:20,marginBottom:4}}>{r.icon}</div>
                <div style={{fontWeight:700,fontSize:13,color:role===r.key?r.color:"#64748B",fontFamily:FF}}>{r.label}</div>
                <div style={{fontSize:10,color:"#94A3B8",marginTop:2,lineHeight:1.4,fontFamily:FF}}>{r.desc}</div>
              </button>
            ))}
          </div>

          {/* Login form */}
          <LoginForm
            key={role}
            accentColor={active.color}
            requireAdmin={role==="admin"}
           
            onSubmit={role==="advisor" ? onAdvisorLogin : onAdminLogin}
          />
        </div>

        <div style={{padding:"16px 32px 24px",textAlign:"center"}}>
          <div style={{fontSize:11,color:"#CBD5E1",letterSpacing:"0.3px",fontFamily:FF}}>Access restricted to authorized personnel</div>
          <button onClick={()=>{setShowPlatform(p=>!p);setPlatError("");}}
            style={{marginTop:10,background:"none",border:"none",color:"#475569",fontSize:10,cursor:"pointer",fontFamily:FF,letterSpacing:"0.5px"}}>
            {showPlatform?"▲ Hide":"⚙ Platform Access"}
          </button>
          {showPlatform&&(
            <div style={{marginTop:12,padding:"14px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:10,textAlign:"left"}}>
              <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:10}}>Platform Admin Login</div>
              <input value={platCode} onChange={e=>setPlatCode(e.target.value)} placeholder="Access Code"
                style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:6,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",marginBottom:7,boxSizing:"border-box"}}/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:7,marginBottom:7}}>
                <input value={platUser} onChange={e=>setPlatUser(e.target.value)} placeholder="Email" type="email"
                  style={{border:"1px solid #E2E8F0",borderRadius:6,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none"}}/>
                <input value={platPass} onChange={e=>setPlatPass(e.target.value)} placeholder="Password" type="password"
                  style={{border:"1px solid #E2E8F0",borderRadius:6,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none"}}/>
              </div>
              {platError&&<div style={{fontSize:11,color:"#DC2626",marginBottom:7}}>{platError}</div>}
              <button onClick={handlePlatformLogin}
                style={{width:"100%",padding:"8px",background:"#1E3A5F",border:"none",borderRadius:7,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:FF}}>
                Access Platform →
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// IMPLEMENTATION STYLE SELECTOR
// ─────────────────────────────────────────────────────────────
function ImplStyleSelector({ value, onChange }) {
  const engine = useEngine();
  const { isMobile } = useBreakpoint();
  return (
    <div style={{marginBottom:20}}>
      <div style={sLabel}>Implementation Style</div>
      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"repeat(3,1fr)",gap:8}}>
        {Object.entries(engine.implementationStyles).map(([key,s]) => (
          <button key={key} onClick={() => onChange(key)}
            style={{padding:"12px 14px",borderRadius:10,cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all 0.15s",textAlign:"left",
              border:value===key?`2px solid ${s.color}`:"2px solid #E2E8F0",
              background:value===key?s.color+"0D":"#fff"}}>
            <div style={{fontWeight:700,fontSize:13,color:value===key?s.color:"#64748B",marginBottom:2}}>{key}</div>
            <div style={{fontSize:11,color:"#94A3B8",lineHeight:1.4}}>{s.description}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BLEND RATIO SELECTOR
// ─────────────────────────────────────────────────────────────
function BlendSelector({ value, onChange }) {
  const engine = useEngine();
  const { isMobile } = useBreakpoint();
  const opp = engine.sleeves.opportunistic;
  const def = engine.sleeves.defensive;
  const steps = [100,90,80,70,60,50,40,30,20,10,0];

  // Color interpolates from opp.color → def.color
  const blendColor = (oppPct) => {
    const t = oppPct / 100;
    const hex2rgb = h => [parseInt(h.slice(1,3),16), parseInt(h.slice(3,5),16), parseInt(h.slice(5,7),16)];
    const rgb2hex = ([r,g,b]) => "#"+[r,g,b].map(x=>Math.round(x).toString(16).padStart(2,"0")).join("");
    const [r1,g1,b1] = hex2rgb(opp.color);
    const [r2,g2,b2] = hex2rgb(def.color);
    return rgb2hex([r1*t+r2*(1-t), g1*t+g2*(1-t), b1*t+b2*(1-t)]);
  };

  return (
    <div style={{marginBottom:20}}>
      <div style={sLabel}>Blend Ratio — Opportunistic / Defensive</div>

      {/* Visual slider track */}
      <div style={{position:"relative",marginBottom:16}}>
        <div style={{height:8,borderRadius:4,background:`linear-gradient(to right, ${opp.color}, ${def.color})`,marginBottom:12}}/>
        <div style={{display:"grid",gridTemplateColumns:"repeat(11,1fr)",gap:isMobile?2:4}}>
          {steps.map(oppPct => {
            const defPct = 100 - oppPct;
            const active = value === oppPct;
            const color  = blendColor(oppPct);
            return (
              <button key={oppPct} onClick={() => onChange(oppPct)}
                style={{display:"flex",flexDirection:"column",alignItems:"center",gap:3,padding:isMobile?"5px 2px":"6px 4px",borderRadius:8,border:active?`2px solid ${color}`:"2px solid transparent",background:active?color+"12":"transparent",cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{width:isMobile?10:12,height:isMobile?10:12,borderRadius:"50%",background:active?color:"#E2E8F0",border:active?`2px solid ${color}`:"2px solid #CBD5E1",transition:"all 0.15s"}}/>
                {!isMobile&&<div style={{fontSize:9,fontWeight:active?800:500,color:active?color:"#94A3B8",lineHeight:1.2,textAlign:"center"}}>{oppPct}<br/>{defPct}</div>}
              </button>
            );
          })}
        </div>
        {isMobile&&(
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
            <span style={{fontSize:10,color:"#94A3B8"}}>100% Opp</span>
            <span style={{fontSize:10,color:"#94A3B8"}}>50/50</span>
            <span style={{fontSize:10,color:"#94A3B8"}}>100% Def</span>
          </div>
        )}
      </div>

      {/* Active blend summary */}
      <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,background:opp.color+"10",border:`1px solid ${opp.color}33`,borderRadius:8,padding:"6px 12px"}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:opp.color}}/>
          <span style={{fontSize:12,fontWeight:700,color:opp.color}}>{value}% {opp.label}</span>
        </div>
        <span style={{fontSize:13,color:"#CBD5E1",fontWeight:700}}>/</span>
        <div style={{display:"flex",alignItems:"center",gap:6,background:def.color+"10",border:`1px solid ${def.color}33`,borderRadius:8,padding:"6px 12px"}}>
          <div style={{width:8,height:8,borderRadius:"50%",background:def.color}}/>
          <span style={{fontSize:12,fontWeight:700,color:def.color}}>{100-value}% {def.label}</span>
        </div>
        <div style={{marginLeft:"auto",fontSize:11,color:"#94A3B8",fontStyle:"italic"}}>
          {value===100?"Pure Opportunistic":value===0?"Pure Defensive":value>=70?"Opportunistic-leaning":value<=30?"Defensive-leaning":"Balanced"}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ALLOCATION TABLE
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// TICKER SELECTION MODAL
// ─────────────────────────────────────────────────────────────
// selTickers shape: { [assetId]: { [tickerSymbol]: splitPct } }
// e.g. { us_eq: { VTI: 60, SCHB: 40 }, ig_inc: { FTBFX: 100 } }
// Ticker symbols are used as keys so cross-style selection works cleanly.

// Resolve tickers for a style filtered by client AUM (minAUM threshold per ticker in tickerData)
// Returns { tickers: { [assetId]: { [ticker]: splitPct } }, skippedByAsset: [{assetId, assetLabel, skipped:[{ticker,minAUM}], allExcluded}] }
function resolveTickersByAUM(engine, styleName, tvNum) {
  const sd = engine.implementationStyles[styleName] || {};
  const assets = allAssets(engine);
  const tickers = {};
  const skippedByAsset = [];

  assets.forEach(a => {
    const tickerList   = sd.tickers?.[a.id] || [];
    const defaultSplits = sd.defaultSplits?.[a.id] || {};
    const skipped = [];
    const eligible = [];

    tickerList.forEach(ticker => {
      const td = engine.tickerData?.[ticker] || {};
      const minAUM = td.minAUM ? Number(td.minAUM) : 0;
      if (minAUM > 0 && tvNum < minAUM) {
        skipped.push({ ticker, minAUM });
      } else {
        eligible.push(ticker);
      }
    });

    if (skipped.length > 0) {
      const allExcluded = eligible.length === 0;
      skippedByAsset.push({ assetId: a.id, assetLabel: a.label, skipped, allExcluded });
      // If all tickers excluded, fall back to full list
      const useList = allExcluded ? tickerList : eligible;
      const splits = {};
      useList.forEach(t => { if (defaultSplits[t] != null) splits[t] = defaultSplits[t]; });
      tickers[a.id] = Object.keys(splits).length > 0 ? splits : defaultTickerSplit(useList);
    } else if (eligible.length > 0) {
      const splits = {};
      eligible.forEach(t => { if (defaultSplits[t] != null) splits[t] = defaultSplits[t]; });
      tickers[a.id] = Object.keys(splits).length > 0 ? splits : defaultTickerSplit(eligible);
    } else {
      tickers[a.id] = {};
    }
  });

  return { tickers, skippedByAsset };
}

function defaultTickerSplit(tickers) {
  // 100% on the first ticker symbol by default
  return tickers.length > 0 ? { [tickers[0]]: 100 } : {};
}

function tickerSplitTotal(split) {
  return Object.values(split || {}).reduce((s, v) => s + (Number(v) || 0), 0);
}

// Collect all unique tickers for an asset across every impl style
function allTickersForAsset(engine, assetId) {
  const seen = new Set();
  const result = []; // { ticker, styleKey, isDefault }
  Object.entries(engine.implementationStyles).forEach(([sk, sd]) => {
    const list = sd.tickers?.[assetId] || [];
    list.forEach((t, ti) => {
      if (!seen.has(t)) {
        seen.add(t);
        result.push({ ticker: t, styleKey: sk, styleColor: sd.color, isDefault: ti === 0 });
      }
    });
  });
  return result;
}

function TickerModal({ sleeveKey, implStyle, selTickers, onApply, onClose }) {
  const engine   = useEngine();
  const sleeve   = engine.sleeves[sleeveKey];

  // localSel: { [assetId]: { [tickerSymbol]: pct } }
  const [localSel, setLocalSel] = useState(() => {
    const init = {};
    sleeve.assets.forEach(a => {
      const existing = selTickers?.[a.id];
      if (existing && typeof existing === "object" && !Array.isArray(existing) && Object.keys(existing).length) {
        init[a.id] = { ...existing };
      } else {
        // default: 100% on first ticker of the current impl style
        const styleDef = engine.implementationStyles[implStyle] || {};
        const tickers  = styleDef.tickers?.[a.id] || [];
        init[a.id] = defaultTickerSplit(tickers);
      }
    });
    return init;
  });

  const toggleTicker = (assetId, ticker) => {
    setLocalSel(prev => {
      const cur = { ...(prev[assetId] || {}) };
      if (cur[ticker] != null) {
        if (Object.keys(cur).length > 1) delete cur[ticker];
      } else {
        cur[ticker] = 0;
      }
      return { ...prev, [assetId]: cur };
    });
  };

  const setPct = (assetId, ticker, val) => {
    const num = Math.max(0, Math.min(100, Number(val) || 0));
    setLocalSel(prev => ({ ...prev, [assetId]: { ...(prev[assetId]||{}), [ticker]: num } }));
  };

  const distributeEvenly = (assetId) => {
    setLocalSel(prev => {
      const keys = Object.keys(prev[assetId] || {});
      if (!keys.length) return prev;
      const each = Math.floor(100 / keys.length);
      const rem  = 100 - each * keys.length;
      const balanced = {};
      keys.forEach((k, i) => { balanced[k] = each + (i === keys.length - 1 ? rem : 0); });
      return { ...prev, [assetId]: balanced };
    });
  };

  const isValid = sleeve.assets.every(a => Math.abs(tickerSplitTotal(localSel[a.id]) - 100) < 0.5);
  const anyChanged = sleeve.assets.some(a => JSON.stringify(selTickers?.[a.id]) !== JSON.stringify(localSel[a.id]));

  const handleDone = () => { if (!isValid) return; onApply(localSel); onClose(); };

  // Style label colors for badges
  const styleColors = Object.fromEntries(
    Object.entries(engine.implementationStyles).map(([k,v]) => [k, v.color])
  );

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(4px)",padding:16}}>
      <div style={{background:"#fff",borderRadius:16,width:"min(660px,96vw)",maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 64px rgba(0,0,0,0.18)",overflow:"hidden"}}>

        {/* Header */}
        <div style={{background:"#1E3A5F",padding:"16px 22px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:700,color:"#fff"}}>{sleeve.label} Sleeve — Custom Ticker Selection</div>
            <div style={{fontSize:11,color:"#93C5FD",marginTop:2}}>Mix tickers from any style · splits per asset class must total 100%</div>
          </div>
          <button onClick={onClose} style={{background:"rgba(255,255,255,0.1)",border:"none",borderRadius:8,color:"#93C5FD",width:32,height:32,fontSize:18,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:1}}>×</button>
        </div>

        {/* Body */}
        <div style={{overflowY:"auto",padding:"20px 22px",flex:1}}>
          {sleeve.assets.map(a => {
            const allTickers    = allTickersForAsset(engine, a.id); // [{ticker, styleKey, styleColor, isDefault}]
            const split         = localSel[a.id] || {};
            const total         = tickerSplitTotal(split);
            const ok            = Math.abs(total - 100) < 0.5;
            const selectedCount = Object.keys(split).length;
            if (!allTickers.length) return null;

            // Group by style for display
            const byStyle = {};
            Object.entries(engine.implementationStyles).forEach(([sk]) => { byStyle[sk] = []; });
            allTickers.forEach(item => { byStyle[item.styleKey].push(item); });

            return (
              <div key={a.id} style={{marginBottom:22,background:"#F8FAFC",borderRadius:12,padding:"14px 16px",border:`1px solid ${ok?"#E2E8F0":"#FCA5A5"}`}}>
                {/* Asset header */}
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12,flexWrap:"wrap"}}>
                  <div style={{width:9,height:9,borderRadius:"50%",background:a.color,flexShrink:0}}/>
                  <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F",flex:1}}>{a.label}</div>
                  <div style={{fontSize:12,fontWeight:700,color:ok?"#16A34A":"#DC2626"}}>{total}% {ok?"✓":"≠ 100%"}</div>
                  {selectedCount > 1 && !ok && (
                    <button onClick={()=>distributeEvenly(a.id)}
                      style={{fontSize:10,color:"#D97706",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:5,padding:"2px 8px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:700,whiteSpace:"nowrap"}}>
                      Split evenly
                    </button>
                  )}
                </div>

                {/* Tickers grouped by style */}
                {Object.entries(byStyle).map(([sk, items]) => {
                  if (!items.length) return null;
                  const sColor = styleColors[sk] || "#94A3B8";
                  return (
                    <div key={sk} style={{marginBottom:10}}>
                      {/* Style group label */}
                      <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:6}}>
                        <div style={{width:6,height:6,borderRadius:"50%",background:sColor}}/>
                        <span style={{fontSize:10,fontWeight:700,color:sColor,textTransform:"uppercase",letterSpacing:"0.5px"}}>{sk}</span>
                        <div style={{flex:1,height:1,background:sColor+"22"}}/>
                      </div>
                      <div style={{display:"flex",flexDirection:"column",gap:5}}>
                        {items.map(({ ticker, isDefault }) => {
                          const td      = engine.tickerData?.[ticker] || {};
                          const checked = split[ticker] != null;
                          const pctVal  = split[ticker] ?? 0;
                          return (
                            <div key={ticker} style={{display:"flex",alignItems:"center",gap:10,padding:"9px 12px",borderRadius:9,border:checked?`2px solid ${a.color}`:"2px solid #E2E8F0",background:checked?a.color+"07":"#fff",transition:"all 0.12s"}}>
                              {/* Checkbox */}
                              <button onClick={()=>toggleTicker(a.id, ticker)}
                                style={{width:18,height:18,borderRadius:4,border:`2px solid ${checked?a.color:"#CBD5E1"}`,background:checked?a.color:"#fff",flexShrink:0,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                                {checked&&<span style={{color:"#fff",fontSize:11,lineHeight:1,fontWeight:900}}>✓</span>}
                              </button>

                              {/* Ticker symbol + badges */}
                              <div style={{flex:1,minWidth:0,display:"flex",alignItems:"center",gap:7,flexWrap:"wrap"}}>
                                <span style={{fontWeight:800,fontSize:14,color:checked?a.color:"#64748B"}}>{ticker}</span>
                                {isDefault&&<span style={{fontSize:9,fontWeight:700,color:sColor,background:sColor+"15",border:`1px solid ${sColor}33`,borderRadius:10,padding:"1px 7px",letterSpacing:"0.5px",whiteSpace:"nowrap"}}>DEFAULT</span>}
                              </div>

                              {/* ER / Yield */}
                              <div style={{display:"flex",gap:12,flexShrink:0}}>
                                {td.expenseRatio!=null&&(
                                  <div style={{textAlign:"right"}}>
                                    <div style={{fontSize:9,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",fontWeight:700}}>ER</div>
                                    <div style={{fontSize:12,fontWeight:700,color:"#DC2626"}}>{td.expenseRatio.toFixed(2)}%</div>
                                  </div>
                                )}
                                {td.yield!=null&&td.yield>0&&(
                                  <div style={{textAlign:"right"}}>
                                    <div style={{fontSize:9,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",fontWeight:700}}>Yld</div>
                                    <div style={{fontSize:12,fontWeight:700,color:"#059669"}}>{td.yield.toFixed(2)}%</div>
                                  </div>
                                )}
                              </div>

                              {/* Split % input */}
                              {checked && (
                                <div style={{display:"flex",alignItems:"center",gap:4,flexShrink:0}}>
                                  <input type="number" min={0} max={100} value={pctVal}
                                    onChange={e=>setPct(a.id, ticker, e.target.value)}
                                    style={{width:54,padding:"5px 7px",border:`1px solid ${ok?"#E2E8F0":"#FCA5A5"}`,borderRadius:7,fontSize:13,fontWeight:700,textAlign:"right",color:"#1E3A5F",background:"#fff",outline:"none",fontFamily:"'Outfit',sans-serif"}}/>
                                  <span style={{fontSize:11,color:"#94A3B8",fontWeight:600}}>%</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}

                {/* Progress bar */}
                {selectedCount > 1 && (
                  <div style={{marginTop:10,height:5,background:"#E2E8F0",borderRadius:3,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${Math.min(total,100)}%`,background:ok?"#16A34A":"#EF4444",borderRadius:3,transition:"width 0.2s"}}/>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{padding:"14px 22px",borderTop:"1px solid #E2E8F0",display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexShrink:0,background:"#F8FAFC",flexWrap:"wrap"}}>
          <div style={{fontSize:12,color:isValid?"#16A34A":"#DC2626",fontWeight:600}}>
            {isValid ? "✓ All asset classes sum to 100%" : "⚠ Some asset classes don't sum to 100%"}
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={onClose} style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:8,color:"#64748B",padding:"9px 18px",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Cancel</button>
            <button onClick={handleDone} disabled={!isValid}
              style={{background:!isValid?"#94A3B8":anyChanged?"#1E3A5F":"#475569",border:"none",borderRadius:8,color:"#fff",padding:"9px 22px",fontSize:13,fontWeight:700,cursor:isValid?"pointer":"not-allowed",fontFamily:"'Outfit',sans-serif",transition:"background 0.2s"}}>
              {!isValid?"Fix splits to apply":anyChanged?"Apply Changes":"Done"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ALLOCATION TABLE
// ─────────────────────────────────────────────────────────────
// Maps a holding's assetClass string to an engine assetId
function mapHoldingToAssetId(assetClass, engine) {
  const mapping = {
    "US Equity":              "us_eq",
    "International Equity":  "intl_eq",
    "Emerging Markets":      "em_eq",
    "Fixed Income":          "ig_inc",
    "Alternative":           "opp_alt",
    "Cash":                  null,
    "Real Estate":           null,
    "Other":                 null,
  };
  // Try direct map first
  if (mapping[assetClass] !== undefined) return mapping[assetClass];
  // Fallback: fuzzy match on asset labels
  const assets = allAssets(engine);
  const lower = assetClass?.toLowerCase() || "";
  const found = assets.find(a => a.label.toLowerCase().includes(lower.split(" ")[0]));
  return found?.id || null;
}

// Given kept holdings + portfolio value, compute:
// { [assetId]: { keptPct, keptValue, keptTickers: [{ticker,value,pct}], capped } }
function computeKeptByAsset(keptHoldings, loadedClient, totalValue, engine) {
  if (!loadedClient || !keptHoldings?.length) return {};
  const tv = totalValue ? Number(String(totalValue).replace(/,/g,"")) : (loadedClient?.totalPortfolioValue || 0);
  if (!tv) return {};
  const result = {};
  const holdings = loadedClient.holdings || [];
  keptHoldings.forEach(hid => {
    const h = holdings.find(x => x.id === hid);
    if (!h) return;
    // Prefer assetId from approved holdings record if set, then fall back to statement asset class
    const approvedEntry = (engine.approvedHoldings||[]).find(a =>
      (typeof a === "string" ? a : a.ticker) === h.ticker?.toUpperCase()
    );
    const assetId = (typeof approvedEntry === "object" && approvedEntry?.assetId)
      ? approvedEntry.assetId
      : mapHoldingToAssetId(h.assetClass, engine);
    if (!assetId) return;
    if (!result[assetId]) result[assetId] = { keptPct:0, keptValue:0, keptTickers:[] };
    const mv = h.marketValue || 0;
    const hPct = (mv / tv) * 100;
    result[assetId].keptValue += mv;
    result[assetId].keptPct  += hPct;
    result[assetId].keptTickers.push({ ticker: h.ticker, name: h.name, value: mv, pct: hPct });
  });
  return result;
}

function AllocationTable({ alloc, implStyle, selTickers, onApplyTickers, totalValue, keptHoldings=[], loadedClient=null }) {
  const engine    = useEngine();
  const styleDef  = engine.implementationStyles[implStyle] || {};
  const oppAssets = engine.sleeves.opportunistic.assets;
  const defAssets = engine.sleeves.defensive.assets;
  const opp       = engine.sleeves.opportunistic;
  const def       = engine.sleeves.defensive;
  const tv        = totalValue ? Number(String(totalValue).replace(/,/g,"")) : null;
  const [openModal,    setOpenModal]    = useState(null);
  // activeSwaps: { [assetId]: swapId | "primary" }
  const [activeSwaps,  setActiveSwaps]  = useState({});

  // Resolve tickers for an asset — primary, sibling style, or custom swap
  const resolveCombo = (assetId, sleeveId) => {
    const activeKey = activeSwaps[assetId]; // undefined=primary | styleName | "csw_..." id
    const slot      = engine.models?.[implStyle]?.[sleeveId]?.[assetId];
    let combo;
    if (!activeKey || activeKey === "primary") {
      combo = slot?.primary;
    } else if (activeKey.startsWith("csw_")) {
      combo = slot?.customSwaps?.find(s=>s.id===activeKey)?.tickers;
    } else {
      combo = engine.models?.[activeKey]?.[sleeveId]?.[assetId]?.primary;
    }
    if (!combo?.length) return selTickers?.[assetId] || {};
    const result = {};
    combo.forEach(({ticker,split})=>{ result[ticker] = split; });
    return result;
  };

  // Compute kept holdings by asset class
  const keptByAsset = useMemo(
    () => computeKeptByAsset(keptHoldings, loadedClient, totalValue, engine),
    [keptHoldings, loadedClient, totalValue, engine]
  );

  // Min alloc warnings — collect all tickers below their minimum allocation dollar amount
  const minAllocWarnings = useMemo(() => {
    const tv = totalValue ? Number(String(totalValue).replace(/,/g,"")) : 0;
    if (!tv) return [];
    const warnings = [];
    allAssets(engine).forEach(a => {
      const targetW = alloc[a.id] || 0;
      const kept    = keptByAsset[a.id];
      const roomPct = Math.max(0, targetW - (kept?.keptPct || 0));
      const splits  = selTickers?.[a.id] || {};
      const multi   = Object.keys(splits).length > 1;
      const hasKept = kept && kept.keptPct > 0;
      Object.entries(splits).forEach(([ticker, splitPct]) => {
        const td = engine.tickerData?.[ticker];
        if (!td?.minAlloc) return;
        const effectivePct = hasKept ? (Number(splitPct)/100 * roomPct) : (multi ? Number(splitPct) : targetW);
        const dollarAlloc  = tv * effectivePct / 100;
        if (dollarAlloc < td.minAlloc) {
          warnings.push({ ticker, assetLabel: a.label, dollarAlloc, minAlloc: td.minAlloc, color: a.color });
        }
      });
    });
    return warnings;
  }, [selTickers, alloc, keptByAsset, totalValue, engine]);

  const tradingNotes = useMemo(() => {
    const notes = [];
    allAssets(engine).forEach(a => {
      const kept = keptByAsset[a.id];
      if (!kept) return;
      const targetPct = alloc[a.id] || 0;
      if (kept.keptPct >= targetPct) {
        notes.push({
          assetId: a.id,
          label: a.label,
          color: a.color,
          tickers: kept.keptTickers,
          keptPct: kept.keptPct,
          targetPct,
          cappedAt: targetPct,
        });
      }
    });
    return notes;
  }, [keptByAsset, alloc, engine]);

  const SleeveSection = ({ sleeveKey, sleeveLabel, sleeveColor, assets }) => (
    <>
      <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 0 4px",borderBottom:`2px solid ${sleeveColor}33`,marginBottom:4}}>
        <div style={{width:9,height:9,borderRadius:"50%",background:sleeveColor,flexShrink:0}}/>
        <div style={{fontSize:11,fontWeight:800,color:sleeveColor,letterSpacing:"1px",textTransform:"uppercase"}}>{sleeveLabel}</div>
        <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:11,fontWeight:700,color:sleeveColor}}>{pct(assets.reduce((s,a)=>s+(alloc[a.id]||0),0))}</span>
          {engine.allocatorSettings?.allowTickerEditing !== false && (
            <button onClick={()=>setOpenModal(sleeveKey)}
              style={{fontSize:10,fontWeight:700,color:sleeveColor,background:sleeveColor+"10",border:`1px solid ${sleeveColor}33`,borderRadius:6,padding:"3px 10px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
              Edit Tickers ›
            </button>
          )}
        </div>
      </div>

      {assets.map(a => {
        const split    = resolveCombo(a.id, sleeveKey);
        const targetW  = alloc[a.id] || 0;
        const kept     = keptByAsset[a.id];
        const hasKept  = !!kept;
        const capped   = hasKept && kept.keptPct >= targetW;

        // Effective display weight: if capped, show targetW; if kept partial, show targetW (kept replaces model tickers)
        const displayW = targetW;

        // Model tickers: reduce by kept pct (remaining room)
        const roomPct     = Math.max(0, targetW - (kept?.keptPct || 0));
        const modelSelected = Object.entries(split).filter(([,pv]) => (Number(pv)||0) > 0);
        const multi        = modelSelected.length > 1;

        // Sibling styles that have a primary combo defined for this asset+sleeve
        const siblingStyles  = Object.keys(engine.implementationStyles).filter(sk => sk !== implStyle && engine.models?.[sk]?.[sleeveKey]?.[a.id]?.primary?.length > 0);
        const activeKey      = activeSwaps[a.id];
        const isSwapActive   = activeKey && activeKey !== "primary";

        return (
          <div key={a.id} style={{padding:"7px 0 7px 12px",borderBottom:"1px solid #F8FAFC",background:hasKept?"#FFFBEB":isSwapActive?"#FAFBFF":undefined,borderRadius:4}}>
            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
              <div style={{width:7,height:7,borderRadius:"50%",background:a.color,flexShrink:0}}/>
              <div style={{flex:1,fontSize:12,color:"#334155"}}>{a.label}</div>
              {(()=>{
                const customSwaps  = engine.models?.[implStyle]?.[sleeveKey]?.[a.id]?.customSwaps || [];
                const hasOptions   = siblingStyles.length > 0 || customSwaps.length > 0;
                if (!hasOptions) return null;
                const activeKey    = activeSwaps[a.id];
                const customActive = customSwaps.find(s=>s.id===activeKey);
                const sibColor     = (!activeKey||activeKey==="primary"||activeKey.startsWith("csw_")) ? null : engine.implementationStyles[activeKey]?.color;
                const badgeLabel   = customActive ? customActive.name : (sibColor ? activeKey : null);
                const badgeColor   = customActive ? "#7C3AED" : sibColor;
                return (
                  <div style={{display:"flex",alignItems:"center",gap:5}}>
                    {badgeLabel&&(
                      <span style={{fontSize:9,padding:"1px 7px",borderRadius:8,background:(badgeColor||"#64748B")+"15",border:`1px solid ${(badgeColor||"#64748B")}44`,color:badgeColor||"#64748B",fontWeight:700,whiteSpace:"nowrap"}}>
                        ⇄ {badgeLabel}
                      </span>
                    )}
                    <select value={activeKey||"primary"}
                      onChange={e=>setActiveSwaps(p=>({...p,[a.id]:e.target.value==="primary"?undefined:e.target.value}))}
                      style={{fontSize:10,padding:"3px 6px",border:"1px solid #E2E8F0",borderRadius:6,background:"#F8FAFC",color:"#475569",fontFamily:"'Outfit',sans-serif",outline:"none",cursor:"pointer"}}>
                      <option value="primary">Primary ({implStyle})</option>
                      {siblingStyles.length>0&&<optgroup label="── Other Styles ──">
                        {siblingStyles.map(sk=><option key={sk} value={sk}>{sk}</option>)}
                      </optgroup>}
                      {customSwaps.length>0&&<optgroup label="── Custom Swaps ──">
                        {customSwaps.map(sw=><option key={sw.id} value={sw.id}>{sw.name}</option>)}
                      </optgroup>}
                    </select>
                  </div>
                );
              })()}
              <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F",minWidth:38,textAlign:"right",flexShrink:0}}>{pct(displayW)}</div>
              {tv&&<div style={{fontSize:11,color:"#94A3B8",minWidth:78,textAlign:"right",flexShrink:0}}>${Math.round(displayW/100*tv).toLocaleString()}</div>}
            </div>

            {/* Kept holding pills */}
            {hasKept && (
              <div style={{paddingLeft:15,marginTop:5,display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                <span style={{fontSize:9,color:"#B45309",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",marginRight:2}}>Kept</span>
                {kept.keptTickers.map((kt,i) => (
                  <div key={i} style={{display:"flex",alignItems:"center",gap:5,background:"#FEF3C7",border:"1px solid #FCD34D",borderRadius:6,padding:"3px 8px"}}>
                    <span style={{fontWeight:700,fontSize:11,color:"#92400E"}}>{kt.ticker}</span>
                    <span style={{fontSize:10,color:"#B45309",fontWeight:600}}>{kt.pct.toFixed(1)}%</span>
                    {capped && <span style={{fontSize:9,color:"#DC2626",fontWeight:700}}>↑ capped</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Model ticker pills — only shown if there's room after kept holdings */}
            {(!hasKept || roomPct > 0.5) && (
              <div style={{paddingLeft:15,marginTop:hasKept?3:5,display:"flex",gap:5,flexWrap:"wrap",alignItems:"center"}}>
                {hasKept && roomPct > 0.5 && (
                  <span style={{fontSize:9,color:"#64748B",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px",marginRight:2}}>
                    Model ({pct(roomPct)} remaining)
                  </span>
                )}
                {modelSelected.map(([ticker, splitPct]) => {
                  const td = engine.tickerData?.[ticker];
                  // Scale the displayed pct by roomPct
                  const effectivePct = hasKept ? (Number(splitPct)/100 * roomPct) : null;
                  // Dollar allocation for this ticker
                  const tv = totalValue ? Number(String(totalValue).replace(/,/g,"")) : 0;
                  const displayPct = effectivePct ?? (multi ? Number(splitPct) : targetW);
                  const dollarAlloc = tv > 0 ? tv * displayPct / 100 : null;
                  // Min allocation check
                  const minAlloc = td?.minAlloc ?? null;
                  const belowMin = minAlloc != null && dollarAlloc != null && dollarAlloc < minAlloc;
                  return (
                    <div key={ticker} style={{display:"flex",flexDirection:"column",gap:2}}>
                      <div style={{display:"flex",alignItems:"center",gap:6,background:belowMin?"#FFF5F5":a.color+"0E",border:`1px solid ${belowMin?"#FCA5A5":a.color+"33"}`,borderRadius:6,padding:"3px 9px",position:"relative"}}>
                        <span style={{fontWeight:700,fontSize:11,color:belowMin?"#DC2626":a.color}}>{ticker}</span>
                        {belowMin && (
                          <span title={`Min allocation: $${minAlloc.toLocaleString()} — current: $${Math.round(dollarAlloc).toLocaleString()}`}
                            style={{fontSize:9,fontWeight:800,color:"#DC2626",background:"#FEE2E2",border:"1px solid #FCA5A5",borderRadius:4,padding:"1px 5px",cursor:"help",whiteSpace:"nowrap"}}>
                            ⚠ below min ${(minAlloc/1000).toFixed(0)}K
                          </span>
                        )}
                        {(multi || hasKept) && effectivePct != null && (
                          <span style={{fontSize:10,color:belowMin?"#DC2626":a.color,fontWeight:600,opacity:0.8}}>{effectivePct.toFixed(1)}%</span>
                        )}
                        {!hasKept && multi && (
                          <span style={{fontSize:10,color:a.color,fontWeight:600,opacity:0.8}}>{splitPct}%</span>
                        )}
                        {td?.expenseRatio!=null&&<span style={{fontSize:9,color:"#94A3B8"}}>ER <strong style={{color:"#475569"}}>{td.expenseRatio.toFixed(2)}%</strong></span>}
                        {td?.yield>0&&<span style={{fontSize:9,color:"#94A3B8"}}>Yld <strong style={{color:"#059669"}}>{td.yield.toFixed(2)}%</strong></span>}
                      </div>
                      {td?.note && (
                        <div style={{fontSize:9,color:"#64748B",fontStyle:"italic",paddingLeft:4,lineHeight:1.4,maxWidth:200}}>
                          📝 {td.note}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* If fully capped — no model tickers shown */}
            {hasKept && capped && roomPct <= 0.5 && (
              <div style={{paddingLeft:15,marginTop:3}}>
                <span style={{fontSize:10,color:"#94A3B8",fontStyle:"italic"}}>Model tickers replaced by kept position — see trading notes below</span>
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  return (
    <>
      <div style={card}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",borderBottom:"1px solid #F1F5F9"}}>
          <div style={{fontSize:12,fontWeight:700,color:"#1E3A5F",letterSpacing:"0.2px"}}>Portfolio Allocation</div>
          <span style={badge(styleDef.color||"#1E3A5F")}>{implStyle}</span>
        </div>
        <div style={{padding:"12px 16px"}}>
        <SleeveSection sleeveKey="opportunistic" sleeveLabel={opp.label} sleeveColor={opp.color} assets={oppAssets}/>
        <div style={{height:12}}/>
        <SleeveSection sleeveKey="defensive" sleeveLabel={def.label} sleeveColor={def.color} assets={defAssets}/>
        <div style={{display:"flex",justifyContent:"flex-end",paddingTop:8,borderTop:"2px solid #E2E8F0",marginTop:8}}>
          <span style={{fontWeight:800,fontSize:14,color:"#1E3A5F"}}>100%</span>
        </div>
        </div>{/* end padding wrapper */}
      </div>

      {/* Min Allocation Warnings — tickers below their configured minimum dollar allocation */}
      {minAllocWarnings.length > 0 && (
        <div style={{...card, marginTop:12, border:"1px solid #BFDBFE", background:"#EFF6FF", padding:"14px 16px"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
            <span style={{fontSize:16}}>⚠️</span>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:"#1E3A5F"}}>Minimum Allocation Warnings</div>
          </div>
          <div style={{fontSize:12,color:"#1E40AF",marginBottom:12,lineHeight:1.6}}>
            The following tickers are allocated below their configured minimum dollar amount. Review the allocation or adjust the portfolio value.
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {minAllocWarnings.map((w,i) => (
              <div key={i} style={{padding:"8px 12px",background:"#fff",border:"1px solid #BFDBFE",borderRadius:8,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:w.color,flexShrink:0}}/>
                <span style={{fontWeight:700,fontSize:12,color:"#1E3A5F"}}>{w.ticker}</span>
                <span style={{fontSize:11,color:"#64748B"}}>{w.assetLabel}</span>
                <span style={{marginLeft:"auto",fontSize:11,color:"#DC2626",fontWeight:600}}>
                  Allocated: <strong>${Math.round(w.dollarAlloc).toLocaleString()}</strong>
                  <span style={{color:"#94A3B8",fontWeight:400,margin:"0 6px"}}>·</span>
                  Minimum: <strong>${w.minAlloc.toLocaleString()}</strong>
                  <span style={{fontSize:10,color:"#DC2626",background:"#FEE2E2",border:"1px solid #FCA5A5",borderRadius:4,padding:"1px 6px",marginLeft:8,fontWeight:700}}>
                    ${(w.minAlloc - Math.round(w.dollarAlloc)).toLocaleString()} short
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Trading Notes — only when at least one kept position was capped */}
      {tradingNotes.length > 0 && (
        <div style={{...card, marginTop:12, border:"1px solid #FCD34D", background:"#FFFBEB"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
            <span style={{fontSize:16}}>📋</span>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:"#92400E"}}>Trading Notes</div>
          </div>
          <div style={{fontSize:12,color:"#78350F",marginBottom:12,lineHeight:1.6}}>
            The following kept positions meet or exceed their target asset class weight. They have been shown at the maximum target weighting in this model. <strong>No immediate action is required</strong> — the true allocation in the trading tool may vary slightly from this model output.
          </div>
          {tradingNotes.map((n, i) => (
            <div key={i} style={{padding:"10px 14px",background:"#FEF9EC",border:"1px solid #FDE68A",borderRadius:8,marginBottom:i<tradingNotes.length-1?8:0}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <div style={{width:7,height:7,borderRadius:"50%",background:n.color,flexShrink:0}}/>
                <span style={{fontWeight:700,fontSize:12,color:"#1E3A5F"}}>{n.label}</span>
                <span style={{fontSize:11,color:"#B45309",marginLeft:"auto"}}>
                  Kept: <strong>{n.keptPct.toFixed(1)}%</strong> → Capped at target: <strong>{n.cappedAt.toFixed(1)}%</strong>
                </span>
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {n.tickers.map((t,j) => (
                  <span key={j} style={{background:"#FEF3C7",border:"1px solid #FCD34D",borderRadius:5,padding:"2px 8px",fontSize:11,fontWeight:700,color:"#92400E"}}>
                    {t.ticker} <span style={{fontWeight:400,color:"#B45309"}}>{t.pct.toFixed(1)}% of portfolio</span>
                  </span>
                ))}
              </div>
              <div style={{marginTop:6,fontSize:10,color:"#B45309",lineHeight:1.5}}>
                Actual position is {n.keptPct.toFixed(1)}% of total portfolio — {(n.keptPct - n.cappedAt).toFixed(1)}% above model target. Advisor should review at next rebalance.
              </div>
            </div>
          ))}
        </div>
      )}

      {openModal&&(
        <TickerModal
          sleeveKey={openModal}
          implStyle={implStyle}
          selTickers={selTickers}
          onApply={(sleeveSelTickers) => onApplyTickers(openModal, sleeveSelTickers)}
          onClose={()=>setOpenModal(null)}
        />
      )}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// PIE CHART CARD
// ─────────────────────────────────────────────────────────────
function AllocPieChart({ alloc, engine }) {
  const data = allAssets(engine).filter(a => (alloc[a.id]||0) > 0).map(a => ({ name:a.label, value:Math.round(alloc[a.id]*10)/10, color:a.color }));
  return (
    <div style={card}>
      <div style={cardTitle}>Allocation Mix</div>
      <ResponsiveContainer width="100%" height={190}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={48} outerRadius={80} paddingAngle={2} dataKey="value">
            {data.map((d,i) => <Cell key={i} fill={d.color}/>)}
          </Pie>
          <Tooltip formatter={v=>`${v}%`} contentStyle={ttStyle}/>
        </PieChart>
      </ResponsiveContainer>
      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:4}}>
        {data.map((d,i) => (
          <div key={i} style={{display:"flex",alignItems:"center",gap:4,fontSize:10,color:"#64748B"}}>
            <div style={{width:7,height:7,borderRadius:"50%",background:d.color,flexShrink:0}}/>
            {d.name.split(" ")[0]} {d.value}%
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SLEEVE DONUT — shows opp/def split visually
// ─────────────────────────────────────────────────────────────
function SleeveDonut({ oppPct }) {
  const engine = useEngine();
  const defPct = 100 - oppPct;
  const opp = engine.sleeves.opportunistic;
  const def = engine.sleeves.defensive;
  const data = [
    { name: opp.label, value: oppPct, color: opp.color },
    { name: def.label, value: defPct, color: def.color },
  ].filter(d => d.value > 0);
  return (
    <div style={{...card,display:"flex",flexDirection:"column",alignItems:"center"}}>
      <div style={cardTitle}>Sleeve Split</div>
      <ResponsiveContainer width="100%" height={140}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={40} outerRadius={62} paddingAngle={3} dataKey="value">
            {data.map((d,i) => <Cell key={i} fill={d.color}/>)}
          </Pie>
          <Tooltip formatter={v=>`${v}%`} contentStyle={ttStyle}/>
        </PieChart>
      </ResponsiveContainer>
      <div style={{display:"flex",gap:16,justifyContent:"center",marginTop:2}}>
        {data.map((d,i) => (
          <div key={i} style={{display:"flex",alignItems:"center",gap:5,fontSize:12}}>
            <div style={{width:8,height:8,borderRadius:"50%",background:d.color}}/>
            <span style={{fontWeight:700,color:d.color}}>{d.value}%</span>
            <span style={{color:"#94A3B8"}}>{d.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BENCHMARK COMPARE BAR CHART
// ─────────────────────────────────────────────────────────────
function BenchmarkChart({ alloc, benchmark, engine }) {
  const bmAlloc = engine.benchmarks[benchmark] || {};
  const assets  = allAssets(engine);
  const data = assets.map(a => ({
    name:      a.label.split(" ")[0],
    portfolio: Math.round((alloc[a.id]||0)*10)/10,
    benchmark: bmAlloc[a.id] || 0,
    color:     a.color,
  })).filter(d => d.portfolio > 0 || d.benchmark > 0);
  return (
    <div style={card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
        <div style={cardTitle}>vs. {benchmark}</div>
        <div style={{display:"flex",gap:16}}>
          <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#64748B"}}><div style={{width:12,height:3,background:"#2563EB",borderRadius:2}}/>Portfolio</div>
          <div style={{display:"flex",alignItems:"center",gap:5,fontSize:11,color:"#64748B"}}><div style={{width:12,height:3,background:"#D97706",borderRadius:2}}/>Benchmark</div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={180}>
        <BarChart data={data} margin={{left:-22}}>
          <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
          <XAxis dataKey="name" tick={{fontSize:9,fill:"#94A3B8"}}/>
          <YAxis tick={{fontSize:9,fill:"#94A3B8"}}/>
          <Tooltip contentStyle={ttStyle} formatter={v=>`${v}%`}/>
          <Bar dataKey="portfolio" name="Portfolio" radius={[3,3,0,0]}>{data.map((d,i)=><Cell key={i} fill={d.color}/>)}</Bar>
          <Bar dataKey="benchmark" name="Benchmark" fill="#D97706" opacity={0.4} radius={[3,3,0,0]}/>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// AI INSIGHTS
// ─────────────────────────────────────────────────────────────
function AIInsights({ alloc, implStyle, oppPct, totalValue, clientName, benchmark, keptHoldings=[], loadedClient=null }) {
  const engine = useEngine();
  const [text,    setText]    = useState("");
  const [loading, setLoading] = useState(false);
  const opp = engine.sleeves.opportunistic;
  const def = engine.sleeves.defensive;

  const run = async () => {
    setLoading(true); setText("");
    const lines  = allAssets(engine).filter(a=>alloc[a.id]>0).map(a=>`${a.label}: ${pct(alloc[a.id])}`).join(", ");
    const bmAlloc= engine.benchmarks[benchmark]||{};
    const bmLine = allAssets(engine).filter(a=>bmAlloc[a.id]>0).map(a=>`${a.label}: ${bmAlloc[a.id]}%`).join(", ");
    const prompt = `You are a senior financial advisor. Analyze this portfolio allocation.

Implementation Style: ${implStyle} — ${engine.implementationStyles[implStyle]?.description||""}
Sleeve Blend: ${oppPct}% ${opp.label} / ${100-oppPct}% ${def.label}
${totalValue?`AUM: $${Number(totalValue.replace(/,/g,"")).toLocaleString()}`:""}

Portfolio allocations: ${lines}
Benchmark (${benchmark}): ${bmLine}

Provide a professional analysis using these exact sections (use **Section Name** format):
**Portfolio Assessment** — 2-3 sentences on the ${oppPct}/${100-oppPct} ${opp.label}/${def.label} blend and ${implStyle} approach.
**Key Strengths** — 2 bullets starting "• "
**Risk Considerations** — 2 bullets starting "• "
**Recommendations** — 2-3 concise actionable suggestions.

Under 220 words. Institutional language.`;
    try {
      const insightsUrl = window.location.hostname === "claude.ai" || window.location.hostname.endsWith(".webcontainer.io") ? "https://api.anthropic.com/v1/messages" : "/api/ai";
      const res = await fetch(insightsUrl,{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({model:"claude-sonnet-4-5",max_tokens:1000,messages:[{role:"user",content:prompt}]})});
      const d = await res.json();
      setText(d.content?.map(b=>b.text||"").join("")||"Unable to generate insights.");
    } catch { setText("Error generating insights."); }
    setLoading(false);
  };

  const rendered = useMemo(()=>{
    if(!text) return null;
    return text.split("\n").map((line,i)=>{
      const bold=line.match(/^\*\*(.*?)\*\*(.*)/);
      if(bold) return <div key={i} style={{marginTop:12}}><span style={{fontFamily:"'Outfit',sans-serif",fontWeight:700,color:"#1E3A5F",fontSize:13}}>{bold[1]}</span>{bold[2]&&<span style={{color:"#475569",fontSize:13}}>{bold[2]}</span>}</div>;
      if(line.startsWith("• ")) return <div key={i} style={{paddingLeft:12,color:"#475569",fontSize:13,lineHeight:1.75,marginTop:2}}>• {line.slice(2)}</div>;
      if(!line.trim()) return <div key={i} style={{height:4}}/>;
      return <div key={i} style={{color:"#475569",fontSize:13,lineHeight:1.75}}>{line}</div>;
    });
  },[text]);

  return (
    <div style={{background:"#F0F5FF",border:"1px solid #BFDBFE",borderRadius:12,padding:20,marginTop:20}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12,flexWrap:"wrap",gap:8}}>
        <div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:17,fontWeight:700,color:"#1E3A5F"}}>AI Portfolio Insights</div>
          <div style={{fontSize:11,color:"#94A3B8",marginTop:1,letterSpacing:"0.5px"}}>POWERED BY CLAUDE</div>
        </div>
        <button onClick={run} disabled={loading}
          style={{background:loading?"#E2E8F0":"#1E3A5F",border:"none",borderRadius:8,color:loading?"#94A3B8":"#fff",padding:"9px 18px",fontSize:12,fontWeight:700,cursor:loading?"not-allowed":"pointer",fontFamily:"'Outfit',sans-serif"}}>
          {loading?"Analyzing…":"Generate Insights"}
        </button>
      </div>
      {loading&&<div style={{display:"flex",gap:8,alignItems:"center",color:"#94A3B8",fontSize:13}}><div style={{width:13,height:13,border:"2px solid #BFDBFE",borderTopColor:"#2563EB",borderRadius:"50%",animation:"spin 0.8s linear infinite"}}/>Analyzing…</div>}
      {rendered&&!loading&&<div style={{borderTop:"1px solid #BFDBFE",paddingTop:12}}>{rendered}</div>}
      {!text&&!loading&&<div style={{color:"#94A3B8",fontSize:13,fontStyle:"italic"}}>Select a blend ratio then click Generate Insights.</div>}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PORTFOLIO CHARACTERISTICS
// ─────────────────────────────────────────────────────────────
function PortfolioCharacteristics({ alloc, implStyle, selTickers }) {
  const engine   = useEngine();
  const styleDef = engine.implementationStyles[implStyle] || {};
  const assets   = allAssets(engine);

  // Weighted blended ER and yield — handles multi-ticker splits per asset
  const { blendedER, blendedYield, coverage } = useMemo(() => {
    let erNum=0, erDen=0, yldNum=0, yldDen=0, covered=0;
    assets.forEach(a => {
      const w       = alloc[a.id] || 0;
      if (w === 0) return;
      const split   = selTickers?.[a.id] || {};
      // iterate each ticker in the split (symbol → pct)
      Object.entries(split).forEach(([ticker, splitPct]) => {
        const td     = engine.tickerData?.[ticker];
        if (!td) return;
        const effectiveW = w * (splitPct / 100);
        covered++;
        if (td.expenseRatio != null) { erNum  += td.expenseRatio  * effectiveW; erDen  += effectiveW; }
        if (td.yield        != null) { yldNum += td.yield         * effectiveW; yldDen += effectiveW; }
      });
    });
    return {
      blendedER:    erDen  > 0 ? erNum  / erDen  : null,
      blendedYield: yldDen > 0 ? yldNum / yldDen : null,
      coverage:     covered,
    };
  }, [alloc, implStyle, selTickers, engine]);

  const total = assets.filter(a=>(alloc[a.id]||0)>0).length;

  const Stat = ({ label, value, color, suffix="%", note }) => (
    <div style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:10,padding:"12px 16px",flex:"1 1 130px"}}>
      <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",letterSpacing:"0.5px",textTransform:"uppercase",marginBottom:6}}>{label}</div>
      {value != null
        ? <div style={{fontFamily:"'Outfit',sans-serif",fontSize:26,fontWeight:700,color}}>{value.toFixed(2)}<span style={{fontSize:14,fontWeight:400,marginLeft:2,color:"#94A3B8"}}>{suffix}</span></div>
        : <div style={{fontSize:13,color:"#CBD5E1",fontStyle:"italic"}}>—</div>}
      {note&&<div style={{fontSize:10,color:"#94A3B8",marginTop:4}}>{note}</div>}
    </div>
  );

  return (
    <div style={{...card, marginTop:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div style={cardTitle}>Portfolio Characteristics</div>
        <span style={{fontSize:11,color:"#94A3B8"}}>{coverage}/{total} positions with data</span>
      </div>
      <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
        <Stat label="Blended Expense Ratio" value={blendedER} color="#DC2626"
          note="Weighted avg cost across positions"/>
        <Stat label="Blended Portfolio Yield" value={blendedYield} color="#059669"
          note="Weighted avg yield across positions"/>
        {blendedER!=null&&blendedYield!=null&&(
          <Stat label="Net Yield (after fees)" value={blendedYield-blendedER} color={blendedYield-blendedER>=0?"#0891B2":"#DC2626"}
            note="Blended yield minus blended ER"/>
        )}
      </div>
      {coverage < total && (
        <div style={{marginTop:10,fontSize:11,color:"#94A3B8",background:"#F8FAFC",padding:"6px 10px",borderRadius:6,border:"1px dashed #E2E8F0"}}>
          ⚠ {total-coverage} position{total-coverage>1?"s":""} missing data — add expense ratio & yield in Admin → Tickers.
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// MAIN ALLOCATOR VIEW  (replaces SimpleMode / ComplexMode)
// ─────────────────────────────────────────────────────────────
function AllocatorView({ clientName, totalValue, keptHoldings=[], loadedClient=null, onSnapshotChange=null }) {
  const { resolveName: resolveAllocName } = usePIIVault();
  // clientName may be a PII token if loaded from a saved client — resolve for display only, never sent to API
  const displayClientName = resolveAllocName ? resolveAllocName(clientName) : clientName;
  const engine  = useEngine();
  const session = useSession();
  const { isMobile, isTablet } = useBreakpoint();

  const styleKeys = Object.keys(engine.implementationStyles);
  const [implStyle,  setImplStyle]  = useState(styleKeys[0]);
  const [oppPct,     setOppPct]     = useState(60);
  const [benchmark,  setBenchmark]  = useState(Object.keys(engine.benchmarks)[0]);

  // selTickers: { [assetId]: { [tickerSymbol]: splitPct } }
  // Resolves eligible tickers for style+AUM using per-ticker minAUM thresholds in tickerData
  const initSelTickers = (style, tv) => {
    const tvNum = Number(String(tv||"").replace(/,/g,"")) || 0;
    const { tickers: resolved } = tvNum > 0
      ? resolveTickersByAUM(engine, style || styleKeys[0], tvNum)
      : { tickers: null };

    const sd = engine.implementationStyles[style || styleKeys[0]] || {};
    return Object.fromEntries(
      allAssets(engine).map(a => {
        if (resolved?.[a.id] && Object.keys(resolved[a.id]).length > 0) return [a.id, resolved[a.id]];
        const tickers = sd.tickers?.[a.id] || [];
        const splits  = sd.defaultSplits?.[a.id];
        if (splits && Object.keys(splits).length) {
          const filtered = {};
          tickers.forEach(t => { if (splits[t] != null) filtered[t] = splits[t]; });
          if (Object.keys(filtered).length) return [a.id, filtered];
        }
        return [a.id, defaultTickerSplit(tickers)];
      })
    );
  };

  const [selTickers,    setSelTickers]    = useState(() => initSelTickers(styleKeys[0], ""));
  const [customized,    setCustomized]    = useState(false);
  const [skippedTickers, setSkippedTickers] = useState([]);

  // Auto-apply AUM filtering when totalValue or style changes (silent, unless advisor customized)
  const prevTvRef    = useRef("");
  const prevStyleRef = useRef(styleKeys[0]);
  useEffect(() => {
    const tvChanged    = totalValue !== prevTvRef.current;
    const styleChanged = implStyle  !== prevStyleRef.current;
    prevTvRef.current    = totalValue;
    prevStyleRef.current = implStyle;
    if (!tvChanged && !styleChanged) return;

    const tvNum = Number(String(totalValue||"").replace(/,/g,"")) || 0;
    if (tvNum > 0) {
      const { tickers: resolved, skippedByAsset } = resolveTickersByAUM(engine, implStyle, tvNum);
      if (resolved && (!customized || styleChanged)) {
        setSelTickers(Object.fromEntries(
          allAssets(engine).map(a => [a.id, (resolved[a.id] && Object.keys(resolved[a.id]).length) ? resolved[a.id] : initSelTickers(implStyle,"")[a.id]])
        ));
        setCustomized(false);
      }
      setSkippedTickers(skippedByAsset || []);
      return;
    }
    if (styleChanged) {
      setSelTickers(initSelTickers(implStyle, totalValue));
      setCustomized(false);
      setSkippedTickers([]);
    }
  }, [totalValue, implStyle]);

  const handleStyleChange = (s) => {
    setImplStyle(s);
    session.track("styleChange", s);
  };
  const handleBlendChange = (v) => {
    setOppPct(v);
    session.track("blendChange", v);
  };
  // Called when advisor applies tickers from modal for one sleeve
  // sleeveSelTickers: { [assetId]: { [tickerIdx]: splitPct } } — only for assets in that sleeve
  const handleApplyTickers = (sleeveKey, sleeveSelTickers) => {
    setSelTickers(prev => ({ ...prev, ...sleeveSelTickers }));
    setCustomized(true);
    session.track("tickerSwap", { sleeveKey, style: implStyle });
  };

  useEffect(() => {
    session.setState({ implStyle, oppPct, benchmark, selTickers });
    // Lift allocation snapshot to App for comparator
    if (onSnapshotChange) {
      const tvNum = Number(String(totalValue||"").replace(/,/g,"")) || 0;
      onSnapshotChange({
        implStyle, oppPct,
        alloc: computeAllocation(engine, oppPct, implStyle),
        selTickers,
        totalValue: tvNum,
        loadedClientId: loadedClient?.id || null,
        snapshotAt: Date.now(),
      });
    }
  }, [implStyle, oppPct, selTickers, totalValue, loadedClient?.id]);

  const alloc = useMemo(() => computeAllocation(engine, oppPct, implStyle), [engine, oppPct, implStyle]);

  return (
    <div>
      {/* Step 1 — Implementation Style */}
      <div style={{...card, marginBottom:16, padding:"16px 18px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <div style={{width:22,height:22,borderRadius:"50%",background:"#1E3A5F",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",flexShrink:0}}>1</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:"#1E3A5F"}}>Select Implementation Style</div><HelpTip articleId="gl-01"/>
          {totalValue && Number(String(totalValue).replace(/,/g,"")) > 0 && (
            <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:6}}>
              {customized
                ? <span style={{fontSize:10,fontWeight:700,color:"#D97706",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:20,padding:"2px 10px"}}>✏ Customized</span>
                : <><span style={{fontSize:10,fontWeight:700,color:"#2563EB",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:20,padding:"2px 10px"}}>auto-filtered by AUM</span><HelpTip articleId="sl-01"/></>
              }
            </div>
          )}
        </div>
        <ImplStyleSelector value={implStyle} onChange={engine.allocatorSettings?.allowStyleChange!==false ? handleStyleChange : (()=>{})}/>
        {engine.allocatorSettings?.allowStyleChange===false&&(
          <div style={{marginTop:8,fontSize:10,color:"#94A3B8",fontStyle:"italic"}}>🔒 Style selection is locked by your administrator</div>
        )}
      </div>

      {/* AUM exclusion notice */}
      {skippedTickers.length > 0 && (
        <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:10,padding:"12px 16px",marginBottom:16,display:"flex",gap:10,alignItems:"flex-start"}}>
          <span style={{fontSize:16,flexShrink:0}}>⚠️</span>
          <div>
            <div style={{fontWeight:700,fontSize:12,color:"#92400E",marginBottom:4}}>Some tickers excluded — minimum AUM not met</div>
            {skippedTickers.map((n,i) => (
              <div key={i} style={{fontSize:11,color:"#78350F",marginBottom:2}}>
                <strong>{n.assetLabel}:</strong>{" "}
                {n.skipped.map((s,si) => (
                  <span key={s.ticker}>{s.ticker} requires ${s.minAUM?.toLocaleString()}{si < n.skipped.length-1 ? " · " : ""}</span>
                ))}
                {n.allExcluded && <span style={{color:"#DC2626"}}> — all tickers excluded, using full list as fallback</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Step 2 — Blend Ratio */}
      <div style={{...card, marginBottom:16, padding:"16px 18px"}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:14}}>
          <div style={{width:22,height:22,borderRadius:"50%",background:"#1E3A5F",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",flexShrink:0}}>2</div>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:"#1E3A5F"}}>Set Blend Ratio</div><HelpTip articleId="gl-02"/>
        </div>
        <BlendSelector value={oppPct} onChange={engine.allocatorSettings?.allowBlendAdjust!==false ? handleBlendChange : (()=>{})}/>
        {engine.allocatorSettings?.allowBlendAdjust===false&&(
          <div style={{marginTop:8,fontSize:10,color:"#94A3B8",fontStyle:"italic"}}>🔒 Blend ratio is locked by your administrator</div>
        )}
      </div>

      {/* Step 3 — Allocation table + charts side by side */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:16,alignItems:"start"}}>
        <AllocationTable alloc={alloc} implStyle={implStyle} selTickers={selTickers} onApplyTickers={handleApplyTickers} totalValue={totalValue} keptHoldings={keptHoldings} loadedClient={loadedClient}/>
        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <SleeveDonut oppPct={oppPct}/>
          <AllocPieChart alloc={alloc} engine={engine}/>
        </div>
      </div>

      {/* Benchmark comparison */}
      <div style={{...card, padding:"16px 18px", marginBottom:16}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
          <div style={{fontSize:13,fontWeight:700,color:"#1E3A5F"}}>Benchmark Comparison</div>
          <select value={benchmark} onChange={e=>setBenchmark(e.target.value)}
            style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:8,color:"#334155",padding:"7px 12px",fontSize:12,outline:"none",fontFamily:"'Outfit',sans-serif",cursor:"pointer"}}>
            {Object.keys(engine.benchmarks).map(b=><option key={b}>{b}</option>)}
          </select>
        </div>
        <BenchmarkChart alloc={alloc} benchmark={benchmark} engine={engine}/>
      </div>

      <PortfolioCharacteristics alloc={alloc} implStyle={implStyle} selTickers={selTickers}/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TICKER CSV MANAGER
// ─────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────
// TICKER IMPORTER — parses uploaded CSV/XLSX, previews changes, commits to engine
// Expected columns (case-insensitive):
//   Ticker | Asset Class | Style | Split % | ER % | Yield % | Min AUM | Min Alloc | Note
// One row per ticker-style-asset combination.
// ─────────────────────────────────────────────────────────────

// Map common asset class label variants → engine asset IDs
function normalizeAssetId(label, engine) {
  const map = {
    "us equity":"us_eq","u.s. equity":"us_eq","us eq":"us_eq","united states equity":"us_eq",
    "international equity":"intl_eq","intl equity":"intl_eq","intl eq":"intl_eq","international":"intl_eq",
    "emerging markets":"em_eq","emerging market":"em_eq","em equity":"em_eq","em eq":"em_eq",
    "opportunistic alternatives":"opp_alt","opp alternatives":"opp_alt","opp alt":"opp_alt","opportunistic alt":"opp_alt",
    "investment grade income":"ig_inc","ig income":"ig_inc","investment grade":"ig_inc","ig inc":"ig_inc",
    "high yield income":"hy_inc","hy income":"hy_inc","high yield":"hy_inc","hy inc":"hy_inc",
    "defensive alternatives":"def_alt","defensive alt":"def_alt","def alternatives":"def_alt","def alt":"def_alt",
  };
  const key = label.toLowerCase().trim();
  if (map[key]) return map[key];
  // try matching engine asset labels directly
  const match = allAssets(engine).find(a =>
    a.label.toLowerCase() === key || a.id === key
  );
  return match?.id || null;
}

// Map style name variants → engine style keys
function normalizeStyle(label, engine) {
  const styleKeys = Object.keys(engine.implementationStyles);
  const key = label.toLowerCase().trim();
  return styleKeys.find(sk => sk.toLowerCase() === key) || styleKeys.find(sk => sk.toLowerCase().includes(key)) || null;
}

function parseCSV(text) {
  const lines = text.replace(/\r/g,"").split("\n").filter(l=>l.trim());
  if (!lines.length) return [];
  const headers = lines[0].split(",").map(h=>h.trim().replace(/^"|"$/g,"").toLowerCase());
  return lines.slice(1).map(line => {
    // Handle quoted commas
    const cols = [];
    let cur = "", inQ = false;
    for (const ch of line) {
      if (ch==="\"" ) { inQ=!inQ; }
      else if (ch==="," && !inQ) { cols.push(cur.trim()); cur=""; }
      else cur+=ch;
    }
    cols.push(cur.trim());
    const row = {};
    headers.forEach((h,i) => { row[h] = (cols[i]||"").replace(/^"|"$/g,"").trim(); });
    return row;
  }).filter(r => r.ticker || r["ticker symbol"] || r["symbol"]);
}

function normalizeRow(raw) {
  // Flatten various column name variants
  return {
    ticker:    (raw.ticker || raw["ticker symbol"] || raw.symbol || "").toUpperCase().trim(),
    assetRaw:  raw["asset class"] || raw.asset || raw["asset_class"] || "",
    styleRaw:  raw.style || raw["implementation style"] || raw["impl style"] || "",
    split:     parseFloat(raw["split %"] || raw.split || raw["default split"] || 0) || 0,
    er:        raw["er %"] !== undefined ? raw["er %"] : (raw.er || raw["expense ratio"] || ""),
    yld:       raw["yield %"] !== undefined ? raw["yield %"] : (raw.yield || raw["yld"] || ""),
    minAUM:    raw["min aum"] || raw.minaum || raw["minimum aum"] || "",
    minAlloc:  raw["min alloc"] || raw["min alloc ($)"] || raw.minalloc || raw["minimum alloc"] || "",
    note:      raw.note || raw.notes || raw.comment || "",
  };
}

function buildImportPreview(rows, engine) {
  // Group by ticker → style → assetId
  const byTicker = {};
  const errors   = [];

  rows.forEach((raw, ri) => {
    const r = normalizeRow(raw);
    if (!r.ticker) return;

    const assetId = normalizeAssetId(r.assetRaw, engine);
    const styleKey = normalizeStyle(r.styleRaw, engine);

    if (!assetId) { errors.push(`Row ${ri+2}: unrecognized asset class "${r.assetRaw}" for ticker ${r.ticker}`); return; }
    if (!styleKey) { errors.push(`Row ${ri+2}: unrecognized style "${r.styleRaw}" for ticker ${r.ticker}`); return; }

    if (!byTicker[r.ticker]) byTicker[r.ticker] = { ticker: r.ticker, er: r.er, yld: r.yld, minAUM: r.minAUM, minAlloc: r.minAlloc, note: r.note, assignments: [] };
    else {
      // later rows can override metadata if set
      if (r.er)       byTicker[r.ticker].er       = r.er;
      if (r.yld)      byTicker[r.ticker].yld       = r.yld;
      if (r.minAUM)   byTicker[r.ticker].minAUM    = r.minAUM;
      if (r.minAlloc) byTicker[r.ticker].minAlloc  = r.minAlloc;
      if (r.note)     byTicker[r.ticker].note      = r.note;
    }
    byTicker[r.ticker].assignments.push({ assetId, styleKey, split: r.split });
  });

  // Validate split totals per style+asset
  const splitTotals = {}; // "styleKey|assetId" → total
  Object.values(byTicker).forEach(td => {
    td.assignments.forEach(a => {
      const k = `${a.styleKey}|${a.assetId}`;
      splitTotals[k] = (splitTotals[k]||0) + a.split;
    });
  });
  Object.entries(splitTotals).forEach(([k, tot]) => {
    if (Math.abs(tot-100) >= 1) {
      const [sk, aid] = k.split("|");
      const aLabel = allAssets(engine).find(a=>a.id===aid)?.label || aid;
      errors.push(`Split warning: ${sk} / ${aLabel} sums to ${tot.toFixed(1)}% (should be 100%)`);
    }
  });

  return { preview: Object.values(byTicker), errors };
}

function applyImport(preview, engine, mode) {
  // mode: "merge" (keep existing, add/update imported) | "replace" (wipe style assignments first)
  const n = JSON.parse(JSON.stringify(engine));

  if (mode === "replace") {
    // Clear all existing ticker assignments in all styles
    Object.keys(n.implementationStyles).forEach(sk => {
      const sd = n.implementationStyles[sk];
      Object.keys(sd.tickers||{}).forEach(aid => { sd.tickers[aid] = []; });
      if (sd.defaultSplits) Object.keys(sd.defaultSplits).forEach(aid => { sd.defaultSplits[aid] = {}; });
    });
  }

  preview.forEach(td => {
    const sym = td.ticker;
    // tickerData
    if (!n.tickerData) n.tickerData = {};
    if (!n.tickerData[sym]) n.tickerData[sym] = {};
    if (td.er      !== "") n.tickerData[sym].expenseRatio = parseFloat(td.er)      || null;
    if (td.yld     !== "") n.tickerData[sym].yield         = parseFloat(td.yld)     || null;
    if (td.minAUM  !== "") n.tickerData[sym].minAUM        = parseFloat(td.minAUM)  || null;
    if (td.minAlloc!== "") n.tickerData[sym].minAlloc      = parseFloat(td.minAlloc)|| null;
    if (td.note    !== "") n.tickerData[sym].note           = td.note ? td.note.slice(0,60) || null : null;

    // style assignments
    td.assignments.forEach(({ assetId, styleKey, split }) => {
      const sd = n.implementationStyles[styleKey];
      if (!sd) return;
      if (!sd.tickers) sd.tickers = {};
      if (!sd.tickers[assetId]) sd.tickers[assetId] = [];
      if (!sd.tickers[assetId].includes(sym)) sd.tickers[assetId].push(sym);
      if (!sd.defaultSplits) sd.defaultSplits = {};
      if (!sd.defaultSplits[assetId]) sd.defaultSplits[assetId] = {};
      sd.defaultSplits[assetId][sym] = split;
    });
  });

  return n;
}

function generateTemplateCSV(engine) {
  const styleKeys = Object.keys(engine.implementationStyles);
  const assets    = allAssets(engine);
  const header    = ["Ticker","Asset Class","Style","Split %","ER %","Yield %","Min AUM","Min Alloc ($)","Note"];
  const rows      = [header.join(",")];

  // Example rows: one per existing ticker assignment
  styleKeys.forEach(sk => {
    const sd = engine.implementationStyles[sk];
    assets.forEach(a => {
      const tickers = sd.tickers?.[a.id] || [];
      tickers.forEach(t => {
        const td = engine.tickerData?.[t] || {};
        const split = sd.defaultSplits?.[a.id]?.[t] ?? (tickers[0]===t ? 100 : 0);
        rows.push([
          t, a.label, sk, split,
          td.expenseRatio ?? "",
          td.yield ?? "",
          td.minAUM ?? "",
          td.minAlloc ?? "",
          td.note ? `"${td.note}"` : "",
        ].join(","));
      });
    });
  });

  return rows.join("\n");
}

function TickerImporter({ engine, setEngine, addAudit=()=>{} }) {
  const [phase,   setPhase]   = useState("idle");   // idle | parsing | preview | done
  const [preview, setPreview] = useState([]);
  const [errors,  setErrors]  = useState([]);
  const [mode,    setMode]    = useState("merge");
  const [expanded, setExpanded] = useState(false);
  const fileRef = useRef(null);

  const assets    = allAssets(engine);
  const styleKeys = Object.keys(engine.implementationStyles);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhase("parsing");
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const rows = parseCSV(text);
        if (!rows.length) { setErrors(["No data rows found — check the file format."]); setPhase("idle"); return; }
        const { preview: p, errors: errs } = buildImportPreview(rows, engine);
        setPreview(p);
        setErrors(errs);
        setPhase("preview");
      } catch(err) {
        setErrors([`Parse error: ${err.message}`]);
        setPhase("idle");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleCommit = () => {
    setEngine(applyImport(preview, engine, mode));
    setPhase("done");
    setTimeout(() => { setPhase("idle"); setPreview([]); setErrors([]); setExpanded(false); }, 2200);
  };

  const handleDownloadTemplate = () => {
    const csv  = generateTemplateCSV(engine);
    const blob = new Blob([csv], { type:"text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a"); a.href=url; a.download="ticker_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const styleColor = (sk) => engine.implementationStyles[sk]?.color || "#2563EB";

  return (
    <div style={{border:"1px solid #E2E8F0",borderRadius:12,marginBottom:24,overflow:"hidden"}}>

      {/* Collapsed header bar */}
      <div
        onClick={()=>{ if(phase==="idle"||phase==="done") setExpanded(e=>!e); }}
        style={{padding:"11px 16px",background:"#F8FAFC",display:"flex",alignItems:"center",gap:10,cursor:phase==="idle"||phase==="done"?"pointer":"default",borderBottom:expanded?"1px solid #E2E8F0":"none"}}>
        <span style={{fontSize:15}}>📤</span>
        <div style={{flex:1}}>
          <span style={{fontWeight:700,fontSize:13,color:"#1E3A5F"}}>Upload Ticker Spreadsheet</span>
          <span style={{fontSize:11,color:"#94A3B8",marginLeft:10}}>CSV format · one row per ticker-style-asset</span>
        </div>
        <button onClick={e=>{e.stopPropagation();handleDownloadTemplate();}}
          style={{background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
          ⬇ Download Template
        </button>
        {phase==="done"
          ? <span style={{fontSize:11,fontWeight:700,color:"#16A34A",background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:20,padding:"3px 10px"}}>✓ Imported</span>
          : <span style={{fontSize:13,color:"#94A3B8",transition:"transform 0.2s",transform:expanded?"rotate(90deg)":"none",display:"inline-block"}}>›</span>
        }
      </div>

      {/* Expanded body */}
      {expanded && phase !== "done" && (
        <div style={{padding:"16px 18px",background:"#fff"}}>

          {phase === "idle" && (
            <div>
              {/* Format reference */}
              <div style={{marginBottom:14,fontSize:12,color:"#64748B",lineHeight:1.7}}>
                Upload a CSV with the following columns. Download the template above to get started with your existing tickers pre-filled.
              </div>
              <div style={{overflowX:"auto",marginBottom:16}}>
                <table style={{borderCollapse:"collapse",fontSize:11,width:"100%"}}>
                  <thead>
                    <tr style={{background:"#F8FAFC"}}>
                      {["Ticker","Asset Class","Style","Split %","ER %","Yield %","Min AUM","Min Alloc ($)","Note"].map(h=>(
                        <th key={h} style={{padding:"6px 12px",textAlign:"left",fontWeight:700,color:"#64748B",borderBottom:"2px solid #E2E8F0",whiteSpace:"nowrap",fontSize:10,textTransform:"uppercase",letterSpacing:"0.5px"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["FCNTX","US Equity","Flagship","60","0.47","0.10","","",""],
                      ["VWUSX","US Equity","Flagship","40","0.42","0.20","1000000","",""],
                      ["VTI","US Equity","Index Passive","100","0.03","1.30","","",""],
                      ["DFUS","US Equity","Systematic","100","0.09","1.40","","","Low-cost factor"],
                    ].map((row,i)=>(
                      <tr key={i} style={{background:i%2===0?"#fff":"#FAFBFC"}}>
                        {row.map((cell,j)=>(
                          <td key={j} style={{padding:"5px 12px",color:j===0?"#1E3A5F":"#475569",fontWeight:j===0?700:400,borderBottom:"1px solid #F4F6F9",whiteSpace:"nowrap",fontSize:11}}>{cell||<span style={{color:"#CBD5E1"}}>—</span>}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Asset class reference */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:6}}>Accepted Asset Class Names</div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {assets.map(a=>(
                    <span key={a.id} style={{fontSize:10,padding:"2px 9px",borderRadius:10,background:a.color+"0D",border:`1px solid ${a.color+"44"}`,color:a.color,fontWeight:600}}>{a.label}</span>
                  ))}
                </div>
              </div>

              {/* Upload drop zone */}
              <div
                onClick={()=>fileRef.current?.click()}
                onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="#EFF6FF";e.currentTarget.style.borderColor="#2563EB";}}
                onDragLeave={e=>{e.currentTarget.style.background="#FAFBFF";e.currentTarget.style.borderColor="#BFDBFE";}}
                onDrop={e=>{e.preventDefault();e.currentTarget.style.background="#FAFBFF";e.currentTarget.style.borderColor="#BFDBFE";const f=e.dataTransfer.files[0];if(f){const fakeEvt={target:{files:[f],value:""}};handleFile(fakeEvt);}}}
                style={{border:"2px dashed #BFDBFE",borderRadius:10,padding:"28px 20px",textAlign:"center",cursor:"pointer",background:"#FAFBFF",transition:"all 0.15s"}}>
                <div style={{fontSize:24,marginBottom:6}}>📂</div>
                <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F",marginBottom:3}}>Drop CSV here or click to browse</div>
                <div style={{fontSize:11,color:"#94A3B8"}}>CSV files only (.csv)</div>
              </div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{display:"none"}}/>
            </div>
          )}

          {phase === "parsing" && (
            <div style={{textAlign:"center",padding:"32px 0",color:"#94A3B8",fontSize:13}}>
              <div style={{width:20,height:20,border:"2px solid #BFDBFE",borderTopColor:"#2563EB",borderRadius:"50%",animation:"spin 0.8s linear infinite",margin:"0 auto 10px"}}/>
              Parsing…
            </div>
          )}

          {phase === "preview" && (
            <div>
              {/* Errors / warnings */}
              {errors.length > 0 && (
                <div style={{background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
                  <div style={{fontWeight:700,fontSize:12,color:"#DC2626",marginBottom:4}}>⚠ {errors.filter(e=>!e.startsWith("Split warning")).length} error(s) · {errors.filter(e=>e.startsWith("Split warning")).length} split warning(s)</div>
                  {errors.map((e,i)=>(
                    <div key={i} style={{fontSize:11,color:e.startsWith("Split warning")?"#B45309":"#991B1B",marginBottom:1}}>{e}</div>
                  ))}
                </div>
              )}

              {/* Mode selector */}
              <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
                <span style={{fontSize:11,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.5px"}}>Import mode:</span>
                {[["merge","Merge — add to existing"],["replace","Replace — wipe and rebuild"]].map(([val,label])=>(
                  <button key={val} onClick={()=>setMode(val)}
                    style={{padding:"6px 14px",borderRadius:7,border:`2px solid ${mode===val?"#1E3A5F":"#E2E8F0"}`,background:mode===val?"#1E3A5F":"#fff",color:mode===val?"#fff":"#64748B",fontSize:11,fontWeight:mode===val?700:500,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                    {label}
                  </button>
                ))}
                {mode==="replace"&&(
                  <span style={{fontSize:10,color:"#DC2626",fontWeight:600}}>⚠ This will clear all current style assignments</span>
                )}
              </div>

              {/* Preview table */}
              <div style={{fontSize:11,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:8}}>
                {preview.length} ticker{preview.length!==1?"s":""} ready to import
              </div>
              <div style={{overflowX:"auto",maxHeight:320,overflowY:"auto",border:"1px solid #E2E8F0",borderRadius:8,marginBottom:14}}>
                <table style={{borderCollapse:"collapse",fontSize:11,width:"100%",minWidth:600}}>
                  <thead style={{position:"sticky",top:0,zIndex:1}}>
                    <tr style={{background:"#F8FAFC"}}>
                      {["Ticker","ER %","Yield %","Min AUM","Min Alloc","Note","Assignments"].map(h=>(
                        <th key={h} style={{padding:"7px 12px",textAlign:"left",fontWeight:700,color:"#64748B",borderBottom:"2px solid #E2E8F0",whiteSpace:"nowrap",fontSize:10,textTransform:"uppercase",letterSpacing:"0.5px"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((td,i)=>{
                      const assetMap = assets.reduce((m,a)=>({...m,[a.id]:a}),{});
                      return (
                        <tr key={td.ticker} style={{background:i%2===0?"#fff":"#FAFBFC"}}>
                          <td style={{padding:"6px 12px",fontWeight:700,color:"#1E3A5F",borderBottom:"1px solid #F4F6F9"}}>{td.ticker}</td>
                          <td style={{padding:"6px 12px",color:"#DC2626",fontWeight:600,borderBottom:"1px solid #F4F6F9"}}>{td.er||"—"}</td>
                          <td style={{padding:"6px 12px",color:"#059669",fontWeight:600,borderBottom:"1px solid #F4F6F9"}}>{td.yld||"—"}</td>
                          <td style={{padding:"6px 12px",color:"#D97706",borderBottom:"1px solid #F4F6F9"}}>{td.minAUM?`$${Number(td.minAUM).toLocaleString()}`:"—"}</td>
                          <td style={{padding:"6px 12px",color:"#2563EB",borderBottom:"1px solid #F4F6F9"}}>{td.minAlloc?`$${Number(td.minAlloc).toLocaleString()}`:"—"}</td>
                          <td style={{padding:"6px 12px",color:"#475569",fontStyle:td.note?"normal":"italic",borderBottom:"1px solid #F4F6F9",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{td.note||"—"}</td>
                          <td style={{padding:"6px 12px",borderBottom:"1px solid #F4F6F9"}}>
                            <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                              {td.assignments.map((a,ai)=>{
                                const asset = assetMap[a.assetId];
                                const sc = styleColor(a.styleKey);
                                return (
                                  <span key={ai} style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:sc+"10",border:`1px solid ${sc+"44"}`,color:sc,fontWeight:700,whiteSpace:"nowrap"}}>
                                    {a.styleKey.split(" ")[0]} · {asset?.label||a.assetId} · {a.split}%
                                  </span>
                                );
                              })}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Actions */}
              <div style={{display:"flex",gap:10,justifyContent:"flex-end",alignItems:"center"}}>
                <button onClick={()=>{setPhase("idle");setPreview([]);setErrors([]);}}
                  style={{background:"none",border:"1px solid #E2E8F0",borderRadius:8,color:"#64748B",padding:"8px 18px",cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>
                  ← Re-upload
                </button>
                <button onClick={handleCommit} disabled={preview.length===0}
                  style={{background:preview.length?"#1E3A5F":"#E2E8F0",border:"none",borderRadius:8,color:preview.length?"#fff":"#94A3B8",padding:"8px 22px",cursor:preview.length?"pointer":"not-allowed",fontSize:12,fontWeight:700,fontFamily:"'Outfit',sans-serif"}}>
                  Confirm Import ({preview.length} tickers)
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TickerCSVManager({ engine, setEngine, addAudit=()=>{} }) {
  const { isMobile } = useBreakpoint();
  const allEngineAssets = allAssets(engine);
  const styleKeys = Object.keys(engine.implementationStyles);

  // ── Collect every unique ticker across all styles ──
  const allTickers = useMemo(() => {
    const seen = new Set();
    const list = [];
    styleKeys.forEach(sk => {
      const sd = engine.implementationStyles[sk];
      allEngineAssets.forEach(a => {
        (sd.tickers?.[a.id] || []).forEach(t => {
          if (t && !seen.has(t)) { seen.add(t); list.push(t); }
        });
      });
    });
    return list.sort();
  }, [engine]);

  // ── defaultSplits: { [styleKey]: { [assetId]: { [ticker]: pct } } }
  // Derived from current engine tickers (first ticker = 100%, rest = 0%)
  // Admin edits here update engine.implementationStyles[sk].tickers and engine.implementationStyles[sk].defaultSplits
  const getDefaultSplits = () => {
    const result = {};
    styleKeys.forEach(sk => {
      result[sk] = {};
      const sd = engine.implementationStyles[sk];
      allEngineAssets.forEach(a => {
        const tickers = sd.tickers?.[a.id] || [];
        const splits  = sd.defaultSplits?.[a.id] || {};
        result[sk][a.id] = {};
        tickers.forEach((t, ti) => {
          result[sk][a.id][t] = splits[t] ?? (ti === 0 ? 100 : 0);
        });
      });
    });
    return result;
  };

  // ── Helpers to read/write ──
  const isTickerInAsset = (sk, assetId, ticker) => {
    return (engine.implementationStyles[sk]?.tickers?.[assetId] || []).includes(ticker);
  };

  const getDefaultPct = (sk, assetId, ticker) => {
    const splits = engine.implementationStyles[sk]?.defaultSplits?.[assetId] || {};
    const tickers = engine.implementationStyles[sk]?.tickers?.[assetId] || [];
    if (splits[ticker] != null) return splits[ticker];
    // fallback: first ticker = 100
    return tickers[0] === ticker ? 100 : 0;
  };

  const getER = (ticker) => engine.tickerData?.[ticker]?.expenseRatio ?? "";
  const getYield = (ticker) => engine.tickerData?.[ticker]?.yield ?? "";

  const setTickerInAsset = (sk, assetId, ticker, include) => {
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      const tickers = n.implementationStyles[sk].tickers[assetId] || [];
      if (include) {
        if (!tickers.includes(ticker)) tickers.push(ticker);
        n.implementationStyles[sk].tickers[assetId] = tickers;
      } else {
        n.implementationStyles[sk].tickers[assetId] = tickers.filter(t => t !== ticker);
        // clean up split
        if (n.implementationStyles[sk].defaultSplits?.[assetId]) {
          delete n.implementationStyles[sk].defaultSplits[assetId][ticker];
        }
      }
      return n;
    });
  };

  const setDefaultPct = (sk, assetId, ticker, val) => {
    const num = Math.max(0, Math.min(100, Number(val) || 0));
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.implementationStyles[sk].defaultSplits) n.implementationStyles[sk].defaultSplits = {};
      if (!n.implementationStyles[sk].defaultSplits[assetId]) n.implementationStyles[sk].defaultSplits[assetId] = {};
      n.implementationStyles[sk].defaultSplits[assetId][ticker] = num;
      return n;
    });
  };

  const setER = (ticker, val) => {
    const v = val === "" ? null : Number(val);
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.tickerData) n.tickerData = {};
      if (!n.tickerData[ticker]) n.tickerData[ticker] = {};
      n.tickerData[ticker].expenseRatio = v;
      return n;
    });
  };

  const setYield = (ticker, val) => {
    const v = val === "" ? null : Number(val);
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.tickerData) n.tickerData = {};
      if (!n.tickerData[ticker]) n.tickerData[ticker] = {};
      n.tickerData[ticker].yield = v;
      return n;
    });
  };

  const getMinAUM = (ticker) => {
    const v = engine.tickerData?.[ticker]?.minAUM;
    return v != null ? v : "";
  };

  const setMinAUM = (ticker, val) => {
    const v = val === "" ? null : Number(val);
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.tickerData) n.tickerData = {};
      if (!n.tickerData[ticker]) n.tickerData[ticker] = {};
      n.tickerData[ticker].minAUM = v;
      return n;
    });
  };

  const fmtMinAUM = (v) => {
    if (!v && v !== 0) return "";
    if (v >= 1000000) return "$" + (v/1000000).toFixed(v%1000000===0?0:1) + "M";
    if (v >= 1000)    return "$" + (v/1000).toFixed(0) + "K";
    return "$" + v;
  };

  const getMinAlloc = (ticker) => {
    const v = engine.tickerData?.[ticker]?.minAlloc;
    return v != null ? v : "";
  };

  const setMinAlloc = (ticker, val) => {
    const v = val === "" ? null : Number(val);
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.tickerData) n.tickerData = {};
      if (!n.tickerData[ticker]) n.tickerData[ticker] = {};
      n.tickerData[ticker].minAlloc = v;
      return n;
    });
  };

  const getNote = (ticker) => engine.tickerData?.[ticker]?.note ?? "";

  const setNote = (ticker, val) => {
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.tickerData) n.tickerData = {};
      if (!n.tickerData[ticker]) n.tickerData[ticker] = {};
      n.tickerData[ticker].note = val || null;
      return n;
    });
  };

  const [newTickerInput, setNewTickerInput] = useState("");
  const [showAll, setShowAll] = useState(false);
  const addTicker = () => {
    const sym = newTickerInput.trim().toUpperCase();
    if (!sym) return;
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.tickerData) n.tickerData = {};
      if (!n.tickerData[sym]) n.tickerData[sym] = {};
      return n;
    });
    setNewTickerInput("");
    setShowAll(true); // show all so the new ticker is visible
  };

  // Collect all unique tickers for a specific asset across all styles
  const tickersForAsset = (assetId) => {
    const seen = new Set();
    const list = [];
    styleKeys.forEach(sk => {
      (engine.implementationStyles[sk]?.tickers?.[assetId] || []).forEach(t => {
        if (t && !seen.has(t)) { seen.add(t); list.push(t); }
      });
    });
    // also include any ticker in tickerData that has been manually assigned
    return list;
  };

  const thBase = { padding:"8px 12px", fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", whiteSpace:"nowrap", background:"#F8FAFC", borderBottom:"2px solid #E2E8F0" };
  const tdBase = { padding:"7px 10px", borderBottom:"1px solid #F4F6F9", verticalAlign:"middle" };

  return (
    <div>
      <TickerImporter engine={engine} setEngine={setEngine} addAudit={addAudit}/>
      <div style={{fontSize:13,color:"#64748B",marginBottom:18}}>
        Tickers are organized by asset class. Each row is a ticker — check the box under each style to include it, set the default split %, and edit ER and yield. Splits per style per asset class must sum to 100%.
      </div>

      {/* Per-asset-class sections */}
      {allEngineAssets.map(a => {
        const assetTickers = tickersForAsset(a.id);
        // split totals per style for this asset
        const styleTotals = {};
        styleKeys.forEach(sk => {
          const tickers = engine.implementationStyles[sk]?.tickers?.[a.id] || [];
          styleTotals[sk] = tickers.reduce((sum, t) => sum + getDefaultPct(sk, a.id, t), 0);
        });
        const anyBad = styleKeys.some(sk => {
          const tickers = engine.implementationStyles[sk]?.tickers?.[a.id] || [];
          return tickers.length > 0 && Math.abs(styleTotals[sk] - 100) >= 0.5;
        });

        return (
          <div key={a.id} style={{marginBottom:24,border:`1px solid ${anyBad?"#FCA5A5":a.color+"44"}`,borderRadius:12,overflow:"hidden"}}>

            {/* Section header */}
            <div style={{background:a.color+"0D",padding:"10px 16px",display:"flex",alignItems:"center",gap:10,borderBottom:`1px solid ${a.color+"33"}`}}>
              <div style={{width:10,height:10,borderRadius:"50%",background:a.color,flexShrink:0}}/>
              <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F",flex:1}}>{a.label}</div>
              {/* Per-style split totals */}
              <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
                {styleKeys.map(sk => {
                  const tickers = engine.implementationStyles[sk]?.tickers?.[a.id] || [];
                  if (!tickers.length) return null;
                  const tot = styleTotals[sk];
                  const ok  = Math.abs(tot - 100) < 0.5;
                  return (
                    <span key={sk} style={{fontSize:10,fontWeight:700,color:ok?"#16A34A":"#DC2626",background:ok?"#F0FDF4":"#FFF5F5",border:`1px solid ${ok?"#BBF7D0":"#FCA5A5"}`,borderRadius:6,padding:"2px 8px",whiteSpace:"nowrap"}}>
                      {sk}: {tot}% {ok?"✓":"⚠"}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Spreadsheet table */}
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,background:"#fff"}}>
                <thead>
                  <tr>
                    <th style={{...thBase,textAlign:"left",minWidth:100,paddingLeft:16}}>Ticker</th>
                    <th style={{...thBase,textAlign:"center",minWidth:72}}>ER %</th>
                    <th style={{...thBase,textAlign:"center",minWidth:72}}>Yield %</th>
                    <th style={{...thBase,textAlign:"center",minWidth:100,borderLeft:"1px solid #E2E8F0"}}>
                      Min AUM
                      <div style={{fontSize:9,color:"#94A3B8",fontWeight:400,marginTop:1}}>blank = all levels</div>
                    </th>
                    <th style={{...thBase,textAlign:"center",minWidth:100,borderLeft:"1px solid #E2E8F0"}}>
                      Min Alloc ($)
                      <div style={{fontSize:9,color:"#94A3B8",fontWeight:400,marginTop:1}}>flags if under</div>
                    </th>
                    <th style={{...thBase,textAlign:"left",minWidth:160,borderLeft:"1px solid #E2E8F0"}}>
                      Note
                      <div style={{fontSize:9,color:"#94A3B8",fontWeight:400,marginTop:1}}>shown on ticker pill</div>
                    </th>
                    {styleKeys.map(sk => {
                      const sc = engine.implementationStyles[sk].color;
                      return (
                        <th key={sk} style={{...thBase,textAlign:"center",minWidth:120,borderLeft:"1px solid #E2E8F0"}}>
                          <span style={{color:sc}}>{sk}</span>
                          <div style={{fontSize:9,color:"#94A3B8",fontWeight:400,marginTop:1}}>✓ include · default %</div>
                        </th>
                      );
                    })}
                    <th style={{...thBase,textAlign:"center",minWidth:60,borderLeft:"1px solid #E2E8F0"}}></th>
                  </tr>
                </thead>
                <tbody>
                  {assetTickers.map((ticker, ri) => (
                    <tr key={ticker} style={{background:ri%2===0?"#fff":"#FAFBFC"}}>
                      {/* Ticker symbol */}
                      <td style={{...tdBase,paddingLeft:16}}>
                        <span style={{fontWeight:700,fontSize:13,color:"#1E3A5F"}}>{ticker}</span>
                      </td>

                      {/* ER */}
                      <td style={{...tdBase,textAlign:"center"}}>
                        <div style={{display:"flex",alignItems:"center",gap:2,justifyContent:"center"}}>
                          <input type="number" min={0} max={5} step={0.01} value={getER(ticker)} placeholder="—"
                            onChange={e=>setER(ticker,e.target.value)}
                            style={{width:52,padding:"4px 6px",border:"1px solid #E2E8F0",borderRadius:6,fontSize:12,fontWeight:700,textAlign:"right",color:"#DC2626",background:"#fff",outline:"none",fontFamily:"'Outfit',sans-serif"}}/>
                          <span style={{fontSize:10,color:"#94A3B8"}}>%</span>
                        </div>
                      </td>

                      {/* Yield */}
                      <td style={{...tdBase,textAlign:"center"}}>
                        <div style={{display:"flex",alignItems:"center",gap:2,justifyContent:"center"}}>
                          <input type="number" min={0} max={20} step={0.01} value={getYield(ticker)} placeholder="—"
                            onChange={e=>setYield(ticker,e.target.value)}
                            style={{width:52,padding:"4px 6px",border:"1px solid #E2E8F0",borderRadius:6,fontSize:12,fontWeight:700,textAlign:"right",color:"#059669",background:"#fff",outline:"none",fontFamily:"'Outfit',sans-serif"}}/>
                          <span style={{fontSize:10,color:"#94A3B8"}}>%</span>
                        </div>
                      </td>

                      {/* Min AUM */}
                      <td style={{...tdBase,textAlign:"center",borderLeft:"1px solid #F1F5F9"}}>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
                          <input type="number" min={0} step={100000} value={getMinAUM(ticker)} placeholder="none"
                            onChange={e=>setMinAUM(ticker,e.target.value)}
                            style={{width:80,padding:"4px 6px",border:`1px solid ${getMinAUM(ticker)!==""?"#FDE68A":"#E2E8F0"}`,borderRadius:6,fontSize:11,fontWeight:700,textAlign:"right",color:getMinAUM(ticker)!==""?"#92400E":"#94A3B8",background:getMinAUM(ticker)!==""?"#FFFBEB":"#fff",outline:"none",fontFamily:"'Outfit',sans-serif"}}/>
                          {getMinAUM(ticker)!==""&&<span style={{fontSize:9,color:"#D97706",fontWeight:700}}>{fmtMinAUM(getMinAUM(ticker))}</span>}
                        </div>
                      </td>

                      {/* Min Alloc */}
                      <td style={{...tdBase,textAlign:"center",borderLeft:"1px solid #F1F5F9"}}>
                        <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:1}}>
                          <input type="number" min={0} step={1000} value={getMinAlloc(ticker)} placeholder="none"
                            onChange={e=>setMinAlloc(ticker,e.target.value)}
                            style={{width:80,padding:"4px 6px",border:`1px solid ${getMinAlloc(ticker)!==""?"#BFDBFE":"#E2E8F0"}`,borderRadius:6,fontSize:11,fontWeight:700,textAlign:"right",color:getMinAlloc(ticker)!==""?"#1D4ED8":"#94A3B8",background:getMinAlloc(ticker)!==""?"#EFF6FF":"#fff",outline:"none",fontFamily:"'Outfit',sans-serif"}}/>
                          {getMinAlloc(ticker)!==""&&<span style={{fontSize:9,color:"#2563EB",fontWeight:700}}>{fmtMinAUM(getMinAlloc(ticker))}</span>}
                        </div>
                      </td>

                      {/* Note */}
                      <td style={{...tdBase,borderLeft:"1px solid #F1F5F9"}}>
                        <div style={{position:"relative"}}>
                          <input value={getNote(ticker)} placeholder="e.g. Institutional class…"
                            maxLength={60}
                            onChange={e=>setNote(ticker,e.target.value.slice(0,60))}
                            style={{width:"100%",padding:"4px 8px",border:`1px solid ${getNote(ticker)?"#E2E8F0":"#F1F5F9"}`,borderRadius:6,fontSize:11,color:"#475569",background:getNote(ticker)?"#fff":"transparent",outline:"none",fontFamily:"'Outfit',sans-serif",minWidth:140,boxSizing:"border-box"}}/>
                          {getNote(ticker).length > 50 && (
                            <span style={{position:"absolute",right:4,bottom:-14,fontSize:9,color:getNote(ticker).length>=60?"#DC2626":"#94A3B8",fontFamily:"'Outfit',sans-serif"}}>
                              {getNote(ticker).length}/60
                            </span>
                          )}
                        </div>
                      </td>

                      {/* One column per style */}
                      {styleKeys.map(sk => {
                        const sc       = engine.implementationStyles[sk].color;
                        const included = isTickerInAsset(sk, a.id, ticker);
                        const defPct   = getDefaultPct(sk, a.id, ticker);
                        const splitOk  = Math.abs(styleTotals[sk] - 100) < 0.5;
                        return (
                          <td key={sk} style={{...tdBase,textAlign:"center",borderLeft:"1px solid #F1F5F9",background:included?sc+"08":undefined}}>
                            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8}}>
                              {/* Checkbox */}
                              <button onClick={()=>setTickerInAsset(sk,a.id,ticker,!included)}
                                style={{width:18,height:18,borderRadius:4,border:`2px solid ${included?sc:"#CBD5E1"}`,background:included?sc:"#fff",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",padding:0,flexShrink:0}}>
                                {included&&<span style={{color:"#fff",fontSize:10,lineHeight:1,fontWeight:900}}>✓</span>}
                              </button>
                              {/* Split % — only when included */}
                              {included && (
                                <div style={{display:"flex",alignItems:"center",gap:2}}>
                                  <input type="number" min={0} max={100} value={defPct}
                                    onChange={e=>setDefaultPct(sk,a.id,ticker,e.target.value)}
                                    style={{width:44,padding:"3px 5px",border:`1px solid ${splitOk?"#E2E8F0":"#FCA5A5"}`,borderRadius:5,fontSize:12,fontWeight:700,textAlign:"center",color:"#1E3A5F",background:"#fff",outline:"none",fontFamily:"'Outfit',sans-serif"}}/>
                                  <span style={{fontSize:9,color:"#94A3B8"}}>%</span>
                                </div>
                              )}
                            </div>
                          </td>
                        );
                      })}

                      {/* Remove ticker from this asset entirely */}
                      <td style={{...tdBase,textAlign:"center",borderLeft:"1px solid #F1F5F9"}}>
                        <button onClick={()=>{
                          styleKeys.forEach(sk => setTickerInAsset(sk, a.id, ticker, false));
                        }} title="Remove from this asset class"
                          style={{background:"none",border:"none",color:"#CBD5E1",cursor:"pointer",fontSize:14,lineHeight:1,padding:"2px 4px"}}
                          onMouseEnter={e=>e.target.style.color="#DC2626"}
                          onMouseLeave={e=>e.target.style.color="#CBD5E1"}>
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}

                  {/* Add ticker row */}
                  <tr style={{background:"#F8FAFC"}}>
                    <td colSpan={7 + styleKeys.length} style={{padding:"8px 16px"}}>
                      <AddTickerToAsset assetId={a.id} engine={engine} setEngine={setEngine} styleKeys={styleKeys} allTickers={allTickers} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {/* Global add ticker to pool */}
      <div style={{marginTop:4,display:"flex",gap:10,alignItems:"center",flexWrap:"wrap"}}>
        <div style={{fontSize:12,color:"#94A3B8",fontWeight:600}}>Add a new ticker to the pool:</div>
        <input value={newTickerInput} onChange={e=>setNewTickerInput(e.target.value.toUpperCase())}
          onKeyDown={e=>e.key==="Enter"&&addTicker()}
          placeholder="e.g. VXUS"
          style={{...inp,width:160,fontSize:12,padding:"7px 11px"}}/>
        <button onClick={addTicker}
          style={{background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
          + Add
        </button>
        {allEngineAssets.some(a=>styleKeys.some(sk=>{
          const tickers=engine.implementationStyles[sk]?.tickers?.[a.id]||[];
          return tickers.length>0 && Math.abs(tickers.reduce((s,t)=>s+getDefaultPct(sk,a.id,t),0)-100)>=0.5;
        }))&&(
          <span style={{fontSize:11,color:"#DC2626",fontWeight:700}}>⚠ Some splits don't sum to 100%</span>
        )}
      </div>
    </div>
  );
}

// Inline add-ticker-to-asset helper (rendered inside table row)
function AddTickerToAsset({ assetId, engine, setEngine, styleKeys, allTickers }) {
  const [input, setInput] = useState("");
  const [open,  setOpen]  = useState(false);

  const existing = new Set();
  styleKeys.forEach(sk => {
    (engine.implementationStyles[sk]?.tickers?.[assetId]||[]).forEach(t=>existing.add(t));
  });

  const suggestions = input.length >= 1
    ? allTickers.filter(t => t.toUpperCase().startsWith(input) && !existing.has(t))
    : [];

  const addTo = (sym) => {
    if (!sym) return;
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      // Add to first style that doesn't have it yet; or all styles that have this asset
      styleKeys.forEach(sk => {
        if (!n.implementationStyles[sk].tickers[assetId]) n.implementationStyles[sk].tickers[assetId] = [];
        if (!n.implementationStyles[sk].tickers[assetId].includes(sym)) {
          n.implementationStyles[sk].tickers[assetId].push(sym);
        }
        if (!n.tickerData) n.tickerData = {};
        if (!n.tickerData[sym]) n.tickerData[sym] = {};
      });
      return n;
    });
    setInput(""); setOpen(false);
  };

  return (
    <div style={{display:"flex",alignItems:"center",gap:8,position:"relative"}}>
      <input value={input} onChange={e=>{setInput(e.target.value.toUpperCase());setOpen(true);}}
        onKeyDown={e=>e.key==="Enter"&&addTo(input)}
        onBlur={()=>setTimeout(()=>setOpen(false),150)}
        placeholder="+ Add ticker to this asset class…"
        style={{border:"1px dashed #BFDBFE",borderRadius:6,padding:"5px 10px",fontSize:11,fontWeight:700,color:"#2563EB",background:"#EFF6FF",outline:"none",fontFamily:"'Outfit',sans-serif",width:240}}/>
      {open && suggestions.length > 0 && (
        <div style={{position:"absolute",top:"100%",left:0,background:"#fff",border:"1px solid #E2E8F0",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,0.1)",zIndex:100,minWidth:160}}>
          {suggestions.slice(0,6).map(t=>(
            <button key={t} onMouseDown={()=>addTo(t)}
              style={{display:"block",width:"100%",padding:"7px 12px",border:"none",background:"none",textAlign:"left",cursor:"pointer",fontSize:12,fontWeight:700,color:"#1E3A5F",fontFamily:"'Outfit',sans-serif"}}
              onMouseEnter={e=>e.target.style.background="#EFF6FF"} onMouseLeave={e=>e.target.style.background="none"}>
              {t}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SLEEVE EDITOR  (admin panel tab)
// ─────────────────────────────────────────────────────────────
function SleeveEditor({ engine, setEngine, addAudit=()=>{} }) {
  const styleKeys  = Object.keys(engine.implementationStyles);
  // "default" = global sleeve weights; else a style name
  const [activeTab, setActiveTab] = useState("default");
  const [uploadPhase, setUploadPhase] = useState("idle");
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadErrors,  setUploadErrors]  = useState([]);
  const [uploadExpanded, setUploadExpanded] = useState(false);
  const fileRef = useRef(null);

  // ── Helpers to get/set weights for current tab ──
  const isDefault = activeTab === "default";

  const getWeight = (sleeveKey, assetId) => {
    if (isDefault) {
      return engine.sleeves[sleeveKey]?.assets.find(a=>a.id===assetId)?.weight ?? 0;
    }
    const sw = engine.implementationStyles[activeTab]?.sleeveWeights;
    if (sw?.[sleeveKey]?.[assetId] !== undefined) return sw[sleeveKey][assetId];
    // fall back to global
    return engine.sleeves[sleeveKey]?.assets.find(a=>a.id===assetId)?.weight ?? 0;
  };

  const hasOverride = (sleeveKey, assetId) => {
    if (isDefault) return false;
    return engine.implementationStyles[activeTab]?.sleeveWeights?.[sleeveKey]?.[assetId] !== undefined;
  };

  const setWeight = (sleeveKey, assetId, val) => {
    const num = Math.max(0, Math.min(100, Number(val)||0));
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (isDefault) {
        const idx = n.sleeves[sleeveKey].assets.findIndex(a=>a.id===assetId);
        if (idx >= 0) n.sleeves[sleeveKey].assets[idx].weight = num;
      } else {
        if (!n.implementationStyles[activeTab].sleeveWeights) n.implementationStyles[activeTab].sleeveWeights = {};
        if (!n.implementationStyles[activeTab].sleeveWeights[sleeveKey]) n.implementationStyles[activeTab].sleeveWeights[sleeveKey] = {};
        n.implementationStyles[activeTab].sleeveWeights[sleeveKey][assetId] = num;
      }
      return n;
    });
  };

  const clearOverride = (sleeveKey, assetId) => {
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (n.implementationStyles[activeTab]?.sleeveWeights?.[sleeveKey]) {
        delete n.implementationStyles[activeTab].sleeveWeights[sleeveKey][assetId];
      }
      return n;
    });
  };

  const clearAllOverrides = () => {
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (n.implementationStyles[activeTab]) n.implementationStyles[activeTab].sleeveWeights = {};
      return n;
    });
  };

  const normalize = (sleeveKey) => {
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      const assets = n.sleeves[sleeveKey].assets;
      if (isDefault) {
        const t = assets.reduce((s,a)=>s+a.weight,0);
        if (!t) return n;
        assets.forEach(a=>{ a.weight=Math.round(a.weight/t*1000)/10; });
        const diff=100-assets.reduce((s,a)=>s+a.weight,0);
        assets[assets.length-1].weight=Math.round((assets[assets.length-1].weight+diff)*10)/10;
      } else {
        const sw = n.implementationStyles[activeTab].sleeveWeights?.[sleeveKey] || {};
        const weights = assets.map(a => sw[a.id] ?? a.weight);
        const t = weights.reduce((s,v)=>s+v,0);
        if (!t) return n;
        const normed = weights.map(w=>Math.round(w/t*1000)/10);
        const diff=100-normed.reduce((s,v)=>s+v,0);
        normed[normed.length-1]=Math.round((normed[normed.length-1]+diff)*10)/10;
        if (!n.implementationStyles[activeTab].sleeveWeights) n.implementationStyles[activeTab].sleeveWeights={};
        n.implementationStyles[activeTab].sleeveWeights[sleeveKey]={};
        assets.forEach((a,i)=>{ n.implementationStyles[activeTab].sleeveWeights[sleeveKey][a.id]=normed[i]; });
      }
      return n;
    });
  };

  // ── CSV parser ──
  const parseSleeveCSV = (text) => {
    const lines = text.replace(/\r/g,"").split("\n").filter(l=>l.trim());
    if (!lines.length) return { rows:[], errors:["Empty file"] };
    const headers = lines[0].split(",").map(h=>h.trim().replace(/^"|"$/g,"").toLowerCase());
    const hasHeader = headers.some(h=>h.includes("asset")||h.includes("sleeve")||h.includes("weight"));
    const dataLines = hasHeader ? lines.slice(1) : lines;

    const rows = [];
    const errors = [];
    dataLines.forEach((line,li) => {
      const cols = line.split(",").map(c=>c.trim().replace(/^"|"$/g,""));
      let assetRaw, sleeveRaw, weightRaw, styleRaw;
      if (hasHeader) {
        const ai = headers.findIndex(h=>h.includes("asset"));
        const si = headers.findIndex(h=>h.includes("sleeve"));
        const wi = headers.findIndex(h=>h.includes("weight"));
        const sti= headers.findIndex(h=>h.includes("style")||h.includes("impl"));
        assetRaw  = cols[ai]  || "";
        sleeveRaw = cols[si]  || "";
        weightRaw = cols[wi]  || "";
        styleRaw  = sti>=0 ? cols[sti]||"" : "";
      } else {
        // headerless: asset, sleeve, weight[, style]
        [assetRaw, sleeveRaw, weightRaw, styleRaw] = cols;
      }
      if (!assetRaw || !weightRaw) return;
      const assetId = normalizeAssetId(assetRaw, engine);
      if (!assetId) { errors.push(`Row ${li+2}: unrecognized asset "${assetRaw}"`); return; }
      const sleeveKey = sleeveRaw.toLowerCase().includes("def") ? "defensive" : "opportunistic";
      const weight = parseFloat(weightRaw);
      if (isNaN(weight)) { errors.push(`Row ${li+2}: invalid weight "${weightRaw}"`); return; }
      // style: blank = default, else match style name
      const styleName = styleRaw ? (Object.keys(engine.implementationStyles).find(sk=>sk.toLowerCase()===styleRaw.toLowerCase().trim())||null) : null;
      if (styleRaw && !styleName) { errors.push(`Row ${li+2}: unrecognized style "${styleRaw}" — applied to default`); }
      rows.push({ assetId, sleeveKey, weight, styleName: styleName||null });
    });

    // Validate totals
    const totals = {};
    rows.forEach(r => {
      const k = `${r.styleName||"default"}|${r.sleeveKey}`;
      totals[k] = (totals[k]||0) + r.weight;
    });
    Object.entries(totals).forEach(([k,t]) => {
      if (Math.abs(t-100)>=1) errors.push(`Weight warning: ${k.replace("|"," / ")} sums to ${t.toFixed(1)}%`);
    });

    return { rows, errors };
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const { rows, errors } = parseSleeveCSV(ev.target.result);
      if (!rows.length) { setUploadErrors(["No data rows found."]); return; }
      setUploadPreview(rows);
      setUploadErrors(errors);
      setUploadPhase("preview");
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const commitUpload = () => {
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      uploadPreview.forEach(row => {
        if (!row.styleName) {
          // update global
          const idx = n.sleeves[row.sleeveKey]?.assets.findIndex(a=>a.id===row.assetId);
          if (idx >= 0) n.sleeves[row.sleeveKey].assets[idx].weight = row.weight;
        } else {
          if (!n.implementationStyles[row.styleName].sleeveWeights) n.implementationStyles[row.styleName].sleeveWeights={};
          if (!n.implementationStyles[row.styleName].sleeveWeights[row.sleeveKey]) n.implementationStyles[row.styleName].sleeveWeights[row.sleeveKey]={};
          n.implementationStyles[row.styleName].sleeveWeights[row.sleeveKey][row.assetId]=row.weight;
        }
      });
      return n;
    });
    addAudit("sleeve_imported", `Sleeve weights CSV import: ${uploadPreview.length} weight rows`);
    setUploadPhase("done");
    setTimeout(()=>{ setUploadPhase("idle"); setUploadPreview(null); setUploadErrors([]); setUploadExpanded(false); },2000);
  };

  const downloadTemplate = () => {
    const rows = ["Asset Class,Sleeve,Weight %,Style (blank = default)"];
    Object.entries(engine.sleeves).forEach(([sk, sleeve]) => {
      sleeve.assets.forEach(a => {
        rows.push(`${a.label},${sleeve.label},${a.weight},`);
        // also emit any style overrides
        styleKeys.forEach(stk => {
          const sw = engine.implementationStyles[stk]?.sleeveWeights?.[sk]?.[a.id];
          if (sw !== undefined) rows.push(`${a.label},${sleeve.label},${sw},${stk}`);
        });
      });
    });
    const blob = new Blob([rows.join("\n")],{type:"text/csv"});
    const url  = URL.createObjectURL(blob);
    const el   = document.createElement("a"); el.href=url; el.download="sleeve_weights.csv"; el.click();
    URL.revokeObjectURL(url);
  };

  // ── Tab bar ──
  const tabDefs = [{ key:"default", label:"Default" }, ...styleKeys.map(sk=>({ key:sk, label:sk }))];

  // ── Per-sleeve weight editor ──
  const SleeveBlock = ({ sleeveKey }) => {
    const sleeve   = engine.sleeves[sleeveKey];
    const assets   = sleeve.assets;
    const weights  = assets.map(a => getWeight(sleeveKey, a.id));
    const total    = weights.reduce((s,v)=>s+v,0);
    const ok       = Math.abs(total-100) < 0.5;
    const anyOverride = !isDefault && assets.some(a=>hasOverride(sleeveKey,a.id));
    const styleColor = isDefault ? sleeve.color : (engine.implementationStyles[activeTab]?.color || sleeve.color);

    return (
      <div style={{border:`1px solid ${ok?styleColor+"44":"#FCA5A5"}`,borderRadius:12,padding:"16px 18px",marginBottom:16,background:"#F8FAFC"}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap"}}>
          <div style={{fontFamily:"'Outfit',sans-serif",fontSize:17,fontWeight:700,color:"#1E3A5F"}}>{sleeve.label}</div>
          <div style={{marginLeft:"auto",display:"flex",alignItems:"center",gap:8}}>
            {anyOverride && (
              <button onClick={()=>clearAllOverrides()}
                style={{fontSize:10,color:"#94A3B8",background:"none",border:"1px solid #E2E8F0",borderRadius:6,padding:"2px 8px",cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                reset to default
              </button>
            )}
            <span style={{fontSize:13,fontWeight:800,color:ok?"#16A34A":"#DC2626"}}>{total.toFixed(1)}% {ok?"✓":"≠ 100%"}</span>
          </div>
        </div>

        {assets.map((a, idx) => {
          const w    = getWeight(sleeveKey, a.id);
          const over = hasOverride(sleeveKey, a.id);
          return (
            <div key={a.id} style={{marginBottom:10}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <div style={{width:8,height:8,borderRadius:"50%",background:a.color,flexShrink:0}}/>
                <span style={{flex:1,fontSize:13,fontWeight:600,color:"#334155"}}>{a.label}</span>
                {over && (
                  <button onClick={()=>clearOverride(sleeveKey,a.id)}
                    title="Reset to default weight"
                    style={{fontSize:9,color:"#D97706",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:4,padding:"1px 6px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:700}}>
                    override ×
                  </button>
                )}
                {!isDefault && !over && (
                  <span style={{fontSize:9,color:"#94A3B8",fontStyle:"italic"}}>default</span>
                )}
                <div style={{display:"flex",alignItems:"center",gap:3}}>
                  <input type="number" min={0} max={100} step={0.5} value={w}
                    onChange={e=>setWeight(sleeveKey, a.id, e.target.value)}
                    style={{width:58,padding:"5px 8px",border:`1px solid ${over?"#FDE68A":"#E2E8F0"}`,borderRadius:6,fontSize:13,fontWeight:700,textAlign:"right",color:over?"#92400E":"#1E3A5F",background:over?"#FFFBEB":"#fff",outline:"none",fontFamily:"'Outfit',sans-serif"}}/>
                  <span style={{fontSize:11,color:"#94A3B8"}}>%</span>
                </div>
              </div>
              <div style={{height:5,background:"#E2E8F0",borderRadius:3,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${Math.min(w,100)}%`,background:over?"#D97706":a.color,borderRadius:3,transition:"width 0.2s"}}/>
              </div>
            </div>
          );
        })}

        {!ok && (
          <button onClick={()=>normalize(sleeveKey)}
            style={{fontSize:12,color:"#D97706",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:7,padding:"6px 14px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:700,marginTop:4}}>
            Auto-normalize to 100%
          </button>
        )}
      </div>
    );
  };

  const hasAnyOverride = !isDefault && styleKeys.some(sk => {
    const sw = engine.implementationStyles[activeTab]?.sleeveWeights;
    return sw && Object.keys(sw.opportunistic||{}).length + Object.keys(sw.defensive||{}).length > 0;
  });

  return (
    <div>
      <div style={{fontSize:13,color:"#64748B",marginBottom:16,lineHeight:1.6}}>
        Set asset weights <strong>within each sleeve</strong>. The Default tab applies to all styles unless overridden.
        Select a style tab to set weights that apply only when that style is active — overridden assets are highlighted in amber.
      </div>

      {/* ── Upload panel ── */}
      <div style={{border:"1px solid #E2E8F0",borderRadius:12,marginBottom:20,overflow:"hidden"}}>
        <div onClick={()=>{ if(uploadPhase==="idle"||uploadPhase==="done") setUploadExpanded(e=>!e); }}
          style={{padding:"11px 16px",background:"#F8FAFC",display:"flex",alignItems:"center",gap:10,cursor:"pointer",borderBottom:uploadExpanded?"1px solid #E2E8F0":"none"}}>
          <span style={{fontSize:15}}>📤</span>
          <div style={{flex:1}}>
            <span style={{fontWeight:700,fontSize:13,color:"#1E3A5F"}}>Upload Sleeve Weights</span>
            <span style={{fontSize:11,color:"#94A3B8",marginLeft:10}}>CSV · Asset Class, Sleeve, Weight %, Style</span>
          </div>
          <button onClick={e=>{e.stopPropagation();downloadTemplate();}}
            style={{background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
            ⬇ Download Template
          </button>
          {uploadPhase==="done"
            ? <span style={{fontSize:11,fontWeight:700,color:"#16A34A",background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:20,padding:"3px 10px"}}>✓ Imported</span>
            : <span style={{fontSize:13,color:"#94A3B8",display:"inline-block",transition:"transform 0.2s",transform:uploadExpanded?"rotate(90deg)":"none"}}>›</span>
          }
        </div>

        {uploadExpanded && uploadPhase !== "done" && (
          <div style={{padding:"16px 18px",background:"#fff"}}>
            {uploadPhase === "idle" && (
              <div>
                <div style={{fontSize:12,color:"#64748B",marginBottom:12,lineHeight:1.7}}>
                  Four columns: <strong>Asset Class</strong>, <strong>Sleeve</strong>, <strong>Weight %</strong>, <strong>Style</strong> (optional — leave blank for default weights).
                  Include a Style column to set per-style overrides in the same file.
                </div>
                <div style={{overflowX:"auto",marginBottom:14}}>
                  <table style={{borderCollapse:"collapse",fontSize:11,width:"100%"}}>
                    <thead>
                      <tr style={{background:"#F8FAFC"}}>
                        {["Asset Class","Sleeve","Weight %","Style (optional)"].map(h=>(
                          <th key={h} style={{padding:"6px 12px",textAlign:"left",fontWeight:700,color:"#64748B",borderBottom:"2px solid #E2E8F0",fontSize:10,textTransform:"uppercase",letterSpacing:"0.5px",whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["US Equity","Opportunistic","50",""],
                        ["International Equity","Opportunistic","25",""],
                        ["US Equity","Opportunistic","60","Flagship"],
                        ["International Equity","Opportunistic","20","Flagship"],
                      ].map((row,i)=>(
                        <tr key={i} style={{background:i%2===0?"#fff":"#FAFBFC"}}>
                          {row.map((cell,j)=>(
                            <td key={j} style={{padding:"5px 12px",color:j===0?"#1E3A5F":"#475569",fontWeight:j===0?700:400,borderBottom:"1px solid #F4F6F9",whiteSpace:"nowrap",fontSize:11}}>
                              {cell||<span style={{color:"#CBD5E1"}}>—</span>}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div onClick={()=>fileRef.current?.click()}
                  onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="#EFF6FF";e.currentTarget.style.borderColor="#2563EB";}}
                  onDragLeave={e=>{e.currentTarget.style.background="#FAFBFF";e.currentTarget.style.borderColor="#BFDBFE";}}
                  onDrop={e=>{e.preventDefault();e.currentTarget.style.background="#FAFBFF";e.currentTarget.style.borderColor="#BFDBFE";const f=e.dataTransfer.files[0];if(f){handleFile({target:{files:[f],value:""}});}}}
                  style={{border:"2px dashed #BFDBFE",borderRadius:10,padding:"24px",textAlign:"center",cursor:"pointer",background:"#FAFBFF",transition:"all 0.15s"}}>
                  <div style={{fontSize:22,marginBottom:5}}>📂</div>
                  <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F",marginBottom:2}}>Drop CSV here or click to browse</div>
                  <div style={{fontSize:11,color:"#94A3B8"}}>CSV files only</div>
                </div>
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{display:"none"}}/>
              </div>
            )}

            {uploadPhase === "preview" && (
              <div>
                {uploadErrors.length > 0 && (
                  <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"10px 14px",marginBottom:12}}>
                    <div style={{fontWeight:700,fontSize:12,color:"#92400E",marginBottom:4}}>⚠ {uploadErrors.length} warning{uploadErrors.length!==1?"s":""}</div>
                    {uploadErrors.map((e,i)=><div key={i} style={{fontSize:11,color:"#78350F"}}>{e}</div>)}
                  </div>
                )}
                <div style={{fontSize:11,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:8}}>
                  {uploadPreview.length} weight{uploadPreview.length!==1?"s":""} ready to import
                </div>
                <div style={{overflowX:"auto",maxHeight:260,overflowY:"auto",border:"1px solid #E2E8F0",borderRadius:8,marginBottom:14}}>
                  <table style={{borderCollapse:"collapse",fontSize:11,width:"100%"}}>
                    <thead style={{position:"sticky",top:0}}>
                      <tr style={{background:"#F8FAFC"}}>
                        {["Asset Class","Sleeve","Weight %","Applies To"].map(h=>(
                          <th key={h} style={{padding:"7px 12px",textAlign:"left",fontWeight:700,color:"#64748B",borderBottom:"2px solid #E2E8F0",fontSize:10,textTransform:"uppercase",letterSpacing:"0.5px"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {uploadPreview.map((row,i)=>{
                        const asset = allAssets(engine).find(a=>a.id===row.assetId);
                        const sleeve = engine.sleeves[row.sleeveKey];
                        return (
                          <tr key={i} style={{background:i%2===0?"#fff":"#FAFBFC"}}>
                            <td style={{padding:"6px 12px",fontWeight:700,color:"#1E3A5F",borderBottom:"1px solid #F4F6F9"}}>{asset?.label||row.assetId}</td>
                            <td style={{padding:"6px 12px",color:"#475569",borderBottom:"1px solid #F4F6F9"}}>{sleeve?.label||row.sleeveKey}</td>
                            <td style={{padding:"6px 12px",fontWeight:700,color:"#1E3A5F",borderBottom:"1px solid #F4F6F9"}}>{row.weight}%</td>
                            <td style={{padding:"6px 12px",borderBottom:"1px solid #F4F6F9"}}>
                              {row.styleName
                                ? <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:engine.implementationStyles[row.styleName]?.color+"15",border:`1px solid ${engine.implementationStyles[row.styleName]?.color+"44"}`,color:engine.implementationStyles[row.styleName]?.color,fontWeight:700}}>{row.styleName}</span>
                                : <span style={{fontSize:10,color:"#94A3B8",fontStyle:"italic"}}>Default (all styles)</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                  <button onClick={()=>{setUploadPhase("idle");setUploadPreview(null);setUploadErrors([]);}}
                    style={{background:"none",border:"1px solid #E2E8F0",borderRadius:8,color:"#64748B",padding:"8px 18px",cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>
                    ← Re-upload
                  </button>
                  <button onClick={commitUpload}
                    style={{background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",padding:"8px 22px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Outfit',sans-serif"}}>
                    Confirm Import ({uploadPreview.length})
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Style tab bar ── */}
      <div style={{display:"flex",gap:6,marginBottom:18,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.8px",marginRight:4}}>Weights for:</span>
        {tabDefs.map(({key,label})=>{
          const active  = activeTab===key;
          const sc      = key==="default" ? "#1E3A5F" : (engine.implementationStyles[key]?.color||"#2563EB");
          const hasOver = key!=="default" && (() => {
            const sw = engine.implementationStyles[key]?.sleeveWeights;
            return sw && (Object.keys(sw.opportunistic||{}).length+Object.keys(sw.defensive||{}).length)>0;
          })();
          return (
            <button key={key} onClick={()=>setActiveTab(key)}
              style={{padding:"6px 14px",borderRadius:8,border:`2px solid ${active?sc:"#E2E8F0"}`,background:active?sc+"12":"#fff",color:active?sc:"#64748B",fontWeight:active?700:500,fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif",display:"flex",alignItems:"center",gap:5,transition:"all 0.15s"}}>
              {label}
              {hasOver && <span style={{width:6,height:6,borderRadius:"50%",background:"#D97706",flexShrink:0}}/>}
            </button>
          );
        })}
      </div>

      {!isDefault && (
        <div style={{fontSize:12,color:"#64748B",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,padding:"8px 14px",marginBottom:16,lineHeight:1.6}}>
          Editing weights for <strong>{activeTab}</strong>. Amber inputs are overridden from the default.
          Grey "default" labels inherit the global weight — click the input to create an override.
          {hasAnyOverride && <button onClick={clearAllOverrides} style={{marginLeft:10,fontSize:11,color:"#DC2626",background:"none",border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",padding:0,fontWeight:600}}>Clear all overrides</button>}
        </div>
      )}

      <SleeveBlock sleeveKey="opportunistic"/>
      <SleeveBlock sleeveKey="defensive"/>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ANALYTICS TAB
// ─────────────────────────────────────────────────────────────
function AnalyticsTab({ engine, logs }) {
  const { isMobile } = useBreakpoint();
  const [view, setView] = useState("firm");

  const getMetrics = (subset) => {
    if (!subset.length) return { sessions:0, swaps:0, adjusts:0, avgTime:0, blendCounts:{}, styleCounts:{} };
    return {
      sessions:    subset.length,
      swaps:       subset.reduce((s,l)=>s+(l.tickerSwaps||0),0),
      avgTime:     Math.round(subset.reduce((s,l)=>s+(l.durationSecs||0),0)/subset.length),
      blendCounts: subset.reduce((acc,l)=>{ const k=`${l.finalOppPct}/${100-(l.finalOppPct||0)}`; acc[k]=(acc[k]||0)+1; return acc; },{}),
      styleCounts: subset.reduce((acc,l)=>{ acc[l.finalStyle]=(acc[l.finalStyle]||0)+1; return acc; },{}),
    };
  };

  const firmMetrics = getMetrics(logs);
  const viewLogs    = view === "firm" ? logs : logs.filter(l => l.advisorId === view);
  const viewMetrics = view === "firm" ? firmMetrics : getMetrics(viewLogs);
  const viewName    = view === "firm" ? "Firm" : (engine.employees.find(e=>e.id===view)?.name||"Advisor");

  const delta = (uv, fv) => {
    if (view === "firm" || fv === 0) return null;
    const d = uv - fv; return { val:Math.abs(d), dir:d>=0?"up":"down", pct:Math.round(Math.abs(d)/fv*100) };
  };

  const StatCard = ({ label, value, color="#1E3A5F", firmVal=null, isTime=false }) => {
    const d = firmVal !== null ? delta(typeof value==="number"?value:0, firmVal) : null;
    return (
      <div style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:10,padding:"12px 14px",textAlign:"center"}}>
        <div style={{fontFamily:"'Outfit',sans-serif",fontSize:26,fontWeight:700,color}}>{isTime?fmtSecs(value):value}</div>
        <div style={{fontSize:11,fontWeight:700,color:"#334155",marginTop:2}}>{label}</div>
        {d&&<div style={{marginTop:4,fontSize:10,color:d.dir==="up"?"#059669":"#DC2626",fontWeight:700}}>{d.dir==="up"?"▲":"▼"} {d.pct}% vs firm</div>}
        {view!=="firm"&&firmVal!==null&&<div style={{fontSize:10,color:"#94A3B8",marginTop:2}}>Firm: {isTime?fmtSecs(firmVal):firmVal}</div>}
      </div>
    );
  };

  const styleBar = Object.keys(engine.implementationStyles).map(k=>({
    name:k,user:viewMetrics.styleCounts[k]||0,
    firm:firmMetrics.sessions?Math.round((firmMetrics.styleCounts[k]||0)/firmMetrics.sessions*viewMetrics.sessions):0,
    color:engine.implementationStyles[k]?.color||"#94A3B8"
  }));

  const blendBar = Object.entries(viewMetrics.blendCounts).sort((a,b)=>parseInt(b[0])-parseInt(a[0])).map(([k,v])=>({name:k,count:v}));

  return (
    <div>
      {/* View switcher */}
      <div style={{display:"flex",alignItems:"center",gap:0,marginBottom:20,background:"#F1F5F9",borderRadius:10,padding:4,width:"fit-content",flexWrap:"wrap"}}>
        <button onClick={()=>setView("firm")} style={{padding:"7px 14px",borderRadius:7,border:"none",background:view==="firm"?"#fff":"transparent",color:view==="firm"?"#1E3A5F":"#94A3B8",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif",boxShadow:view==="firm"?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>🏢 Firm</button>
        {engine.employees.map(emp=>(
          <button key={emp.id} onClick={()=>setView(emp.id)} style={{padding:"7px 12px",borderRadius:7,border:"none",background:view===emp.id?"#fff":"transparent",color:view===emp.id?"#1E3A5F":"#94A3B8",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif",boxShadow:view===emp.id?"0 1px 4px rgba(0,0,0,0.08)":"none"}}>
            {emp.name.split(" ")[0]}
            {logs.filter(l=>l.advisorId===emp.id).length>0&&<span style={{marginLeft:4,background:"#E2E8F0",borderRadius:8,padding:"0px 5px",fontSize:10,color:"#64748B"}}>{logs.filter(l=>l.advisorId===emp.id).length}</span>}
          </button>
        ))}
      </div>

      <div style={{display:"flex",alignItems:"baseline",gap:8,marginBottom:16}}>
        <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:700,color:"#1E3A5F"}}>{viewName}</div>
        {view!=="firm"&&<div style={{fontSize:12,color:"#94A3B8"}}>{viewMetrics.sessions} sessions · vs firm avg</div>}
      </div>

      {viewLogs.length === 0 ? (
        <div style={{textAlign:"center",padding:"40px 0",color:"#94A3B8",background:"#F8FAFC",borderRadius:12}}>
          <div style={{fontSize:28,marginBottom:10}}>📊</div>
          <div style={{fontSize:14,fontWeight:600}}>{view==="firm"?"No sessions yet":` No sessions for ${viewName} yet`}</div>
          <div style={{fontSize:12,marginTop:4}}>Captured when advisors export PDF.</div>
        </div>
      ) : (
        <>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr 1fr":"repeat(4,1fr)",gap:10,marginBottom:20}}>
            <StatCard label="Sessions" value={viewMetrics.sessions} firmVal={view!=="firm"?firmMetrics.sessions:null}/>
            <StatCard label="Ticker Swaps" value={viewMetrics.swaps} color="#7C3AED" firmVal={view!=="firm"?firmMetrics.swaps:null}/>
            <StatCard label="Avg. Session" value={viewMetrics.avgTime} color="#059669" isTime={true} firmVal={view!=="firm"?firmMetrics.avgTime:null}/>
            <StatCard label="Blend Changes" value={viewLogs.reduce((s,l)=>s+(l.blendChanges||0),0)} color="#D97706" firmVal={view!=="firm"?logs.reduce((s,l)=>s+(l.blendChanges||0),0):null}/>
          </div>

          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:16,marginBottom:20}}>
            <div style={card}>
              <div style={cardTitle}>Blend Ratio Usage</div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={blendBar} margin={{left:-20}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                  <XAxis dataKey="name" tick={{fontSize:9,fill:"#94A3B8"}}/>
                  <YAxis tick={{fontSize:9,fill:"#94A3B8"}} allowDecimals={false}/>
                  <Tooltip contentStyle={ttStyle}/>
                  <Bar dataKey="count" name="Sessions" fill="#2563EB" radius={[4,4,0,0]}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={card}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:12}}>
                <div style={cardTitle}>Implementation Style Usage</div>
                {view!=="firm"&&<span style={{fontSize:10,color:"#94A3B8"}}>colored = user, gray = firm avg</span>}
              </div>
              <ResponsiveContainer width="100%" height={160}>
                <BarChart data={styleBar} margin={{left:-20}}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                  <XAxis dataKey="name" tick={{fontSize:9,fill:"#94A3B8"}}/>
                  <YAxis tick={{fontSize:9,fill:"#94A3B8"}} allowDecimals={false}/>
                  <Tooltip contentStyle={ttStyle}/>
                  <Bar dataKey="user" name={view==="firm"?"Sessions":"This Advisor"} radius={[4,4,0,0]}>
                    {styleBar.map((d,i)=><Cell key={i} fill={d.color}/>)}
                  </Bar>
                  {view!=="firm"&&<Bar dataKey="firm" name="Firm Avg" fill="#CBD5E1" opacity={0.6} radius={[4,4,0,0]}/>}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Advisor breakdown — firm view only */}
          {view==="firm"&&(
            <div style={{...card,marginBottom:16}}>
              <div style={cardTitle}>Advisor Breakdown</div>
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:560}}>
                  <thead><tr style={{borderBottom:"2px solid #E2E8F0"}}>
                    {["Advisor","Sessions","Ticker Swaps","Blend Changes","Avg. Time","Most Used Style","Most Used Blend"].map(h=>(
                      <th key={h} style={{padding:"6px 10px",textAlign:"left",color:"#94A3B8",fontWeight:700,fontSize:10,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {engine.employees.map((emp,i)=>{
                      const em=getMetrics(logs.filter(l=>l.advisorId===emp.id));
                      const topStyle=Object.entries(em.styleCounts).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—";
                      const topBlend=Object.entries(em.blendCounts).sort((a,b)=>b[1]-a[1])[0]?.[0]||"—";
                      return (
                        <tr key={emp.id} style={{borderBottom:"1px solid #F8FAFC",background:i%2===0?"#fff":"#FAFAFA"}}>
                          <td style={{padding:"8px 10px",fontWeight:700,color:"#1E3A5F"}}>{emp.name}</td>
                          <td style={{padding:"8px 10px",textAlign:"center",fontWeight:700,color:em.sessions>0?"#1E3A5F":"#CBD5E1"}}>{em.sessions||"—"}</td>
                          <td style={{padding:"8px 10px",textAlign:"center",fontWeight:700,color:em.swaps>0?"#7C3AED":"#CBD5E1"}}>{em.sessions>0?em.swaps:"—"}</td>
                          <td style={{padding:"8px 10px",textAlign:"center",color:"#64748B"}}>{em.sessions>0?viewLogs.filter(l=>l.advisorId===emp.id).reduce((s,l)=>s+(l.blendChanges||0),0):"—"}</td>
                          <td style={{padding:"8px 10px",color:"#64748B"}}>{em.sessions>0?fmtSecs(em.avgTime):"—"}</td>
                          <td style={{padding:"8px 10px",fontSize:11}}>{topStyle}</td>
                          <td style={{padding:"8px 10px",fontWeight:700,color:"#2563EB",fontSize:11}}>{topBlend}</td>
                        </tr>
                      );
                    })}
                    <tr style={{borderTop:"2px solid #E2E8F0",background:"#F0F5FF"}}>
                      <td style={{padding:"8px 10px",fontWeight:800,color:"#1E3A5F"}}>Firm Total</td>
                      <td style={{padding:"8px 10px",textAlign:"center",fontWeight:800,color:"#1E3A5F"}}>{firmMetrics.sessions}</td>
                      <td style={{padding:"8px 10px",textAlign:"center",fontWeight:700,color:"#7C3AED"}}>{firmMetrics.swaps}</td>
                      <td style={{padding:"8px 10px",textAlign:"center",color:"#D97706",fontWeight:700}}>{logs.reduce((s,l)=>s+(l.blendChanges||0),0)}</td>
                      <td style={{padding:"8px 10px",fontWeight:700,color:"#059669"}}>{fmtSecs(firmMetrics.avgTime)}</td>
                      <td colSpan={2} style={{padding:"8px 10px"}}>—</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Session log */}
          <div style={card}>
            <div style={cardTitle}>Session Log {view!=="firm"&&`— ${viewName}`}</div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:500}}>
                <thead><tr style={{borderBottom:"2px solid #E2E8F0"}}>
                  {["Time","Advisor","Style","Blend","Ticker Swaps","Blend Changes","Duration"].map(h=>(
                    <th key={h} style={{padding:"6px 10px",textAlign:"left",color:"#94A3B8",fontWeight:700,fontSize:10,textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {[...viewLogs].reverse().map((l,i)=>{
                    const styleColor=engine.implementationStyles[l.finalStyle]?.color||"#94A3B8";
                    return (
                      <tr key={i} style={{borderBottom:"1px solid #F8FAFC",background:i%2===0?"#fff":"#FAFAFA"}}>
                        <td style={{padding:"7px 10px",color:"#64748B",whiteSpace:"nowrap"}}>{fmtDate(l.timestamp)}</td>
                        <td style={{padding:"7px 10px",fontWeight:600,color:"#1E3A5F"}}>{l.advisorName}</td>
                        <td style={{padding:"7px 10px"}}><span style={{background:styleColor+"15",color:styleColor,borderRadius:4,padding:"2px 8px",fontSize:11,fontWeight:700}}>{l.finalStyle}</span></td>
                        <td style={{padding:"7px 10px",fontWeight:700,color:"#2563EB",fontSize:11}}>{l.finalOppPct}/{100-(l.finalOppPct||0)}</td>
                        <td style={{padding:"7px 10px",textAlign:"center",fontWeight:700,color:l.tickerSwaps>0?"#7C3AED":"#CBD5E1"}}>{l.tickerSwaps}</td>
                        <td style={{padding:"7px 10px",textAlign:"center",fontWeight:700,color:l.blendChanges>0?"#D97706":"#CBD5E1"}}>{l.blendChanges}</td>
                        <td style={{padding:"7px 10px",color:"#64748B"}}>{fmtSecs(l.durationSecs||0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ASSET CLASS EDITOR (for sleeves)
// ─────────────────────────────────────────────────────────────
function AssetClassEditor({ engine, setEngine }) {
  const [pendingRemove, setPendingRemove] = useState(null);
  const [adding,        setAdding]        = useState(null); // "opportunistic" | "defensive"
  const [newLabel,      setNewLabel]      = useState("");
  const [newColor,      setNewColor]      = useState("#2563EB");
  const [addError,      setAddError]      = useState("");

  const confirmRemove = (sleeveKey, assetId) => {
    setEngine(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sleeves[sleeveKey].assets = next.sleeves[sleeveKey].assets.filter(a => a.id !== assetId);
      Object.keys(next.implementationStyles).forEach(sk => { delete next.implementationStyles[sk].tickers[assetId]; });
      return next;
    });
    setPendingRemove(null);
  };

  const handleAdd = (sleeveKey) => {
    const label = newLabel.trim();
    if (!label) { setAddError("Please enter a name."); return; }
    const id = `${sleeveKey.slice(0,3)}_${label.toLowerCase().replace(/[^a-z0-9]/g,"_")}`;
    if (allAssets(engine).find(a => a.id === id)) { setAddError("Asset already exists."); return; }
    setEngine(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      next.sleeves[sleeveKey].assets.push({ id, label, color: newColor, weight: 0 });
      Object.keys(next.implementationStyles).forEach(sk => { next.implementationStyles[sk].tickers[id] = []; });
      return next;
    });
    setNewLabel(""); setNewColor("#2563EB"); setAdding(null); setAddError("");
  };

  return (
    <div>
      <div style={{fontSize:13,color:"#64748B",marginBottom:18}}>Add or remove assets within each sleeve. Changes apply across implementation style ticker lists immediately.</div>
      {Object.entries(engine.sleeves).map(([sleeveKey, sleeve]) => (
        <div key={sleeveKey} style={{background:"#F8FAFC",border:`1px solid ${sleeve.color}33`,borderRadius:12,padding:"14px 16px",marginBottom:16}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:12}}>
            <div style={{width:10,height:10,borderRadius:"50%",background:sleeve.color}}/>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:16,fontWeight:700,color:"#1E3A5F"}}>{sleeve.label} Sleeve</div>
          </div>
          {sleeve.assets.map((a, idx) => (
            <div key={a.id}>
              <div style={{display:"flex",alignItems:"center",gap:10,background:"#fff",border:`1px solid ${pendingRemove?.id===a.id?"#FCA5A5":"#E2E8F0"}`,borderRadius:8,padding:"10px 12px",marginBottom:pendingRemove?.id===a.id?0:8,borderBottomLeftRadius:pendingRemove?.id===a.id?0:8,borderBottomRightRadius:pendingRemove?.id===a.id?0:8}}>
                <input type="color" value={a.color} onChange={e=>{const n=JSON.parse(JSON.stringify(engine));n.sleeves[sleeveKey].assets[idx].color=e.target.value;setEngine(n);}}
                  style={{width:24,height:24,border:"none",borderRadius:4,cursor:"pointer",padding:1,background:"none"}}/>
                <input value={a.label} onChange={e=>{const n=JSON.parse(JSON.stringify(engine));n.sleeves[sleeveKey].assets[idx].label=e.target.value;setEngine(n);}}
                  style={{...smallInp({flex:1,fontWeight:700,fontSize:13,padding:"5px 8px"})}}/>
                <span style={{fontSize:10,color:"#94A3B8"}}><code style={{background:"#E2E8F0",padding:"1px 4px",borderRadius:3,fontSize:10}}>{a.id}</code></span>
                {sleeve.assets.length > 1 && (
                  pendingRemove?.id===a.id
                    ? <span style={{fontSize:11,color:"#DC2626",fontWeight:600,whiteSpace:"nowrap"}}>Remove?</span>
                    : <button onClick={()=>setPendingRemove({sleeveKey,id:a.id})} style={{background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:6,color:"#DC2626",padding:"4px 10px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontSize:11,fontWeight:700,whiteSpace:"nowrap"}}>Remove</button>
                )}
              </div>
              {pendingRemove?.id===a.id&&(
                <div style={{background:"#FFF5F5",border:"1px solid #FCA5A5",borderTop:"none",borderRadius:"0 0 8px 8px",padding:"8px 12px",marginBottom:8,display:"flex",alignItems:"center",gap:8}}>
                  <span style={{fontSize:12,color:"#64748B",flex:1}}>Remove <strong>{a.label}</strong> from {sleeve.label}?</span>
                  <button onClick={()=>confirmRemove(sleeveKey,a.id)} style={{background:"#DC2626",border:"none",borderRadius:6,color:"#fff",padding:"5px 12px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontSize:12,fontWeight:700}}>Yes</button>
                  <button onClick={()=>setPendingRemove(null)} style={{background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:6,color:"#64748B",padding:"5px 10px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontSize:12}}>Cancel</button>
                </div>
              )}
            </div>
          ))}
          {adding===sleeveKey ? (
            <div style={{background:"#F0F9FF",border:"1px solid #BAE6FD",borderRadius:8,padding:"12px 14px",marginTop:4}}>
              <div style={{display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap"}}>
                <div style={{flex:1,minWidth:150}}>
                  <div style={{...sLabel,marginBottom:4}}>Asset Name</div>
                  <input value={newLabel} onChange={e=>{setNewLabel(e.target.value);setAddError("");}} onKeyDown={e=>e.key==="Enter"&&handleAdd(sleeveKey)}
                    placeholder="e.g. Private Credit" autoFocus style={{...smallInp({width:"100%",padding:"7px 10px",fontSize:13,fontWeight:600})}}/>
                </div>
                <div>
                  <div style={{...sLabel,marginBottom:4}}>Color</div>
                  <input type="color" value={newColor} onChange={e=>setNewColor(e.target.value)} style={{width:36,height:32,border:"1px solid #E2E8F0",borderRadius:6,cursor:"pointer",padding:2,background:"#fff"}}/>
                </div>
                <button onClick={()=>handleAdd(sleeveKey)} style={{background:"#1E3A5F",border:"none",borderRadius:7,color:"#fff",padding:"7px 16px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontSize:13,fontWeight:700}}>Add</button>
                <button onClick={()=>{setAdding(null);setNewLabel("");setAddError("");}} style={{background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",padding:"7px 12px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontSize:12}}>Cancel</button>
              </div>
              {addError&&<div style={{fontSize:12,color:"#DC2626",marginTop:6}}>{addError}</div>}
            </div>
          ) : (
            <button onClick={()=>setAdding(sleeveKey)} style={{background:"#EFF6FF",border:"1px dashed #BFDBFE",borderRadius:7,color:"#2563EB",padding:"7px 14px",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontSize:12,fontWeight:700,width:"100%",marginTop:4}}>
              + Add Asset to {sleeve.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// PORTFOLIO ANALYZER — helpers & constants
// ─────────────────────────────────────────────────────────────
const PA_MODEL = "claude-sonnet-4-5"; // fallback — engine.aiConfig.model takes precedence

// SheetJS for Excel parsing — loaded from CDN via script tag injected once
// ── Native PDF text extractor using PDF.js ──
const ensurePDFJS = () => new Promise((res, rej) => {
  if (window.pdfjsLib) return res(window.pdfjsLib);
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
  s.onload = () => {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
    res(window.pdfjsLib);
  };
  s.onerror = rej;
  document.head.appendChild(s);
});

// Extract raw text lines from a PDF file, grouping by Y position to reconstruct rows
const extractPDFText = async (file) => {
  const pdfjsLib = await ensurePDFJS();
  const buf = await file.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
  const lines = [];
  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const tc   = await page.getTextContent();
    const byY  = {};
    tc.items.forEach(item => {
      const y = Math.round(item.transform[5]);
      if (!byY[y]) byY[y] = [];
      byY[y].push(item.str);
    });
    Object.keys(byY).sort((a,b) => b-a).forEach(y => {
      const line = byY[y].join(" ").trim();
      if (line) lines.push(line);
    });
  }
  return lines;
};

// Known non-ticker uppercase words to skip during native parse
const NATIVE_SKIP_WORDS = new Set([
  "ACCOUNT","TOTAL","PAGE","DATE","YOUR","PORTFOLIO","VALUE","BALANCE","DESCRIPTION",
  "SYMBOL","TICKER","CUSIP","TYPE","CLASS","PRICE","SHARES","UNITS","COST","BASIS",
  "GAIN","LOSS","MARKET","CURRENT","ENDING","BEGINNING","AS","OF","THE","AND","OR",
  "FOR","IN","AT","TO","FROM","BY","WITH","NOT","IS","ARE","WAS","WERE","BEEN","HAVE",
  "HAS","HAD","WILL","WOULD","COULD","SHOULD","MAY","MIGHT","ETF","FUND","INC","LLC",
  "LP","CORP","LTD","NA","NR","USD","N/A","YTD","MTD",
]);

// Attempt to parse holdings from a PDF file without AI
// Returns { holdings:[], confidence:"high"|"low"|"none" }
const nativeParseStatement = async (file) => {
  try {
    const lines = await extractPDFText(file);
    const holdings = [];
    const seenTickers = new Set();

    const inferAssetClass = (desc) => {
      const d = desc.toLowerCase();
      if (d.includes("bond")||d.includes("treasury")||d.includes("note")||d.includes("fixed")||d.includes("income")||d.includes("muni")) return "Fixed Income";
      if (d.includes("international")||d.includes("intl")||d.includes("foreign")||d.includes("emerging")) return "International Equity";
      if (d.includes("reit")||d.includes("real estate")||d.includes("realty")) return "Real Estate";
      if (d.includes("money market")||d.includes("cash")||d.includes("sweep")) return "Cash";
      if (d.includes("commodity")||d.includes("gold")||d.includes("silver")||d.includes("alternative")) return "Alternative";
      return "US Equity";
    };

    for (let li = 0; li < lines.length; li++) {
      const line = lines[li];
      // Ticker: 1-5 uppercase letters, standalone word
      const tickerMatch = line.match(/\b([A-Z]{1,5}[0-9]?)\b/);
      // Dollar value: optional $, digits with commas, optional decimals
      const allValues = [...line.matchAll(/\$?([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]{1,2})?)/g)]
        .map(m => parseFloat(m[1].replace(/,/g,"")))
        .filter(n => !isNaN(n) && n >= 1);

      if (!tickerMatch || !allValues.length) continue;
      const ticker = tickerMatch[1];
      if (NATIVE_SKIP_WORDS.has(ticker) || ticker.length < 2 || seenTickers.has(ticker)) continue;

      const marketValue = Math.max(...allValues);
      if (marketValue < 10) continue;

      // Description: non-numeric text on the line excluding ticker
      const desc = line.replace(/\$?[0-9,]+(?:\.[0-9]+)?/g,"").replace(ticker,"").replace(/[^a-zA-Z\s]/g," ").trim().slice(0,60);

      // Gain/loss: signed or parenthetical number smaller than market value
      let gainLoss = null;
      const glMatch = line.match(/([+-]\$?[0-9,]+(?:\.[0-9]+)?|\([0-9,]+(?:\.[0-9]+)?\))/);
      if (glMatch) {
        const raw = glMatch[1].replace(/[$,]/g,"");
        const val = raw.startsWith("(") ? -parseFloat(raw.slice(1,-1)) : parseFloat(raw);
        if (!isNaN(val) && Math.abs(val) < marketValue) gainLoss = val;
      }
      const costBasis   = gainLoss != null ? marketValue - gainLoss : 0;
      const gainLossPct = gainLoss != null && costBasis > 0 ? (gainLoss / costBasis * 100) : 0;

      seenTickers.add(ticker);
      holdings.push({ ticker, name: desc||ticker, assetClass: inferAssetClass(desc),
        shares:0, marketValue, costBasis, gainLoss:gainLoss||0, gainLossPct, holdingPeriod:"Unknown", _src:"native" });
    }

    const confidence = holdings.length >= 3 ? "high" : holdings.length >= 1 ? "low" : "none";
    return { holdings, confidence };
  } catch(e) {
    return { holdings:[], confidence:"none", error:e.message };
  }
};

const ensureSheetJS = () => new Promise((res, rej) => {
  if (window.XLSX) return res(window.XLSX);
  const s = document.createElement("script");
  s.src = "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js";
  s.onload = () => res(window.XLSX);
  s.onerror = rej;
  document.head.appendChild(s);
});

// Parse Excel/CSV file → array of holding rows
const parseExcelToHoldings = async (file) => {
  const XLSX = await ensureSheetJS();
  const buf  = await file.arrayBuffer();
  const wb   = XLSX.read(buf, { type:"array" });
  const ws   = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(ws, { defval:"" });
  if (!rows.length) throw new Error("Spreadsheet appears empty.");

  // Normalize a string for fuzzy matching: lowercase, strip non-alphanumeric
  const norm = (s) => String(s||"").toLowerCase().replace(/[^a-z0-9]/g,"");

  // Match a row value by checking if any header contains any candidate token
  // Priority: exact normalized match first, then includes match
  const match = (row, ...candidates) => {
    const keys = Object.keys(row);
    const normedKeys = keys.map(k => norm(k));
    // 1. Exact match
    for (const c of candidates) {
      const idx = normedKeys.findIndex(nk => nk === norm(c));
      if (idx > -1) return row[keys[idx]];
    }
    // 2. Includes match
    for (const c of candidates) {
      const idx = normedKeys.findIndex(nk => nk.includes(norm(c)));
      if (idx > -1) return row[keys[idx]];
    }
    return "";
  };

  const toNum = (v) => {
    const n = parseFloat(String(v||"").replace(/[$,%()]/g,"").replace(/\((.+)\)/, "-$1").trim());
    return isNaN(n) ? null : n;
  };

  // Normalize asset class strings to our standard labels
  const normalizeAssetClass = (raw) => {
    const r = norm(raw);
    if (!r) return "Other";
    if (r.includes("intl")||r.includes("international")||r.includes("foreign")||r.includes("overseas")||r.includes("emerging")||r.includes("developed")) return "International Equity";
    if (r.includes("fixed")||r.includes("bond")||r.includes("income")||r.includes("debt")||r.includes("treasury")||r.includes("govt")||r.includes("municipal")||r.includes("muni")||r.includes("credit")) return "Fixed Income";
    if (r.includes("real")||r.includes("reit")||r.includes("property")) return "Real Estate";
    if (r.includes("cash")||r.includes("money")||r.includes("liquid")||r.includes("mmkt")||r.includes("sweep")) return "Cash";
    if (r.includes("alt")||r.includes("commodity")||r.includes("hedge")||r.includes("private")||r.includes("infra")||r.includes("gold")||r.includes("silver")) return "Alternative";
    if (r.includes("equity")||r.includes("stock")||r.includes("equit")||r.includes("share")||r.includes("common")||r.includes("domestic")||r.includes("us")||r.includes("large")||r.includes("mid")||r.includes("small")||r.includes("growth")||r.includes("value")||r.includes("blend")) return "US Equity";
    return "Other";
  };

  return rows.map((row, i) => {
    // Ticker — many custodians use different column names
    const ticker = String(match(row,
      "ticker","symbol","sym","cusip","isin","sedol","secid","tickersymbol","stock"
    )||"").toUpperCase().trim();

    const name = String(match(row,
      "name","description","security","securityname","secname","fullname","longname","shortname","desc","holding","holdingname","investment","investmentname"
    )||"");

    const rawAssetClass = String(match(row,
      "assetclass","assetcategory","class","type","securitytype","category","sector","holdingtype","investmenttype","classification","assettype"
    )||"");

    const shares = toNum(match(row,
      "shares","qty","quantity","units","sharesowned","numshares","numberofshares","position","positionqty"
    ));

    const price = toNum(match(row,
      "price","currentprice","mktprice","closingprice","close","lastprice","unitprice","navprice","nav","pricepershare"
    ));

    const marketValue = toNum(match(row,
      "marketvalue","mktval","mv","value","currentvalue","endingvalue","endvalue","marketval","totalvalue","amount","balance","portfoliovalue","holdingvalue","presentvalue"
    ));

    const costBasis = toNum(match(row,
      "costbasis","cost","basis","totalcost","purchaseprice","bookvalue","adjustedcost","acb","purchasecost","originalcost"
    ));

    const rawGainLoss = toNum(match(row,
      "gainloss","gain","loss","unrealizedgain","unrealizedgainloss","unrlzdgain","unrealizedpl","pl","profitloss","unrealizedprofit"
    ));

    const gainLossPct = toNum(match(row,
      "gainlosspct","gainpct","returnpct","percentreturn","return","pctreturn","unrealizedreturn","gainlosspercent","%gainloss"
    ));

    const holdingPeriod = String(match(row,
      "holdingperiod","period","termtype","shortlong","term"
    )||"Unknown");

    const purchaseDate = String(match(row,
      "purchasedate","acquireddate","dateacquired","buydate","opendate","tradedate","date"
    )||"");

    const expenseRatio = toNum(match(row,
      "expenseratio","er","expense","expenseration","annualexpense","managementfee","mer"
    ));

    const yieldPct = toNum(match(row,
      "yield","dividendyield","currentyield","annualyield","ttmyield","distributionyield"
    ));

    // Compute market value from shares × price if not present
    const mv = marketValue ?? (shares && price ? shares * price : null);

    // Auto-calculate gain/loss if both market value and cost basis are present but gain/loss is missing
    const gl  = rawGainLoss != null ? rawGainLoss : (mv != null && costBasis != null ? mv - costBasis : null);
    const glp = gainLossPct != null ? gainLossPct : (gl != null && costBasis && costBasis > 0 ? (gl / costBasis * 100) : null);

    return {
      id:           `xl_${i}`,
      ticker,
      name,
      assetClass:   normalizeAssetClass(rawAssetClass),
      shares:       shares ?? 0,
      currentPrice: price  ?? 0,
      marketValue:  mv     ?? 0,
      costBasis:    costBasis ?? 0,
      gainLoss:     gl     ?? 0,
      gainLossPct:  glp    ?? 0,
      holdingPeriod: holdingPeriod === "Unknown" ? "Unknown" : holdingPeriod,
      purchaseDate,
      expenseRatio: expenseRatio ?? null,
      yieldPct:     yieldPct    ?? null,
    };
  }).filter(h => h.ticker || h.name);
};
const fmt$ = (n) => n == null || isNaN(n) ? "—" : new Intl.NumberFormat("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}).format(n);
const fmtPct = (n) => n == null || isNaN(n) ? "—" : (n >= 0 ? "+" : "") + Number(n).toFixed(2) + "%";
const fileToBase64 = (file) => new Promise((res,rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(",")[1]); r.onerror = rej; r.readAsDataURL(file); });

const EXTRACT_PROMPT = `You are an expert financial data extractor. This statement may contain one or more accounts. Extract ALL accounts and their holdings and return ONLY valid JSON — no markdown, no explanation.

IMPORTANT: If the document contains multiple accounts (e.g. a brokerage account AND an IRA), return each as a separate entry in the accounts array. Do NOT merge holdings from different accounts.

Schema:
{
  "clientName": "string or null",
  "statementDate": "string or null",
  "accounts": [
    {
      "accountNumber": "string or null",
      "accountType": "Brokerage|IRA|401k|Roth IRA|529|HSA|Mutual Fund|Unknown",
      "accountLabel": "string or null (nickname/label shown on statement, e.g. 'Joint Taxable', 'Rollover IRA')",
      "custodian": "string or null",
      "totalValue": number,
      "cashBalance": number,
      "holdings": [
        {
          "id": "unique string",
          "ticker": "string",
          "name": "string",
          "assetClass": "US Equity|International Equity|Fixed Income|Alternative|Cash|Real Estate|Other",
          "shares": number,
          "currentPrice": number,
          "marketValue": number,
          "costBasis": number,
          "gainLoss": number,
          "gainLossPct": number,
          "holdingPeriod": "Short-term|Long-term|Unknown",
          "purchaseDate": "string or null"
        }
      ]
    }
  ]
}

Rules:
- If only one account exists, still return it inside the accounts array.
- Set null for any field not visible in the document.
- Extract every holding visible. Do not omit any positions.
- Identify account type from labels like "IRA", "Roth", "401(k)", "Brokerage", "Individual", "Joint" etc.`;

const TAX_SENSITIVE_ANALYSES = new Set(["taxConsequences","taxLossHarvesting"]);

const buildAnalysisPrompt = (holdings, analysisId, systemPrompt) => {
  const basePrompt = systemPrompt || "You are a senior financial advisor analyst. Given these holdings, run the requested analysis and return ONLY valid JSON with ONLY that single key.";
  const isTaxAnalysis = TAX_SENSITIVE_ANALYSES.has(analysisId);
  const preparedHoldings = holdings.map(h => {
    if (isTaxAnalysis && h.taxExempt) {
      // Strip cost basis and gain/loss — not applicable for non-taxable accounts
      const { costBasis, gainLoss, gainLossPct, holdingPeriod, ...rest } = h;
      return { ...rest, taxExempt:true, note:"Non-taxable account — cost basis and gain/loss not applicable" };
    }
    return h;
  });
  const exemptCount = preparedHoldings.filter(h=>h.taxExempt).length;
  const exemptNote  = isTaxAnalysis && exemptCount > 0
    ? `\nNOTE: ${exemptCount} holding(s) are in non-taxable accounts (IRA/401k/etc). Their cost basis and gain/loss data has been excluded. Do not estimate or impute tax consequences for these holdings.`
    : "";
  return `${basePrompt}${exemptNote}
Holdings: ${JSON.stringify(preparedHoldings)}
Requested: ${analysisId}
Return JSON with only one key matching this schema subset:
{"allocationBreakdown":{"summary":{"USEquity":0,"InternationalEquity":0,"FixedIncome":0,"Alternative":0,"Cash":0,"RealEstate":0,"Other":0},"insights":[],"suggestedRebalance":[{"assetClass":"","current":0,"suggested":0,"action":""}]},"taxConsequences":{"totalUnrealizedGains":0,"totalUnrealizedLosses":0,"estimatedLTCG":0,"estimatedLTCGTax":0,"estimatedSTCG":0,"estimatedSTCGTax":0,"netTaxableGain":0,"notes":"","keyFindings":[]},"taxLossHarvesting":{"totalHarvestable":0,"candidates":[{"ticker":"","name":"","unrealizedLoss":0,"pctLoss":0,"holdingPeriod":"","suggestion":"","replacementIdeas":[]}],"washSaleWarnings":[],"estimatedTaxSavings":0,"notes":""},"concentrationRisk":{"riskScore":"Low|Moderate|High|Very High","topHoldings":[{"ticker":"","name":"","pct":0,"riskNote":""}],"sectorConcentration":[{"sector":"","pct":0,"note":""}],"diversificationGaps":[],"recommendations":[]},"incomeAnalysis":{"estimatedAnnualIncome":0,"yieldOnCost":0,"currentYield":0,"incomeByHolding":[{"ticker":"","estimatedAnnualIncome":0,"yield":0}],"notes":""},"riskMetrics":{"overallRiskProfile":"Conservative|Moderate-Conservative|Moderate|Moderate-Aggressive|Aggressive","equityBondRatio":"","volatilityAssessment":"","recommendations":[],"keyRisks":[]},"advisorSummary":{"headline":"","keyFindings":[],"priorityActions":[{"priority":"High|Medium|Low","action":"","rationale":""}],"positives":[],"concerns":[]}}`;
};

const PA_ANALYSES = [
  { id:"allocationBreakdown", label:"Allocation Breakdown",    icon:"◈", desc:"Asset class distribution & rebalancing suggestions" },
  { id:"taxConsequences",     label:"Tax Consequences",        icon:"⊕", desc:"Unrealized gains/losses & estimated tax liability" },
  { id:"taxLossHarvesting",   label:"Tax-Loss Harvesting",     icon:"◇", desc:"Harvest candidates, wash-sale risks, estimated savings" },
  { id:"concentrationRisk",   label:"Concentration & Risk",    icon:"◉", desc:"Single-stock risk & diversification gaps" },
  { id:"incomeAnalysis",      label:"Income Analysis",         icon:"◎", desc:"Dividend income estimates & yield on cost" },
  { id:"riskMetrics",         label:"Risk Profile",            icon:"△", desc:"Overall risk rating & volatility assessment" },
  { id:"advisorSummary",      label:"Advisor Action Summary",  icon:"★", desc:"Priority actions & key findings" },
];

// ── Eligibility check: can this holding be kept? ──
function holdingEligibility(holding, totalPortfolioValue, approvedHoldings, clientDirectedTickers=[]) {
  const gain   = holding.gainLoss ?? 0;
  const ticker = holding.ticker?.toUpperCase();

  // Client directed overrides ALL rules including non-taxable block
  const isClientDirected = clientDirectedTickers.map(t=>t?.toUpperCase()).includes(ticker);
  if (isClientDirected) {
    return { eligible:true, clientDirected:true, blocked:false, reasons:["Client Directed"] };
  }

  // Non-taxable accounts cannot be kept unless client directed
  if (holding.taxExempt) {
    return { eligible:false, clientDirected:false, blocked:true, reasons:[] };
  }

  const reasons = [];
  const approvedTickers = (approvedHoldings||[]).map(h => typeof h === "string" ? h : h.ticker?.toUpperCase());
  if (approvedTickers.includes(ticker)) reasons.push("On approved list");
  if (totalPortfolioValue > 0 && gain > totalPortfolioValue * 0.05) reasons.push("Gain > 5% of portfolio");
  if (gain > 50000) reasons.push("Gain > $50k");
  return { eligible:reasons.length > 0, clientDirected:false, blocked:false, reasons };
}

// ─────────────────────────────────────────────────────────────
// PA SUB-COMPONENTS
// ─────────────────────────────────────────────────────────────
const paCard = { padding:"16px 18px", background:"#ffffff", border:"1px solid #E2E8F0", borderRadius:12, boxShadow:"0 1px 4px rgba(0,0,0,0.04)" };
const paThStyle = { padding:"9px 12px", color:"#94A3B8", fontWeight:700, textAlign:"left", fontSize:10, textTransform:"uppercase", letterSpacing:"0.07em", borderBottom:"2px solid #E2E8F0", background:"#F8FAFC" };
const paTdStyle = { padding:"8px 12px", borderBottom:"1px solid #F1F5F9", fontSize:12, color:"#334155" };

function PALabel({ children, color }) {
  return <p style={{color:color||"#94A3B8",fontSize:10,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:12,fontWeight:700}}>{children}</p>;
}
function PAInsight({ text, icon="◆", color="#475569" }) {
  return (
    <div style={{display:"flex",gap:10,marginBottom:8,alignItems:"flex-start"}}>
      <span style={{color,fontSize:10,marginTop:2,flexShrink:0}}>{icon}</span>
      <p style={{color:"#475569",fontSize:12,lineHeight:1.7,margin:0}}>{text}</p>
    </div>
  );
}

function PAStepBar({ step }) {
  const steps = ["Upload & Extract","Review & Select","Results"];
  return (
    <div style={{display:"flex",alignItems:"center",justifyContent:"center",marginBottom:36}}>
      {steps.map((s,i) => (
        <div key={i} style={{display:"flex",alignItems:"center"}}>
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:6}}>
            <div style={{width:32,height:32,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",border:`1.5px solid ${i+1<=step?"#2563EB":"#E2E8F0"}`,background:i+1<step?"#2563EB":i+1===step?"rgba(37,99,235,0.08)":"transparent",color:i+1<step?"#ffffff":i+1===step?"#2563EB":"#94A3B8",fontSize:12,transition:"all 0.3s"}}>
              {i+1<step?"✓":i+1}
            </div>
            <span style={{fontSize:10,color:i+1===step?"#2563EB":"#94A3B8",letterSpacing:"0.06em",textTransform:"uppercase",whiteSpace:"nowrap"}}>{s}</span>
          </div>
          {i<steps.length-1&&<div style={{width:70,height:1,background:i+1<step?"#2563EB":"#BFDBFE",margin:"0 8px",marginBottom:22,transition:"background 0.3s"}}/>}
        </div>
      ))}
    </div>
  );
}

function PADonutChart({ data }) {
  const size=160,cx=80,cy=80,r=60,ir=36;
  const colors={USEquity:"#2563EB",InternationalEquity:"#7C3AED",FixedIncome:"#059669",Alternative:"#D97706",Cash:"#64748B",RealEstate:"#DB2777",Other:"#94A3B8"};
  const labels={USEquity:"US Equity",InternationalEquity:"Intl Equity",FixedIncome:"Fixed Income",Alternative:"Alternatives",Cash:"Cash",RealEstate:"Real Estate",Other:"Other"};
  const total=Object.values(data).reduce((s,v)=>s+(v||0),0);
  if(!total) return null;
  let angle=-Math.PI/2;
  const slices=Object.entries(data).filter(([,v])=>v>0).map(([key,val])=>{
    const pct=val/total,sweep=pct*2*Math.PI;
    const x1=cx+r*Math.cos(angle),y1=cy+r*Math.sin(angle);
    angle+=sweep;
    const x2=cx+r*Math.cos(angle),y2=cy+r*Math.sin(angle);
    const ix1=cx+ir*Math.cos(angle),iy1=cy+ir*Math.sin(angle);
    const ix2=cx+ir*Math.cos(angle-sweep),iy2=cy+ir*Math.sin(angle-sweep);
    const lg=sweep>Math.PI?1:0;
    return {key,d:`M${x1} ${y1}A${r} ${r} 0 ${lg} 1 ${x2} ${y2}L${ix1} ${iy1}A${ir} ${ir} 0 ${lg} 0 ${ix2} ${iy2}Z`,color:colors[key],pct:(pct*100).toFixed(1)};
  });
  return (
    <div style={{display:"flex",alignItems:"center",gap:20}}>
      <svg width={size} height={size} style={{flexShrink:0}}>
        {slices.map(s=><path key={s.key} d={s.d} fill={s.color} stroke="#F4F7FC" strokeWidth={1.5} opacity={0.9}/>)}
        <text x={cx} y={cy+4} textAnchor="middle" fill="#94A3B8" fontSize={9} fontFamily="monospace">{slices.length} classes</text>
      </svg>
      <div style={{display:"flex",flexDirection:"column",gap:5}}>
        {slices.map(s=>(
          <div key={s.key} style={{display:"flex",alignItems:"center",gap:7}}>
            <div style={{width:7,height:7,borderRadius:2,background:s.color,flexShrink:0}}/>
            <span style={{color:"#475569",fontSize:11,minWidth:90}}>{labels[s.key]}</span>
            <span style={{color:"#2563EB",fontSize:11}}>{s.pct}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PAHoldingsEditor({ holdings, onChange, accountTaxExempt=false, onAccountTaxExemptChange }) {
  const [editingCell, setEditingCell] = useState(null);
  const [editVal, setEditVal] = useState("");
  const fields = [
    {key:"ticker",     label:"Ticker",     type:"text",   width:72},
    {key:"name",       label:"Name",       type:"text",   width:160},
    {key:"assetClass", label:"Class",      type:"select", width:130, options:["US Equity","International Equity","Fixed Income","Alternative","Cash","Real Estate","Other"]},
    {key:"shares",     label:"Shares",     type:"number", width:78},
    {key:"currentPrice",label:"Price",     type:"number", width:78},
    {key:"marketValue",label:"Mkt Value",  type:"number", width:96},
    {key:"costBasis",  label:"Cost Basis", type:"number", width:96},
    {key:"gainLoss",   label:"Gain/Loss",  type:"number", width:96},
    {key:"holdingPeriod",label:"Period",   type:"select", width:96, options:["Short-term","Long-term","Unknown"]},
  ];
  const startEdit=(row,key,val)=>{setEditingCell(`${row}-${key}`);setEditVal(val??"");};
  const commitEdit=(row,key,type)=>{
    const updated=[...holdings];
    let v=type==="number"?(editVal===""?null:parseFloat(editVal)):editVal;
    updated[row]={...updated[row],[key]:v};
    if(key==="shares"||key==="currentPrice") updated[row].marketValue=(parseFloat(updated[row].shares)||0)*(parseFloat(updated[row].currentPrice)||0);
    if((key==="marketValue"||key==="costBasis")&&updated[row].costBasis!=null){const mv=updated[row].marketValue||0,cb=updated[row].costBasis||0;updated[row].gainLoss=mv-cb;updated[row].gainLossPct=cb?((mv-cb)/cb*100):null;}
    onChange(updated);setEditingCell(null);
  };
  const toggleRowExempt = (row) => {
    const updated = [...holdings];
    updated[row] = { ...updated[row], taxExempt: !updated[row].taxExempt };
    onChange(updated);
  };
  const taxFields = new Set(["costBasis","gainLoss"]);
  return (
    <div>
      {/* Account-level tax-exempt toggle */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10,padding:"8px 12px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8}}>
        <span style={{fontSize:11,color:"#64748B",flex:1}}>Account Type: <strong style={{color:"#1E3A5F"}}>{accountTaxExempt?"Non-Taxable (tax-deferred/exempt)":"Taxable"}</strong></span>
        <button onClick={()=>{
          const newExempt = !accountTaxExempt;
          if(onAccountTaxExemptChange) onAccountTaxExemptChange(newExempt);
          // Apply to all holdings that haven't been individually overridden
          onChange(holdings.map(h=>({ ...h, taxExempt: newExempt })));
        }} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 12px",border:`1px solid ${accountTaxExempt?"#059669":"#E2E8F0"}`,borderRadius:7,background:accountTaxExempt?"#ECFDF5":"#fff",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontSize:11,fontWeight:600,color:accountTaxExempt?"#065F46":"#64748B",transition:"all 0.15s"}}>
          <span style={{fontSize:14}}>{accountTaxExempt?"🛡":"◻"}</span>
          {accountTaxExempt?"Non-Taxable Account":"Mark as Non-Taxable"}
        </button>
        {accountTaxExempt&&<span style={{fontSize:10,color:"#059669",fontStyle:"italic"}}>Cost basis & gains ignored in tax analyses</span>}
      </div>

      <div style={{overflowX:"auto",border:"1px solid #E2E8F0",borderRadius:6}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
          <thead>
            <tr style={{background:"#F8FAFC"}}>
              {fields.map(f=><th key={f.key} style={{...paThStyle,minWidth:f.width}}>{f.label}</th>)}
              <th style={{...paThStyle,width:56,whiteSpace:"nowrap"}}>🛡 Exempt</th>
              <th style={{...paThStyle,width:36}}/>
            </tr>
          </thead>
          <tbody>
            {holdings.map((h,row)=>{
              const isExempt = !!h.taxExempt;
              return (
                <tr key={h.id||row} style={{borderBottom:"1px solid #F1F5F9",background:isExempt?"#F0FDF4":row%2?"rgba(255,255,255,0.008)":"transparent"}}>
                  {fields.map(f=>{
                    const isEditing = editingCell===`${row}-${f.key}`;
                    const val       = h[f.key];
                    const isTaxField = taxFields.has(f.key);
                    return (
                      <td key={f.key} style={{...paTdStyle,minWidth:f.width,opacity:isExempt&&isTaxField?0.45:1}}
                        onClick={()=>!isEditing&&!isExempt&&startEdit(row,f.key,val)}>
                        {isEditing&&!isExempt?(f.type==="select"?(
                          <select value={editVal} autoFocus onChange={e=>{setEditVal(e.target.value);setTimeout(()=>commitEdit(row,f.key,"text"),0);}} onBlur={()=>commitEdit(row,f.key,"text")} style={{background:"#EFF6FF",border:"1px solid #2563EB",color:"#1E3A5F",padding:"3px 6px",borderRadius:3,fontSize:11,width:"100%"}}>
                            {f.options.map(o=><option key={o} value={o}>{o}</option>)}
                          </select>
                        ):(
                          <input type={f.type==="number"?"number":"text"} value={editVal} autoFocus onChange={e=>setEditVal(e.target.value)} onBlur={()=>commitEdit(row,f.key,f.type)} onKeyDown={e=>{if(e.key==="Enter")commitEdit(row,f.key,f.type);if(e.key==="Escape")setEditingCell(null);}} style={{background:"#EFF6FF",border:"1px solid #2563EB",color:"#1E3A5F",padding:"3px 6px",borderRadius:3,fontSize:11,width:"100%",outline:"none"}}/>
                        )):(
                          <div style={{cursor:isExempt&&isTaxField?"default":"text",padding:"2px 4px",borderRadius:3,minHeight:20,display:"flex",alignItems:"center",gap:4}}>
                            {isTaxField&&isExempt?(
                              <span style={{textDecoration:"line-through",color:"#94A3B8",fontSize:11}}>
                                {f.key==="gainLoss"?(val!=null?fmt$(val):"—"):(val!=null?fmt$(val):"—")}
                              </span>
                            ):f.key==="ticker"?<span style={{color:"#2563EB",fontWeight:700}}>{val||"—"}</span>
                            :f.key==="gainLoss"?<span style={{color:val>=0?"#6ec8a9":"#c87e7e"}}>{val!=null?fmt$(val):"—"}</span>
                            :f.type==="number"?<span style={{color:"#64748B"}}>{val!=null?fmt$(val):"—"}</span>
                            :<span style={{color:"#64748B"}}>{val||"—"}</span>}
                          </div>
                        )}
                      </td>
                    );
                  })}
                  {/* Per-row tax-exempt toggle */}
                  <td style={{...paTdStyle,textAlign:"center"}}>
                    <button onClick={()=>toggleRowExempt(row)} title={isExempt?"Click to mark as taxable":"Click to mark as non-taxable"}
                      style={{background:isExempt?"#ECFDF5":"transparent",border:`1px solid ${isExempt?"#6EE7B7":"#E2E8F0"}`,borderRadius:5,padding:"2px 6px",cursor:"pointer",fontSize:12,color:isExempt?"#059669":"#CBD5E1",transition:"all 0.15s"}}
                      onMouseEnter={e=>{if(!isExempt)e.currentTarget.style.borderColor="#6EE7B7";}} onMouseLeave={e=>{if(!isExempt)e.currentTarget.style.borderColor="#E2E8F0";}}>
                      🛡
                    </button>
                  </td>
                  <td style={{...paTdStyle,textAlign:"center"}}>
                    <button onClick={()=>onChange(holdings.filter((_,i)=>i!==row))} style={{background:"none",border:"none",color:"#94A3B8",cursor:"pointer",fontSize:14}} onMouseOver={e=>e.target.style.color="#c87e7e"} onMouseOut={e=>e.target.style.color="#94A3B8"}>×</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}

// Result renderers
function PAAllocResult({ data }) {
  if(!data) return null;
  return (
    <div style={{display:"grid",gap:12}}>
      <div style={paCard}><PALabel>Asset Allocation</PALabel><PADonutChart data={data.summary||{}}/></div>
      {data.insights?.length>0&&<div style={paCard}><PALabel>Insights</PALabel>{data.insights.map((t,i)=><PAInsight key={i} text={t}/>)}</div>}
      {data.suggestedRebalance?.length>0&&(
        <div style={paCard}><PALabel>Rebalancing Suggestions</PALabel>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead><tr>{["Asset Class","Current","Suggested","Action"].map(h=><th key={h} style={paThStyle}>{h}</th>)}</tr></thead>
            <tbody>{data.suggestedRebalance.map((r,i)=>(
              <tr key={i} style={{borderBottom:"1px solid #F1F5F9"}}>
                <td style={paTdStyle}><span style={{color:"#64748B"}}>{r.assetClass}</span></td>
                <td style={paTdStyle}><span style={{color:"#2563EB"}}>{Number(r.current).toFixed(1)}%</span></td>
                <td style={paTdStyle}><span style={{color:"#6ec8a9"}}>{Number(r.suggested).toFixed(1)}%</span></td>
                <td style={paTdStyle}><span style={{color:"#475569"}}>{r.action}</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
function PATaxResult({ data }) {
  if(!data) return null;
  return (
    <div style={{display:"grid",gap:12}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
        {[{label:"Unrealized Gains",val:fmt$(data.totalUnrealizedGains),color:"#6ec8a9"},{label:"Unrealized Losses",val:fmt$(data.totalUnrealizedLosses),color:"#c87e7e"},{label:"Net Taxable Gain",val:fmt$(data.netTaxableGain),color:"#D97706"},{label:"Est. LTCG",val:fmt$(data.estimatedLTCG),color:"#64748B"},{label:"Est. LTCG Tax",val:fmt$(data.estimatedLTCGTax),color:"#c87e7e"},{label:"Est. STCG Tax",val:fmt$(data.estimatedSTCGTax),color:"#c87e7e"}].map((c,i)=>(
          <div key={i} style={paCard}><p style={{color:"#94A3B8",fontSize:10,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontWeight:700}}>{c.label}</p><p style={{color:c.color,fontSize:22,fontFamily:"'Outfit',sans-serif",fontWeight:300,margin:0}}>{c.val}</p></div>
        ))}
      </div>
      {data.keyFindings?.map((f,i)=><PAInsight key={i} text={f} icon="◆"/>)}
      {data.notes&&<div style={paCard}><p style={{color:"#475569",fontSize:12,lineHeight:1.75,margin:0}}>{data.notes}</p></div>}
    </div>
  );
}
function PAHarvestResult({ data }) {
  if(!data) return null;
  return (
    <div style={{display:"grid",gap:12}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
        {[{label:"Total Harvestable",val:fmt$(data.totalHarvestable),color:"#D97706"},{label:"Est. Tax Savings",val:fmt$(data.estimatedTaxSavings),color:"#6ec8a9"},{label:"Candidates",val:data.candidates?.length||0,color:"#7eb8d4"}].map((c,i)=>(
          <div key={i} style={paCard}><p style={{color:"#94A3B8",fontSize:10,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontWeight:700}}>{c.label}</p><p style={{color:c.color,fontSize:22,fontFamily:"'Outfit',sans-serif",fontWeight:300,margin:0}}>{c.val}</p></div>
        ))}
      </div>
      {data.candidates?.map((c,i)=>(
        <div key={i} style={{...paCard,borderColor:"#BFDBFE"}}>
          <div style={{display:"flex",justifyContent:"space-between",marginBottom:5}}>
            <span style={{color:"#2563EB",fontWeight:700,marginRight:8}}>{c.ticker}</span><span style={{color:"#64748B",fontSize:11}}>{c.name}</span>
            <span style={{color:"#c87e7e"}}>{fmt$(c.unrealizedLoss)} <span style={{color:"#64748B",fontSize:10}}>({fmtPct(c.pctLoss)})</span></span>
          </div>
          <p style={{color:"#475569",fontSize:11,margin:"0 0 6px"}}>{c.suggestion}</p>
          {c.replacementIdeas?.length>0&&<div style={{display:"flex",gap:5,flexWrap:"wrap"}}>{c.replacementIdeas.map((r,j)=><span key={j} style={{padding:"2px 8px",background:"rgba(110,200,169,0.08)",borderRadius:3,color:"#6ec8a9",fontSize:10}}>{r}</span>)}</div>}
        </div>
      ))}
    </div>
  );
}
function PAConcentrationResult({ data }) {
  if(!data) return null;
  const rc={Low:"#059669",Moderate:"#D97706",High:"#d4956e","Very High":"#c87e7e"};
  return (
    <div style={{display:"grid",gap:12}}>
      <div style={paCard}><PALabel>Overall Risk Score</PALabel><p style={{fontSize:28,fontFamily:"'Outfit',sans-serif",color:rc[data.riskScore]||"#D97706",margin:0}}>{data.riskScore}</p></div>
      {data.topHoldings?.length>0&&(
        <div style={paCard}><PALabel>Top Position Concentration</PALabel>
          {data.topHoldings.map((h,i)=>(
            <div key={i} style={{marginBottom:12}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}><span style={{color:"#2563EB",fontWeight:700}}>{h.ticker} <span style={{color:"#64748B",fontSize:11}}>{h.name}</span></span><span style={{color:h.pct>15?"#DC2626":"#2563EB"}}>{Number(h.pct).toFixed(1)}%</span></div>
              <div style={{height:4,background:"#E2E8F0",borderRadius:2}}><div style={{height:"100%",width:Math.min(h.pct,100)+"%",background:h.pct>20?"#DC2626":h.pct>10?"#D97706":"#059669",borderRadius:2}}/></div>
              <p style={{color:"#64748B",fontSize:10,marginTop:3}}>{h.riskNote}</p>
            </div>
          ))}
        </div>
      )}
      {data.diversificationGaps?.length>0&&<div style={paCard}><PALabel>Diversification Gaps</PALabel>{data.diversificationGaps.map((g,i)=><PAInsight key={i} text={g} icon="◻"/>)}</div>}
      {data.recommendations?.length>0&&<div style={paCard}><PALabel>Recommendations</PALabel>{data.recommendations.map((r,i)=><PAInsight key={i} text={r} icon="→" color="#6ec8a9"/>)}</div>}
    </div>
  );
}
function PAIncomeResult({ data }) {
  if(!data) return null;
  return (
    <div style={{display:"grid",gap:12}}>
      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
        {[{label:"Est. Annual Income",val:fmt$(data.estimatedAnnualIncome),color:"#6ec8a9"},{label:"Yield on Cost",val:data.yieldOnCost!=null?data.yieldOnCost.toFixed(2)+"%":"—",color:"#2563EB"},{label:"Current Yield",val:data.currentYield!=null?data.currentYield.toFixed(2)+"%":"—",color:"#7eb8d4"}].map((c,i)=>(
          <div key={i} style={paCard}><p style={{color:"#94A3B8",fontSize:10,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:6,fontWeight:700}}>{c.label}</p><p style={{color:c.color,fontSize:22,fontFamily:"'Outfit',sans-serif",fontWeight:300,margin:0}}>{c.val}</p></div>
        ))}
      </div>
      {data.notes&&<div style={paCard}><p style={{color:"#475569",fontSize:12,lineHeight:1.75,margin:0}}>{data.notes}</p></div>}
    </div>
  );
}
function PARiskResult({ data }) {
  if(!data) return null;
  const pc={Conservative:"#059669","Moderate-Conservative":"#2563EB",Moderate:"#D97706","Moderate-Aggressive":"#d4956e",Aggressive:"#c87e7e"};
  return (
    <div style={{display:"grid",gap:12}}>
      <div style={paCard}><PALabel>Risk Profile</PALabel><p style={{fontSize:22,fontFamily:"'Outfit',sans-serif",color:pc[data.overallRiskProfile]||"#D97706",margin:"0 0 8px",fontFamily:"'Outfit',sans-serif"}}>{data.overallRiskProfile}</p><p style={{color:"#475569",fontSize:12,margin:0}}>{data.volatilityAssessment}</p></div>
      {data.keyRisks?.length>0&&<div style={paCard}><PALabel>Key Risks</PALabel>{data.keyRisks.map((r,i)=><PAInsight key={i} text={r} icon="▲" color="#c87e7e"/>)}</div>}
      {data.recommendations?.length>0&&<div style={paCard}><PALabel>Recommendations</PALabel>{data.recommendations.map((r,i)=><PAInsight key={i} text={r} icon="→" color="#6ec8a9"/>)}</div>}
    </div>
  );
}
function PASummaryResult({ data }) {
  if(!data) return null;
  const pc={High:"#DC2626",Medium:"#D97706",Low:"#6ec8a9"};
  return (
    <div style={{display:"grid",gap:12}}>
      {data.headline&&<div style={paCard}><p style={{color:"#1E3A5F",fontSize:16,fontFamily:"'Outfit',sans-serif",fontWeight:300,margin:0}}>{data.headline}</p></div>}
      {data.priorityActions?.length>0&&(
        <div style={paCard}><PALabel>Priority Actions</PALabel>
          {data.priorityActions.map((a,i)=>(
            <div key={i} style={{marginBottom:12,paddingBottom:12,borderBottom:i<data.priorityActions.length-1?"1px solid #E2E8F0":"none"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                <span style={{padding:"1px 8px",borderRadius:3,fontSize:9,fontWeight:700,background:pc[a.priority]+"18",color:pc[a.priority],border:`1px solid ${pc[a.priority]}44`}}>{a.priority}</span>
                <span style={{color:"#1E3A5F",fontSize:12,fontWeight:600}}>{a.action}</span>
              </div>
              <p style={{color:"#475569",fontSize:11,margin:0}}>{a.rationale}</p>
            </div>
          ))}
        </div>
      )}
      {data.positives?.length>0&&<div style={paCard}><PALabel color="#6ec8a9">Positives</PALabel>{data.positives.map((p,i)=><PAInsight key={i} text={p} icon="✓" color="#6ec8a9"/>)}</div>}
      {data.concerns?.length>0&&<div style={paCard}><PALabel color="#c87e7e">Concerns</PALabel>{data.concerns.map((c,i)=><PAInsight key={i} text={c} icon="!" color="#c87e7e"/>)}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PORTFOLIO ANALYZER — main view
// ─────────────────────────────────────────────────────────────
const ACCOUNT_TYPES = [
  "Taxable Brokerage","Traditional IRA","Roth IRA","401(k)","403(b)","457(b)",
  "SEP IRA","SIMPLE IRA","Inherited IRA","Roth Inherited IRA",
  "Revocable Trust","Irrevocable Trust","529 Plan","HSA","Pension",
  "Joint Tenancy","TOD Account","Custodial (UGMA/UTMA)",
];
const TAX_EXEMPT_ACCT = new Set([
  "Traditional IRA","Roth IRA","401(k)","403(b)","457(b)",
  "SEP IRA","SIMPLE IRA","Inherited IRA","Roth Inherited IRA","HSA","Pension",
]);
const isAccountTaxExempt = (type) => TAX_EXEMPT_ACCT.has(type);

const newManualRow = () => ({ ticker:"", name:"", assetClass:"US Equity", shares:"", currentPrice:"", marketValue:"", costBasis:"" });

function PortfolioAnalyzer({ currentUser, engine, setEngine, sbClients=[], loadClients=()=>{}, onLoadClient, addAudit=()=>{}, onDirtyChange=null }) {
  const { resolveName } = usePIIVault();

  const emitDirty = useCallback((dirty) => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [onDirtyChange]);

  const [step, setStep]               = useState(1);
  // Step 1 — combined pool
  const [pool, setPool]               = useState([]);    // { ...holding, _src:"ai"|"xl"|"manual" }
  const [poolMeta, setPoolMeta]       = useState(null);  // account info from first source
  const [files, setFiles]             = useState([]);    // statement files queued
  const [manualRows, setManualRows]   = useState([newManualRow()]);
  const [manualAcctLabel, setManualAcctLabel] = useState("");
  const [manualAcctType,  setManualAcctType]  = useState("Taxable Brokerage");
  const [xlPending,  setXlPending]   = useState(null);
  const [xlAcctLabel, setXlAcctLabel] = useState("");
  const [xlAcctType,  setXlAcctType]  = useState("Taxable Brokerage");
  const [extracting, setExtracting]   = useState(false);
  const [extractProgress, setExtractProgress] = useState("");
  const [nativeResult, setNativeResult] = useState(null); // { holdings, confidence, fileCount, imgOnly }
  // Multi-account extraction review
  const [extractedAccounts, setExtractedAccounts] = useState(null); // null | array of accounts from AI
  const [includedAccounts,  setIncludedAccounts]  = useState({});   // { [i]: bool }
  const [accountEdits,      setAccountEdits]      = useState({});   // { [i]: { accountType, taxExempt } }
  const TAX_EXEMPT_TYPES_PA = ["IRA","Roth IRA","401k","403b","457","SEP IRA","SIMPLE IRA","Pension","529","HSA"];
  // Step 2+
  const [holdings, setHoldings]       = useState([]);
  const [accountInfo, setAccountInfo] = useState(null);
  const [analyzing, setAnalyzing]     = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState("");
  const [selectedAnalyses, setSelectedAnalyses] = useState([]);
  const [analysisResults, setAnalysisResults]   = useState(null);
  const [activeResult, setActiveResult]         = useState(null);
  const [error, setError]             = useState("");
  const [saving, setSaving]           = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [sessionSavedId, setSessionSavedId] = useState(null);
  // Save modal state declared before loadClientToPool so setSaveModalName is in scope
  const [showSaveModal, setShowSaveModal]   = useState(false);
  const [saveModalName, setSaveModalName]   = useState("");

  // ── Load a saved client back into the analyzer pool for editing ──
  const loadClientToPool = (client) => {
    const clientHoldings = client.holdings || [];
    if (!clientHoldings.length) return;
    const newRows = clientHoldings.map((h, i) => ({
      ...h,
      id: h.id || `saved_${Date.now()}_${i}`,
      _src: "saved",
    }));
    setPool(newRows);
    setPoolMeta({
      clientName:          client.name,
      accountNumber:       null,
      accountType:         client.accountType || "Taxable Brokerage",
      custodian:           client.custodian || null,
      statementDate:       null,
      totalPortfolioValue: client.totalPortfolioValue || clientHoldings.reduce((s,h)=>s+(h.marketValue||0),0),
      cashBalance:         0,
    });
    setSessionSavedId(client.id);
    setSaveModalName(resolveName(client.name) || "");
    setStep(1);
    emitDirty(true);
  };

  const fileRef    = useRef(null);
  const xlFileRef  = useRef(null);

  // My saved clients (this advisor)
  const myClients = (sbClients || []).filter(c => c.advisorId === currentUser.id);

  // ── Statement drop / file pick ──
  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files).filter(f =>
      f.type==="application/pdf" || f.type.startsWith("image/") ||
      f.name?.toLowerCase().endsWith(".pdf") // fallback for browsers that report blank MIME
    );
    if (dropped.length) setFiles(p => [...p, ...dropped]);
  };

  // ── Native PDF parse — runs first before any AI call ──
  const runNativeParse = async () => {
    if (!files.length) return;
    setExtracting(true); setError(""); setExtractProgress("Scanning document…");
    try {
      const pdfFiles = files.filter(f => f.type === "application/pdf");
      const imgFiles = files.filter(f => f.type !== "application/pdf");
      let allHoldings = [];
      let worstConfidence = "high";
      const confOrder = { high:2, low:1, none:0 };
      for (const f of pdfFiles) {
        setExtractProgress(`Reading ${f.name}…`);
        const result = await nativeParseStatement(f);
        allHoldings = [...allHoldings, ...result.holdings];
        if (confOrder[result.confidence] < confOrder[worstConfidence]) worstConfidence = result.confidence;
      }
      if (imgFiles.length) worstConfidence = "none";
      setNativeResult({ holdings:allHoldings, confidence:worstConfidence, fileCount:files.length, imgOnly:!pdfFiles.length&&imgFiles.length>0 });
    } catch(e) { setError("Native scan failed: "+e.message); }
    setExtracting(false); setExtractProgress("");
  };

  // Commit native parse results directly to pool
  const commitNativeToPool = () => {
    if (!nativeResult?.holdings?.length) return;
    const newRows = nativeResult.holdings.map((h,i)=>({ ...h, id:`nat_${Date.now()}_${i}`, _src:"native" }));
    setPool(p => [...p, ...newRows]);
    setNativeResult(null);
    setFiles([]);
    emitDirty(true);
  };

  // ── AI extraction — parses multi-account response, shows account picker ──
  // PRODUCTION: statement extraction must be proxied through your backend to protect the API key
  const extractStatement = async () => {
    if (!files.length) return;
    setExtracting(true); setError(""); setExtractProgress("Reading files…");
    try {
      const hasPDF = files.some(f => f.type === "application/pdf");
      const contents = await Promise.all(files.map(async f => {
        const data = await fileToBase64(f);
        if (f.type === "application/pdf") return { type:"document", source:{ type:"base64", media_type:"application/pdf", data } };
        return { type:"image", source:{ type:"base64", media_type:f.type, data } };
      }));
      setExtractProgress("Extracting holdings with AI…");
      const headers = { "Content-Type":"application/json" };
      if (hasPDF) headers["anthropic-beta"] = "pdfs-2024-09-25";
      // Use /api/ai proxy when running on your own server (Replit/Vercel/etc)
      // Falls back to direct Anthropic call when running inside Claude artifacts
      const AI_URL = window.location.hostname === "claude.ai" || window.location.hostname.endsWith(".webcontainer.io")
        ? "https://api.anthropic.com/v1/messages"
        : "/api/ai";
      const res = await fetch(AI_URL, {
        method:"POST", headers,
        body: JSON.stringify({ model:engine.aiConfig?.model||PA_MODEL, max_tokens:8000, messages:[{ role:"user", content:[...contents,{type:"text",text:engine.aiConfig?.extractPrompt||EXTRACT_PROMPT}] }] })
      });
      const data = await res.json();
      if (data.error) throw new Error(`API error: ${data.error.message || JSON.stringify(data.error)}`);
      const text = data.content?.map(b=>b.text||"").join("").replace(/```json|```/g,"").trim();
      const parsed = JSON.parse(text);

      // Normalise: support both new multi-account shape and legacy single-account shape
      let accounts;
      if (parsed.accounts && Array.isArray(parsed.accounts)) {
        accounts = parsed.accounts;
      } else {
        // Legacy single-account — wrap it
        accounts = [{
          accountNumber: parsed.accountNumber,
          accountType:   parsed.accountType || "Brokerage",
          accountLabel:  null,
          custodian:     parsed.custodian,
          totalValue:    parsed.totalPortfolioValue,
          cashBalance:   parsed.cashBalance,
          holdings:      parsed.holdings || [],
        }];
      }

      // Auto-detect taxExempt from accountType
      const accountsWithExempt = accounts.map(acc => ({
        ...acc,
        taxExempt: TAX_EXEMPT_TYPES_PA.includes(acc.accountType),
      }));

      setExtractedAccounts({ clientName: parsed.clientName, statementDate: parsed.statementDate, accounts: accountsWithExempt });
      // Default all accounts to included
      const included = {};
      const edits = {};
      accountsWithExempt.forEach((acc, i) => {
        included[i] = true;
        edits[i]    = { accountType: acc.accountType, taxExempt: acc.taxExempt };
      });
      setIncludedAccounts(included);
      setAccountEdits(edits);
      setFiles([]);
    } catch(e) { setError("Extraction failed: "+e.message); }
    setExtracting(false); setExtractProgress("");
  };

  // ── Commit selected accounts from picker into the pool ──
  const commitExtractedAccounts = () => {
    if (!extractedAccounts) return;
    const { clientName, statementDate, accounts } = extractedAccounts;
    let newRows = [];
    let firstMeta = null;
    accounts.forEach((acc, i) => {
      if (!includedAccounts[i]) return;
      const edit     = accountEdits[i] || {};
      const type     = edit.accountType || acc.accountType;
      const exempt   = edit.taxExempt   ?? acc.taxExempt;
      const rows     = (acc.holdings || []).map((h, hi) => ({
        ...h,
        id: `ai_${Date.now()}_${i}_${hi}`,
        _src: "ai",
        // _account is display-only in the pool — use label or account type, never the raw account number
        _account: acc.accountLabel || type || `Account ${i+1}`,
        taxExempt: exempt,
      }));
      newRows = [...newRows, ...rows];
      if (!firstMeta) firstMeta = {
        clientName, statementDate,
        accountNumber: acc.accountNumber,
        accountType:   type,
        taxExempt:     exempt,
        custodian:     acc.custodian,
        totalPortfolioValue: acc.totalValue,
        cashBalance:   acc.cashBalance,
      };
    });
    setPool(p => [...p, ...newRows]);
    if (!poolMeta && firstMeta) setPoolMeta(firstMeta);
    setExtractedAccounts(null);
    emitDirty(true);
  };

  // ── Excel/CSV — appends to pool ──
  const handleExcelFile = async (file) => {
    setExtracting(true); setError(""); setExtractProgress("Parsing spreadsheet…");
    try {
      const rows = await parseExcelToHoldings(file);
      if (!rows.length) throw new Error("No holdings found. Check column headers.");
      setXlPending({ rows, fileName: file.name });
      setXlAcctLabel(file.name.replace(/\.xlsx?|\.csv/i,"").trim());
      setXlAcctType("Taxable Brokerage");
    } catch(e) { setError("Excel import failed: "+e.message); }
    setExtracting(false); setExtractProgress("");
  };

  const commitXlToPool = () => {
    if (!xlPending) return;
    const exempt = isAccountTaxExempt(xlAcctType);
    const label  = xlAcctLabel.trim() || xlAcctType;
    const newRows = xlPending.rows.map((r,i)=>({
      ...r, id:`xl_${Date.now()}_${i}`, _src:"xl",
      accountLabel: label, accountType: xlAcctType, taxExempt: exempt,
    }));
    setPool(p => [...p, ...newRows]);
    setXlPending(null);
    emitDirty(true);
  };

  // ── Ticker auto-fill via Yahoo Finance search ──
  const [tickerLookupStatus, setTickerLookupStatus] = useState({}); // { [rowIdx]: "loading"|"ok"|"error" }
  const [batchLookupRunning, setBatchLookupRunning] = useState(false);

  const SECTOR_TO_ASSET_CLASS = {
    "Technology":"US Equity","Communication Services":"US Equity",
    "Consumer Cyclical":"US Equity","Consumer Defensive":"US Equity",
    "Healthcare":"US Equity","Industrials":"US Equity",
    "Basic Materials":"US Equity","Energy":"US Equity","Utilities":"US Equity",
    "Real Estate":"Real Estate","Financial Services":"US Equity",
  };

  const guessAssetClass = (quote) => {
    const qt = (quote.quoteType||"").toLowerCase();
    const nm = (quote.shortname||quote.longname||"").toLowerCase();
    if (qt==="bond") return "Fixed Income";
    if (nm.includes("bond")||nm.includes("fixed income")||nm.includes("treasury")) return "Fixed Income";
    if (nm.includes("international")||nm.includes("emerging")||nm.includes("europe")||nm.includes("asia")) return "International Equity";
    if (nm.includes("real estate")||nm.includes("reit")) return "Real Estate";
    if (nm.includes("cash")||nm.includes("money market")) return "Cash";
    if (nm.includes("commodity")||nm.includes("gold")||nm.includes("silver")) return "Alternative";
    return SECTOR_TO_ASSET_CLASS[quote.sector] || "US Equity";
  };

  // DEMO: corsproxy.io handles CORS for the artifact preview.
  // PRODUCTION: replace TICKER_API with your own backend endpoint e.g. /api/ticker-lookup?q=${sym}
  const TICKER_API = (s) => `https://corsproxy.io/?https://query1.finance.yahoo.com/v1/finance/search?q=${encodeURIComponent(s)}&quotesCount=1&newsCount=0&enableFuzzyQuery=false`;

  const lookupOneTicker = async (sym) => {
    const res = await fetch(TICKER_API(sym), { headers:{"Accept":"application/json"} });
    if (!res.ok) throw new Error("Network error");
    const data = await res.json();
    const quote = (data.quotes||[]).find(q =>
      q.symbol?.toUpperCase()===sym && q.quoteType!=="FUTURE" && q.quoteType!=="OPTION"
    ) || data.quotes?.[0];
    if (!quote) throw new Error("Not found");
    return { name: quote.shortname||quote.longname||"", assetClass: guessAssetClass(quote) };
  };

  const lookupAllTickers = async () => {
    const rows = manualRows;
    const toLookup = rows.map((r,i) => ({ i, sym: (r.ticker||"").trim().toUpperCase() })).filter(x => x.sym);
    if (!toLookup.length) return;
    setBatchLookupRunning(true);
    // Mark all as loading
    const loadingStatus = {};
    toLookup.forEach(x => { loadingStatus[x.i] = "loading"; });
    setTickerLookupStatus(loadingStatus);

    const newStatus = {};
    const updates = {};
    // Sequential to avoid hammering the API
    for (const { i, sym } of toLookup) {
      try {
        const result = await lookupOneTicker(sym);
        updates[i] = result;
        newStatus[i] = "ok";
      } catch(e) {
        newStatus[i] = "error";
      }
      setTickerLookupStatus(p => ({...p, [i]: newStatus[i]}));
    }
    // Apply all updates at once
    setManualRows(p => p.map((r,i) => {
      if (!updates[i]) return r;
      return {
        ...r,
        name:       r.name || updates[i].name,
        assetClass: updates[i].assetClass || r.assetClass,
      };
    }));
    setBatchLookupRunning(false);
    // Clear ok statuses after 3s, leave errors visible
    setTimeout(() => setTickerLookupStatus(p => {
      const n = {...p};
      Object.keys(n).forEach(k => { if (n[k]==="ok") delete n[k]; });
      return n;
    }), 3000);
  };

  // ── Enrich pool data — fetch yield + expense ratio for all pool positions ──
  const [enrichRunning,  setEnrichRunning]  = useState(false);
  const [enrichProgress, setEnrichProgress] = useState("");
  const [enrichResults,  setEnrichResults]  = useState({ filled:0, skipped:0 });

  // PRODUCTION: replace ENRICH_API with your own backend endpoint
  const ENRICH_API = (sym) =>
    `https://corsproxy.io/?https://query1.finance.yahoo.com/v10/finance/quoteSummary/${encodeURIComponent(sym)}?modules=summaryDetail%2CdefaultKeyStatistics`;

  // Shared enrich function — works on any array via setter
  const enrichRows = async (rows, setter) => {
    const tickers = [...new Set(rows.map(h => h.ticker?.trim().toUpperCase()).filter(Boolean))];
    if (!tickers.length) return;
    setEnrichRunning(true);
    setEnrichResults({ filled:0, skipped:0 });
    let filled = 0, skipped = 0;
    const enrichData = {};
    for (let i = 0; i < tickers.length; i++) {
      const sym = tickers[i];
      setEnrichProgress(`${sym} (${i+1}/${tickers.length})`);
      try {
        const res  = await fetch(ENRICH_API(sym), { headers:{"Accept":"application/json"} });
        if (!res.ok) throw new Error();
        const data = await res.json();
        const sd   = data?.quoteSummary?.result?.[0]?.summaryDetail;
        const ks   = data?.quoteSummary?.result?.[0]?.defaultKeyStatistics;
        const yld  = sd?.dividendYield?.raw ?? sd?.yield?.raw ?? null;
        const er   = ks?.annualReportExpenseRatio?.raw ?? sd?.expenseRatio?.raw ?? null;
        if (yld !== null || er !== null) {
          enrichData[sym] = {
            yieldPct:     yld !== null ? yld * 100 : null,
            expenseRatio: er  !== null ? er  * 100 : null,
          };
          filled++;
        } else { skipped++; }
      } catch(e) { skipped++; }
      await new Promise(r => setTimeout(r, 150));
    }
    setter(p => p.map(h => {
      const d = enrichData[h.ticker?.trim().toUpperCase()];
      if (!d) return h;
      return { ...h, yieldPct: h.yieldPct ?? d.yieldPct, expenseRatio: h.expenseRatio ?? d.expenseRatio };
    }));
    setEnrichResults({ filled, skipped });
    setEnrichRunning(false);
    setEnrichProgress("");
  };

  // Lookup all tickers in holdings array (used at step 2)
  const lookupAllHoldings = async () => {
    setBatchLookupRunning(true);
    const toLookup = holdings.map((r,i) => ({ i, sym: (r.ticker||"").trim().toUpperCase() })).filter(x => x.sym);
    const loadingStatus = {};
    toLookup.forEach(x => { loadingStatus[x.i] = "loading"; });
    setTickerLookupStatus(loadingStatus);
    const newStatus = {}, updates = {};
    for (const { i, sym } of toLookup) {
      try {
        const result = await lookupOneTicker(sym);
        updates[i] = result;
        newStatus[i] = "ok";
      } catch(e) { newStatus[i] = "error"; }
      setTickerLookupStatus(p => ({...p, [i]: newStatus[i]}));
    }
    setHoldings(p => p.map((r,i) => {
      if (!updates[i]) return r;
      return { ...r, name: r.name || updates[i].name, assetClass: updates[i].assetClass || r.assetClass };
    }));
    setBatchLookupRunning(false);
    setTimeout(() => setTickerLookupStatus(p => {
      const n = {...p};
      Object.keys(n).forEach(k => { if (n[k]==="ok") delete n[k]; });
      return n;
    }), 3000);
  };

  const enrichPoolData     = () => enrichRows(pool,     setPool);
  const enrichHoldingsData = () => enrichRows(holdings, setHoldings);

  const addManualToPool = () => {
    const valid = manualRows.filter(r=>r.ticker||r.name);
    if (!valid.length) { setError("Fill in at least one row before adding."); return; }
    setError("");
    const exempt = isAccountTaxExempt(manualAcctType);
    const label  = manualAcctLabel.trim() || manualAcctType;
    const newRows = valid.map((r,i)=>({
      id:`m_${Date.now()}_${i}`, _src:"manual",
      ticker:(r.ticker||"").toUpperCase().trim(), name:r.name,
      assetClass:r.assetClass||"Other",
      shares:parseFloat(r.shares)||0, currentPrice:parseFloat(r.currentPrice)||0,
      marketValue:parseFloat(r.marketValue)||(parseFloat(r.shares||0)*parseFloat(r.currentPrice||0)),
      costBasis:parseFloat(r.costBasis)||0,
      gainLoss: (parseFloat(r.marketValue)||(parseFloat(r.shares||0)*parseFloat(r.currentPrice||0))) - (parseFloat(r.costBasis)||0),
      gainLossPct: (parseFloat(r.costBasis)||0) > 0 ? (((parseFloat(r.marketValue)||(parseFloat(r.shares||0)*parseFloat(r.currentPrice||0))) - (parseFloat(r.costBasis)||0)) / (parseFloat(r.costBasis)||0) * 100) : 0,
      holdingPeriod:"Unknown", purchaseDate:null,
      accountLabel: label, accountType: manualAcctType, taxExempt: exempt,
    }));
    setPool(p => [...p, ...newRows]);
    setManualRows([newManualRow()]);
    emitDirty(true);
  };

  // ── Proceed to Review ──
  const proceedToReview = () => {
    if (!pool.length) { setError("Add at least one holding before continuing."); return; }
    const meta  = poolMeta || { clientName:null, accountNumber:null, accountType:"Taxable Brokerage", custodian:null, statementDate:null, totalPortfolioValue:pool.reduce((s,r)=>s+(r.marketValue||0),0), cashBalance:0 };
    const metaExempt = isAccountTaxExempt(meta.accountType);
    const infoWithExempt = { ...meta, taxExempt: meta.taxExempt ?? metaExempt };
    setHoldings(pool.map(({_src,...h})=>{
      const mv = h.marketValue!=null ? h.marketValue : 0;
      const cb = h.costBasis!=null ? h.costBasis : null;
      const gl  = (cb!=null && mv!==0) ? mv-cb : (h.gainLoss??0);
      const glp = (cb!=null && cb!==0) ? ((mv-cb)/cb*100) : (h.gainLossPct??0);
      return {
        ...h,
        taxExempt: h.taxExempt !== undefined ? h.taxExempt : metaExempt,
        gainLoss:    gl,
        gainLossPct: glp,
      };
    }));
    setAccountInfo(infoWithExempt);
    setStep(2);
    emitDirty(true);
  };

  const toggleAnalysis = (id) => setSelectedAnalyses(p => p.includes(id)?p.filter(x=>x!==id):[...p,id]);

  // PRODUCTION: AI calls must go through your backend — never expose the Anthropic API key client-side
  const runAnalysis = async () => {
    if (!selectedAnalyses.length) return;
    setAnalyzing(true); setError(""); const results={};
    for (let i=0; i<selectedAnalyses.length; i++) {
      const id=selectedAnalyses[i];
      const opt=PA_ANALYSES.find(a=>a.id===id);
      setAnalyzeProgress(`Running ${opt?.label} (${i+1}/${selectedAnalyses.length})…`);
      try {
        const aiUrl = window.location.hostname === "claude.ai" || window.location.hostname.endsWith(".webcontainer.io") ? "https://api.anthropic.com/v1/messages" : "/api/ai";
        const res=await fetch(aiUrl,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:engine.aiConfig?.model||PA_MODEL,max_tokens:2000,messages:[{role:"user",content:buildAnalysisPrompt(holdings,id,engine.aiConfig?.analysisPrompt)}]})});
        const data=await res.json();
        const text=data.content?.map(b=>b.text||"").join("").replace(/```json|```/g,"").trim();
        const parsed=JSON.parse(text);
        results[id]=parsed[id];
      } catch(e){ results[id]=null; }
    }
    setAnalysisResults(results); setActiveResult(selectedAnalyses[0]); setAnalyzing(false); setAnalyzeProgress(""); setStep(3);
  };

  const { tokenizePII } = usePIIVault();

  // ── Derive the best available client name from current state ──
  const bestClientName = () =>
    accountInfo?.clientName || poolMeta?.clientName ||
    (pool.length>0 ? "Unnamed Client" : null);

  // ── Open save modal — pre-fill name, check if session record exists ──
  const openSaveModal = () => {
    setSaveModalName(bestClientName() || "");
    setShowSaveModal(true);
  };

  // ── Execute save — mode: "new" | "update" ──
  const executeSave = async (mode) => {
    setShowSaveModal(false);
    setSaving(true);

    // Use holdings from current step — pool at step 1, holdings at step 2+
    const currentHoldings = step >= 2 ? holdings : pool.map(({_src,...h})=>h);
    const rawName = saveModalName.trim() || bestClientName() || "Unnamed Client";
    const rawAcct = accountInfo?.accountNumber || null;
    const { nameToken, acctToken } = await tokenizePII(rawName, rawAcct);

    const totalPV = accountInfo?.totalPortfolioValue
      || currentHoldings.reduce((s,h)=>s+(h.marketValue||0),0);

    if (mode === "update" && sessionSavedId) {
      // Update existing record — merge in latest holdings + analysis
      // Update existing client in Supabase
      const updatedClient = {
        ...sbClients.find(c => c.id === sessionSavedId),
        holdings:            currentHoldings,
        analysisResults:     analysisResults || {},
        totalPortfolioValue: totalPV,
        savedAt:             new Date().toISOString(),
      };
      sbSaveClient(updatedClient, currentUser?.firm_id, currentUser?.id).then(() => {
        loadClients(currentUser?.firm_id);
      });
      addAudit("client_saved", `Client record updated (identity protected — token: ${nameToken})`);
    } else {
      // Create new record
      const client = {
        id:                  `client_${Date.now()}`,
        name:                nameToken,
        accountNumber:       acctToken,
        accountType:         accountInfo?.accountType || poolMeta?.accountType || "Brokerage",
        custodian:           accountInfo?.custodian   || poolMeta?.custodian   || null,
        statementDate:       accountInfo?.statementDate || poolMeta?.statementDate || null,
        totalPortfolioValue: totalPV,
        cashBalance:         accountInfo?.cashBalance || poolMeta?.cashBalance || 0,
        holdings:            currentHoldings,
        analysisResults:     analysisResults || {},
        advisorId:           currentUser.id,
        advisorName:         currentUser.name,
        savedAt:             new Date().toISOString(),
        sharedWith:          [],
        savedAllocations:    [],
      };
      // Save to Supabase clients table
      const firmId = currentUser?.firm_id;
      if (firmId) {
        sbSaveClient(client, firmId, currentUser.id).then(() => {
          loadClients(firmId);
        });
      }
      setSessionSavedId(client.id);
      addAudit("client_saved", `Client saved (identity protected — token: ${nameToken})`);
    }

    setSaving(false);
    setSaveSuccess(true);
    emitDirty(false);
    setTimeout(()=>setSaveSuccess(false), 3000);
  };

  // ── Legacy alias used by step 3 Save Client button ──
  const saveClient = () => openSaveModal();

  const resultMap = {
    allocationBreakdown: <PAAllocResult data={analysisResults?.allocationBreakdown}/>,
    taxConsequences:     <PATaxResult   data={analysisResults?.taxConsequences}/>,
    taxLossHarvesting:   <PAHarvestResult data={analysisResults?.taxLossHarvesting}/>,
    concentrationRisk:   <PAConcentrationResult data={analysisResults?.concentrationRisk}/>,
    incomeAnalysis:      <PAIncomeResult data={analysisResults?.incomeAnalysis}/>,
    riskMetrics:         <PARiskResult  data={analysisResults?.riskMetrics}/>,
    advisorSummary:      <PASummaryResult data={analysisResults?.advisorSummary}/>,
  };

  const totalPV = accountInfo?.totalPortfolioValue || holdings.reduce((s,h)=>s+(h.marketValue||0),0);

  return (
    <div style={{minHeight:"100vh",background:"#F4F7FC",fontFamily:"'Outfit',sans-serif",padding:"28px 0"}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .pa-drop:hover{border-color:#2563EB!important;background:#EFF6FF!important;}`}</style>
      <div style={{maxWidth:880,margin:"0 auto",padding:"0 20px"}}>

        {/* Header row */}
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:28}}>
          <div>
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:300,color:"#1E3A5F",letterSpacing:"0.5px"}}>Portfolio Analyzer</div>
            <div style={{fontSize:11,color:"#94A3B8",letterSpacing:"1.5px",textTransform:"uppercase",marginTop:3,fontWeight:600}}>Client Onboarding · Statement Analysis</div>
          </div>
          <button onClick={()=>setStep(step===4?1:4)}
            style={{padding:"7px 16px",background:step===4?"#EFF6FF":"transparent",border:`1px solid ${step===4?"#2563EB":"#E2E8F0"}`,borderRadius:6,color:"#2563EB",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif",letterSpacing:"0.05em"}}>
            {step===4?"← New Analysis":"My Clients ("+myClients.length+")"}
          </button>
        </div>

        {/* Saved clients list */}
        {step===4&&(
          <div>
            {myClients.length===0?(
              <div style={{textAlign:"center",padding:"60px 0",color:"#64748B",fontSize:13}}>No saved clients yet. Run an analysis and save a client to see them here.</div>
            ):(
              <div style={{display:"grid",gap:10}}>
                {myClients.map(c=>(
                  <div key={c.id} style={{...paCard,display:"flex",alignItems:"center",gap:14}}>
                    <div style={{width:38,height:38,borderRadius:"50%",background:"#EFF6FF",border:"1px solid #BFDBFE",display:"flex",alignItems:"center",justifyContent:"center",fontSize:15,color:"#2563EB",flexShrink:0,cursor:"pointer"}} onClick={()=>onLoadClient(c)}>
                      {(resolveName(c.name)||"?")[0].toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0,cursor:"pointer"}} onClick={()=>onLoadClient(c)}>
                      <div style={{color:"#1E3A5F",fontWeight:700,fontSize:13}}><ClientName token={c.name}/></div>
                      <div style={{color:"#64748B",fontSize:11,marginTop:2,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                        <span>{c.accountType} · {fmt$(c.totalPortfolioValue)} · {c.holdings?.length||0} positions · saved {new Date(c.savedAt).toLocaleDateString()}</span>
                        {(c.savedAllocations||[]).length>0&&(
                          <span style={{fontSize:9,padding:"1px 7px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,color:"#2563EB",fontWeight:700}}>
                            📊 {c.savedAllocations.length} allocation{c.savedAllocations.length!==1?"s":""}
                          </span>
                        )}
                      </div>
                    </div>
                    <button onClick={e=>{e.stopPropagation();loadClientToPool(c);}}
                      style={{padding:"6px 14px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:6,color:"#7C3AED",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
                      ✏️ Edit
                    </button>
                    <button onClick={e=>{e.stopPropagation();onLoadClient(c);}}
                      style={{padding:"6px 14px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:6,color:"#2563EB",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
                      📊 Allocator
                    </button>
                    <button onClick={e=>{e.stopPropagation();addAudit("client_deleted",`Client deleted: ${resolveName(c.name)}`);sbDeleteClient(c.id, currentUser?.firm_id).then(()=>loadClients(currentUser?.firm_id));}}
                      style={{padding:"6px 10px",background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:6,color:"#DC2626",fontSize:13,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}
                      title="Delete client">
                      🗑
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {step!==4&&<PAStepBar step={step}/>}

        {/* STEP 1 — Combined input pool */}
        {step===1&&(
          <div>
            {/* ── Source badges key ── */}
            <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{fontSize:11,color:"#94A3B8",fontWeight:600}}>Add holdings from any combination of sources:</span>
              {[["📄","AI Statement","#2563EB","#EFF6FF","#BFDBFE"],["📊","Excel / CSV","#059669","#ECFDF5","#6EE7B7"],["✏️","Manual","#7C3AED","#F5F3FF","#DDD6FE"]].map(([icon,label,color,bg,border])=>(
                <span key={label} style={{fontSize:10,padding:"2px 9px",borderRadius:10,background:bg,color,border:`1px solid ${border}`,fontWeight:700}}>{icon} {label}</span>
              ))}
            </div>

            {/* ── PDF + Excel on top row, Manual Entry full width below ── */}
            <div style={{display:"flex",flexDirection:"column",gap:12,marginBottom:16}}>

              {/* Top row: PDF and Excel side by side */}
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>

              {/* Statement */}
              <div style={{border:"1px solid #BFDBFE",borderRadius:10,overflow:"hidden"}}>
                <div style={{background:"#EFF6FF",padding:"8px 12px",borderBottom:"1px solid #BFDBFE",display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:13}}>📄</span>
                  <span style={{fontSize:11,fontWeight:700,color:"#1E40AF"}}>Statement (PDF / Image)</span>
                </div>
                <div style={{padding:10}}>
                  <div onDragOver={e=>e.preventDefault()} onDrop={handleDrop}
                    onClick={e=>{ if(e.target===e.currentTarget||e.target.tagName==="P"||e.target.tagName==="DIV") fileRef.current?.click(); }}
                    style={{border:"2px dashed #BFDBFE",borderRadius:7,padding:"18px 10px",textAlign:"center",cursor:"pointer",transition:"all 0.15s",background:"#FAFEFF"}}
                    onMouseEnter={e=>{e.currentTarget.style.background="#EFF6FF";e.currentTarget.style.borderColor="#93C5FD";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="#FAFEFF";e.currentTarget.style.borderColor="#BFDBFE";}}>
                    <input ref={fileRef} type="file" accept=".pdf,image/*" multiple
                      onClick={e=>e.stopPropagation()}
                      onChange={e=>{ const picked=Array.from(e.target.files||[]); if(picked.length) setFiles(p=>[...p,...picked]); e.target.value=""; }}
                      style={{display:"none"}}/>
                    <div style={{fontSize:20,marginBottom:5}}>📄</div>
                    <p style={{color:"#2563EB",fontSize:11,fontWeight:600,margin:"0 0 2px"}}>Drop or click to upload</p>
                    <p style={{color:"#94A3B8",fontSize:10,margin:0}}>PDF or image</p>
                  </div>
                  {files.length>0&&(
                    <div style={{marginTop:8,display:"flex",flexDirection:"column",gap:4}}>
                      {files.map((f,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 7px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:5,fontSize:10}}>
                          <span>{f.type==="application/pdf"?"📄":"🖼️"}</span>
                          <span style={{flex:1,color:"#475569",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</span>
                          <button onClick={e=>{e.stopPropagation();setFiles(p=>p.filter((_,j)=>j!==i));}} style={{background:"none",border:"none",color:"#94A3B8",cursor:"pointer",fontSize:12,lineHeight:1,padding:0}}>×</button>
                        </div>
                      ))}
                      {!extracting&&(
                        <button onClick={runNativeParse} style={{marginTop:4,width:"100%",padding:"7px",background:"#1E3A5F",border:"none",borderRadius:6,color:"#fff",fontSize:10,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                          Scan {files.length} File{files.length>1?"s":""} →
                        </button>
                      )}
                    </div>
                  )}
                  {/* ── Native parse result panel ── */}
                  {nativeResult&&!extracting&&(
                    <div style={{marginTop:10,border:"1px solid #E2E8F0",borderRadius:10,overflow:"hidden",fontFamily:"'Outfit',sans-serif"}}>
                      <div style={{padding:"8px 12px",background:nativeResult.confidence==="high"?"#F0FDF4":nativeResult.confidence==="low"?"#FFFBEB":"#FFF5F5",borderBottom:"1px solid #E2E8F0",display:"flex",alignItems:"center",gap:8}}>
                        <span style={{fontSize:12}}>{nativeResult.confidence==="high"?"✅":nativeResult.confidence==="low"?"⚠️":"❌"}</span>
                        <div style={{flex:1}}>
                          <span style={{fontSize:11,fontWeight:700,color:nativeResult.confidence==="high"?"#059669":nativeResult.confidence==="low"?"#D97706":"#DC2626"}}>
                            {nativeResult.imgOnly
                              ? "Image file — native parsing not available"
                              : nativeResult.confidence==="none"
                                ? "Could not extract positions natively"
                                : `Native scan found ${nativeResult.holdings.length} position${nativeResult.holdings.length!==1?"s":""}`}
                          </span>
                          <div style={{fontSize:9,color:"#94A3B8",marginTop:1}}>
                            {nativeResult.imgOnly?"Images require AI to extract holdings":nativeResult.confidence==="low"?"Low confidence — consider AI review to fill gaps":nativeResult.confidence==="none"?"Statement format not recognised":"Review results then add to pool, or run AI for higher accuracy"}
                          </div>
                        </div>
                      </div>
                      {nativeResult.holdings.length>0&&(
                        <div style={{maxHeight:160,overflowY:"auto",padding:"6px 0"}}>
                          {nativeResult.holdings.slice(0,20).map((h,i)=>(
                            <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"3px 12px",fontSize:10,borderBottom:"1px solid #F8FAFC"}}>
                              <span style={{fontWeight:700,color:"#2563EB",minWidth:40}}>{h.ticker}</span>
                              <span style={{flex:1,color:"#64748B",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.name}</span>
                              <span style={{color:"#1E3A5F",fontWeight:600}}>{fmt$(h.marketValue)}</span>
                            </div>
                          ))}
                          {nativeResult.holdings.length>20&&<div style={{padding:"4px 12px",fontSize:10,color:"#94A3B8"}}>+{nativeResult.holdings.length-20} more</div>}
                        </div>
                      )}
                      <div style={{padding:"8px 12px",display:"flex",gap:8,background:"#FAFAFA",borderTop:"1px solid #E2E8F0"}}>
                        <button onClick={()=>setNativeResult(null)}
                          style={{padding:"5px 12px",background:"#fff",border:"1px solid #E2E8F0",borderRadius:6,color:"#64748B",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                          Discard
                        </button>
                        {nativeResult.holdings.length>0&&(
                          <button onClick={commitNativeToPool}
                            style={{flex:1,padding:"6px",background:"#059669",border:"none",borderRadius:6,color:"#fff",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                            Add {nativeResult.holdings.length} to Pool ↓
                          </button>
                        )}
                        <button onClick={extractStatement} disabled={extracting}
                          style={{padding:"5px 12px",background:"#7C3AED",border:"none",borderRadius:6,color:"#fff",fontSize:11,fontWeight:700,cursor:extracting?"not-allowed":"pointer",fontFamily:"'Outfit',sans-serif"}}>
                          🤖 AI Review
                        </button>
                      </div>
                    </div>
                  )}
                  {extracting&&extractProgress.includes("AI")&&(
                    <div style={{textAlign:"center",padding:"10px 0"}}>
                      <div style={{width:20,height:20,border:"2px solid #BFDBFE",borderTopColor:"#2563EB",borderRadius:"50%",margin:"0 auto 6px",animation:"spin 0.85s linear infinite"}}/>
                      <p style={{color:"#2563EB",fontSize:10}}>{extractProgress}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Excel */}
              <div style={{border:"1px solid #6EE7B7",borderRadius:10,overflow:"hidden"}}>
                <div style={{background:"#ECFDF5",padding:"8px 12px",borderBottom:"1px solid #6EE7B7",display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:13}}>📊</span>
                  <span style={{fontSize:11,fontWeight:700,color:"#065F46"}}>Excel / CSV</span>
                </div>
                <div style={{padding:10}}>
                  <div onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="#ECFDF5";e.currentTarget.style.borderColor="#059669";}}
                    onDragLeave={e=>{e.currentTarget.style.background="#FAFFFE";e.currentTarget.style.borderColor="#6EE7B7";}}
                    onDrop={e=>{e.preventDefault();e.currentTarget.style.background="#FAFFFE";e.currentTarget.style.borderColor="#6EE7B7";const f=Array.from(e.dataTransfer.files).find(x=>/\.xlsx?$|\.csv$/i.test(x.name));if(f)handleExcelFile(f);}}
                    onClick={()=>xlFileRef.current?.click()}
                    style={{border:"2px dashed #6EE7B7",borderRadius:7,padding:"18px 10px",textAlign:"center",cursor:"pointer",transition:"all 0.15s",background:"#FAFFFE"}}
                    onMouseEnter={e=>{e.currentTarget.style.background="#ECFDF5";e.currentTarget.style.borderColor="#059669";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="#FAFFFE";e.currentTarget.style.borderColor="#6EE7B7";}}>
                    <input ref={xlFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={e=>{if(e.target.files[0])handleExcelFile(e.target.files[0]);e.target.value="";}} style={{display:"none"}}/>
                    <div style={{fontSize:20,marginBottom:5}}>📊</div>
                    <p style={{color:"#059669",fontSize:11,fontWeight:600,margin:"0 0 2px"}}>Drop or click to upload</p>
                    <p style={{color:"#94A3B8",fontSize:10,margin:0}}>.xlsx · .xls · .csv</p>
                  </div>
                  {extracting&&extractProgress.includes("spreadsheet")&&(
                    <div style={{textAlign:"center",padding:"10px 0"}}>
                      <div style={{width:20,height:20,border:"2px solid #6EE7B7",borderTopColor:"#059669",borderRadius:"50%",margin:"0 auto 6px",animation:"spin 0.85s linear infinite"}}/>
                      <p style={{color:"#059669",fontSize:10}}>{extractProgress}</p>
                    </div>
                  )}
                  <div style={{marginTop:8,background:"#F0FDF4",borderRadius:6,padding:"7px 9px",fontSize:9,color:"#065F46"}}>
                    <strong>Flexible columns:</strong> Ticker · Name · Asset Class · Shares · Price · Market Value · Cost Basis
                  </div>
                </div>
              </div>{/* end Excel panel */}

              </div>{/* end top row grid */}

              {/* Manual Entry — full width below */}
              <div style={{border:"1px solid #DDD6FE",borderRadius:10,overflow:"hidden"}}>
                <div style={{background:"#F5F3FF",padding:"8px 12px",borderBottom:"1px solid #DDD6FE",display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontSize:13}}>✏️</span>
                  <span style={{fontSize:11,fontWeight:700,color:"#5B21B6"}}>Manual Entry</span>
                  {isAccountTaxExempt(manualAcctType)&&<span style={{fontSize:9,padding:"1px 6px",background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:5,color:"#059669",fontWeight:700,marginLeft:"auto"}}>🛡 Non-taxable</span>}
                </div>
                <div style={{padding:10}}>
                  <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)",gap:7,marginBottom:8}}>
                    <div>
                      <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Account Nickname</div>
                      <input value={manualAcctLabel} onChange={e=>setManualAcctLabel(e.target.value)}
                        placeholder="e.g. Joint Brokerage"
                        style={{width:"100%",border:"1px solid #EDE9FE",borderRadius:6,padding:"5px 8px",fontSize:10,fontFamily:"'Outfit',sans-serif",outline:"none",boxSizing:"border-box"}}/>
                    </div>
                    <div>
                      <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Account Type</div>
                      <select value={manualAcctType} onChange={e=>setManualAcctType(e.target.value)}
                        style={{width:"100%",border:"1px solid #EDE9FE",borderRadius:6,padding:"5px 8px",fontSize:10,fontFamily:"'Outfit',sans-serif",outline:"none",background:"#fff"}}>
                        {ACCOUNT_TYPES.map(t=><option key={t}>{t}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{marginBottom:7}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr style={{background:"#FAF9FF"}}>
                          {["Ticker","Name","Asset Class","Shares","Price","Mkt Value","Cost Basis",""].map(h=>(
                            <th key={h} style={{padding:"7px 8px",fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"left",borderBottom:"1px solid #EDE9FE",whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {manualRows.map((row,ri)=>(
                          <tr key={ri}>
                            {[
                              ["ticker","AAPL",60],["name","Apple Inc.",null],
                              ["assetClass",null,null],
                              ["shares","100",60],["currentPrice","185",70],["marketValue","18500",80],["costBasis","15000",80]
                            ].map(([field,ph,mw])=>(
                              <td key={field} style={{padding:"4px 4px",borderBottom:"1px solid #F3F0FF"}}>
                                {field==="assetClass"?(
                                  <select value={row[field]} onChange={e=>setManualRows(p=>p.map((r,i)=>i===ri?{...r,[field]:e.target.value}:r))}
                                    style={{width:"100%",border:"1px solid #EDE9FE",borderRadius:5,padding:"5px 4px",fontSize:10,fontFamily:"'Outfit',sans-serif",outline:"none",background:"#fff"}}>
                                    {["US Eq","Intl Eq","Fixed Inc","Alt","Cash","Real Est","Other"].map((o,oi)=>(
                                      <option key={o} value={["US Equity","International Equity","Fixed Income","Alternative","Cash","Real Estate","Other"][oi]}>{o}</option>
                                    ))}
                                  </select>
                                ):(
                                  <div style={{position:"relative",display:"inline-block"}}>
                                    <input value={row[field]}
                                      onChange={e=>setManualRows(p=>p.map((r,i)=>i===ri?{...r,[field]:e.target.value}:r))}
                                      placeholder={ph}
                                      style={{width:"100%",minWidth:mw||50,border:`1px solid ${field==="ticker"&&tickerLookupStatus[ri]==="error"?"#FCA5A5":field==="ticker"&&tickerLookupStatus[ri]==="ok"?"#6EE7B7":"#EDE9FE"}`,borderRadius:5,padding:"5px 6px",fontSize:11,fontFamily:"'Outfit',sans-serif",outline:"none",boxSizing:"border-box",textTransform:field==="ticker"?"uppercase":"none"}}/>
                                    {field==="ticker"&&tickerLookupStatus[ri]&&(
                                      <span style={{position:"absolute",right:2,top:"50%",transform:"translateY(-50%)",fontSize:8,pointerEvents:"none",color:tickerLookupStatus[ri]==="ok"?"#059669":tickerLookupStatus[ri]==="error"?"#DC2626":"#94A3B8"}}>
                                        {tickerLookupStatus[ri]==="loading"?"⟳":tickerLookupStatus[ri]==="ok"?"✓":"✕"}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                            ))}
                            <td style={{padding:"3px 3px",borderBottom:"1px solid #F3F0FF",textAlign:"center"}}>
                              <button onClick={()=>setManualRows(p=>p.length>1?p.filter((_,i)=>i!==ri):p)}
                                style={{background:"none",border:"none",color:"#C4B5FD",cursor:"pointer",fontSize:12,lineHeight:1,padding:0}}
                                onMouseEnter={e=>{if(manualRows.length>1)e.target.style.color="#DC2626"}} onMouseLeave={e=>e.target.style.color="#C4B5FD"}>×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div style={{display:"flex",gap:6}}>
                    <button onClick={()=>setManualRows(p=>[...p,newManualRow()])}
                      style={{padding:"5px 10px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:6,color:"#7C3AED",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                      + Row
                    </button>
                    <button onClick={addManualToPool}
                      style={{flex:1,padding:"5px 10px",background:"#7C3AED",border:"none",borderRadius:6,color:"#fff",fontSize:10,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",letterSpacing:"0.04em",textTransform:"uppercase"}}>
                      Add to Pool ↓
                    </button>
                  </div>
                </div>
              </div>
            </div>{/* end outer flex column */}

            {/* ── Excel account assignment modal ── */}
            {xlPending&&(
              <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(3px)"}}>
                <div style={{background:"#fff",borderRadius:14,width:"min(420px,96vw)",boxShadow:"0 20px 60px rgba(0,0,0,0.18)",overflow:"hidden",fontFamily:"'Outfit',sans-serif"}}>
                  <div style={{background:"linear-gradient(135deg,#065F46,#059669)",padding:"16px 20px"}}>
                    <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>Assign Account — {xlPending.fileName}</div>
                    <div style={{fontSize:10,color:"#A7F3D0",marginTop:2}}>{xlPending.rows.length} holding{xlPending.rows.length!==1?"s":""} found · assign an account before adding to pool</div>
                  </div>
                  <div style={{padding:"20px"}}>
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Account Nickname</div>
                      <input value={xlAcctLabel} onChange={e=>setXlAcctLabel(e.target.value)}
                        placeholder="e.g. IRA — Schwab"
                        style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:13,fontFamily:"'Outfit',sans-serif",outline:"none",boxSizing:"border-box"}}
                        autoFocus/>
                    </div>
                    <div style={{marginBottom:14}}>
                      <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Account Type</div>
                      <select value={xlAcctType} onChange={e=>setXlAcctType(e.target.value)}
                        style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:13,fontFamily:"'Outfit',sans-serif",outline:"none",background:"#fff"}}>
                        {ACCOUNT_TYPES.map(t=><option key={t}>{t}</option>)}
                      </select>
                    </div>
                    {isAccountTaxExempt(xlAcctType)&&(
                      <div style={{background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:8,padding:"8px 12px",marginBottom:14,fontSize:11,color:"#065F46",fontWeight:600}}>
                        🛡 This account type is non-taxable — all holdings will be marked tax-exempt
                      </div>
                    )}
                    <div style={{display:"flex",gap:8}}>
                      <button onClick={()=>setXlPending(null)}
                        style={{flex:1,padding:"10px",background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:8,color:"#475569",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                        Cancel
                      </button>
                      <button onClick={commitXlToPool}
                        style={{flex:2,padding:"10px",background:"#059669",border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                        Add to Pool ↓
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Spinner ── */}
            {extracting&&(
              <div style={{textAlign:"center",padding:"12px 0"}}>
                <div style={{width:22,height:22,border:"2px solid #BFDBFE",borderTopColor:"#2563EB",borderRadius:"50%",margin:"0 auto 6px",animation:"spin 0.85s linear infinite"}}/>
                <p style={{color:"#2563EB",fontSize:11}}>{extractProgress}</p>
              </div>
            )}

            {/* ── Error ── */}
            {error&&<div style={{padding:"9px 14px",background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:6,marginBottom:12}}><p style={{color:"#DC2626",fontSize:12,margin:0}}>⚠ {error}</p></div>}

            {/* ── Multi-account picker ── */}
            {extractedAccounts&&(
              <div style={{border:"1px solid #BFDBFE",borderRadius:12,overflow:"hidden",marginBottom:16}}>
                <div style={{background:"linear-gradient(135deg,#1E3A5F,#2563EB)",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"#fff",fontFamily:"'Outfit',sans-serif"}}>
                      {extractedAccounts.accounts.length} Account{extractedAccounts.accounts.length!==1?"s":""} Detected
                    </div>
                    <div style={{fontSize:10,color:"#BFDBFE",marginTop:2}}>
                      {extractedAccounts.clientName&&`${extractedAccounts.clientName} · `}Select which accounts to include
                    </div>
                  </div>
                  <div style={{fontSize:11,color:"#93C5FD"}}>
                    {Object.values(includedAccounts).filter(Boolean).length} of {extractedAccounts.accounts.length} selected
                  </div>
                </div>
                <div style={{background:"#fff"}}>
                  {extractedAccounts.accounts.map((acc, i) => {
                    const included = !!includedAccounts[i];
                    const edit     = accountEdits[i] || {};
                    const exempt   = edit.taxExempt ?? acc.taxExempt;
                    const acctType = edit.accountType || acc.accountType;
                    const holdingCount = (acc.holdings||[]).length;
                    const totalVal = acc.totalValue || (acc.holdings||[]).reduce((s,h)=>s+(h.marketValue||0),0);
                    return (
                      <div key={i} style={{padding:"14px 16px",borderBottom:i<extractedAccounts.accounts.length-1?"1px solid #F1F5F9":"none",background:included?"#fff":"#F8FAFC",transition:"background 0.15s"}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:12}}>
                          {/* Include checkbox */}
                          <button onClick={()=>setIncludedAccounts(p=>({...p,[i]:!p[i]}))}
                            style={{width:20,height:20,borderRadius:5,border:`2px solid ${included?"#2563EB":"#CBD5E1"}`,background:included?"#2563EB":"transparent",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:2,padding:0}}>
                            {included&&<span style={{color:"#fff",fontSize:11,fontWeight:900,lineHeight:1}}>✓</span>}
                          </button>

                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:8}}>
                              <span style={{fontSize:13,fontWeight:700,color:included?"#1E3A5F":"#94A3B8"}}>
                                {acc.accountLabel || `${acc.accountType||"Account"} ${i+1}`}
                              </span>
                              {acc.accountNumber&&(
                                <span style={{fontSize:10,color:"#94A3B8"}}>····{acc.accountNumber.slice(-4)}</span>
                              )}
                              <span style={{fontSize:10,color:"#64748B",marginLeft:"auto"}}>{holdingCount} positions · {fmt$(totalVal)}</span>
                            </div>

                            {/* Editable account type + tax-exempt */}
                            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                              <div>
                                <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Account Type</div>
                                <select value={acctType}
                                  onChange={e=>{
                                    const newType = e.target.value;
                                    const autoExempt = TAX_EXEMPT_TYPES_PA.includes(newType);
                                    setAccountEdits(p=>({...p,[i]:{...p[i],accountType:newType,taxExempt:autoExempt}}));
                                  }}
                                  style={{border:"1px solid #E2E8F0",borderRadius:6,padding:"4px 8px",fontSize:11,fontFamily:"'Outfit',sans-serif",outline:"none",background:"#fff",color:"#1E3A5F"}}>
                                  {["Brokerage","IRA","Roth IRA","401k","403b","457","SEP IRA","SIMPLE IRA","529","HSA","Pension","Mutual Fund","Unknown"].map(t=><option key={t}>{t}</option>)}
                                </select>
                              </div>
                              <div>
                                <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Tax Status</div>
                                <button onClick={()=>setAccountEdits(p=>({...p,[i]:{...p[i],taxExempt:!exempt}}))}
                                  style={{display:"flex",alignItems:"center",gap:5,padding:"4px 10px",border:`1px solid ${exempt?"#6EE7B7":"#E2E8F0"}`,borderRadius:6,background:exempt?"#ECFDF5":"#fff",cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontSize:11,fontWeight:600,color:exempt?"#059669":"#64748B"}}>
                                  <span>{exempt?"🛡":"◻"}</span>
                                  {exempt?"Non-Taxable":"Taxable"}
                                </button>
                              </div>
                              {acc.custodian&&<div style={{fontSize:10,color:"#94A3B8"}}>Custodian: <strong style={{color:"#475569"}}>{acc.custodian}</strong></div>}
                            </div>

                            {/* Holdings preview */}
                            {included&&holdingCount>0&&(
                              <div style={{marginTop:8,display:"flex",gap:4,flexWrap:"wrap"}}>
                                {(acc.holdings||[]).slice(0,8).map((h,hi)=>(
                                  <span key={hi} style={{fontSize:9,padding:"1px 6px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:4,color:"#2563EB",fontWeight:700}}>{h.ticker||"?"}</span>
                                ))}
                                {holdingCount>8&&<span style={{fontSize:9,color:"#94A3B8",padding:"1px 4px"}}>+{holdingCount-8} more</span>}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{padding:"12px 16px",background:"#F8FAFC",borderTop:"1px solid #E2E8F0",display:"flex",gap:8,alignItems:"center"}}>
                  <button onClick={()=>{setExtractedAccounts(null);setIncludedAccounts({});setAccountEdits({});}}
                    style={{padding:"7px 14px",background:"#fff",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                    Discard
                  </button>
                  <button onClick={commitExtractedAccounts}
                    disabled={!Object.values(includedAccounts).some(Boolean)}
                    style={{flex:1,padding:"9px",background:Object.values(includedAccounts).some(Boolean)?"#1E3A5F":"#E2E8F0",border:"none",borderRadius:7,color:Object.values(includedAccounts).some(Boolean)?"#fff":"#94A3B8",fontSize:12,fontWeight:700,cursor:Object.values(includedAccounts).some(Boolean)?"pointer":"not-allowed",fontFamily:"'Outfit',sans-serif",letterSpacing:"0.04em",textTransform:"uppercase"}}>
                    Add {Object.values(includedAccounts).filter(Boolean).length} Account{Object.values(includedAccounts).filter(Boolean).length!==1?"s":""} to Pool →
                  </button>
                </div>
              </div>
            )}

            {/* ── Combined pool ── */}
            {pool.length>0&&(
              <div style={{marginBottom:16}}>
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
                  <div style={{fontSize:12,fontWeight:700,color:"#1E3A5F"}}>Holdings Pool <span style={{fontSize:11,color:"#94A3B8",fontWeight:400}}>— {pool.length} position{pool.length!==1?"s":""}</span></div>
                  <button onClick={()=>setPool([])} style={{background:"none",border:"none",color:"#94A3B8",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Clear all</button>
                </div>
                <div style={{border:"1px solid #E2E8F0",borderRadius:10,overflow:"hidden",background:"#fff"}}>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",minWidth:500,fontSize:11}}>
                      <thead>
                        <tr style={{background:"#F8FAFC"}}>
                          <th style={{padding:"7px 12px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"left",borderBottom:"2px solid #E2E8F0",whiteSpace:"nowrap"}}>Source</th>
                          <th style={{padding:"7px 12px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"left",borderBottom:"2px solid #E2E8F0"}}>Ticker</th>
                          <th style={{padding:"7px 12px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"left",borderBottom:"2px solid #E2E8F0"}}>Name</th>
                          <th style={{padding:"7px 12px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"left",borderBottom:"2px solid #E2E8F0"}}>Asset Class</th>
                          <th style={{padding:"7px 12px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"right",borderBottom:"2px solid #E2E8F0"}}>Mkt Value</th>
                          <th style={{padding:"7px 12px",borderBottom:"2px solid #E2E8F0"}}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pool.map((h,ri)=>{
                          const srcMeta = h._src==="ai"?["📄","#2563EB","#EFF6FF","#BFDBFE"]:h._src==="xl"?["📊","#059669","#ECFDF5","#6EE7B7"]:h._src==="saved"?["💾","#7C3AED","#F5F3FF","#DDD6FE"]:["✏️","#7C3AED","#F5F3FF","#DDD6FE"];
                          return (
                            <tr key={h.id} style={{background:ri%2===0?"#fff":"#FAFBFC"}}>
                              <td style={{padding:"6px 12px",borderBottom:"1px solid #F4F6F9"}}>
                                <div style={{display:"flex",flexDirection:"column",gap:3}}>
                                  <span style={{fontSize:9,padding:"1px 7px",borderRadius:8,background:srcMeta[2],color:srcMeta[1],border:`1px solid ${srcMeta[3]}`,fontWeight:700}}>{srcMeta[0]}</span>
                                  {(h.accountLabel||h._account)&&<span style={{fontSize:8,color:"#64748B",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:80,fontWeight:600}}>{h.accountLabel||h._account}</span>}
                                  {h.taxExempt&&<span style={{fontSize:8,padding:"1px 4px",borderRadius:4,background:"#ECFDF5",border:"1px solid #6EE7B7",color:"#059669",fontWeight:700}}>🛡 Non-tax</span>}
                                </div>
                              </td>
                              <td style={{padding:"6px 12px",borderBottom:"1px solid #F4F6F9",fontWeight:700,color:"#1E3A5F"}}>{h.ticker||"—"}</td>
                              <td style={{padding:"6px 12px",borderBottom:"1px solid #F4F6F9",color:"#475569",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.name||"—"}</td>
                              <td style={{padding:"6px 12px",borderBottom:"1px solid #F4F6F9",color:"#64748B"}}>{h.assetClass||"—"}</td>
                              <td style={{padding:"6px 12px",borderBottom:"1px solid #F4F6F9",textAlign:"right",fontWeight:600,color:"#1E3A5F"}}>{fmt$(h.marketValue)}</td>
                              <td style={{padding:"6px 12px",borderBottom:"1px solid #F4F6F9",textAlign:"center"}}>
                                <button onClick={()=>setPool(p=>p.filter(x=>x.id!==h.id))}
                                  style={{background:"none",border:"none",color:"#94A3B8",cursor:"pointer",fontSize:13,lineHeight:1,padding:0}}
                                  onMouseEnter={e=>e.target.style.color="#DC2626"} onMouseLeave={e=>e.target.style.color="#94A3B8"}>×</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div style={{padding:"8px 12px",background:"#F8FAFC",borderTop:"1px solid #E2E8F0",display:"flex",justifyContent:"space-between",fontSize:11,color:"#64748B"}}>
                    <span>{pool.length} position{pool.length!==1?"s":""}</span>
                    <span style={{fontWeight:700,color:"#1E3A5F"}}>{fmt$(pool.reduce((s,h)=>s+(h.marketValue||0),0))} total</span>
                  </div>
                </div>
                <button onClick={proceedToReview}
                  style={{marginTop:8,width:"100%",padding:"13px",background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                  Review {pool.length} Holdings →
                </button>
              </div>
            )}
          </div>
        )}

        {/* STEP 2 — Review */}
        {step===2&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:16}}>
              <div>
                <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:300,color:"#1E3A5F",margin:"0 0 4px"}}>Review Holdings</h2>
                <p style={{color:"#64748B",fontSize:11,margin:0}}>Click any cell to edit · verify before proceeding</p>
              </div>
              <span style={{color:"#64748B",fontSize:11}}>{holdings.length} positions · {fmt$(totalPV)}</span>
            </div>
            <PAHoldingsEditor
              holdings={holdings}
              onChange={setHoldings}
              accountTaxExempt={!!(accountInfo?.taxExempt)}
              onAccountTaxExemptChange={exempt=>setAccountInfo(prev=>({...prev,taxExempt:exempt}))}
            />

            <div style={{margin:"28px 0 20px",borderTop:"2px solid #E2E8F0",paddingTop:24}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
                <h3 style={{fontFamily:"'Outfit',sans-serif",fontSize:18,fontWeight:300,color:"#1E3A5F",margin:0}}>Select Analyses</h3>
                <span style={{color:"#64748B",fontSize:11}}>{selectedAnalyses.length} selected</span>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:8,marginBottom:18}}>
                {PA_ANALYSES.map(opt=>{
                  const sel = selectedAnalyses.includes(opt.id);
                  const isTaxOpt = TAX_SENSITIVE_ANALYSES.has(opt.id);
                  const exemptCount = isTaxOpt ? holdings.filter(h=>h.taxExempt).length : 0;
                  return (
                    <div key={opt.id} onClick={()=>toggleAnalysis(opt.id)}
                      style={{padding:"12px 14px",border:`1px solid ${sel?"#2563EB":"#E2E8F0"}`,borderRadius:8,cursor:"pointer",background:sel?"#EFF6FF":"#fff",display:"flex",gap:10,alignItems:"flex-start",transition:"all 0.15s"}}>
                      <span style={{fontSize:14,color:sel?"#2563EB":"#CBD5E1",flexShrink:0,marginTop:1}}>{opt.icon}</span>
                      <div style={{flex:1}}>
                        <p style={{color:sel?"#2563EB":"#94A3B8",fontSize:12,margin:"0 0 2px"}}>{opt.label}</p>
                        <p style={{color:"#64748B",fontSize:10,lineHeight:1.5,margin:0}}>{opt.desc}</p>
                        {exemptCount>0&&<p style={{color:"#059669",fontSize:10,margin:"4px 0 0",fontWeight:600}}>🛡 {exemptCount} non-taxable holding{exemptCount!==1?"s":""} will be excluded</p>}
                      </div>
                      <div style={{width:14,height:14,borderRadius:3,border:`1.5px solid ${sel?"#2563EB":"#CBD5E1"}`,background:sel?"#2563EB":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        {sel&&<span style={{color:"#F4F7FC",fontSize:9}}>✓</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {error&&<div style={{padding:"10px 14px",background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:4,marginBottom:12}}><p style={{color:"#DC2626",fontSize:12,margin:0}}>⚠ {error}</p></div>}

              {/* ── Data quality checklist ── */}
              {(()=>{
                const FF3 = "'Outfit',sans-serif";
                const isFundH = h => {
                  const ac=(h.assetClass||"").toLowerCase(), nm=(h.name||"").toLowerCase();
                  return nm.includes("etf")||nm.includes("fund")||nm.includes("trust")||
                         ac.includes("fixed")||ac.includes("real estate")||ac.includes("alternative");
                };
                const needsLookup = holdings.some(h => h.ticker?.trim() && (!h.name?.trim() || !h.assetClass?.trim()));
                const needsEnrich = holdings.some(h => isFundH(h) && h.yieldPct==null && h.expenseRatio==null);
                const missingReq  = holdings.filter(h => !h.ticker?.trim()||!h.name?.trim()||!h.assetClass?.trim()||h.marketValue==null||h.marketValue===""||h.costBasis==null||h.costBasis===""||h.costBasis===undefined);
                return (
                  <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:16}}>
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:!needsLookup?"#F0FDF4":"#F8FAFC",border:`1px solid ${!needsLookup?"#6EE7B7":"#E2E8F0"}`,borderRadius:8}}>
                      <span style={{fontSize:13,flexShrink:0}}>{!needsLookup?"✅":"🔍"}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,fontWeight:700,color:!needsLookup?"#059669":"#1E3A5F"}}>{!needsLookup?"Names & asset classes complete":"Look up missing names & asset classes"}</div>
                        {needsLookup&&<div style={{fontSize:10,color:"#94A3B8",marginTop:1}}>{holdings.filter(h=>h.ticker?.trim()&&(!h.name?.trim()||!h.assetClass?.trim())).length} positions need lookup</div>}
                      </div>
                      {needsLookup&&<button onClick={lookupAllHoldings} disabled={batchLookupRunning}
                        style={{padding:"5px 12px",background:batchLookupRunning?"#F1F5F9":"#1E3A5F",border:"none",borderRadius:7,color:batchLookupRunning?"#94A3B8":"#fff",fontSize:11,fontWeight:700,cursor:batchLookupRunning?"not-allowed":"pointer",fontFamily:FF3,whiteSpace:"nowrap",flexShrink:0}}>
                        {batchLookupRunning?"⟳ Looking up…":"🔍 Lookup All"}
                      </button>}
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8,padding:"8px 12px",background:!needsEnrich?"#F0FDF4":"#F8FAFC",border:`1px solid ${!needsEnrich?"#6EE7B7":"#E2E8F0"}`,borderRadius:8}}>
                      <span style={{fontSize:13,flexShrink:0}}>{!needsEnrich?"✅":"📡"}</span>
                      <div style={{flex:1}}>
                        <div style={{fontSize:11,fontWeight:700,color:!needsEnrich?"#059669":"#1E3A5F"}}>{!needsEnrich?"Yield & expense ratio complete":"Pull yield & expense ratio"}</div>
                        {needsEnrich&&<div style={{fontSize:10,color:"#94A3B8",marginTop:1}}>{holdings.filter(h=>isFundH(h)&&h.yieldPct==null&&h.expenseRatio==null).length} applicable positions missing data</div>}
                        {enrichResults.filled>0&&!needsEnrich&&<div style={{fontSize:10,color:"#059669",marginTop:1}}>✓ {enrichResults.filled} filled via API</div>}
                      </div>
                      {needsEnrich&&<button onClick={enrichHoldingsData} disabled={enrichRunning}
                        style={{padding:"5px 12px",background:enrichRunning?"#F1F5F9":"#1E3A5F",border:"none",borderRadius:7,color:enrichRunning?"#94A3B8":"#fff",fontSize:11,fontWeight:700,cursor:enrichRunning?"not-allowed":"pointer",fontFamily:FF3,whiteSpace:"nowrap",flexShrink:0}}>
                        {enrichRunning?`⟳ ${enrichProgress}…`:"📡 Pull Yield & ER"}
                      </button>}
                    </div>
                    {missingReq.length>0&&(
                      <div style={{padding:"8px 12px",background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:8}}>
                        <div style={{fontSize:11,fontWeight:700,color:"#DC2626",marginBottom:4}}>⚠ Required fields — edit inline above before running analysis</div>
                        <div style={{display:"flex",gap:5,flexWrap:"wrap"}}>
                          {missingReq.map((h,i)=>(
                            <span key={i} style={{fontSize:10,padding:"1px 7px",background:"#fff",border:"1px solid #FCA5A5",borderRadius:5,color:"#DC2626",fontWeight:600}}>
                              {h.ticker||"(no ticker)"} — {[!h.ticker?.trim()&&"ticker",!h.name?.trim()&&"name",!h.assetClass?.trim()&&"class",(h.marketValue==null||h.marketValue==="")&&"mkt value",(h.costBasis==null||h.costBasis===""||h.costBasis===undefined)&&"cost basis"].filter(Boolean).join(", ")}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              <div style={{display:"flex",gap:10}}>
                <button onClick={()=>{setStep(1);setHoldings([]);setAccountInfo(null);setSelectedAnalyses([]);setAnalysisResults(null);}} style={{padding:"11px 20px",background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif",padding:"8px 16px"}}>← Back</button>
                {selectedAnalyses.length>0&&!analyzing&&(
                  <button onClick={runAnalysis} style={{flex:1,padding:"11px",background:"#1E3A5F",border:"none",borderRadius:5,color:"#fff",fontSize:12,letterSpacing:"0.05em",textTransform:"uppercase",cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                    Run {selectedAnalyses.length} Analys{selectedAnalyses.length===1?"is":"es"} →
                  </button>
                )}
              </div>
              {analyzing&&(
                <div style={{textAlign:"center",padding:"26px 0"}}>
                  <div style={{width:28,height:28,border:"2px solid #BFDBFE",borderTopColor:"#2563EB",borderRadius:"50%",margin:"0 auto 10px",animation:"spin 0.85s linear infinite"}}/>
                  <p style={{color:"#2563EB",fontSize:11,letterSpacing:"0.02em"}}>{analyzeProgress||"Running analyses…"}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 3 — Results */}
        {step===3&&analysisResults&&(
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-end",marginBottom:18,flexWrap:"wrap",gap:10}}>
              <div>
                <h2 style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:300,color:"#1E3A5F",margin:"0 0 4px"}}>{resolveName ? resolveName(accountInfo?.clientName)||accountInfo?.clientName||"Analysis Results" : accountInfo?.clientName||"Analysis Results"}</h2>
                <p style={{color:"#64748B",fontSize:11,margin:0}}>{holdings.length} positions · {fmt$(totalPV)}</p>
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>{setStep(2);setAnalysisResults(null);}} style={{padding:"7px 14px",background:"none",border:"1px solid #E2E8F0",borderRadius:6,color:"#64748B",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif",padding:"7px 14px"}}>← Edit</button>
                <button onClick={saveClient} disabled={saving||saveSuccess}
                  style={{padding:"7px 16px",background:saveSuccess?"#F0FDF4":"#EFF6FF",border:`1px solid ${saveSuccess?"#86EFAC":"#BFDBFE"}`,borderRadius:6,color:saveSuccess?"#16A34A":"#2563EB",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif",fontWeight:600}}>
                  {saveSuccess?"✓ Client Saved":saving?"Saving…":"Save Client →"}
                </button>
              </div>
            </div>

            {/* Analysis tabs */}
            <div style={{display:"flex",gap:0,marginBottom:18,borderBottom:"2px solid #E2E8F0",flexWrap:"wrap"}}>
              {selectedAnalyses.map(id=>{
                const opt=PA_ANALYSES.find(o=>o.id===id);
                return (
                  <button key={id} onClick={()=>setActiveResult(id)}
                    style={{padding:"9px 14px",background:"none",border:"none",borderBottom:`2px solid ${activeResult===id?"#2563EB":"transparent"}`,color:activeResult===id?"#2563EB":"#64748B",fontSize:11,letterSpacing:"0.06em",cursor:"pointer",fontFamily:"'Outfit',sans-serif",textTransform:"uppercase",marginBottom:-1,whiteSpace:"nowrap"}}>
                    <span style={{marginRight:5,fontSize:12}}>{opt?.icon}</span>{opt?.label}
                  </button>
                );
              })}
            </div>
            <div style={{minHeight:300}}>{activeResult&&resultMap[activeResult]}</div>
            <div style={{marginTop:28,padding:"10px 14px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:6}}>
              <p style={{color:"#94A3B8",fontSize:10,lineHeight:1.65,margin:0}}>AI-generated analysis for advisor review only. Not financial, legal, or tax advice.</p>
            </div>
          </div>
        )}
      </div>

      {/* ── Floating Save Client button — visible at any step once pool/holdings exist ── */}
      {(pool.length>0||holdings.length>0)&&step!==4&&(
        <div style={{position:"fixed",bottom:28,right:28,zIndex:100,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
          {saveSuccess&&(
            <div style={{background:"#059669",color:"#fff",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,boxShadow:"0 4px 12px rgba(0,0,0,0.15)"}}>
              ✓ Saved
            </div>
          )}
          <button onClick={openSaveModal} disabled={saving}
            style={{display:"flex",alignItems:"center",gap:8,padding:"11px 20px",background:"#1E3A5F",border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:"'Outfit',sans-serif",boxShadow:"0 4px 16px rgba(30,58,95,0.35)",transition:"all 0.2s"}}
            onMouseEnter={e=>{e.currentTarget.style.background="#2563EB";e.currentTarget.style.transform="translateY(-1px)";}}
            onMouseLeave={e=>{e.currentTarget.style.background="#1E3A5F";e.currentTarget.style.transform="translateY(0)";}}>
            <span style={{fontSize:15}}>💾</span>
            {saving?"Saving…":sessionSavedId?"Save / Update Client":"Save Client"}
          </button>
        </div>
      )}

      {/* ── Save modal ── */}
      {showSaveModal&&(
        <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(3px)"}}>
          <div style={{background:"#fff",borderRadius:14,width:"min(400px,96vw)",boxShadow:"0 20px 60px rgba(0,0,0,0.18)",overflow:"hidden",fontFamily:"'Outfit',sans-serif"}}>
            <div style={{background:"linear-gradient(135deg,#1E3A5F,#2563EB)",padding:"16px 20px"}}>
              <div style={{fontSize:14,fontWeight:700,color:"#fff"}}>Save Client Record</div>
              <div style={{fontSize:10,color:"#BFDBFE",marginTop:2}}>
                {step===1?`${pool.length} holding${pool.length!==1?"s":""} in pool`
                :step===2?`${holdings.length} holding${holdings.length!==1?"s":""} reviewed`
                :`${holdings.length} holdings · ${Object.keys(analysisResults||{}).length} analyses`}
              </div>
            </div>
            <div style={{padding:"20px"}}>
              <div style={{marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:6}}>Client Name</div>
                <input value={saveModalName} onChange={e=>setSaveModalName(e.target.value)}
                  placeholder="e.g. Robert & Susan Chen"
                  style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"9px 11px",fontSize:13,fontFamily:"'Outfit',sans-serif",outline:"none",boxSizing:"border-box"}}
                  autoFocus onKeyDown={e=>{if(e.key==="Enter") executeSave(sessionSavedId?"update":"new");}}/>
              </div>
              {sessionSavedId?(
                <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:4}}>
                  <div style={{fontSize:11,color:"#64748B",marginBottom:4}}>A record was already saved in this session. What would you like to do?</div>
                  <button onClick={()=>executeSave("update")}
                    style={{padding:"10px",background:"#EFF6FF",border:"2px solid #2563EB",borderRadius:8,color:"#1E3A5F",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",textAlign:"left"}}>
                    <div style={{fontWeight:700}}>↻ Update existing record</div>
                    <div style={{fontSize:11,color:"#64748B",fontWeight:400,marginTop:2}}>Overwrites holdings{analysisResults?` and adds ${Object.keys(analysisResults).length} analysis result${Object.keys(analysisResults).length!==1?"s":""}` :" (no analysis yet)"}</div>
                  </button>
                  <button onClick={()=>executeSave("new")}
                    style={{padding:"10px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,color:"#475569",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif",textAlign:"left"}}>
                    <div style={{fontWeight:600}}>+ Save as new record</div>
                    <div style={{fontSize:11,color:"#94A3B8",fontWeight:400,marginTop:2}}>Creates an additional entry — manage duplicates in the Clients tab</div>
                  </button>
                </div>
              ):(
                <button onClick={()=>executeSave("new")}
                  style={{width:"100%",padding:"11px",background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                  💾 Save Client
                </button>
              )}
              <button onClick={()=>setShowSaveModal(false)}
                style={{width:"100%",marginTop:8,padding:"9px",background:"transparent",border:"none",color:"#94A3B8",fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CLIENT SELECTOR — top of allocator, pick saved client or new
// ─────────────────────────────────────────────────────────────
function ClientSelector({ engine, currentUser, sbClients=[], loadedClient, clientName, totalValue, keptHoldings,
  clientDirected=[], onClientDirectedChange,
  additionalCash="", onAdditionalCashChange=()=>{},
  onLoadClient, onNewClient, onClearClient, onToggleKeep, onClientNameChange, onTotalValueChange, isMobile }) {

  const [search,           setSearch]           = useState("");
  const [open,             setOpen]             = useState(false);
  const [mode,             setMode]             = useState(loadedClient ? "loaded" : "choose"); // "choose" | "new" | "loaded"
  // additionalDollars is lifted to App — alias the prop so the rest of the component is unchanged
  const additionalDollars    = additionalCash;
  const setAdditionalDollars = onAdditionalCashChange;
  const searchRef = useRef(null);

  // sync mode when loadedClient changes externally (e.g. loaded from Analyzer)
  useEffect(() => {
    if (loadedClient) setMode("loaded");
  }, [loadedClient]);

  const { resolveName } = usePIIVault();
  const myClients = (sbClients || []).filter(c => c.advisorId === currentUser.id);
  const results = search.length > 0
    ? myClients.filter(c => resolveName(c.name)?.toLowerCase().includes(search.toLowerCase()) || c.custodian?.toLowerCase().includes(search.toLowerCase()))
    : myClients;

  const handleSelect = (client) => {
    onLoadClient(client);
    setMode("loaded");
    setOpen(false);
    setSearch("");
    setAdditionalDollars("");
  };

  const handleNew = () => {
    onNewClient();
    setMode("new");
    setOpen(false);
    setSearch("");
  };

  const handleClear = () => {
    onClearClient();
    setMode("choose");
    setSearch("");
    setAdditionalDollars("");
  };

  // ── CHOOSE MODE — initial state, show two options ──
  if (mode === "choose") {
    return (
      <div style={{marginBottom:22}}>
        <div style={{fontSize:11,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:"1px",marginBottom:12}}>
          Start with a client
        </div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12,marginBottom:0}}>

          {/* Select saved client */}
          <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:12,padding:"18px 20px",cursor:"pointer",transition:"all 0.15s",position:"relative"}}
            onClick={()=>{setOpen(true);setTimeout(()=>searchRef.current?.focus(),50);}}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{width:34,height:34,borderRadius:8,background:"#EFF6FF",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>👤</div>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F"}}>Existing Client</div>
                <div style={{fontSize:11,color:"#94A3B8",marginTop:1}}>{myClients.length} saved client{myClients.length!==1?"s":""}</div>
              </div>
            </div>
            <div style={{fontSize:12,color:"#64748B",lineHeight:1.5}}>Search and load a client from the Portfolio Analyzer. Their holdings and portfolio value will auto-fill.</div>
            <div style={{marginTop:12,display:"flex",alignItems:"center",gap:6,color:"#2563EB",fontSize:12,fontWeight:600}}>
              <span>Search clients</span><span>→</span>
            </div>

            {/* Inline dropdown */}
            {open && (
              <div style={{position:"absolute",top:"100%",left:0,right:0,zIndex:200,marginTop:4,background:"#fff",border:"1px solid #E2E8F0",borderRadius:12,boxShadow:"0 8px 32px rgba(0,0,0,0.12)",overflow:"hidden"}}
                onClick={e=>e.stopPropagation()}>
                <div style={{padding:"10px 12px",borderBottom:"1px solid #F1F5F9"}}>
                  <input ref={searchRef} value={search} onChange={e=>setSearch(e.target.value)}
                    placeholder="Search by name or custodian…"
                    style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:8,padding:"8px 12px",fontSize:12,outline:"none",fontFamily:"'Outfit',sans-serif",color:"#1E293B"}}
                    onKeyDown={e=>e.key==="Escape"&&setOpen(false)}/>
                </div>
                <div style={{maxHeight:220,overflowY:"auto"}}>
                  {results.length === 0 ? (
                    <div style={{padding:"20px",textAlign:"center",color:"#94A3B8",fontSize:12}}>
                      {myClients.length === 0 ? "No saved clients yet — run the Analyzer first." : "No matches found."}
                    </div>
                  ) : results.map(c => (
                    <div key={c.id} onClick={()=>handleSelect(c)}
                      style={{padding:"10px 14px",cursor:"pointer",borderBottom:"1px solid #F8FAFC",display:"flex",alignItems:"center",gap:10,transition:"background 0.1s"}}
                      onMouseEnter={e=>e.currentTarget.style.background="#F8FAFC"}
                      onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                      <div style={{width:30,height:30,borderRadius:"50%",background:"#EFF6FF",border:"1px solid #BFDBFE",display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#2563EB",fontWeight:700,flexShrink:0}}>
                        {(resolveName(c.name)||"?")[0].toUpperCase()}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:600,fontSize:12,color:"#1E3A5F",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}><ClientName token={c.name}/></div>
                        <div style={{fontSize:10,color:"#94A3B8",marginTop:1}}>{fmt$(c.totalPortfolioValue)} · {c.holdings?.length||0} positions · {c.accountType}</div>
                      </div>
                      <span style={{fontSize:11,color:"#2563EB",fontWeight:600,flexShrink:0}}>Load →</span>
                    </div>
                  ))}
                </div>
                <div style={{padding:"8px 12px",borderTop:"1px solid #F1F5F9"}}>
                  <button onClick={()=>setOpen(false)}
                    style={{background:"none",border:"none",color:"#94A3B8",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                    ✕ Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* New client */}
          <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:12,padding:"18px 20px",cursor:"pointer",transition:"all 0.15s"}}
            onClick={handleNew}
            onMouseEnter={e=>e.currentTarget.style.borderColor="#2563EB"}
            onMouseLeave={e=>e.currentTarget.style.borderColor="#E2E8F0"}>
            <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:8}}>
              <div style={{width:34,height:34,borderRadius:8,background:"#F0FDF4",display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>✦</div>
              <div>
                <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F"}}>New Client</div>
                <div style={{fontSize:11,color:"#94A3B8",marginTop:1}}>Enter details manually</div>
              </div>
            </div>
            <div style={{fontSize:12,color:"#64748B",lineHeight:1.5}}>Build a portfolio from scratch. Enter the client name and portfolio value to get started.</div>
            <div style={{marginTop:12,display:"flex",alignItems:"center",gap:6,color:"#059669",fontSize:12,fontWeight:600}}>
              <span>Start new</span><span>→</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── NEW CLIENT MODE — plain name/value inputs, no saved client ──
  if (mode === "new") {
    return (
      <div style={{marginBottom:18}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
          <button onClick={handleClear}
            style={{background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",fontSize:11,padding:"4px 10px",cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
            ← Change
          </button>
          <span style={{fontSize:11,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:"1px"}}>New Client</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
          <div><div style={sLabel}>Client Name</div><input style={inp} placeholder="e.g. Robert & Susan Chen" value={clientName} onChange={e=>onClientNameChange(e.target.value)}/></div>
          <div><div style={sLabel}>Portfolio Value ($)</div><input style={inp} placeholder="e.g. 1,250,000" value={totalValue} onChange={e=>onTotalValueChange(e.target.value)}/></div>
        </div>
      </div>
    );
  }

  // ── LOADED MODE — client loaded from analyzer, show panel + keep holdings ──
  if (mode === "loaded" && loadedClient) {
    const existingPV = loadedClient.totalPortfolioValue || 0;
    const additionalRaw = Number(String(additionalDollars||"0").replace(/,/g,"")) || 0;
    const combinedTotal = existingPV + additionalRaw;

    return (
      <div style={{marginBottom:18}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
          <button onClick={handleClear}
            style={{background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",fontSize:11,padding:"4px 10px",cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
            ← Change Client
          </button>
          <span style={{fontSize:11,color:"#94A3B8",fontWeight:600,textTransform:"uppercase",letterSpacing:"1px"}}>Loaded from Analyzer</span>
          {(loadedClient?.savedAllocations||[]).length>0&&(
            <span style={{fontSize:10,padding:"2px 9px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,color:"#2563EB",fontWeight:700,marginLeft:"auto"}}>
              📊 {loadedClient.savedAllocations.length} saved allocation{loadedClient.savedAllocations.length!==1?"s":""}
              {" — last: "+new Date(loadedClient.savedAllocations[loadedClient.savedAllocations.length-1].savedAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <LoadedClientPanel
          client={loadedClient}
          engine={engine}
          keptHoldings={keptHoldings}
          onToggleKeep={onToggleKeep}
          onClear={handleClear}
          clientDirectedTickers={(clientDirected||[]).map(cd=>cd.ticker?.toUpperCase())}
        />

        {/* ── Client Directed Holdings override — gated by admin ── */}
        {engine.allocatorSettings?.allowClientDirected!==false&&(
          <ClientDirectedPanel
            clientDirected={clientDirected}
            onChange={onClientDirectedChange}
            client={loadedClient}
            isMobile={isMobile}
          />
        )}

        {/* Client name + dollar inputs */}
        <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:12,padding:"16px 18px",marginTop:12}}>
          <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":additionalRaw>0?"1fr 1fr 1fr 1fr":"1fr 1fr 1fr",gap:12,alignItems:"end"}}>

            {/* Client name */}
            <div>
              <div style={sLabel}>Client Name</div>
              <input style={inp} value={clientName} onChange={e=>onClientNameChange(e.target.value)}/>
            </div>

            {/* Existing portfolio — read-only */}
            <div>
              <div style={sLabel}>Existing Portfolio</div>
              <div style={{...inp, background:"#F8FAFC", color:"#64748B", cursor:"default", display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                <span>${existingPV.toLocaleString()}</span>
                <span style={{fontSize:10,color:"#CBD5E1",fontWeight:600}}>from statement</span>
              </div>
            </div>

            {/* Additional dollars */}
            <div>
              <div style={{...sLabel,display:"flex",alignItems:"center",gap:4}}>Additional Dollars to Allocate<HelpTip articleId="aw-03"/></div>
              <input
                style={inp}
                placeholder="e.g. 250,000"
                value={additionalDollars}
                onChange={e => {
                  const raw = e.target.value.replace(/[^0-9]/g,"");
                  const formatted = raw ? Number(raw).toLocaleString() : "";
                  setAdditionalDollars(formatted);
                  const combined = existingPV + (Number(raw)||0);
                  onTotalValueChange(String(combined));
                }}
              />
            </div>

            {/* Combined total — shown only when there are additional dollars */}
            {additionalRaw > 0 && (
              <div>
                <div style={sLabel}>Total to Allocate</div>
                <div style={{...inp, background:"#EFF6FF", border:"1px solid #BFDBFE", color:"#1E3A5F", fontWeight:700, display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                  <span>${combinedTotal.toLocaleString()}</span>
                  <span style={{fontSize:10,color:"#2563EB",fontWeight:600}}>combined</span>
                </div>
              </div>
            )}
          </div>

          {additionalRaw > 0 && (
            <div style={{marginTop:10,padding:"8px 12px",background:"#EFF6FF",borderRadius:8,display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:12}}>ℹ️</span>
              <span style={{fontSize:11,color:"#2563EB"}}>
                Allocating <strong>${additionalRaw.toLocaleString()}</strong> of new money on top of the existing <strong>${existingPV.toLocaleString()}</strong> portfolio — total allocation basis is <strong>${combinedTotal.toLocaleString()}</strong>.
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return null;
}

// ─────────────────────────────────────────────────────────────
// LOADED CLIENT PANEL — shown in allocator when client is active
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// CLIENT DIRECTED HOLDINGS — advisor override panel
// Bypasses all eligibility rules including non-taxable block
// ─────────────────────────────────────────────────────────────
function ClientDirectedPanel({ clientDirected=[], onChange, client, isMobile }) {
  const [input, setInput] = useState("");
  const FF = "'Outfit',sans-serif";

  const addTicker = () => {
    const ticker = input.trim().toUpperCase();
    if (!ticker) return;
    if ((clientDirected||[]).some(cd => cd.ticker === ticker)) { setInput(""); return; }
    onChange([...(clientDirected||[]), { ticker, reason:"Client Directed" }]);
    setInput("");
  };

  const remove = (ticker) => onChange((clientDirected||[]).filter(cd => cd.ticker !== ticker));

  return (
    <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:12,padding:"14px 16px",marginTop:12}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
        <span style={{fontSize:12}}>⚑</span>
        <span style={{fontSize:12,fontWeight:700,color:"#92400E"}}>Client Directed Holdings</span>
        <span style={{fontSize:10,color:"#B45309",marginLeft:4}}>Overrides all keep rules including non-taxable accounts</span>
      </div>
      <div style={{fontSize:10,color:"#78350F",marginBottom:10,lineHeight:1.6}}>
        Add ticker symbols for positions the client has directed to be retained. These bypass all eligibility and tax-exempt restrictions. A "Client Directed" reason is required.
      </div>
      <div style={{display:"flex",gap:8,marginBottom:10,flexWrap:"wrap"}}>
        <input
          value={input}
          onChange={e=>setInput(e.target.value.toUpperCase())}
          onKeyDown={e=>e.key==="Enter"&&addTicker()}
          placeholder="Enter ticker symbol"
          style={{flex:1,minWidth:120,border:"1px solid #FDE68A",borderRadius:7,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",background:"#FFFDF0",textTransform:"uppercase"}}
        />
        <select style={{border:"1px solid #FDE68A",borderRadius:7,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",background:"#FFFDF0",color:"#92400E",fontWeight:600,cursor:"not-allowed",opacity:0.85}}>
          <option>Client Directed</option>
        </select>
        <button onClick={addTicker}
          style={{padding:"7px 16px",background:"#D97706",border:"none",borderRadius:7,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:FF}}>
          Add
        </button>
      </div>
      {(clientDirected||[]).length>0?(
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {clientDirected.map((cd,i)=>(
            <div key={i} style={{display:"flex",alignItems:"center",gap:5,padding:"3px 10px",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8}}>
              <span style={{fontSize:11,fontWeight:700,color:"#92400E"}}>{cd.ticker}</span>
              <span style={{fontSize:9,color:"#B45309"}}>Client Directed</span>
              <button onClick={()=>remove(cd.ticker)}
                style={{background:"none",border:"none",color:"#D97706",cursor:"pointer",fontSize:12,padding:0,lineHeight:1,marginLeft:2}}>×</button>
            </div>
          ))}
        </div>
      ):(
        <div style={{fontSize:10,color:"#B45309",fontStyle:"italic"}}>No client directed holdings added</div>
      )}
    </div>
  );
}

function LoadedClientPanel({ client, engine, keptHoldings, onToggleKeep, onClear, clientDirectedTickers=[] }) {
  const { resolveName } = usePIIVault();
  const [collapsed, setCollapsed] = useState(false);
  const totalPV = client.totalPortfolioValue || client.holdings?.reduce((s,h)=>s+(h.marketValue||0),0) || 0;

  return (
    <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:12,marginBottom:16,overflow:"hidden",boxShadow:"0 1px 4px rgba(0,0,0,0.04)"}}>
      {/* Header */}
      <div style={{padding:"10px 16px",display:"flex",alignItems:"center",gap:10,borderBottom:collapsed?"none":"1px solid rgba(200,169,110,0.1)",background:"rgba(200,169,110,0.04)"}}>
        <div style={{width:32,height:32,borderRadius:"50%",background:"#EFF6FF",border:"1px solid #BFDBFE",display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,color:"#2563EB",flexShrink:0,fontWeight:700}}>
          {(resolveName(client.name)||client.name||"?")[0].toUpperCase()}
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{color:"#ddd0b8",fontWeight:700,fontSize:13}}><ClientName token={client.name}/></div>
          <div style={{color:"#2a3a55",fontSize:11}}>{client.accountType} · {fmt$(totalPV)} · {client.holdings?.length||0} positions</div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {keptHoldings.length>0&&<span style={{fontSize:11,color:"#D97706",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:10,padding:"2px 10px",fontWeight:600}}>{keptHoldings.length} kept</span>}
          <button onClick={()=>setCollapsed(c=>!c)} style={{background:"none",border:"1px solid #182638",borderRadius:5,color:"#405060",padding:"4px 10px",cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>{collapsed?"Show ▾":"Hide ▴"}</button>
          <button onClick={onClear} style={{background:"none",border:"1px solid #1a2a3a",borderRadius:5,color:"#3a4a5a",padding:"4px 10px",cursor:"pointer",fontSize:11,fontFamily:"'Outfit',sans-serif"}}>✕ Clear</button>
        </div>
      </div>

      {/* Holdings table */}
      {!collapsed&&(
        <div style={{overflowX:"auto",padding:"12px 16px"}}>
          <div style={{fontSize:10,color:"#2a3a55",textTransform:"uppercase",letterSpacing:"1px",fontWeight:700,marginBottom:10}}>
            <span>Existing Holdings — check eligible positions to keep in new portfolio</span><HelpTip articleId="sl-03"/>
          </div>
          {/* Account summary — shown when holdings span multiple accounts */}
          {(()=>{
            const accts = [...new Map((client.holdings||[])
              .filter(h => h.accountLabel||h._account||h.accountType)
              .map(h => {
                const key = h.accountLabel||h._account||h.accountType||"Unknown";
                return [key, { label:key, type:h.accountType, exempt:h.taxExempt,
                  count:(client.holdings||[]).filter(x=>(x.accountLabel||x._account||x.accountType||"Unknown")===key).length,
                  value:(client.holdings||[]).filter(x=>(x.accountLabel||x._account||x.accountType||"Unknown")===key).reduce((s,x)=>s+(x.marketValue||0),0),
                }];
              })
            ).values()];
            if (accts.length < 2) return null;
            return (
              <div style={{display:"flex",gap:6,flexWrap:"wrap",marginBottom:10}}>
                {accts.map((a,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:6,padding:"4px 10px",background:a.exempt?"#ECFDF5":"#F8FAFC",border:`1px solid ${a.exempt?"#6EE7B7":"#E2E8F0"}`,borderRadius:8}}>
                    <span style={{fontSize:9,fontWeight:800,color:a.exempt?"#059669":"#475569"}}>{a.label}</span>
                    <span style={{fontSize:9,color:"#94A3B8"}}>{a.count} pos · {fmt$(a.value)}</span>
                    {a.exempt&&<span style={{fontSize:8,color:"#059669",fontWeight:700}}>🛡</span>}
                  </div>
                ))}
              </div>
            );
          })()}
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr style={{borderBottom:"1px solid #111e30"}}>
                {["Keep","Ticker","Account","Name","Asset Class","Mkt Value","Gain/Loss","Gain %","Eligibility"].map(h=>(
                  <th key={h} style={{...paThStyle,textAlign:h==="Keep"?"center":"left",paddingBottom:8}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(client.holdings||[]).map(h=>{
                const elig = holdingEligibility(h, totalPV, engine.approvedHoldings, clientDirectedTickers);
                const kept = keptHoldings.includes(h.id);
                const acctLabel = h.accountLabel || h._account || h.accountType || null;
                const isExempt  = h.taxExempt;
                return (
                  <tr key={h.id} style={{borderBottom:"1px solid #0a1525",background:kept?"rgba(200,169,110,0.04)":"transparent",opacity:elig.eligible||elig.clientDirected?1:0.6}}>
                    <td style={{...paTdStyle,textAlign:"center"}}>
                      <button onClick={()=>(elig.eligible||elig.clientDirected)&&onToggleKeep(h.id)}
                        style={{width:18,height:18,borderRadius:4,border:`2px solid ${kept?"#2563EB":(elig.eligible||elig.clientDirected)?"#CBD5E1":"#E2E8F0"}`,background:kept?"#2563EB":"transparent",cursor:(elig.eligible||elig.clientDirected)?"pointer":"not-allowed",display:"flex",alignItems:"center",justifyContent:"center",padding:0}}>
                        {kept&&<span style={{color:"#fff",fontSize:10,fontWeight:900}}>✓</span>}
                      </button>
                    </td>
                    <td style={paTdStyle}><span style={{color:"#2563EB",fontWeight:700}}>{h.ticker}</span></td>
                    <td style={{...paTdStyle,minWidth:90}}>
                      {acctLabel?(
                        <div style={{display:"flex",flexDirection:"column",gap:2}}>
                          <span style={{fontSize:10,fontWeight:600,color:"#475569",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",maxWidth:100}}>{acctLabel}</span>
                          {isExempt
                            ? <span style={{fontSize:8,padding:"0px 5px",background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:4,color:"#059669",fontWeight:700,alignSelf:"flex-start"}}>🛡 Non-tax</span>
                            : <span style={{fontSize:8,padding:"0px 5px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:4,color:"#94A3B8",fontWeight:600,alignSelf:"flex-start"}}>Taxable</span>
                          }
                        </div>
                      ):(
                        <span style={{fontSize:10,color:"#CBD5E1"}}>—</span>
                      )}
                    </td>
                    <td style={{...paTdStyle,color:"#606880",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{h.name}</td>
                    <td style={paTdStyle}><span style={{color:"#505870",fontSize:10}}>{h.assetClass}</span></td>
                    <td style={paTdStyle}><span style={{color:"#8090a8"}}>{fmt$(h.marketValue)}</span></td>
                    <td style={paTdStyle}><span style={{color:(h.gainLoss||0)>=0?"#6ec8a9":"#c87e7e"}}>{h.taxExempt?<span style={{color:"#94A3B8",textDecoration:"line-through",fontSize:10}}>{fmt$(h.gainLoss)}</span>:fmt$(h.gainLoss)}</span></td>
                    <td style={paTdStyle}><span style={{color:(h.gainLossPct||0)>=0?"#6ec8a9":"#c87e7e"}}>{h.taxExempt?<span style={{color:"#94A3B8",textDecoration:"line-through",fontSize:10}}>{fmtPct(h.gainLossPct)}</span>:fmtPct(h.gainLossPct)}{h.taxExempt&&<span style={{marginLeft:4,fontSize:9,background:"#ECFDF5",color:"#059669",border:"1px solid #6EE7B7",borderRadius:4,padding:"0 4px",fontWeight:700}}>🛡</span>}</span></td>
                    <td style={{...paTdStyle,minWidth:140}}>
                      {elig.clientDirected&&(
                        <span style={{fontSize:9,padding:"1px 7px",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:5,color:"#92400E",fontWeight:700}}>⚑ Client Directed</span>
                      )}
                      {elig.blocked&&(
                        <span style={{fontSize:9,padding:"1px 7px",background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:5,color:"#DC2626",fontWeight:700}}>🛡 Non-taxable — cannot keep</span>
                      )}
                      {elig.eligible&&!elig.clientDirected&&(
                        <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
                          {elig.reasons.map((r,i)=>(
                            <span key={i} style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:"#EFF6FF",color:"#2563EB",border:"1px solid #BFDBFE",whiteSpace:"nowrap",fontWeight:600}}>{r}</span>
                          ))}
                        </div>
                      )}
                      {!elig.eligible&&!elig.blocked&&!elig.clientDirected&&(
                        <span style={{fontSize:10,color:"#253040"}}>Not eligible</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// CLIENTS TAB — admin view of all clients
// ─────────────────────────────────────────────────────────────
function ClientsTab({ engine, setEngine, sbClients=[], loadClients=()=>{}, currentUser }) {
  const { resolveName } = usePIIVault();
  const [search, setSearch] = useState("");

  const clients = Array.isArray(sbClients) ? sbClients : [];
  const filtered = clients.filter(c =>
    !search ||
    (resolveName(c.name)||"").toLowerCase().includes(search.toLowerCase()) ||
    (c.advisorName||"").toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{fontFamily:"'Outfit',sans-serif"}}>
      <div style={{fontSize:13,color:"#64748B",marginBottom:16}}>
        All clients saved by advisors across the firm.
      </div>
      <input value={search} onChange={e=>setSearch(e.target.value)}
        placeholder="Search clients…"
        style={{...inp, width:"100%", boxSizing:"border-box", marginBottom:16, fontSize:12}}/>
      <div style={{fontSize:12,color:"#94A3B8",marginBottom:12}}>
        {filtered.length} client{filtered.length!==1?"s":""}
      </div>
      {filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"48px 0",color:"#94A3B8",fontSize:13}}>
          {clients.length === 0 ? "No clients saved yet." : "No clients match your search."}
        </div>
      ) : (
        <div style={{display:"grid",gap:10}}>
          {filtered.map(c => (
            <div key={c.id} style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:10,padding:"12px 16px"}}>
              <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F",marginBottom:4}}>
                <ClientName token={c.name}/>
              </div>
              <div style={{fontSize:11,color:"#64748B"}}>
                {c.accountType||"—"} · {fmt$(c.totalPortfolioValue||0)} · {c.holdings?.length||0} positions
              </div>
              <div style={{fontSize:11,color:"#94A3B8",marginTop:2}}>
                Advisor: <strong>{c.advisorName||"—"}</strong>
                {c.savedAt ? ` · Saved: ${new Date(c.savedAt).toLocaleDateString()}` : ""}
              </div>
              {(c.savedAllocations||[]).length > 0 && (
                <span style={{fontSize:9,padding:"1px 7px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:6,color:"#2563EB",fontWeight:700,marginTop:4,display:"inline-block"}}>
                  📊 {c.savedAllocations.length} allocation{c.savedAllocations.length!==1?"s":""}
                </span>
              )}
              <div style={{marginTop:8}}>
                <button onClick={()=>{ sbDeleteClient(c.id, currentUser?.firm_id).then(()=>loadClients(currentUser?.firm_id)); }}
                  style={{background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:6,color:"#DC2626",padding:"4px 10px",cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"'Outfit',sans-serif"}}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
// ─────────────────────────────────────────────────────────────
// APPROVED HOLDINGS TAB — admin manages committee-approved tickers
// ─────────────────────────────────────────────────────────────
function ApprovedHoldingsTab({ engine, setEngine, addAudit=()=>{} }) {
  const [newTicker,   setNewTicker]   = useState("");
  const [newAsset,    setNewAsset]    = useState("");
  const [phase,       setPhase]       = useState("idle");   // idle | preview | done
  const [preview,     setPreview]     = useState([]);
  const [uploadErrors,setUploadErrors]= useState([]);
  const [importMode,  setImportMode]  = useState("merge");
  const [expanded,    setExpanded]    = useState(false);
  const fileRef = useRef(null);

  const assets   = allAssets(engine);
  // normalise to [{ticker, assetId}] regardless of legacy shape
  const approved = (engine.approvedHoldings || []).map(h =>
    typeof h === "string" ? { ticker: h, assetId: "" } : h
  );

  // ── CRUD ──
  const addTicker = () => {
    const sym = newTicker.trim().toUpperCase();
    if (!sym || approved.find(h => h.ticker === sym)) { setNewTicker(""); return; }
    setEngine(prev => ({
      ...prev,
      approvedHoldings: [...(prev.approvedHoldings||[]).map(h=>typeof h==="string"?{ticker:h,assetId:""}:h),
        { ticker: sym, assetId: newAsset || "" }],
    }));
    setNewTicker(""); setNewAsset("");
  };

  const removeTicker = (sym) => {
    setEngine(prev => ({
      ...prev,
      approvedHoldings: (prev.approvedHoldings||[])
        .map(h=>typeof h==="string"?{ticker:h,assetId:""}:h)
        .filter(h => h.ticker !== sym),
    }));
  };

  const updateAsset = (sym, assetId) => {
    setEngine(prev => ({
      ...prev,
      approvedHoldings: (prev.approvedHoldings||[])
        .map(h => {
          const n = typeof h==="string" ? {ticker:h,assetId:""} : {...h};
          if (n.ticker === sym) n.assetId = assetId;
          return n;
        }),
    }));
  };

  // ── CSV UPLOAD ──
  const parseApprovedCSV = (text) => {
    const lines = text.replace(/\r/g,"").split("\n").filter(l=>l.trim());
    if (!lines.length) return { rows:[], errors:["Empty file"] };
    const headers = lines[0].split(",").map(h=>h.trim().replace(/^"|"$/g,"").toLowerCase());
    const hasHeader = headers.some(h=>h.includes("ticker")||h.includes("symbol")||h.includes("asset"));
    const dataLines = hasHeader ? lines.slice(1) : lines;
    const startIdx  = hasHeader ? 1 : 0;

    const rows   = [];
    const errors = [];

    dataLines.forEach((line, li) => {
      const cols = line.split(",").map(c=>c.trim().replace(/^"|"$/g,""));
      let ticker, assetRaw;
      if (hasHeader) {
        const tickerIdx = headers.findIndex(h=>h.includes("ticker")||h.includes("symbol"));
        const assetIdx  = headers.findIndex(h=>h.includes("asset"));
        ticker   = cols[tickerIdx]?.toUpperCase() || "";
        assetRaw = cols[assetIdx] || "";
      } else {
        // headerless: col 0 = ticker, col 1 (optional) = asset
        ticker   = cols[0]?.toUpperCase() || "";
        assetRaw = cols[1] || "";
      }
      if (!ticker) return;

      // resolve assetId
      let assetId = "";
      if (assetRaw) {
        assetId = normalizeAssetId(assetRaw, engine) || "";
        if (!assetId) errors.push(`Row ${startIdx+li+1}: unrecognized asset class "${assetRaw}" for ${ticker} — imported without asset assignment`);
      }
      rows.push({ ticker, assetId });
    });

    return { rows, errors };
  };

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const { rows, errors } = parseApprovedCSV(ev.target.result);
      if (!rows.length) { setUploadErrors(["No ticker rows found."]); return; }
      // dedupe
      const seen = new Set();
      const deduped = rows.filter(r => { if (seen.has(r.ticker)) return false; seen.add(r.ticker); return true; });
      setPreview(deduped);
      setUploadErrors(errors);
      setPhase("preview");
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleCommit = () => {
    let base = importMode === "replace" ? [] : [...approved];
    preview.forEach(row => {
      const exists = base.findIndex(h => h.ticker === row.ticker);
      if (exists >= 0) { base[exists] = row; } // update asset if already present
      else             { base.push(row); }
    });
    setEngine(prev => ({ ...prev, approvedHoldings: base }));
    addAudit("approved_imported", `Approved holdings import (${importMode}): ${preview.length} tickers`);
    setPhase("done");
    setTimeout(() => { setPhase("idle"); setPreview([]); setUploadErrors([]); setExpanded(false); }, 2000);
  };

  const handleDownloadTemplate = () => {
    const rows = ["Ticker,Asset Class", ...approved.map(h => {
      const a = assets.find(a=>a.id===h.assetId);
      return `${h.ticker},${a?.label||""}`;
    })];
    const blob = new Blob([rows.join("\n")], { type:"text/csv" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a"); a.href=url; a.download="approved_holdings_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  // ── asset selector shared style ──
  const assetSel = (value, onChange, compact) => (
    <select value={value} onChange={e=>onChange(e.target.value)}
      style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:6,color:value?"#1E3A5F":"#94A3B8",
        padding:compact?"3px 6px":"7px 10px",fontSize:compact?10:12,outline:"none",
        fontFamily:"'Outfit',sans-serif",cursor:"pointer",maxWidth:compact?140:200}}>
      <option value="">— no asset —</option>
      {assets.map(a=><option key={a.id} value={a.id}>{a.label}</option>)}
    </select>
  );

  // group approved by asset for display
  const byAsset = {};
  approved.forEach(h => {
    const key = h.assetId || "__none__";
    if (!byAsset[key]) byAsset[key] = [];
    byAsset[key].push(h);
  });
  const assetOrder = [...assets.map(a=>a.id), "__none__"];

  return (
    <div>
      <div style={{fontSize:13,color:"#64748B",marginBottom:6,lineHeight:1.6}}>
        Tickers on this list can be marked as "keep" by advisors when building a portfolio for an existing client,
        regardless of unrealized gain. Assigning an asset class maps the held position to the correct sleeve in the allocation.
      </div>
      <div style={{fontSize:11,color:"#94A3B8",marginBottom:18}}>
        {approved.length} approved ticker{approved.length!==1?"s":""}
        {approved.filter(h=>!h.assetId).length > 0 &&
          <span style={{color:"#D97706",marginLeft:8}}>· {approved.filter(h=>!h.assetId).length} without asset class</span>}
      </div>

      {/* ── Upload panel ── */}
      <div style={{border:"1px solid #E2E8F0",borderRadius:12,marginBottom:22,overflow:"hidden"}}>
        <div onClick={()=>{ if(phase==="idle"||phase==="done") setExpanded(e=>!e); }}
          style={{padding:"11px 16px",background:"#F8FAFC",display:"flex",alignItems:"center",gap:10,cursor:"pointer",borderBottom:expanded?"1px solid #E2E8F0":"none"}}>
          <span style={{fontSize:15}}>📤</span>
          <div style={{flex:1}}>
            <span style={{fontWeight:700,fontSize:13,color:"#1E3A5F"}}>Upload Approved Holdings</span>
            <span style={{fontSize:11,color:"#94A3B8",marginLeft:10}}>CSV · two columns: Ticker, Asset Class</span>
          </div>
          <button onClick={e=>{e.stopPropagation();handleDownloadTemplate();}}
            style={{background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
            ⬇ Download Template
          </button>
          {phase==="done"
            ? <span style={{fontSize:11,fontWeight:700,color:"#16A34A",background:"#F0FDF4",border:"1px solid #BBF7D0",borderRadius:20,padding:"3px 10px"}}>✓ Imported</span>
            : <span style={{fontSize:13,color:"#94A3B8",display:"inline-block",transition:"transform 0.2s",transform:expanded?"rotate(90deg)":"none"}}>›</span>
          }
        </div>

        {expanded && phase !== "done" && (
          <div style={{padding:"16px 18px",background:"#fff"}}>
            {phase === "idle" && (
              <div>
                <div style={{fontSize:12,color:"#64748B",marginBottom:12,lineHeight:1.7}}>
                  Upload a two-column CSV: <strong>Ticker</strong> and <strong>Asset Class</strong>.
                  The header row is optional. Asset class is optional per row — tickers without one will show as unassigned.
                  Download the template above to get the current list pre-filled.
                </div>
                {/* accepted names */}
                <div style={{marginBottom:14}}>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:6}}>Accepted Asset Class Names</div>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {assets.map(a=>(
                      <span key={a.id} style={{fontSize:10,padding:"2px 9px",borderRadius:10,background:a.color+"0D",border:`1px solid ${a.color+"44"}`,color:a.color,fontWeight:600}}>{a.label}</span>
                    ))}
                  </div>
                </div>
                {/* drop zone */}
                <div onClick={()=>fileRef.current?.click()}
                  onDragOver={e=>{e.preventDefault();e.currentTarget.style.background="#EFF6FF";e.currentTarget.style.borderColor="#2563EB";}}
                  onDragLeave={e=>{e.currentTarget.style.background="#FAFBFF";e.currentTarget.style.borderColor="#BFDBFE";}}
                  onDrop={e=>{e.preventDefault();e.currentTarget.style.background="#FAFBFF";e.currentTarget.style.borderColor="#BFDBFE";const f=e.dataTransfer.files[0];if(f){const ev={target:{files:[f],value:""}};handleFile(ev);}}}
                  style={{border:"2px dashed #BFDBFE",borderRadius:10,padding:"24px 20px",textAlign:"center",cursor:"pointer",background:"#FAFBFF",transition:"all 0.15s"}}>
                  <div style={{fontSize:22,marginBottom:5}}>📂</div>
                  <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F",marginBottom:2}}>Drop CSV here or click to browse</div>
                  <div style={{fontSize:11,color:"#94A3B8"}}>CSV files only</div>
                </div>
                <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={handleFile} style={{display:"none"}}/>
              </div>
            )}

            {phase === "preview" && (
              <div>
                {uploadErrors.length > 0 && (
                  <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,padding:"10px 14px",marginBottom:12}}>
                    <div style={{fontWeight:700,fontSize:12,color:"#92400E",marginBottom:4}}>⚠ {uploadErrors.length} warning{uploadErrors.length!==1?"s":""}</div>
                    {uploadErrors.map((e,i)=><div key={i} style={{fontSize:11,color:"#78350F"}}>{e}</div>)}
                  </div>
                )}
                {/* Mode */}
                <div style={{display:"flex",gap:8,marginBottom:14,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{fontSize:11,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.5px"}}>Import mode:</span>
                  {[["merge","Merge — add to existing"],["replace","Replace — wipe and rebuild"]].map(([val,label])=>(
                    <button key={val} onClick={()=>setImportMode(val)}
                      style={{padding:"6px 14px",borderRadius:7,border:`2px solid ${importMode===val?"#1E3A5F":"#E2E8F0"}`,background:importMode===val?"#1E3A5F":"#fff",color:importMode===val?"#fff":"#64748B",fontSize:11,fontWeight:importMode===val?700:500,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                      {label}
                    </button>
                  ))}
                  {importMode==="replace"&&<span style={{fontSize:10,color:"#DC2626",fontWeight:600}}>⚠ Current approved list will be cleared</span>}
                </div>
                {/* Preview table */}
                <div style={{fontSize:11,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:8}}>
                  {preview.length} ticker{preview.length!==1?"s":""} ready to import
                </div>
                <div style={{overflowX:"auto",maxHeight:280,overflowY:"auto",border:"1px solid #E2E8F0",borderRadius:8,marginBottom:14}}>
                  <table style={{borderCollapse:"collapse",fontSize:11,width:"100%"}}>
                    <thead style={{position:"sticky",top:0}}>
                      <tr style={{background:"#F8FAFC"}}>
                        {["Ticker","Asset Class"].map(h=>(
                          <th key={h} style={{padding:"7px 14px",textAlign:"left",fontWeight:700,color:"#64748B",borderBottom:"2px solid #E2E8F0",fontSize:10,textTransform:"uppercase",letterSpacing:"0.5px",whiteSpace:"nowrap"}}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.map((row,i) => {
                        const a = assets.find(a=>a.id===row.assetId);
                        return (
                          <tr key={row.ticker} style={{background:i%2===0?"#fff":"#FAFBFC"}}>
                            <td style={{padding:"6px 14px",fontWeight:700,color:"#1E3A5F",borderBottom:"1px solid #F4F6F9"}}>{row.ticker}</td>
                            <td style={{padding:"6px 14px",borderBottom:"1px solid #F4F6F9"}}>
                              {a
                                ? <span style={{fontSize:10,padding:"2px 9px",borderRadius:10,background:a.color+"0D",border:`1px solid ${a.color+"44"}`,color:a.color,fontWeight:600}}>{a.label}</span>
                                : <span style={{fontSize:10,color:"#D97706",fontStyle:"italic"}}>unassigned</span>
                              }
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                  <button onClick={()=>{setPhase("idle");setPreview([]);setUploadErrors([]);}}
                    style={{background:"none",border:"1px solid #E2E8F0",borderRadius:8,color:"#64748B",padding:"8px 18px",cursor:"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>
                    ← Re-upload
                  </button>
                  <button onClick={handleCommit}
                    style={{background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",padding:"8px 22px",cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"'Outfit',sans-serif"}}>
                    Confirm Import ({preview.length})
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Manual add ── */}
      <div style={{display:"flex",gap:10,marginBottom:24,alignItems:"center",flexWrap:"wrap"}}>
        <input value={newTicker} onChange={e=>setNewTicker(e.target.value.toUpperCase())}
          onKeyDown={e=>e.key==="Enter"&&addTicker()}
          placeholder="Ticker symbol…"
          style={{...inp,width:140,fontSize:12,padding:"8px 12px"}}/>
        {assetSel(newAsset, setNewAsset, false)}
        <button onClick={addTicker}
          style={{background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",padding:"8px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
          + Add
        </button>
      </div>

      {/* ── Holdings list grouped by asset ── */}
      {approved.length === 0 && (
        <div style={{color:"#94A3B8",fontSize:13,padding:"24px 0"}}>No approved holdings yet.</div>
      )}
      {assetOrder.map(aid => {
        const group = byAsset[aid];
        if (!group?.length) return null;
        const asset = assets.find(a=>a.id===aid);
        return (
          <div key={aid} style={{marginBottom:16}}>
            {/* Group label */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
              {asset
                ? <><div style={{width:8,height:8,borderRadius:"50%",background:asset.color}}/><span style={{fontSize:11,fontWeight:700,color:asset.color,textTransform:"uppercase",letterSpacing:"0.5px"}}>{asset.label}</span></>
                : <span style={{fontSize:11,fontWeight:700,color:"#D97706",textTransform:"uppercase",letterSpacing:"0.5px"}}>⚠ Unassigned</span>
              }
              <div style={{flex:1,height:1,background:"#E2E8F0"}}/>
              <span style={{fontSize:10,color:"#94A3B8"}}>{group.length}</span>
            </div>
            {/* Ticker pills */}
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {group.map(h => (
                <div key={h.ticker} style={{display:"flex",alignItems:"center",gap:6,background:asset?asset.color+"0D":"#FEF3C7",border:`1px solid ${asset?asset.color+"44":"#FDE68A"}`,borderRadius:8,padding:"5px 10px"}}>
                  <span style={{fontWeight:700,fontSize:12,color:asset?asset.color:"#92400E"}}>{h.ticker}</span>
                  {/* inline asset reassign */}
                  <select value={h.assetId||""} onChange={e=>updateAsset(h.ticker,e.target.value)}
                    style={{background:"transparent",border:"none",color:"#94A3B8",fontSize:10,outline:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",maxWidth:120,padding:0}}>
                    <option value="">— reassign —</option>
                    {assets.map(a=><option key={a.id} value={a.id}>{a.label}</option>)}
                  </select>
                  <button onClick={()=>removeTicker(h.ticker)}
                    style={{background:"none",border:"none",color:"#CBD5E1",cursor:"pointer",fontSize:13,lineHeight:1,padding:"0 0 0 2px",fontWeight:700}}
                    onMouseEnter={e=>e.target.style.color="#DC2626"} onMouseLeave={e=>e.target.style.color="#CBD5E1"}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// ADMIN HELP TAB — full searchable knowledge base
// ─────────────────────────────────────────────────────────────
function AdminHelpTab() {
  const [query,      setQuery]      = useState("");
  const [activecat,  setActiveCat]  = useState("All");
  const [openId,     setOpenId]     = useState(null);
  const searchRef = useRef(null);

  const cats = ["All", ...HELP_CATEGORIES];

  const filtered = HELP_ARTICLES.filter(a => {
    const matchCat = activecat === "All" || a.category === activecat;
    const q = query.toLowerCase();
    const matchQ = !q || a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q)
      || a.body.toLowerCase().includes(q) || a.tags?.some(t=>t.toLowerCase().includes(q));
    return matchCat && matchQ;
  });

  const grouped = HELP_CATEGORIES.reduce((acc, cat) => {
    const articles = filtered.filter(a => a.category === cat);
    if (articles.length) acc[cat] = articles;
    return acc;
  }, {});

  const openArticle = HELP_ARTICLES.find(a => a.id === openId);

  return (
    <div>
      {/* Page header */}
      <div style={{marginBottom:20}}>
        <div style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:700,color:"#1E3A5F",marginBottom:4}}>Help & Documentation</div>
        <div style={{fontSize:13,color:"#64748B",lineHeight:1.6}}>
          System logic, admin settings, advisor workflows, and glossary definitions.
          Search below or browse by category.
        </div>
      </div>

      {/* Search + category filter */}
      <div style={{display:"flex",gap:10,marginBottom:18,flexWrap:"wrap",alignItems:"center"}}>
        <div style={{flex:1,minWidth:200,position:"relative"}}>
          <span style={{position:"absolute",left:11,top:"50%",transform:"translateY(-50%)",fontSize:13,color:"#94A3B8",pointerEvents:"none"}}>🔍</span>
          <input ref={searchRef} value={query} onChange={e=>setQuery(e.target.value)}
            placeholder="Search articles, settings, terms…"
            style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:9,color:"#1E3A5F",padding:"9px 13px 9px 32px",fontSize:13,outline:"none",fontFamily:"'Outfit',sans-serif",boxSizing:"border-box",width:"100%"}}
          />
          {query && <button onClick={()=>setQuery("")} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",background:"none",border:"none",color:"#94A3B8",cursor:"pointer",fontSize:14,padding:0}}>✕</button>}
        </div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {cats.map(cat => {
            const c = HELP_CAT_COLORS[cat] || {bg:"#EFF6FF",border:"#BFDBFE",text:"#2563EB"};
            const active = activecat === cat;
            return (
              <button key={cat} onClick={()=>setActiveCat(cat)}
                style={{padding:"6px 13px",borderRadius:20,border:`1px solid ${active?c.border:"#E2E8F0"}`,background:active?c.bg:"#fff",color:active?c.text:"#64748B",fontSize:11,fontWeight:active?700:500,cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all 0.15s",whiteSpace:"nowrap"}}>
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results count */}
      {query && (
        <div style={{fontSize:12,color:"#94A3B8",marginBottom:12}}>
          {filtered.length} article{filtered.length!==1?"s":""} matching "{query}"
        </div>
      )}

      {filtered.length === 0 && (
        <div style={{textAlign:"center",padding:"48px 0",color:"#94A3B8",fontSize:13}}>
          No articles found. Try a different search term.
        </div>
      )}

      {/* Article list grouped by category */}
      {Object.entries(grouped).map(([cat, articles]) => {
        const c = HELP_CAT_COLORS[cat] || {bg:"#EFF6FF",border:"#BFDBFE",text:"#2563EB"};
        return (
          <div key={cat} style={{marginBottom:24}}>
            {/* Category header */}
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
              <span style={{fontSize:11,fontWeight:700,color:c.text,background:c.bg,border:`1px solid ${c.border}`,borderRadius:6,padding:"2px 10px",letterSpacing:"0.04em"}}>
                {cat}
              </span>
              <div style={{flex:1,height:1,background:"#E2E8F0"}}/>
              <span style={{fontSize:11,color:"#94A3B8"}}>{articles.length} article{articles.length!==1?"s":""}</span>
            </div>

            {/* Article cards */}
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {articles.map(article => {
                const isOpen = openId === article.id;
                return (
                  <div key={article.id}
                    style={{border:`1px solid ${isOpen?c.border:"#E2E8F0"}`,borderRadius:10,overflow:"hidden",transition:"border-color 0.2s",background:isOpen?c.bg+"44":"#fff"}}>

                    {/* Article header (clickable) */}
                    <div onClick={()=>setOpenId(isOpen?null:article.id)}
                      style={{padding:"12px 16px",cursor:"pointer",display:"flex",alignItems:"flex-start",gap:12}}>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:13,color:"#1E3A5F",marginBottom:3}}>{article.title}</div>
                        <div style={{fontSize:12,color:"#64748B",lineHeight:1.5}}>{article.summary}</div>
                        {article.tags && (
                          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginTop:6}}>
                            {article.tags.map(tag => (
                              <span key={tag} onClick={e=>{e.stopPropagation();setQuery(tag);setActiveCat("All");}}
                                style={{fontSize:9,padding:"1px 7px",borderRadius:10,background:"#F1F5F9",color:"#64748B",border:"1px solid #E2E8F0",cursor:"pointer",fontWeight:600,letterSpacing:"0.03em"}}>
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span style={{fontSize:14,color:"#94A3B8",flexShrink:0,marginTop:1,transition:"transform 0.2s",transform:isOpen?"rotate(90deg)":"none"}}>›</span>
                    </div>

                    {/* Article body */}
                    {isOpen && (
                      <div style={{borderTop:`1px solid ${c.border}`,padding:"16px 18px",background:"#fff"}}>
                        {article.body.split("\n\n").map((para, pi) => {
                          // Detect bullet-like lines (start with number or •)
                          if (para.includes("\n") && (para.match(/^\d\./) || para.match(/^•/m))) {
                            const lines = para.split("\n");
                            return (
                              <div key={pi} style={{marginBottom:12}}>
                                {lines.map((line, li) => (
                                  <div key={li} style={{fontSize:13,color:"#334155",lineHeight:1.7,paddingLeft: line.match(/^\d\.|^•/) ? 0 : 0,marginBottom:line.match(/^\d\.|^•/)? 4:0}}>
                                    {line.startsWith("Flagship") || line.startsWith("Systematic") || line.startsWith("Index Passive") || line.startsWith("Opportunistic") || line.startsWith("Defensive") || line.startsWith("Advisor") || line.startsWith("Admin") ? (
                                      <><strong style={{color:"#1E3A5F"}}>{line.split(" — ")[0]}</strong>{line.includes(" — ") ? " — " + line.split(" — ").slice(1).join(" — ") : ""}</>
                                    ) : line}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          // Bold inline labels like "Step 1 — "
                          const boldMatch = para.match(/^(Step \d+|Note|Best practice) — /);
                          return (
                            <p key={pi} style={{fontSize:13,color:boldMatch?"#1E3A5F":"#334155",lineHeight:1.75,margin:"0 0 12px",fontWeight:boldMatch?600:400}}>
                              {para}
                            </p>
                          );
                        })}
                        {article.note && (
                          <div style={{marginTop:12,padding:"10px 14px",background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:8,display:"flex",gap:8,alignItems:"flex-start"}}>
                            <span style={{fontSize:14,flexShrink:0}}>💡</span>
                            <p style={{fontSize:12,color:"#78350F",lineHeight:1.65,margin:0}}>{article.note}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// EMPLOYEES TAB — spreadsheet view
// ─────────────────────────────────────────────────────────────
function EmployeesTab({ engine, setEngine, firmId, adminUser }) {
  const FF = "'Outfit',sans-serif";
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole,  setInviteRole]  = useState("advisor");
  const [inviting,    setInviting]    = useState(false);
  const [inviteMsg,   setInviteMsg]   = useState(null);
  const [profiles,    setProfiles]    = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loading,     setLoading]     = useState(true);

  // Load current team + pending invites from Supabase
  useEffect(() => {
    Promise.all([
      supabase.from("profiles").select("*").eq("firm_id", firmId),
      supabase.from("invites").select("*").eq("firm_id", firmId).eq("accepted", false).gt("expires_at", new Date().toISOString()).order("created_at", { ascending: false }),
    ]).then(([{ data: profileData }, { data: inviteData }]) => {
      if (profileData) setProfiles(profileData);
      if (inviteData)  setPendingInvites(inviteData);
      setLoading(false);
    });
  }, []);

  const cancelInvite = async (inviteId) => {
    const invite = pendingInvites.find(i => i.id === inviteId);
    // Call server-side to delete auth user + invite record
    await fetch("/api/cancel-invite", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: invite?.email, inviteId }),
    });
    setPendingInvites(prev => prev.filter(i => i.id !== inviteId));
  };

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    setInviteMsg(null);
    const email = inviteEmail.trim().toLowerCase();
    const effectiveFirmId = firmId || adminUser?.firm_id;

    try {
      // Check if pending invite already exists for this email
      const { data: existing } = await supabase
        .from("invites")
        .select("id")
        .eq("firm_id", effectiveFirmId)
        .eq("email", email)
        .eq("accepted", false)
        .single();

      if (existing) {
        setInviteMsg({ type:"error", text: `A pending invite already exists for ${email}. Cancel it first to resend.` });
        setInviting(false);
        return;
      }

      // Step 1: Record invite in database
      const { data: inviteData, error: insertErr } = await supabase
        .from("invites")
        .insert({
          firm_id:    effectiveFirmId,
          email,
          role:       inviteRole,
          invited_by: adminUser?.id,
        })
        .select()
        .single();

      if (insertErr) {
        setInviteMsg({ type:"error", text: `Failed to record invite: ${insertErr.message}` });
        setInviting(false);
        return;
      }

      // Step 2: Send proper Supabase invite via server-side API
      const inviteApiRes = await fetch("/api/invite", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          firmId:     effectiveFirmId,
          role:       inviteRole,
          firmName:   engine?.firmName || "your firm",
          invitedBy:  adminUser?.name || "",
        }),
      });
      const inviteApiData = await inviteApiRes.json();
      if (!inviteApiRes.ok) throw new Error(inviteApiData.error || "Invite failed");

      // Step 3: Update local state so pending section appears immediately
      setPendingInvites(prev => [...prev, inviteData]);
      setInviteMsg({ type:"success", text:`Invite sent to ${email}` });
      setInviteEmail("");

    } catch(e) {
      setInviteMsg({ type:"error", text: e.message || "Failed to send invite." });
    }
    setInviting(false);
  };

  return (
    <div style={{fontFamily:FF}}>

      {/* ── Invite new advisor ── */}
      <div style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:12,padding:"18px 20px",marginBottom:24}}>
        <div style={{fontSize:14,fontWeight:700,color:"#1E3A5F",marginBottom:4}}>Invite a Team Member</div>
        <div style={{fontSize:12,color:"#64748B",marginBottom:14,lineHeight:1.6}}>
          They'll receive an email with a link to set their password and join your firm.
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr auto auto",gap:8,alignItems:"end"}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Email Address</div>
            <input value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)}
              onKeyDown={e=>e.key==="Enter"&&sendInvite()}
              placeholder="advisor@example.com" type="email"
              style={{...inp,width:"100%",boxSizing:"border-box"}}/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Role</div>
            <select value={inviteRole} onChange={e=>setInviteRole(e.target.value)}
              style={{...inp,cursor:"pointer",minWidth:110}}>
              <option value="advisor">Advisor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button onClick={sendInvite} disabled={inviting||!inviteEmail.trim()}
            style={{padding:"9px 20px",background:inviting||!inviteEmail.trim()?"#94A3B8":"#1E3A5F",border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:inviting||!inviteEmail.trim()?"not-allowed":"pointer",fontFamily:FF,whiteSpace:"nowrap"}}>
            {inviting ? "Sending…" : "Send Invite ✉"}
          </button>
        </div>
        {inviteMsg && (
          <div style={{marginTop:10,padding:"8px 12px",borderRadius:7,background:inviteMsg.type==="success"?"#D1FAE5":"#FEE2E2",color:inviteMsg.type==="success"?"#065F46":"#991B1B",fontSize:12,fontWeight:600}}>
            {inviteMsg.type==="success"?"✓ ":"✗ "}{inviteMsg.text}
          </div>
        )}
      </div>

      {/* ── Pending invites ── */}
      {pendingInvites.length > 0 && (
        <div style={{marginBottom:24}}>
          <div style={{fontSize:13,fontWeight:700,color:"#1E3A5F",marginBottom:12,display:"flex",alignItems:"center",gap:8}}>
            Pending Invites
            <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:20,background:"#FEF3C7",color:"#92400E",border:"1px solid #FDE68A"}}>{pendingInvites.length}</span>
          </div>
          <div style={{border:"1px solid #E2E8F0",borderRadius:12,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
              <thead>
                <tr style={{background:"#FFFBEB"}}>
                  {["Email","Role","Invited","Expires",""].map(h=>(
                    <th key={h} style={{padding:"10px 14px",fontSize:10,fontWeight:700,color:"#92400E",textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"left",borderBottom:"1px solid #FDE68A"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((inv,i)=>(
                  <tr key={inv.id} style={{background:i%2===0?"#fff":"#FFFDF5"}}>
                    <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9",color:"#1E3A5F",fontWeight:600}}>{inv.email}</td>
                    <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9"}}>
                      <span style={{fontSize:10,padding:"2px 10px",borderRadius:20,fontWeight:700,
                        background:inv.role==="admin"?"#FEF3C7":"#EFF6FF",
                        color:inv.role==="admin"?"#92400E":"#1E40AF",
                        border:`1px solid ${inv.role==="admin"?"#FDE68A":"#BFDBFE"}`}}>
                        {inv.role}
                      </span>
                    </td>
                    <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9",color:"#94A3B8",fontSize:11}}>{new Date(inv.created_at).toLocaleDateString()}</td>
                    <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9",color:"#94A3B8",fontSize:11}}>{new Date(inv.expires_at).toLocaleDateString()}</td>
                    <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9"}}>
                      <button onClick={()=>cancelInvite(inv.id)}
                        style={{background:"none",border:"1px solid #FCA5A5",borderRadius:6,color:"#DC2626",padding:"3px 10px",cursor:"pointer",fontSize:11,fontWeight:600,fontFamily:FF}}>
                        Cancel
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Current team ── */}
      <div style={{fontSize:13,fontWeight:700,color:"#1E3A5F",marginBottom:12}}>Current Team</div>
      {loading ? (
        <div style={{textAlign:"center",padding:24,color:"#94A3B8",fontSize:13}}>Loading team…</div>
      ) : profiles.length === 0 ? (
        <div style={{textAlign:"center",padding:24,color:"#94A3B8",fontSize:13}}>No team members yet.</div>
      ) : (
        <div style={{border:"1px solid #E2E8F0",borderRadius:12,overflow:"hidden"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
            <thead>
              <tr style={{background:"#F8FAFC"}}>
                {["Name","Email","Role","Joined"].map(h=>(
                  <th key={h} style={{padding:"10px 14px",fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"left",borderBottom:"1px solid #E2E8F0"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {profiles.map((p,i)=>(
                <tr key={p.id} style={{background:i%2===0?"#fff":"#FAFBFC"}}>
                  <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9",fontWeight:600,color:"#1E3A5F"}}>{p.name}</td>
                  <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9",color:"#64748B"}}>{p.email}</td>
                  <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9"}}>
                    <span style={{fontSize:10,padding:"2px 10px",borderRadius:20,fontWeight:700,
                      background:p.role==="admin"?"#FEF3C7":"#EFF6FF",
                      color:p.role==="admin"?"#92400E":"#1E40AF",
                      border:`1px solid ${p.role==="admin"?"#FDE68A":"#BFDBFE"}`}}>
                      {p.role}
                    </span>
                  </td>
                  <td style={{padding:"10px 14px",borderBottom:"1px solid #F1F5F9",color:"#94A3B8",fontSize:11}}>
                    {new Date(p.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// IMPLEMENTATION STYLES TAB
// ─────────────────────────────────────────────────────────────
function ImplementationStylesTab({ engine, setEngine, addAudit=()=>{} }) {
  const styleKeys  = Object.keys(engine.implementationStyles);
  const [activeKey, setActiveKey] = useState(styleKeys[0] || "");
  const [renaming,  setRenaming]  = useState(false);
  const [renameVal, setRenameVal] = useState("");
  const [newName,   setNewName]   = useState("");
  const [showNew,   setShowNew]   = useState(false);
  const renameRef = useRef(null);

  // keep activeKey valid if styles change
  const validKey = engine.implementationStyles[activeKey] ? activeKey : styleKeys[0] || "";

  // ── Mutators ──
  const updateStyle = (key, field, value) => {
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      n.implementationStyles[key][field] = value;
      return n;
    });
  };

  const addStyle = () => {
    const name = newName.trim();
    if (!name || engine.implementationStyles[name]) return;
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      // scaffold empty tickers for every asset
      const tickers = {};
      allAssets(n).forEach(a => { tickers[a.id] = []; });
      n.implementationStyles[name] = {
        description: "",
        color: "#6366F1",
        tickers,
        defaultSplits: {},
      };
      return n;
    });
    setActiveKey(name);
    addAudit("engine_changed", `Implementation style created: "${name}"`);
    setNewName("");
    setShowNew(false);
  };

  const removeStyle = (key) => {
    if (styleKeys.length <= 1) return; // never remove the last one
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      delete n.implementationStyles[key];
      return n;
    });
    addAudit("engine_changed", `Implementation style deleted: "${key}"`);
    const remaining = styleKeys.filter(k => k !== key);
    setActiveKey(remaining[0] || "");
  };

  const renameStyle = (oldKey, newKey) => {
    newKey = newKey.trim();
    if (!newKey || newKey === oldKey || engine.implementationStyles[newKey]) return;
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      const val = n.implementationStyles[oldKey];
      delete n.implementationStyles[oldKey];
      // rebuild preserving insertion order
      const rebuilt = {};
      Object.keys(n.implementationStyles).forEach(k => {
        rebuilt[k === oldKey ? newKey : k] = n.implementationStyles[k];
      });
      rebuilt[newKey] = val;
      n.implementationStyles = rebuilt;
      return n;
    });
    setActiveKey(newKey);
    addAudit("engine_changed", `Implementation style renamed: "${oldKey}" → "${newKey}"`);
    setRenaming(false);
  };

  const duplicateStyle = (key) => {
    const base    = engine.implementationStyles[key];
    let   newKey  = key + " (Copy)";
    let   counter = 2;
    while (engine.implementationStyles[newKey]) newKey = `${key} (Copy ${counter++})`;
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      n.implementationStyles[newKey] = JSON.parse(JSON.stringify(base));
      return n;
    });
    setActiveKey(newKey);
  };

  const style = engine.implementationStyles[validKey];
  if (!style) return <div style={{color:"#94A3B8",padding:24}}>No implementation styles configured.</div>;

  const sc = style.color || "#2563EB";

  // ── Summary row: how many tickers per sleeve ──
  const opp = engine.sleeves.opportunistic;
  const def = engine.sleeves.defensive;

  const tickerCount = (sleeveKey) => {
    const sleeve = engine.sleeves[sleeveKey];
    return sleeve.assets.reduce((s, a) => s + (style.tickers?.[a.id]?.length || 0), 0);
  };

  const splitStatus = (assetId) => {
    const tickers = style.tickers?.[assetId] || [];
    if (!tickers.length) return null;
    const splits = style.defaultSplits?.[assetId] || {};
    const total  = tickers.reduce((s,t) => s + (splits[t] ?? (tickers[0]===t?100:0)), 0);
    return Math.abs(total-100) < 0.5 ? "ok" : "warn";
  };

  const PRESET_COLORS = ["#1E3A5F","#0891B2","#059669","#7C3AED","#DB2777","#D97706","#DC2626","#6366F1","#0F766E","#92400E"];

  return (
    <div>
      <div style={{fontSize:13,color:"#64748B",marginBottom:18,lineHeight:1.6}}>
        Create and manage implementation styles. Each style has its own name, description, color, and ticker pool.
        Tickers and splits are configured in the <strong>Tickers</strong> tab — this page manages the style definitions themselves.
      </div>

      {/* ── Style tab strip ── */}
      <div style={{display:"flex",gap:6,marginBottom:20,flexWrap:"wrap",alignItems:"center"}}>
        {styleKeys.map(key => {
          const s      = engine.implementationStyles[key];
          const active = key === validKey;
          return (
            <button key={key} onClick={()=>{ setActiveKey(key); setRenaming(false); }}
              style={{padding:"7px 16px",borderRadius:9,border:`2px solid ${active?s.color:"#E2E8F0"}`,
                background:active?s.color+"12":"#fff",color:active?s.color:"#64748B",
                fontWeight:active?700:500,fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif",
                transition:"all 0.15s",display:"flex",alignItems:"center",gap:6}}>
              <span style={{width:8,height:8,borderRadius:"50%",background:s.color,flexShrink:0,display:"inline-block"}}/>
              {key}
            </button>
          );
        })}

        {/* Add new style */}
        {showNew ? (
          <div style={{display:"flex",gap:6,alignItems:"center"}}>
            <input autoFocus value={newName} onChange={e=>setNewName(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter") addStyle(); if(e.key==="Escape"){ setShowNew(false); setNewName(""); }}}
              placeholder="Style name…"
              style={{border:"2px dashed #BFDBFE",borderRadius:8,padding:"6px 12px",fontSize:12,color:"#1E3A5F",outline:"none",fontFamily:"'Outfit',sans-serif",width:160,background:"#EFF6FF"}}/>
            <button onClick={addStyle} style={{background:"#1E3A5F",border:"none",borderRadius:7,color:"#fff",padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Add</button>
            <button onClick={()=>{ setShowNew(false); setNewName(""); }} style={{background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#94A3B8",padding:"6px 10px",fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>✕</button>
          </div>
        ) : (
          <button onClick={()=>setShowNew(true)}
            style={{padding:"7px 14px",borderRadius:9,border:"2px dashed #BFDBFE",background:"#EFF6FF",color:"#2563EB",fontWeight:700,fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
            + New Style
          </button>
        )}
      </div>

      {/* ── Style detail card ── */}
      <div style={{border:`2px solid ${sc}33`,borderRadius:14,overflow:"hidden"}}>

        {/* Card header */}
        <div style={{background:sc+"0A",borderBottom:`1px solid ${sc}22`,padding:"16px 20px",display:"flex",alignItems:"flex-start",gap:12,flexWrap:"wrap"}}>
          <div style={{flex:1,minWidth:0}}>
            {/* Name + rename */}
            {renaming ? (
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:8}}>
                <input ref={renameRef} defaultValue={validKey}
                  onKeyDown={e=>{ if(e.key==="Enter") renameStyle(validKey,e.target.value); if(e.key==="Escape") setRenaming(false); }}
                  style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:700,color:"#1E3A5F",border:"2px solid #BFDBFE",borderRadius:8,padding:"4px 10px",outline:"none",background:"#fff",width:240}}
                  autoFocus/>
                <button onClick={()=>renameStyle(validKey,renameRef.current?.value||validKey)}
                  style={{background:"#1E3A5F",border:"none",borderRadius:7,color:"#fff",padding:"6px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Save</button>
                <button onClick={()=>setRenaming(false)}
                  style={{background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#94A3B8",padding:"6px 10px",fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Cancel</button>
              </div>
            ) : (
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
                <div style={{fontFamily:"'Outfit',sans-serif",fontSize:22,fontWeight:700,color:"#1E3A5F"}}>{validKey}</div>
                <button onClick={()=>setRenaming(true)}
                  style={{background:"none",border:"1px solid #E2E8F0",borderRadius:6,color:"#94A3B8",padding:"3px 9px",fontSize:11,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                  rename
                </button>
              </div>
            )}

            {/* Description */}
            <input value={style.description||""} onChange={e=>updateStyle(validKey,"description",e.target.value)}
              placeholder="Short description shown to advisors…"
              style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:8,padding:"8px 12px",fontSize:13,color:"#475569",outline:"none",fontFamily:"'Outfit',sans-serif",background:"#fff",boxSizing:"border-box",maxWidth:520}}/>
          </div>

          {/* Color picker + presets */}
          <div style={{display:"flex",flexDirection:"column",gap:8,alignItems:"flex-end"}}>
            <div style={{display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:11,color:"#94A3B8",fontWeight:600}}>Color</span>
              <input type="color" value={style.color||"#2563EB"} onChange={e=>updateStyle(validKey,"color",e.target.value)}
                style={{width:36,height:36,border:"2px solid #E2E8F0",borderRadius:8,cursor:"pointer",padding:2,background:"#fff"}}/>
            </div>
            <div style={{display:"flex",gap:5,flexWrap:"wrap",justifyContent:"flex-end",maxWidth:220}}>
              {PRESET_COLORS.map(c=>(
                <button key={c} onClick={()=>updateStyle(validKey,"color",c)}
                  style={{width:18,height:18,borderRadius:"50%",background:c,border:style.color===c?"3px solid #1E3A5F":"2px solid transparent",cursor:"pointer",padding:0,flexShrink:0}}/>
              ))}
            </div>
          </div>
        </div>

        {/* Ticker summary — per sleeve */}
        <div style={{padding:"14px 20px",background:"#fff",borderBottom:`1px solid ${sc}15`}}>
          <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:10}}>Ticker Coverage</div>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
            {[["opportunistic",opp],["defensive",def]].map(([sk,sleeve])=>(
              <div key={sk} style={{border:`1px solid ${sleeve.color}33`,borderRadius:10,padding:"12px 14px",background:sleeve.color+"05"}}>
                <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                  <div style={{width:8,height:8,borderRadius:"50%",background:sleeve.color}}/>
                  <span style={{fontWeight:700,fontSize:12,color:"#1E3A5F"}}>{sleeve.label}</span>
                  <span style={{marginLeft:"auto",fontSize:11,fontWeight:700,color:tickerCount(sk)>0?"#16A34A":"#94A3B8"}}>
                    {tickerCount(sk)} ticker{tickerCount(sk)!==1?"s":""}
                  </span>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {sleeve.assets.map(a => {
                    const tickers = style.tickers?.[a.id] || [];
                    const status  = splitStatus(a.id);
                    return (
                      <div key={a.id} style={{display:"flex",alignItems:"center",gap:6}}>
                        <div style={{width:5,height:5,borderRadius:"50%",background:a.color,flexShrink:0}}/>
                        <span style={{fontSize:11,color:"#64748B",flex:1}}>{a.label}</span>
                        {tickers.length > 0 ? (
                          <div style={{display:"flex",alignItems:"center",gap:4}}>
                            <div style={{display:"flex",gap:3,flexWrap:"wrap",justifyContent:"flex-end"}}>
                              {tickers.map(t=>(
                                <span key={t} style={{fontSize:9,padding:"1px 5px",borderRadius:4,background:a.color+"12",border:`1px solid ${a.color+"33"}`,color:a.color,fontWeight:700}}>{t}</span>
                              ))}
                            </div>
                            {status==="warn" && <span style={{fontSize:9,color:"#DC2626",fontWeight:700}}>⚠</span>}
                            {status==="ok"   && <span style={{fontSize:9,color:"#16A34A",fontWeight:700}}>✓</span>}
                          </div>
                        ) : (
                          <span style={{fontSize:9,color:"#CBD5E1",fontStyle:"italic"}}>no tickers</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
          <div style={{marginTop:10,fontSize:11,color:"#94A3B8",lineHeight:1.6}}>
            Tickers and split percentages are assigned in <strong style={{color:"#2563EB"}}>Admin → Tickers</strong>. 
            The coverage summary above reflects current assignments for this style.
          </div>
        </div>

        {/* Sleeve weights override summary */}
        {(() => {
          const sw = style.sleeveWeights;
          if (!sw || (Object.keys(sw.opportunistic||{}).length + Object.keys(sw.defensive||{}).length) === 0) return (
            <div style={{padding:"12px 20px",background:"#fff",borderBottom:`1px solid ${sc}15`,display:"flex",alignItems:"center",gap:8}}>
              <span style={{fontSize:11,color:"#94A3B8"}}>Sleeve weights:</span>
              <span style={{fontSize:11,color:"#94A3B8",fontStyle:"italic"}}>using defaults</span>
              <span style={{fontSize:11,color:"#2563EB",marginLeft:"auto",cursor:"pointer",fontWeight:600}}
                onClick={()=>{ /* navigate to sleeves tab */ }}>
                Edit in Sleeve Weights →
              </span>
            </div>
          );
          const overrides = [];
          Object.entries(sw).forEach(([sk,assetMap])=>{
            Object.entries(assetMap).forEach(([aid,w])=>{
              const a = allAssets(engine).find(x=>x.id===aid);
              if(a) overrides.push({label:a.label,color:a.color,weight:w,sleeve:engine.sleeves[sk]?.label});
            });
          });
          return (
            <div style={{padding:"12px 20px",background:"#FFFBEB",borderBottom:`1px solid #FDE68A`}}>
              <div style={{fontSize:10,fontWeight:700,color:"#92400E",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:6}}>
                Custom Sleeve Weights ({overrides.length} override{overrides.length!==1?"s":""})
              </div>
              <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                {overrides.map((o,i)=>(
                  <span key={i} style={{fontSize:10,padding:"2px 9px",borderRadius:10,background:o.color+"12",border:`1px solid ${o.color+"44"}`,color:o.color,fontWeight:700}}>
                    {o.label} {o.weight}%
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Actions footer */}
        <div style={{padding:"12px 20px",background:"#F8FAFC",display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <button onClick={()=>duplicateStyle(validKey)}
            style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:8,color:"#1E3A5F",padding:"7px 16px",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
            ⎘ Duplicate Style
          </button>
          <div style={{flex:1}}/>
          {styleKeys.length > 1 && (
            <button onClick={()=>removeStyle(validKey)}
              style={{background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:8,color:"#DC2626",padding:"7px 16px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
              Delete Style
            </button>
          )}
        </div>
      </div>

      {/* ── All styles overview table ── */}
      {styleKeys.length > 1 && (
        <div style={{marginTop:24}}>
          <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.8px",marginBottom:10}}>All Styles Overview</div>
          <div style={{border:"1px solid #E2E8F0",borderRadius:12,overflow:"hidden"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:"#F8FAFC"}}>
                  {["Style","Description","Color","Opp Tickers","Def Tickers","Sleeve Weights"].map(h=>(
                    <th key={h} style={{padding:"9px 14px",textAlign:"left",fontWeight:700,color:"#94A3B8",borderBottom:"2px solid #E2E8F0",fontSize:10,textTransform:"uppercase",letterSpacing:"0.5px",whiteSpace:"nowrap"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {styleKeys.map((key,ri)=>{
                  const s  = engine.implementationStyles[key];
                  const sw = s.sleeveWeights;
                  const overrideCount = sw ? Object.values(sw).reduce((n,m)=>n+Object.keys(m).length,0) : 0;
                  const oppTix = engine.sleeves.opportunistic.assets.reduce((n,a)=>n+(s.tickers?.[a.id]?.length||0),0);
                  const defTix = engine.sleeves.defensive.assets.reduce((n,a)=>n+(s.tickers?.[a.id]?.length||0),0);
                  return (
                    <tr key={key} style={{background:ri%2===0?"#fff":"#FAFBFC",cursor:"pointer"}} onClick={()=>setActiveKey(key)}>
                      <td style={{padding:"10px 14px",borderBottom:"1px solid #F4F6F9"}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <div style={{width:10,height:10,borderRadius:"50%",background:s.color,flexShrink:0}}/>
                          <span style={{fontWeight:700,color:"#1E3A5F"}}>{key}</span>
                        </div>
                      </td>
                      <td style={{padding:"10px 14px",color:"#64748B",borderBottom:"1px solid #F4F6F9",maxWidth:200,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.description||"—"}</td>
                      <td style={{padding:"10px 14px",borderBottom:"1px solid #F4F6F9"}}>
                        <div style={{width:18,height:18,borderRadius:4,background:s.color}}/>
                      </td>
                      <td style={{padding:"10px 14px",borderBottom:"1px solid #F4F6F9",fontWeight:700,color:oppTix>0?"#16A34A":"#94A3B8"}}>{oppTix}</td>
                      <td style={{padding:"10px 14px",borderBottom:"1px solid #F4F6F9",fontWeight:700,color:defTix>0?"#16A34A":"#94A3B8"}}>{defTix}</td>
                      <td style={{padding:"10px 14px",borderBottom:"1px solid #F4F6F9"}}>
                        {overrideCount > 0
                          ? <span style={{fontSize:10,fontWeight:700,color:"#D97706",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:10,padding:"2px 8px"}}>{overrideCount} override{overrideCount!==1?"s":""}</span>
                          : <span style={{fontSize:10,color:"#94A3B8",fontStyle:"italic"}}>defaults</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// AUDIT LOG TAB
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// TRADING RULES TAB — DCA config + future trading restrictions
// ─────────────────────────────────────────────────────────────
function TradingRulesTab({ engine, setEngine }) {
  const FF  = "'Outfit',sans-serif";
  const dca = engine.dcaConfig || {};

  const ALL_INTERVALS = [
    { id:"weekly",     label:"Weekly" },
    { id:"biweekly",   label:"Bi-weekly" },
    { id:"monthly",    label:"Monthly" },
    { id:"quarterly",  label:"Quarterly" },
    { id:"semiannual", label:"Semi-annual" },
    { id:"annual",     label:"Annual" },
  ];
  const MAX_UNITS = ["weeks","months","quarters","years"];

  const setDCA = (key, val) => setEngine(prev => ({
    ...prev,
    dcaConfig: { ...(prev.dcaConfig||{}), [key]: val },
  }));

  const toggleInterval = (id) => {
    const cur = dca.allowedIntervals || [];
    const next = cur.includes(id) ? cur.filter(x=>x!==id) : [...cur, id];
    if (next.length === 0) return; // must keep at least one
    setDCA("allowedIntervals", next);
  };

  const sel  = { ...inp, cursor:"pointer" };
  const lbl  = { fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6, display:"block" };

  return (
    <div style={{padding:24,fontFamily:FF}}>
      <h2 style={{fontSize:20,fontWeight:300,color:"#1E3A5F",margin:"0 0 4px"}}>Trading Rules</h2>
      <p style={{fontSize:12,color:"#64748B",margin:"0 0 24px"}}>Configure restrictions and parameters for advisor trading tools</p>

      {/* DCA Card */}
      <div style={card}>
        <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:20}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15,fontWeight:700,color:"#1E3A5F"}}>Dollar Cost Averaging</div>
            <div style={{fontSize:11,color:"#64748B",marginTop:2}}>Control advisor access to DCA scheduling when deploying new client cash</div>
          </div>
          <button onClick={()=>setDCA("enabled",!dca.enabled)}
            style={{padding:"7px 18px",background:dca.enabled?"#059669":"#E2E8F0",border:"none",borderRadius:8,color:dca.enabled?"#fff":"#64748B",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
            {dca.enabled?"✓ Enabled":"Disabled"}
          </button>
        </div>

        {dca.enabled&&(
          <>
            {/* Max deployment period */}
            <div style={{marginBottom:20}}>
              <label style={lbl}>Maximum Deployment Period</label>
              <div style={{display:"flex",gap:10,alignItems:"center"}}>
                <input type="number" min={1} max={520} value={dca.maxPeriod||12}
                  onChange={e=>setDCA("maxPeriod",Math.max(1,parseInt(e.target.value)||1))}
                  style={{...inp,width:90,textAlign:"center"}}/>
                <select value={dca.maxUnit||"months"} onChange={e=>setDCA("maxUnit",e.target.value)} style={{...sel,width:140}}>
                  {MAX_UNITS.map(u=><option key={u} value={u}>{u.charAt(0).toUpperCase()+u.slice(1)}</option>)}
                </select>
                <span style={{fontSize:11,color:"#94A3B8"}}>Advisors cannot schedule DCA beyond this limit</span>
              </div>
            </div>

            {/* Allowed intervals */}
            <div>
              <label style={lbl}>Allowed Deployment Intervals</label>
              <div style={{fontSize:11,color:"#94A3B8",marginBottom:10}}>Check the intervals advisors are permitted to select. At least one must remain enabled.</div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                {ALL_INTERVALS.map(iv=>{
                  const on = (dca.allowedIntervals||[]).includes(iv.id);
                  return (
                    <button key={iv.id} onClick={()=>toggleInterval(iv.id)}
                      style={{padding:"7px 16px",background:on?"#EFF6FF":"#F8FAFC",border:`1.5px solid ${on?"#2563EB":"#E2E8F0"}`,borderRadius:8,color:on?"#2563EB":"#94A3B8",fontSize:12,fontWeight:on?700:500,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
                      {on?"✓ ":""}{iv.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Summary */}
            <div style={{marginTop:16,padding:"10px 14px",background:"#F0FDF4",border:"1px solid #6EE7B7",borderRadius:8,fontSize:11,color:"#065F46"}}>
              Advisors can schedule DCA deployments at{" "}
              <strong>{(dca.allowedIntervals||[]).map(id=>ALL_INTERVALS.find(x=>x.id===id)?.label).filter(Boolean).join(", ")}</strong>{" "}
              intervals, up to a maximum of <strong>{dca.maxPeriod} {dca.maxUnit}</strong>.
            </div>
          </>
        )}
        {!dca.enabled&&(
          <div style={{padding:"12px 16px",background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:8,fontSize:11,color:"#DC2626"}}>
            DCA is disabled — advisors will not see the Dollar Cost Averaging strategy on the Trading page.
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ALLOCATOR SETTINGS TAB — controls for allocator behaviour
// ─────────────────────────────────────────────────────────────
function AllocatorSettingsTab({ engine, setEngine }) {
  const FF  = "'Outfit',sans-serif";
  const s   = engine.allocatorSettings || {};

  const set = (key, val) => setEngine(prev => ({
    ...prev,
    allocatorSettings: { ...(prev.allocatorSettings||{}), [key]: val },
  }));

  const lbl  = { fontSize:10, fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:6, display:"block" };

  return (
    <div style={{padding:24, fontFamily:FF}}>
      <h2 style={{fontSize:20, fontWeight:300, color:"#1E3A5F", margin:"0 0 4px"}}>Allocator Settings</h2>
      <p style={{fontSize:12, color:"#64748B", margin:"0 0 24px"}}>Control advisor capabilities within the Allocator tool</p>

      {/* Ticker Editing */}
      <div style={card}>
        <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15, fontWeight:700, color:"#1E3A5F"}}>Edit Tickers</div>
            <div style={{fontSize:11, color:"#64748B", marginTop:2}}>Allow advisors to override default ticker selections per sleeve using the "Edit Tickers ›" button</div>
          </div>
          <button onClick={()=>set("allowTickerEditing", s.allowTickerEditing===false ? true : false)}
            style={{padding:"7px 18px", background:s.allowTickerEditing!==false?"#059669":"#E2E8F0", border:"none", borderRadius:8, color:s.allowTickerEditing!==false?"#fff":"#64748B", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FF, transition:"all 0.15s"}}>
            {s.allowTickerEditing!==false?"✓ Enabled":"Disabled"}
          </button>
        </div>
        <div style={{padding:"10px 14px", background:s.allowTickerEditing!==false?"#F0FDF4":"#FFF5F5", border:`1px solid ${s.allowTickerEditing!==false?"#6EE7B7":"#FCA5A5"}`, borderRadius:8, fontSize:11, color:s.allowTickerEditing!==false?"#065F46":"#DC2626"}}>
          {s.allowTickerEditing!==false
            ? "✓ Advisors can see and use the \"Edit Tickers ›\" button to customise ticker selections per sleeve."
            : "✕ The \"Edit Tickers ›\" button is hidden — advisors use model defaults only."}
        </div>
      </div>

      {/* Blend Ratio */}
      <div style={card}>
        <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15, fontWeight:700, color:"#1E3A5F"}}>Blend Ratio Adjustment</div>
            <div style={{fontSize:11, color:"#64748B", marginTop:2}}>Allow advisors to change the Opportunistic / Defensive blend slider</div>
          </div>
          <button onClick={()=>set("allowBlendAdjust", s.allowBlendAdjust===false ? true : false)}
            style={{padding:"7px 18px", background:s.allowBlendAdjust!==false?"#059669":"#E2E8F0", border:"none", borderRadius:8, color:s.allowBlendAdjust!==false?"#fff":"#64748B", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FF, transition:"all 0.15s"}}>
            {s.allowBlendAdjust!==false?"✓ Enabled":"Disabled"}
          </button>
        </div>
        <div style={{padding:"10px 14px", background:s.allowBlendAdjust!==false?"#F0FDF4":"#FFF5F5", border:`1px solid ${s.allowBlendAdjust!==false?"#6EE7B7":"#FCA5A5"}`, borderRadius:8, fontSize:11, color:s.allowBlendAdjust!==false?"#065F46":"#DC2626"}}>
          {s.allowBlendAdjust!==false
            ? "✓ Advisors can adjust the Opportunistic/Defensive blend ratio freely."
            : "✕ Blend ratio is locked — advisors use the model default blend only."}
        </div>
      </div>

      {/* Implementation Style */}
      <div style={card}>
        <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15, fontWeight:700, color:"#1E3A5F"}}>Implementation Style Selection</div>
            <div style={{fontSize:11, color:"#64748B", marginTop:2}}>Allow advisors to switch between Flagship, Systematic, and Index Passive styles</div>
          </div>
          <button onClick={()=>set("allowStyleChange", s.allowStyleChange===false ? true : false)}
            style={{padding:"7px 18px", background:s.allowStyleChange!==false?"#059669":"#E2E8F0", border:"none", borderRadius:8, color:s.allowStyleChange!==false?"#fff":"#64748B", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FF, transition:"all 0.15s"}}>
            {s.allowStyleChange!==false?"✓ Enabled":"Disabled"}
          </button>
        </div>
        <div style={{padding:"10px 14px", background:s.allowStyleChange!==false?"#F0FDF4":"#FFF5F5", border:`1px solid ${s.allowStyleChange!==false?"#6EE7B7":"#FCA5A5"}`, borderRadius:8, fontSize:11, color:s.allowStyleChange!==false?"#065F46":"#DC2626"}}>
          {s.allowStyleChange!==false
            ? "✓ Advisors can select any available implementation style."
            : "✕ Implementation style is locked — advisors use the model default style only."}
        </div>
      </div>

      {/* Client Directed Holdings */}
      <div style={card}>
        <div style={{display:"flex", alignItems:"center", gap:12, marginBottom:12}}>
          <div style={{flex:1}}>
            <div style={{fontSize:15, fontWeight:700, color:"#1E3A5F"}}>Client Directed Holdings</div>
            <div style={{fontSize:11, color:"#64748B", marginTop:2}}>Allow advisors to add client-directed overrides that bypass all keep rules including non-taxable restrictions</div>
          </div>
          <button onClick={()=>set("allowClientDirected", s.allowClientDirected===false ? true : false)}
            style={{padding:"7px 18px", background:s.allowClientDirected!==false?"#059669":"#E2E8F0", border:"none", borderRadius:8, color:s.allowClientDirected!==false?"#fff":"#64748B", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:FF, transition:"all 0.15s"}}>
            {s.allowClientDirected!==false?"✓ Enabled":"Disabled"}
          </button>
        </div>
        <div style={{padding:"10px 14px", background:s.allowClientDirected!==false?"#F0FDF4":"#FFF5F5", border:`1px solid ${s.allowClientDirected!==false?"#6EE7B7":"#FCA5A5"}`, borderRadius:8, fontSize:11, color:s.allowClientDirected!==false?"#065F46":"#DC2626"}}>
          {s.allowClientDirected!==false
            ? "✓ Advisors can enter client-directed holdings to override keep eligibility rules."
            : "✕ Client-directed holdings panel is hidden — keep rules are strictly enforced."}
        </div>
      </div>
    </div>
  );
}

const AUDIT_EVENT_META = {
  login:            { label:"Login",              color:"#2563EB", icon:"🔑" },
  logout:           { label:"Logout",             color:"#64748B", icon:"🚪" },
  client_saved:     { label:"Client Saved",       color:"#059669", icon:"💾" },
  client_deleted:   { label:"Client Deleted",     color:"#DC2626", icon:"🗑"  },
  alloc_exported:   { label:"Allocation Exported",color:"#7C3AED", icon:"📄" },
  engine_changed:   { label:"Engine Config",      color:"#D97706", icon:"⚙️" },
  ticker_imported:  { label:"Ticker Import",      color:"#0891B2", icon:"📥" },
  approved_imported:{ label:"Approved Import",    color:"#0891B2", icon:"📥" },
  sleeve_imported:  { label:"Sleeve Import",      color:"#0891B2", icon:"📥" },
  client_shared:    { label:"Client Shared",      color:"#7C3AED", icon:"↗" },
};

function AuditLogTab({ engine, setEngine }) {
  const [search,     setSearch]     = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterUser, setFilterUser] = useState("all");
  const [dateFrom,   setDateFrom]   = useState("");
  const [dateTo,     setDateTo]     = useState("");
  const [page,       setPage]       = useState(1);
  const PAGE_SIZE = 25;

  const log = [...(engine.auditLog || [])].reverse();
  const users = [...new Map(log.map(e => [e.userId, e.userName])).entries()].map(([id,name])=>({id,name}));

  const filtered = log.filter(e => {
    if (filterType !== "all" && e.type !== filterType) return false;
    if (filterUser !== "all" && e.userId !== filterUser) return false;
    if (search && !e.detail?.toLowerCase().includes(search.toLowerCase()) && !e.userName?.toLowerCase().includes(search.toLowerCase())) return false;
    if (dateFrom && new Date(e.timestamp) < new Date(dateFrom)) return false;
    if (dateTo   && new Date(e.timestamp) > new Date(dateTo + "T23:59:59")) return false;
    return true;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  const exportCSV = () => {
    const headers = ["Timestamp","Event Type","User","Role","Detail"];
    const rows = filtered.map(e => [
      new Date(e.timestamp).toLocaleString(),
      AUDIT_EVENT_META[e.type]?.label || e.type,
      e.userName, e.userRole,
      '"' + (e.detail||"").replace(/"/g,'""') + '"'
    ]);
    const csv = [headers,...rows].map(r=>r.join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href=url; a.download=`audit_log_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const thS = { padding:"9px 14px",fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",background:"#F8FAFC",borderBottom:"2px solid #E2E8F0",textAlign:"left",whiteSpace:"nowrap" };
  const tdS = { padding:"10px 14px",borderBottom:"1px solid #F4F6F9",fontSize:12,verticalAlign:"middle" };
  const selS = { border:"1px solid #E2E8F0",borderRadius:8,padding:"7px 10px",fontSize:12,color:"#1E293B",outline:"none",fontFamily:"'Outfit',sans-serif",background:"#fff",cursor:"pointer" };

  return (
    <div>
      {/* ── AI Override Summary ── */}
      {(()=>{
        const aiEvents = log.filter(e => e.type === "ai_statement_review");
        if (!aiEvents.length) return null;
        // Group by advisor
        const byAdvisor = {};
        aiEvents.forEach(e => {
          const k = e.userId || e.userName || "Unknown";
          if (!byAdvisor[k]) byAdvisor[k] = { name: e.userName || "Unknown", count: 0, last: e.timestamp };
          byAdvisor[k].count++;
          if (e.timestamp > byAdvisor[k].last) byAdvisor[k].last = e.timestamp;
        });
        const advisors = Object.values(byAdvisor).sort((a,b) => b.count - a.count);
        return (
          <div style={{background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:10,padding:"12px 16px",marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#5B21B6",marginBottom:8}}>🤖 AI Statement Review Usage</div>
            <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
              {advisors.map((a,i)=>(
                <div key={i} style={{display:"flex",alignItems:"center",gap:8,padding:"5px 12px",background:"#fff",border:"1px solid #DDD6FE",borderRadius:8}}>
                  <span style={{fontSize:11,fontWeight:700,color:"#5B21B6"}}>{a.name}</span>
                  <span style={{fontSize:11,color:"#7C3AED",fontWeight:600}}>{a.count} override{a.count!==1?"s":""}</span>
                  <span style={{fontSize:9,color:"#94A3B8"}}>last: {new Date(a.last).toLocaleDateString()}</span>
                </div>
              ))}
              <div style={{fontSize:10,color:"#7C3AED",display:"flex",alignItems:"center",gap:4,marginLeft:"auto"}}>
                <span style={{fontWeight:700}}>Total: {aiEvents.length}</span>
              </div>
            </div>
          </div>
        );
      })()}
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Search events or users…" style={{flex:1,minWidth:180,...selS}}/>
        <select value={filterType} onChange={e=>{setFilterType(e.target.value);setPage(1);}} style={selS}>
          <option value="all">All event types</option>
          {Object.entries(AUDIT_EVENT_META).map(([k,v])=><option key={k} value={k}>{v.icon} {v.label}</option>)}
        </select>
        <select value={filterUser} onChange={e=>{setFilterUser(e.target.value);setPage(1);}} style={selS}>
          <option value="all">All users</option>
          {users.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <input type="date" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setPage(1);}} style={{...selS,width:140}} title="From"/>
        <input type="date" value={dateTo}   onChange={e=>{setDateTo(e.target.value);  setPage(1);}} style={{...selS,width:140}} title="To"/>
        <button onClick={exportCSV} style={{background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,color:"#2563EB",padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>⬇ Export CSV</button>
        {log.length > 0 && <button onClick={()=>setEngine(prev=>({...prev,auditLog:[]}))} style={{background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:8,color:"#DC2626",padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>Clear Log</button>}
      </div>
      <div style={{display:"flex",gap:8,marginBottom:16,flexWrap:"wrap",alignItems:"center"}}>
        <span style={{fontSize:11,color:"#94A3B8"}}>{filtered.length} event{filtered.length!==1?"s":""}{filtered.length!==log.length?` of ${log.length} total`:""}</span>
        {(search||filterType!=="all"||filterUser!=="all"||dateFrom||dateTo) && <button onClick={()=>{setSearch("");setFilterType("all");setFilterUser("all");setDateFrom("");setDateTo("");setPage(1);}} style={{fontSize:11,color:"#2563EB",background:"none",border:"none",cursor:"pointer",fontFamily:"'Outfit',sans-serif",padding:0,fontWeight:600}}>× Clear filters</button>}
      </div>
      {filtered.length === 0 ? (
        <div style={{textAlign:"center",padding:"60px 24px",background:"#F8FAFC",borderRadius:12,border:"1px solid #E2E8F0"}}>
          <div style={{fontSize:28,marginBottom:10}}>📋</div>
          <div style={{fontWeight:700,fontSize:14,color:"#1E3A5F",marginBottom:6}}>{log.length===0?"No events logged yet":"No events match your filters"}</div>
          <div style={{fontSize:12,color:"#64748B"}}>{log.length===0?"Events will appear here as advisors and admins use the system.":"Try adjusting your search or filters."}</div>
        </div>
      ) : (
        <>
          <div style={{border:"1px solid #E2E8F0",borderRadius:12,overflow:"hidden",background:"#fff"}}>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",minWidth:640}}>
                <thead>
                  <tr>
                    <th style={{...thS,minWidth:160}}>Timestamp</th>
                    <th style={{...thS,minWidth:150}}>Event</th>
                    <th style={{...thS,minWidth:140}}>User</th>
                    <th style={{...thS,minWidth:80,textAlign:"center"}}>Role</th>
                    <th style={thS}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((e,ri) => {
                    const meta = AUDIT_EVENT_META[e.type] || {label:e.type,color:"#94A3B8",icon:"•"};
                    return (
                      <tr key={e.id||ri} style={{background:ri%2===0?"#fff":"#FAFBFC"}}>
                        <td style={{...tdS,color:"#64748B",fontSize:11,whiteSpace:"nowrap"}}>
                          {new Date(e.timestamp).toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
                          <span style={{marginLeft:6,color:"#94A3B8"}}>{new Date(e.timestamp).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}</span>
                        </td>
                        <td style={tdS}>
                          <span style={{display:"inline-flex",alignItems:"center",gap:5,fontSize:11,padding:"3px 9px",borderRadius:10,background:meta.color+"12",border:`1px solid ${meta.color+"33"}`,color:meta.color,fontWeight:700,whiteSpace:"nowrap"}}>{meta.icon} {meta.label}</span>
                        </td>
                        <td style={{...tdS,fontWeight:600,color:"#1E3A5F"}}>{e.userName||"—"}</td>
                        <td style={{...tdS,textAlign:"center"}}>
                          <span style={{fontSize:10,padding:"2px 8px",borderRadius:10,background:e.userRole==="admin"?"#7C3AED12":"#2563EB12",color:e.userRole==="admin"?"#7C3AED":"#2563EB",fontWeight:700}}>{e.userRole||"—"}</span>
                        </td>
                        <td style={{...tdS,color:"#475569",maxWidth:320,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={e.detail}>{e.detail||"—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {totalPages > 1 && (
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",gap:8,marginTop:14}}>
              <button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} style={{padding:"6px 12px",borderRadius:7,border:"1px solid #E2E8F0",background:"#fff",color:page===1?"#CBD5E1":"#1E3A5F",cursor:page===1?"not-allowed":"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>← Prev</button>
              <span style={{fontSize:12,color:"#64748B"}}>Page {page} of {totalPages}</span>
              <button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} style={{padding:"6px 12px",borderRadius:7,border:"1px solid #E2E8F0",background:"#fff",color:page===totalPages?"#CBD5E1":"#1E3A5F",cursor:page===totalPages?"not-allowed":"pointer",fontSize:12,fontFamily:"'Outfit',sans-serif"}}>Next →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// MODELS TAB — investment committee builds primary combos + custom swaps
// Auto-swaps (sibling styles) are derived at runtime; custom swaps are stored per slot
// ─────────────────────────────────────────────────────────────
function ModelsTab({ engine, setEngine, addAudit=()=>{} }) {
  const styleKeys  = Object.keys(engine.implementationStyles);
  const sleeveKeys = [
    { id:"opportunistic", label: engine.sleeves.opportunistic.label, color: engine.sleeves.opportunistic.color },
    { id:"defensive",     label: engine.sleeves.defensive.label,     color: engine.sleeves.defensive.color     },
  ];
  const assets = {
    opportunistic: engine.sleeves.opportunistic.assets,
    defensive:     engine.sleeves.defensive.assets,
  };

  const [activeStyle,  setActiveStyle]  = useState(styleKeys[0] || "");
  const [activeSleeve, setActiveSleeve] = useState("opportunistic");

  const models = engine.models || {};
  const getSlot = (style, sleeve, assetId) =>
    models?.[style]?.[sleeve]?.[assetId] || { primary:[], customSwaps:[] };

  const setSlot = (style, sleeve, assetId, slot) => {
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.models) n.models = {};
      if (!n.models[style]) n.models[style] = {};
      if (!n.models[style][sleeve]) n.models[style][sleeve] = {};
      n.models[style][sleeve][assetId] = slot;
      return n;
    });
  };

  const [uploadState,  setUploadState]  = useState(null); // null | "preview" | "importing"
  const [uploadRows,   setUploadRows]   = useState([]);   // parsed rows before commit
  const [uploadMode,   setUploadMode]   = useState("merge"); // "merge" | "replace"
  const [uploadErrors, setUploadErrors] = useState([]);
  const csvRef = useRef(null);

  // ── Parse uploaded CSV/Excel into row objects ──
  const handleModelUpload = async (file) => {
    setUploadErrors([]);
    try {
      const XLSX = await ensureSheetJS();
      const buf  = await file.arrayBuffer();
      const wb   = XLSX.read(buf, { type:"array" });
      const ws   = wb.Sheets[wb.SheetNames[0]];
      const raw  = XLSX.utils.sheet_to_json(ws, { defval:"" });
      // Normalize headers
      const rows = raw.map((r,i) => {
        const norm = {};
        Object.entries(r).forEach(([k,v]) => { norm[k.toLowerCase().trim().replace(/[\s/]+/g,"_")] = String(v).trim(); });
        // Flexible column name matching
        const style     = norm.style      || norm.implementation_style || norm.impl_style || "";
        const sleeve    = (norm.sleeve    || norm.sleeve_name           || "").toLowerCase();
        const assetId   = norm.asset_class|| norm.asset_id              || norm.assetclass || "";
        const type      = (norm.type      || norm.combo_type            || "primary").toLowerCase();
        const swapName  = norm.swap_name  || norm.name                  || "";
        const ticker    = (norm.ticker    || norm.symbol                || "").toUpperCase();
        const split     = parseFloat(norm.split || norm.split_pct || norm["split_%"] || norm.weight || 0) || 0;
        return { _row:i+2, style, sleeve, assetId, type, swapName, ticker, split };
      }).filter(r => r.style && r.ticker);

      // Validate
      const errs = [];
      const knownStyles  = Object.keys(engine.implementationStyles);
      const knownSleeves = ["opportunistic","defensive"];
      const allAssetIds  = [...engine.sleeves.opportunistic.assets, ...engine.sleeves.defensive.assets].map(a=>a.id);
      rows.forEach(r => {
        if (!knownStyles.includes(r.style))   errs.push(`Row ${r._row}: unknown style "${r.style}"`);
        if (!knownSleeves.some(s=>r.sleeve.includes(s.slice(0,4)))) errs.push(`Row ${r._row}: unrecognised sleeve "${r.sleeve}"`);
        if (!allAssetIds.includes(r.assetId)) errs.push(`Row ${r._row}: unknown asset class "${r.assetId}"`);
        if (!["primary","custom"].includes(r.type)) errs.push(`Row ${r._row}: type must be "primary" or "custom", got "${r.type}"`);
        if (r.type==="custom" && !r.swapName) errs.push(`Row ${r._row}: custom rows require a Swap Name`);
        if (r.split<=0||r.split>100) errs.push(`Row ${r._row}: split must be 1–100, got ${r.split}`);
      });

      // Normalise sleeve to full id
      const resolved = rows.map(r => ({
        ...r,
        sleeve: knownSleeves.find(s=>r.sleeve.includes(s.slice(0,4))) || r.sleeve,
      }));

      setUploadRows(resolved);
      setUploadErrors(errs);
      setUploadState("preview");
    } catch(e) {
      setUploadErrors([`Parse error: ${e.message}`]);
      setUploadState("preview");
    }
  };

  // ── Commit parsed rows into engine.models ──
  const commitUpload = () => {
    setUploadState("importing");
    setEngine(prev => {
      const n = JSON.parse(JSON.stringify(prev));
      if (!n.models) n.models = {};

      // If replace mode: clear primary+customSwaps for every style/sleeve/asset touched
      if (uploadMode === "replace") {
        const touched = new Set(uploadRows.map(r=>`${r.style}|${r.sleeve}|${r.assetId}`));
        touched.forEach(key => {
          const [style,sleeve,assetId] = key.split("|");
          if (!n.models[style]) n.models[style] = {};
          if (!n.models[style][sleeve]) n.models[style][sleeve] = {};
          n.models[style][sleeve][assetId] = { primary:[], customSwaps:[] };
        });
      }

      // Group rows by slot + type + swapName
      uploadRows.forEach(r => {
        const { style, sleeve, assetId, type, swapName, ticker, split } = r;
        if (!n.models[style]) n.models[style] = {};
        if (!n.models[style][sleeve]) n.models[style][sleeve] = {};
        if (!n.models[style][sleeve][assetId]) n.models[style][sleeve][assetId] = { primary:[], customSwaps:[] };
        const slot = n.models[style][sleeve][assetId];
        if (type === "primary") {
          slot.primary = slot.primary || [];
          slot.primary.push({ ticker, split });
        } else {
          slot.customSwaps = slot.customSwaps || [];
          let sw = slot.customSwaps.find(s=>s.name===swapName);
          if (!sw) { sw = { id:`csw_${Date.now()}_${Math.random().toString(36).slice(2,6)}`, name:swapName, tickers:[] }; slot.customSwaps.push(sw); }
          sw.tickers.push({ ticker, split });
        }
      });
      return n;
    });
    addAudit("engine_changed", `Models uploaded via CSV (${uploadMode}) — ${uploadRows.length} rows`);
    setUploadState(null);
    setUploadRows([]);
  };

  const [editingSwap, setEditingSwap] = useState(null);
  const [swapDraft,   setSwapDraft]   = useState({ name:"", tickers:[{ticker:"",split:100}] });

  const openNewSwap  = (assetId, style, sleeve) => { setSwapDraft({name:"",tickers:[{ticker:"",split:100}]}); setEditingSwap({assetId,swapId:null,style,sleeve}); };
  const openEditSwap = (sw, assetId, style, sleeve) => { setSwapDraft(JSON.parse(JSON.stringify({name:sw.name,tickers:sw.tickers}))); setEditingSwap({assetId,swapId:sw.id,style,sleeve}); };

  const saveSwap = () => {
    if (!editingSwap) return;
    const { assetId, swapId, style, sleeve } = editingSwap;
    const slot = getSlot(style, sleeve, assetId);
    const existing = slot.customSwaps || [];
    const total = swapDraft.tickers.reduce((s,r)=>s+(parseFloat(r.split)||0),0);
    if (Math.round(total)!==100 || !swapDraft.name.trim()) return;
    const entry = { id:swapId||`csw_${Date.now()}`, name:swapDraft.name.trim(), tickers:swapDraft.tickers.filter(r=>r.ticker.trim()) };
    const newSwaps = swapId ? existing.map(s=>s.id===swapId?entry:s) : [...existing, entry];
    setSlot(style, sleeve, assetId, {...slot, customSwaps:newSwaps});
    addAudit("engine_changed", `Custom swap "${entry.name}" ${swapId?"updated":"added"} — ${style}/${sleeve}/${assetId}`);
    setEditingSwap(null);
  };

  const removeCustomSwap = (swapId, assetId, style, sleeve) => {
    const slot = getSlot(style, sleeve, assetId);
    setSlot(style, sleeve, assetId, {...slot, customSwaps:(slot.customSwaps||[]).filter(s=>s.id!==swapId)});
    addAudit("engine_changed", `Custom swap removed — ${style}/${sleeve}/${assetId}`);
  };

  const swapTotal = swapDraft.tickers.reduce((s,r)=>s+(parseFloat(r.split)||0),0);
  const swapOk    = Math.round(swapTotal)===100 && swapDraft.name.trim() && swapDraft.tickers.every(r=>r.ticker.trim());

  const PrimaryEditor = ({ assetId, style, sleeve }) => {
    const slot    = getSlot(style, sleeve, assetId);
    const primary = slot.primary || [];
    const updateTicker = (i, field, val) => setSlot(style, sleeve, assetId, {...slot, primary:primary.map((r,ri)=>ri===i?{...r,[field]:val}:r)});
    const addRow    = () => setSlot(style, sleeve, assetId, {...slot, primary:[...primary,{ticker:"",split:0}]});
    const removeRow = (i) => setSlot(style, sleeve, assetId, {...slot, primary:primary.filter((_,ri)=>ri!==i)});
    const normalize = () => {
      const total = primary.reduce((s,r)=>s+(parseFloat(r.split)||0),0);
      if (!total) return;
      const norm = primary.map(r=>({...r,split:Math.round((parseFloat(r.split)||0)/total*100)}));
      const diff = 100 - norm.reduce((s,r)=>s+r.split,0);
      if (norm.length) norm[0].split += diff;
      setSlot(style, sleeve, assetId, {...slot, primary:norm});
    };
    const total = primary.reduce((s,r)=>s+(parseFloat(r.split)||0),0);
    const ok    = Math.round(total)===100 && primary.every(r=>r.ticker.trim());
    return (
      <div style={{marginBottom:4}}>
        {primary.map((row,i)=>(
          <div key={i} style={{display:"flex",gap:5,alignItems:"center",marginBottom:4}}>
            <input value={row.ticker} onChange={e=>updateTicker(i,"ticker",e.target.value.toUpperCase())}
              placeholder="TICK" style={{width:72,border:"1px solid #E2E8F0",borderRadius:6,padding:"5px 7px",fontSize:11,fontFamily:"'Outfit',sans-serif",outline:"none",fontWeight:700,textTransform:"uppercase"}}/>
            <input value={row.split} onChange={e=>updateTicker(i,"split",e.target.value)}
              type="number" min="0" max="100" placeholder="%" style={{width:52,border:"1px solid #E2E8F0",borderRadius:6,padding:"5px 7px",fontSize:11,fontFamily:"'Outfit',sans-serif",outline:"none",textAlign:"right"}}/>
            <span style={{fontSize:10,color:"#94A3B8"}}>%</span>
            <button onClick={()=>removeRow(i)} disabled={primary.length===1}
              style={{background:"none",border:"none",color:primary.length===1?"#E2E8F0":"#94A3B8",cursor:primary.length===1?"not-allowed":"pointer",fontSize:13,padding:"0 2px",lineHeight:1}}
              onMouseEnter={e=>{if(primary.length>1)e.target.style.color="#DC2626";}} onMouseLeave={e=>{e.target.style.color="#94A3B8";}}>x</button>
          </div>
        ))}
        <div style={{display:"flex",gap:6,alignItems:"center",marginTop:2}}>
          <button onClick={addRow} style={{fontSize:10,padding:"3px 8px",background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:5,color:"#475569",cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>+ Ticker</button>
          <button onClick={normalize} disabled={!total} style={{fontSize:10,padding:"3px 8px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:5,color:"#2563EB",cursor:total?"pointer":"not-allowed",fontFamily:"'Outfit',sans-serif"}}>Normalize</button>
          <span style={{fontSize:10,color:ok?"#059669":"#DC2626",fontWeight:700,marginLeft:"auto"}}>{Math.round(total)}%</span>
        </div>
      </div>
    );
  };

  const sleeveMeta = sleeveKeys.find(s=>s.id===activeSleeve);
  const FF = "'Outfit',sans-serif";

  // For a given style+sleeve+asset, collect sibling styles' primary combos as read-only swap preview
  const getSiblingCombos = (style, sleeve, assetId) =>
    styleKeys
      .filter(sk => sk !== style)
      .map(sk => ({
        styleName: sk,
        color: engine.implementationStyles[sk]?.color || "#64748B",
        tickers: models?.[sk]?.[sleeve]?.[assetId]?.primary || [],
      }))
      .filter(s => s.tickers.length > 0);

  return (
    <div>
      {/* ── Custom swap editor modal ── */}
      {editingSwap && (
        <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.5)",zIndex:1999,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(3px)"}}>
          <div style={{background:"#fff",borderRadius:14,width:"min(440px,96vw)",boxShadow:"0 20px 60px rgba(0,0,0,0.18)",overflow:"hidden"}}>
            <div style={{background:"linear-gradient(135deg,#7C3AED,#5B21B6)",padding:"16px 20px"}}>
              <div style={{fontSize:14,fontWeight:700,color:"#fff",fontFamily:FF}}>{editingSwap.swapId?"Edit":"New"} Custom Swap</div>
              <div style={{fontSize:10,color:"#DDD6FE",marginTop:2,fontFamily:FF}}>{editingSwap.style} · {editingSwap.sleeve} · {editingSwap.assetId}</div>
            </div>
            <div style={{padding:"18px 20px"}}>
              <div style={{marginBottom:14}}>
                <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:5,fontFamily:FF}}>Swap Name</div>
                <input value={swapDraft.name} onChange={e=>setSwapDraft(p=>({...p,name:e.target.value}))}
                  placeholder="e.g. Tax-Loss Harvesting Alt" style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
              </div>
              <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:8,fontFamily:FF}}>Tickers & Locked Splits</div>
              {swapDraft.tickers.map((row,i)=>(
                <div key={i} style={{display:"flex",gap:8,alignItems:"center",marginBottom:7}}>
                  <input value={row.ticker} onChange={e=>setSwapDraft(p=>({...p,tickers:p.tickers.map((r,ri)=>ri===i?{...r,ticker:e.target.value.toUpperCase()}:r)}))}
                    placeholder="TICK" style={{flex:1,border:"1px solid #E2E8F0",borderRadius:6,padding:"7px 9px",fontSize:12,fontFamily:FF,outline:"none",fontWeight:700,textTransform:"uppercase"}}/>
                  <input value={row.split} onChange={e=>setSwapDraft(p=>({...p,tickers:p.tickers.map((r,ri)=>ri===i?{...r,split:e.target.value}:r)}))}
                    type="number" min="0" max="100" placeholder="%" style={{width:64,border:"1px solid #E2E8F0",borderRadius:6,padding:"7px 9px",fontSize:12,fontFamily:FF,outline:"none",textAlign:"right"}}/>
                  <span style={{fontSize:11,color:"#94A3B8"}}>%</span>
                  <button onClick={()=>setSwapDraft(p=>({...p,tickers:p.tickers.filter((_,ri)=>ri!==i)}))} disabled={swapDraft.tickers.length===1}
                    style={{background:"none",border:"none",color:swapDraft.tickers.length===1?"#E2E8F0":"#94A3B8",cursor:"pointer",fontSize:15,padding:0,lineHeight:1}}
                    onMouseEnter={e=>{if(swapDraft.tickers.length>1)e.target.style.color="#DC2626";}} onMouseLeave={e=>{e.target.style.color="#94A3B8";}}>×</button>
                </div>
              ))}
              <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:16}}>
                <button onClick={()=>setSwapDraft(p=>({...p,tickers:[...p.tickers,{ticker:"",split:0}]}))}
                  style={{fontSize:11,padding:"5px 10px",background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:6,color:"#475569",cursor:"pointer",fontFamily:FF}}>+ Ticker</button>
                <button onClick={()=>{
                  const tot=swapDraft.tickers.reduce((s,r)=>s+(parseFloat(r.split)||0),0);
                  if(!tot)return;
                  const norm=swapDraft.tickers.map(r=>({...r,split:Math.round((parseFloat(r.split)||0)/tot*100)}));
                  const diff=100-norm.reduce((s,r)=>s+r.split,0);
                  if(norm.length)norm[0].split+=diff;
                  setSwapDraft(p=>({...p,tickers:norm}));
                }} style={{fontSize:11,padding:"5px 10px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:6,color:"#2563EB",cursor:"pointer",fontFamily:FF}}>Normalize</button>
                <span style={{marginLeft:"auto",fontSize:12,fontWeight:700,color:Math.round(swapTotal)===100?"#059669":"#DC2626"}}>{Math.round(swapTotal)}%</span>
              </div>
              {Math.round(swapTotal)!==100&&<div style={{fontSize:10,color:"#DC2626",marginBottom:10}}>⚠ Splits must total exactly 100%</div>}
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setEditingSwap(null)} style={{flex:1,padding:"9px",background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:8,color:"#64748B",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:FF}}>Cancel</button>
                <button onClick={saveSwap} disabled={!swapOk}
                  style={{flex:2,padding:"9px",background:swapOk?"#7C3AED":"#E2E8F0",border:"none",borderRadius:8,color:swapOk?"#fff":"#94A3B8",fontSize:12,fontWeight:700,cursor:swapOk?"pointer":"not-allowed",fontFamily:FF}}>
                  {editingSwap.swapId?"Save Changes":"Add Swap"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CSV upload preview modal ── */}
      {uploadState==="preview" && (
        <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",zIndex:1998,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(3px)"}}>
          <div style={{background:"#fff",borderRadius:14,width:"min(700px,96vw)",maxHeight:"85vh",boxShadow:"0 20px 60px rgba(0,0,0,0.2)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
            <div style={{background:"linear-gradient(135deg,#1E3A5F,#2563EB)",padding:"16px 20px",flexShrink:0}}>
              <div style={{fontSize:14,fontWeight:700,color:"#fff",fontFamily:FF}}>Models Upload Preview</div>
              <div style={{fontSize:10,color:"#BFDBFE",marginTop:2,fontFamily:FF}}>{uploadRows.length} rows parsed · {uploadErrors.length} issue{uploadErrors.length!==1?"s":""}</div>
            </div>
            <div style={{padding:"16px 20px",overflowY:"auto",flex:1}}>
              {uploadErrors.length>0&&(
                <div style={{background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:8,padding:"10px 14px",marginBottom:14}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#DC2626",marginBottom:6}}>⚠ Validation Issues</div>
                  {uploadErrors.map((e,i)=><div key={i} style={{fontSize:11,color:"#DC2626",marginBottom:2}}>{e}</div>)}
                  <div style={{fontSize:10,color:"#94A3B8",marginTop:6}}>Rows with errors will be skipped on import.</div>
                </div>
              )}

              {/* Mode selector */}
              <div style={{display:"flex",gap:8,marginBottom:14}}>
                {[["merge","Merge","Add/update only slots in this file — leave others untouched"],["replace","Replace","Clear primary + custom swaps for every slot in this file first"]].map(([val,label,desc])=>(
                  <button key={val} onClick={()=>setUploadMode(val)}
                    style={{flex:1,padding:"10px 12px",borderRadius:8,border:`2px solid ${uploadMode===val?"#2563EB":"#E2E8F0"}`,background:uploadMode===val?"#EFF6FF":"#fff",cursor:"pointer",fontFamily:FF,textAlign:"left",transition:"all 0.15s"}}>
                    <div style={{fontSize:12,fontWeight:700,color:uploadMode===val?"#1E3A5F":"#64748B"}}>{label}</div>
                    <div style={{fontSize:10,color:"#94A3B8",marginTop:2,lineHeight:1.4}}>{desc}</div>
                  </button>
                ))}
              </div>

              {/* Preview table */}
              {uploadRows.length>0&&(
                <div style={{border:"1px solid #E2E8F0",borderRadius:8,overflow:"hidden"}}>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11,minWidth:520}}>
                      <thead>
                        <tr style={{background:"#F8FAFC"}}>
                          {["Style","Sleeve","Asset Class","Type","Swap Name","Ticker","Split %"].map(h=>(
                            <th key={h} style={{padding:"7px 10px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"left",borderBottom:"1px solid #E2E8F0",whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {uploadRows.slice(0,50).map((r,i)=>(
                          <tr key={i} style={{background:i%2===0?"#fff":"#FAFBFC"}}>
                            <td style={{padding:"5px 10px",borderBottom:"1px solid #F4F6F9",color:"#1E3A5F",fontWeight:600}}>{r.style}</td>
                            <td style={{padding:"5px 10px",borderBottom:"1px solid #F4F6F9",color:"#475569"}}>{r.sleeve}</td>
                            <td style={{padding:"5px 10px",borderBottom:"1px solid #F4F6F9",color:"#475569"}}>{r.assetId}</td>
                            <td style={{padding:"5px 10px",borderBottom:"1px solid #F4F6F9"}}>
                              <span style={{fontSize:9,padding:"1px 7px",borderRadius:6,background:r.type==="primary"?"#EFF6FF":"#F5F3FF",color:r.type==="primary"?"#2563EB":"#7C3AED",border:`1px solid ${r.type==="primary"?"#BFDBFE":"#DDD6FE"}`,fontWeight:700}}>{r.type}</span>
                            </td>
                            <td style={{padding:"5px 10px",borderBottom:"1px solid #F4F6F9",color:"#7C3AED",fontWeight:r.swapName?600:400,fontStyle:r.swapName?"normal":"italic"}}>{r.swapName||"—"}</td>
                            <td style={{padding:"5px 10px",borderBottom:"1px solid #F4F6F9",fontWeight:700,color:"#1E3A5F"}}>{r.ticker}</td>
                            <td style={{padding:"5px 10px",borderBottom:"1px solid #F4F6F9",textAlign:"right",color:"#475569"}}>{r.split}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {uploadRows.length>50&&<div style={{padding:"8px 12px",background:"#F8FAFC",fontSize:10,color:"#94A3B8",borderTop:"1px solid #E2E8F0"}}>Showing first 50 of {uploadRows.length} rows</div>}
                </div>
              )}
            </div>
            <div style={{padding:"14px 20px",borderTop:"1px solid #E2E8F0",display:"flex",gap:8,flexShrink:0}}>
              <button onClick={()=>{setUploadState(null);setUploadRows([]);setUploadErrors([]);}}
                style={{flex:1,padding:"9px",background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:8,color:"#64748B",fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:FF}}>Cancel</button>
              <button onClick={commitUpload} disabled={uploadRows.length===0}
                style={{flex:2,padding:"9px",background:uploadRows.length>0?"#1E3A5F":"#E2E8F0",border:"none",borderRadius:8,color:uploadRows.length>0?"#fff":"#94A3B8",fontSize:12,fontWeight:700,cursor:uploadRows.length>0?"pointer":"not-allowed",fontFamily:FF}}>
                Import {uploadRows.length} Row{uploadRows.length!==1?"s":""} ({uploadMode})
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{display:"flex",gap:4,marginBottom:16,flexWrap:"wrap"}}>
        {styleKeys.map(sk=>{
          const sc = engine.implementationStyles[sk]?.color||"#1E3A5F";
          const isActive = activeStyle===sk;
          return (
            <button key={sk} onClick={()=>setActiveStyle(sk)}
              style={{padding:"7px 14px",borderRadius:8,border:`2px solid ${isActive?sc:"#E2E8F0"}`,background:isActive?sc+"15":"#fff",color:isActive?sc:"#64748B",fontWeight:isActive?700:500,fontSize:12,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
              {sk}
            </button>
          );
        })}
      </div>

      <div style={{display:"flex",gap:4,marginBottom:16}}>
        {sleeveKeys.map(sl=>{
          const isActive = activeSleeve===sl.id;
          return (
            <button key={sl.id} onClick={()=>setActiveSleeve(sl.id)}
              style={{padding:"6px 14px",borderRadius:7,border:`2px solid ${isActive?sl.color:"#E2E8F0"}`,background:isActive?sl.color+"12":"#fff",color:isActive?sl.color:"#64748B",fontWeight:isActive?700:500,fontSize:11,cursor:"pointer",fontFamily:FF}}>
              {sl.label}
            </button>
          );
        })}
      </div>

      <div style={{border:"1px solid #E2E8F0",borderRadius:12,overflow:"hidden",background:"#fff"}}>
        <div style={{background:(sleeveMeta?.color||"#2563EB")+"12",padding:"10px 16px",borderBottom:`2px solid ${(sleeveMeta?.color||"#2563EB")}33`,display:"flex",gap:8,alignItems:"center"}}>
          <div style={{width:9,height:9,borderRadius:"50%",background:sleeveMeta?.color,flexShrink:0}}/>
          <span style={{fontSize:11,fontWeight:800,color:sleeveMeta?.color,letterSpacing:"1px",textTransform:"uppercase"}}>{sleeveMeta?.label}</span>
          <span style={{fontSize:10,color:"#94A3B8",marginLeft:4}}>· {activeStyle}</span>
          <span style={{fontSize:10,color:"#94A3B8",marginLeft:"auto"}}>Primary = default · Auto-swaps from sibling styles · Custom swaps admin-defined</span>
          <input ref={csvRef} type="file" accept=".csv,.xlsx,.xls" style={{display:"none"}} onChange={e=>{if(e.target.files[0])handleModelUpload(e.target.files[0]);e.target.value="";}}/>
          <button onClick={()=>{
            const header="Style,Sleeve,Asset Class,Type,Swap Name,Ticker,Split %\n";
            const eg=[
              "Flagship,opportunistic,us_eq,primary,,FCNTX,60",
              "Flagship,opportunistic,us_eq,primary,,VWUSX,40",
              "Flagship,opportunistic,us_eq,custom,Tax-Loss Alt,VTI,70",
              "Flagship,opportunistic,us_eq,custom,Tax-Loss Alt,SCHB,30",
            ].join("\n");
            const blob=new Blob([header+eg],{type:"text/csv"});
            const a=document.createElement("a");a.href=URL.createObjectURL(blob);a.download="models_template.csv";a.click();
          }} style={{fontSize:10,padding:"4px 10px",background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:6,color:"#475569",cursor:"pointer",fontFamily:FF,whiteSpace:"nowrap",flexShrink:0}}>⬇ Template</button>
          <button onClick={()=>csvRef.current?.click()}
            style={{fontSize:10,padding:"4px 10px",background:"#1E3A5F",border:"none",borderRadius:6,color:"#fff",cursor:"pointer",fontFamily:FF,whiteSpace:"nowrap",fontWeight:700,flexShrink:0}}>⬆ Upload CSV</button>
        </div>
        <table style={{width:"100%",borderCollapse:"collapse"}}>
          <thead>
            <tr style={{background:"#F8FAFC"}}>
              <th style={{padding:"9px 16px",fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"left",borderBottom:"1px solid #E2E8F0",width:"14%"}}>Asset Class</th>
              <th style={{padding:"9px 16px",fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"left",borderBottom:"1px solid #E2E8F0",width:"26%"}}>Primary Combo</th>
              <th style={{padding:"9px 16px",fontSize:10,fontWeight:700,color:"#64748B",textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"left",borderBottom:"1px solid #E2E8F0",width:"28%"}}>
                Auto-Swaps <span style={{fontSize:9,fontWeight:400,color:"#94A3B8"}}>(sibling styles · read-only)</span>
              </th>
              <th style={{padding:"9px 16px",fontSize:10,fontWeight:700,color:"#7C3AED",textTransform:"uppercase",letterSpacing:"0.5px",textAlign:"left",borderBottom:"1px solid #E2E8F0"}}>
                Custom Swaps <span style={{fontSize:9,fontWeight:400,color:"#94A3B8"}}>(locked splits)</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {(assets[activeSleeve]||[]).map((a,ri)=>{
              const siblings    = getSiblingCombos(activeStyle, activeSleeve, a.id);
              const slot        = getSlot(activeStyle, activeSleeve, a.id);
              const customSwaps = slot.customSwaps || [];
              return (
                <tr key={a.id} style={{background:ri%2===0?"#fff":"#FAFBFC",verticalAlign:"top"}}>
                  <td style={{padding:"12px 16px",borderBottom:"1px solid #F4F6F9"}}>
                    <div style={{display:"flex",alignItems:"center",gap:7}}>
                      <div style={{width:7,height:7,borderRadius:"50%",background:a.color,flexShrink:0}}/>
                      <span style={{fontSize:12,fontWeight:700,color:"#1E3A5F"}}>{a.label}</span>
                    </div>
                  </td>
                  <td style={{padding:"12px 16px",borderBottom:"1px solid #F4F6F9"}}>
                    <PrimaryEditor assetId={a.id} style={activeStyle} sleeve={activeSleeve}/>
                  </td>
                  <td style={{padding:"12px 16px",borderBottom:"1px solid #F4F6F9"}}>
                    {siblings.length===0
                      ? <span style={{fontSize:10,color:"#CBD5E1",fontStyle:"italic"}}>No other styles defined</span>
                      : siblings.map(sib=>(
                          <div key={sib.styleName} style={{display:"flex",alignItems:"center",gap:6,marginBottom:5,padding:"4px 8px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:6}}>
                            <span style={{fontSize:10,fontWeight:700,minWidth:80,color:sib.color}}>{sib.styleName}</span>
                            <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                              {sib.tickers.map((t,ti)=>(
                                <span key={ti} style={{fontSize:9,padding:"1px 6px",background:sib.color+"12",border:`1px solid ${sib.color}33`,borderRadius:5,color:sib.color,fontWeight:700}}>
                                  {t.ticker}{sib.tickers.length>1?" "+t.split+"%":""}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))
                    }
                  </td>
                  <td style={{padding:"12px 16px",borderBottom:"1px solid #F4F6F9"}}>
                    {customSwaps.length===0
                      ? <div style={{fontSize:10,color:"#C4B5FD",fontStyle:"italic",marginBottom:6}}>None defined</div>
                      : customSwaps.map(sw=>(
                          <div key={sw.id} style={{display:"flex",alignItems:"center",gap:5,marginBottom:5,padding:"4px 8px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:6}}>
                            <span style={{fontSize:10,fontWeight:700,color:"#6D28D9",flex:1}}>{sw.name}</span>
                            <div style={{display:"flex",gap:3,flexWrap:"wrap"}}>
                              {sw.tickers.map((t,ti)=>(
                                <span key={ti} style={{fontSize:9,padding:"1px 6px",background:"#EDE9FE",border:"1px solid #DDD6FE",borderRadius:5,color:"#5B21B6",fontWeight:700}}>
                                  {t.ticker}{sw.tickers.length>1?" "+t.split+"%":""}
                                </span>
                              ))}
                            </div>
                            <button onClick={()=>openEditSwap(sw,a.id,activeStyle,activeSleeve)}
                              style={{fontSize:9,padding:"2px 6px",background:"#fff",border:"1px solid #DDD6FE",borderRadius:4,color:"#7C3AED",cursor:"pointer",fontFamily:FF,whiteSpace:"nowrap"}}>Edit</button>
                            <button onClick={()=>removeCustomSwap(sw.id,a.id,activeStyle,activeSleeve)}
                              style={{background:"none",border:"none",color:"#C4B5FD",cursor:"pointer",fontSize:12,padding:0,lineHeight:1}}
                              onMouseEnter={e=>{e.target.style.color="#DC2626";}} onMouseLeave={e=>{e.target.style.color="#C4B5FD";}}>×</button>
                          </div>
                        ))
                    }
                    <button onClick={()=>openNewSwap(a.id,activeStyle,activeSleeve)}
                      style={{fontSize:10,padding:"3px 9px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:5,color:"#7C3AED",cursor:"pointer",fontFamily:FF,fontWeight:700}}>+ Custom Swap</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PORTFOLIO COMPARATOR
// Compares a client's current holdings (from analyzer) against
// the proposed allocation (from allocator) side by side.
// ─────────────────────────────────────────────────────────────
function PortfolioComparator({ engine, loadedClient, allocSnapshot, keptHoldings=[], clientDirected=[], onGoAllocator, onGoAnalyzer }) {
  // Version picker state — which saved allocation to compare (overridden by live snapshot)
  const savedAllocations = loadedClient?.savedAllocations || [];
  const [selectedAllocIdx, setSelectedAllocIdx] = useState(() => Math.max(0, savedAllocations.length - 1));

  // Fall back to most recent saved allocation if no live snapshot
  const latestSaved = savedAllocations.length
    ? savedAllocations[selectedAllocIdx] || savedAllocations[savedAllocations.length - 1]
    : null;
  const effectiveSnapshot = allocSnapshot || latestSaved;
  const { resolveName } = usePIIVault();
  const FF = "'Outfit',sans-serif";
  const assets = allAssets(engine);
  const tv     = effectiveSnapshot?.totalValue || loadedClient?.totalPortfolioValue || 0;

  // ── Guard: need both sides ──
  const hasClient = !!loadedClient;
  const hasAlloc  = !!(effectiveSnapshot?.alloc && Object.keys(effectiveSnapshot.alloc).length > 0);
  const linked    = hasClient && hasAlloc && (effectiveSnapshot?.loadedClientId === loadedClient?.id || !!latestSaved);

  if (!hasClient || !hasAlloc) {
    return (
      <div style={{maxWidth:760,margin:"40px auto",padding:"0 24px",fontFamily:FF}}>
        <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:"48px 32px",textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:16}}>⇄</div>
          <div style={{fontSize:20,fontWeight:300,color:"#1E3A5F",marginBottom:8}}>Portfolio Comparator</div>
          <div style={{fontSize:13,color:"#64748B",marginBottom:32,lineHeight:1.7}}>
            {!hasClient
              ? <>No client is loaded. Load a client from the Analyzer or Clients tab,<br/>then build and save an allocation in the Allocator.</>
              : <>
                  <strong style={{color:"#1E3A5F"}}>{resolveName(loadedClient.name)||"This client"}</strong> has no saved allocation yet.<br/>
                  Go to the Allocator, build a portfolio, then click <strong style={{color:"#7C3AED"}}>⇄ Compare</strong> to save and jump here in one step.
                </>
            }
          </div>
          <div style={{display:"flex",gap:12,justifyContent:"center",flexWrap:"wrap"}}>
            {!hasClient&&(
              <button onClick={onGoAnalyzer} style={{padding:"10px 22px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,color:"#2563EB",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:FF}}>
                📋 Go to Analyzer
              </button>
            )}
            <button onClick={onGoAllocator} style={{padding:"10px 22px",background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:FF}}>
              📊 {hasClient?"Build Allocation →":"Go to Allocator"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Build current holdings by asset class ──
  const current = {}; // { [assetId]: { pct, dollars, holdings:[...] } }
  const clientHoldings = loadedClient.holdings || [];
  const totalCurrent   = clientHoldings.reduce((s,h)=>s+(h.marketValue||0),0) || tv;
  assets.forEach(a => { current[a.id] = { pct:0, dollars:0, holdings:[] }; });
  clientHoldings.forEach(h => {
    const assetId = normalizeAssetId(h.assetClass, engine);
    if (!current[assetId]) current[assetId] = { pct:0, dollars:0, holdings:[] };
    current[assetId].dollars += h.marketValue||0;
    current[assetId].holdings.push(h);
  });
  assets.forEach(a => {
    current[a.id].pct = totalCurrent > 0 ? (current[a.id].dollars / totalCurrent * 100) : 0;
  });

  // ── Build proposed allocation by asset class ──
  const proposed = {}; // { [assetId]: { pct, dollars, tickers:{[t]:split} } }
  assets.forEach(a => {
    const targetPct = effectiveSnapshot.alloc[a.id] || 0;
    proposed[a.id] = {
      pct: targetPct,
      dollars: tv * targetPct / 100,
      tickers: effectiveSnapshot.selTickers?.[a.id] || {},
    };
  });

  // ── Blended ER helpers ──
  const blendedER = (tickerMap) => {
    const entries = Object.entries(tickerMap);
    if (!entries.length) return null;
    let er = 0, total = 0;
    entries.forEach(([t, split]) => {
      const r = engine.tickerData?.[t]?.expenseRatio;
      if (r != null) { er += r * (Number(split)||0); total += Number(split)||0; }
    });
    return total > 0 ? er / total : null;
  };

  const currentERByAsset = {};
  assets.forEach(a => {
    const tMap = {};
    current[a.id].holdings.forEach(h => { if(h.ticker) tMap[h.ticker] = 100/current[a.id].holdings.length; });
    currentERByAsset[a.id] = blendedER(tMap);
  });

  // ── Sleeve groupings ──
  const sleeves = [
    { key:"opportunistic", label:engine.sleeves.opportunistic.label, color:engine.sleeves.opportunistic.color, assets:engine.sleeves.opportunistic.assets },
    { key:"defensive",     label:engine.sleeves.defensive.label,     color:engine.sleeves.defensive.color,     assets:engine.sleeves.defensive.assets     },
  ];

  // ── Transition plan ──
  const keptHoldingIds = new Set(keptHoldings);
  const cdTickers = new Set((clientDirected||[]).map(cd=>cd.ticker?.toUpperCase()));
  const proposedTickers = new Set(
    assets.flatMap(a => Object.keys(proposed[a.id]?.tickers||{}))
  );
  const currentTickerMap = {};
  clientHoldings.forEach(h => { if(h.ticker) currentTickerMap[h.ticker] = h; });

  // Keep: client directed (always) OR advisor selected (non-taxable blocked) OR in proposed model (non-taxable blocked)
  const toKeep = clientHoldings.filter(h => {
    if (!h.ticker) return false;
    if (cdTickers.has(h.ticker?.toUpperCase())) return true; // client directed overrides everything
    if (h.taxExempt) return false; // non-taxable cannot be kept unless client directed
    return keptHoldingIds.has(h.id) || proposedTickers.has(h.ticker);
  });
  const keptTickers = new Set(toKeep.map(h => h.ticker));

  // Sell: not kept — includes short positions (negative market value)
  const toSell = clientHoldings.filter(h =>
    h.ticker && !keptTickers.has(h.ticker) && h.marketValue != null && h.marketValue !== 0
  );

  const toBuy  = assets.flatMap(a =>
    Object.entries(proposed[a.id]?.tickers||{}).map(([ticker, split]) => {
      const targetDollars = tv * (proposed[a.id].pct/100) * (Number(split)/100);
      const existing = currentTickerMap[ticker];
      const delta = targetDollars - (existing?.marketValue||0);
      return { ticker, assetId:a.id, assetLabel:a.label, assetColor:a.color, targetDollars, existingDollars:existing?.marketValue||0, delta, existing };
    }).filter(r => r.delta > 100)
  );

  const clientDisplayName = resolveName(loadedClient.name) || "Client";
  const styleColor = engine.implementationStyles[effectiveSnapshot.implStyle]?.color || "#1E3A5F";

  return (
    <div style={{maxWidth:1060,margin:"0 auto",padding:"24px 24px 56px",fontFamily:FF}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:24,fontWeight:300,color:"#1E3A5F",letterSpacing:"0.3px"}}>Portfolio Comparison</div>
          <div style={{fontSize:11,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"1.5px",marginTop:3,fontWeight:600}}>
            Current Holdings vs Proposed Allocation · {clientDisplayName}
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {!linked&&<span style={{fontSize:10,padding:"3px 10px",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8,color:"#92400E",fontWeight:600}}>⚠ Allocation built for a different client</span>}
          {/* Allocation version picker when multiple saved and no live snapshot */}
          {savedAllocations.length>1&&!allocSnapshot&&(
            <select value={selectedAllocIdx} onChange={e=>setSelectedAllocIdx(Number(e.target.value))}
              style={{fontSize:11,padding:"4px 10px",border:"1px solid #E2E8F0",borderRadius:7,background:"#F8FAFC",color:"#475569",fontFamily:FF,outline:"none",cursor:"pointer"}}>
              {savedAllocations.map((s,i)=>(
                <option key={s.id} value={i}>
                  {s.implStyle} {s.oppPct}/{100-s.oppPct} · {new Date(s.savedAt).toLocaleDateString()}{i===savedAllocations.length-1?" (latest)":""}
                </option>
              ))}
            </select>
          )}
          {allocSnapshot&&<span style={{fontSize:9,padding:"2px 8px",background:"#F5F3FF",border:"1px solid #DDD6FE",borderRadius:6,color:"#7C3AED",fontWeight:700}}>Live</span>}
          <span style={{fontSize:11,padding:"4px 12px",background:styleColor+"15",border:`1px solid ${styleColor}33`,borderRadius:8,color:styleColor,fontWeight:700}}>{effectiveSnapshot.implStyle}</span>
          <span style={{fontSize:11,padding:"4px 12px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,color:"#475569",fontWeight:600}}>{effectiveSnapshot.oppPct}/{100-effectiveSnapshot.oppPct} blend</span>
          {tv>0&&<span style={{fontSize:11,padding:"4px 12px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,color:"#475569",fontWeight:600}}>{fmt$(tv)}</span>}
          <button onClick={onGoAllocator} style={{fontSize:11,padding:"5px 12px",background:"#fff",border:"1px solid #E2E8F0",borderRadius:7,color:"#475569",cursor:"pointer",fontFamily:FF,fontWeight:600}}>← Allocator</button>
        </div>
      </div>

      {/* ── SECTION 1: Asset class overview ── */}
      <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:14,overflow:"hidden",marginBottom:20}}>
        <div style={{background:"#F8FAFC",padding:"12px 20px",borderBottom:"1px solid #E2E8F0",display:"flex",gap:16,alignItems:"center"}}>
          <span style={{fontSize:13,fontWeight:700,color:"#1E3A5F"}}>Asset Class Overview</span>
          <span style={{fontSize:10,color:"#94A3B8",marginLeft:"auto"}}>Current holdings vs proposed allocation</span>
        </div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:620}}>
            <thead>
              <tr style={{background:"#F8FAFC"}}>
                <th style={{padding:"9px 16px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"left",borderBottom:"1px solid #E2E8F0",width:"18%"}}>Asset Class</th>
                <th style={{padding:"9px 12px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Current %</th>
                <th style={{padding:"9px 12px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Current $</th>
                <th style={{padding:"9px 12px",fontSize:9,fontWeight:700,color:"#2563EB",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Target %</th>
                <th style={{padding:"9px 12px",fontSize:9,fontWeight:700,color:"#2563EB",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Target $</th>
                <th style={{padding:"9px 12px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Drift</th>
                <th style={{padding:"9px 16px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>ER Δ</th>
              </tr>
            </thead>
            <tbody>
              {sleeves.map(sl => (
                <React.Fragment key={sl.key}>
                  <tr>
                    <td colSpan={7} style={{padding:"6px 16px",background:sl.color+"12",borderBottom:"1px solid #E2E8F0"}}>
                      <span style={{fontSize:10,fontWeight:800,color:sl.color,letterSpacing:"1px",textTransform:"uppercase"}}>{sl.label}</span>
                    </td>
                  </tr>
                  {sl.assets.map((a,ri) => {
                    const cur  = current[a.id]  || { pct:0, dollars:0 };
                    const prop = proposed[a.id] || { pct:0, dollars:0, tickers:{} };
                    const drift = prop.pct - cur.pct;
                    const curER  = currentERByAsset[a.id];
                    const propER = blendedER(prop.tickers);
                    const erDelta = (curER!=null&&propER!=null) ? propER-curER : null;
                    const driftColor = Math.abs(drift)<1?"#94A3B8":drift>0?"#2563EB":"#DC2626";
                    return (
                      <tr key={a.id} style={{background:ri%2===0?"#fff":"#FAFBFC",borderBottom:"1px solid #F4F6F9"}}>
                        <td style={{padding:"10px 16px"}}>
                          <div style={{display:"flex",alignItems:"center",gap:7}}>
                            <div style={{width:7,height:7,borderRadius:"50%",background:a.color,flexShrink:0}}/>
                            <span style={{fontWeight:600,color:"#1E3A5F"}}>{a.label}</span>
                          </div>
                        </td>
                        <td style={{padding:"10px 12px",textAlign:"right",color:"#475569",fontWeight:600}}>{cur.pct.toFixed(1)}%</td>
                        <td style={{padding:"10px 12px",textAlign:"right",color:"#94A3B8"}}>{fmt$(cur.dollars)}</td>
                        <td style={{padding:"10px 12px",textAlign:"right",color:"#2563EB",fontWeight:700}}>{prop.pct.toFixed(1)}%</td>
                        <td style={{padding:"10px 12px",textAlign:"right",color:"#2563EB"}}>{fmt$(prop.dollars)}</td>
                        <td style={{padding:"10px 12px",textAlign:"right"}}>
                          <span style={{color:driftColor,fontWeight:700}}>
                            {drift>=0?"+":""}{drift.toFixed(1)}%
                          </span>
                          {/* Visual bar */}
                          <div style={{marginTop:3,height:3,borderRadius:2,background:"#F1F5F9",width:60,display:"inline-block",verticalAlign:"middle",marginLeft:6}}>
                            <div style={{height:"100%",borderRadius:2,background:driftColor,width:`${Math.min(Math.abs(drift)/20*100,100)}%`,marginLeft:drift<0?"auto":0}}/>
                          </div>
                        </td>
                        <td style={{padding:"10px 16px",textAlign:"right"}}>
                          {erDelta!=null?(
                            <span style={{fontSize:11,color:erDelta>0?"#DC2626":erDelta<0?"#059669":"#94A3B8",fontWeight:600}}>
                              {erDelta>0?"+":""}{erDelta.toFixed(2)}%
                            </span>
                          ):<span style={{color:"#CBD5E1",fontSize:11}}>—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>


    </div>
  );
}


// ─────────────────────────────────────────────────────────────
// TRADING VIEW — transition plan derived from comparator data
// Sell/Exit · Buy/Add · Keep/Retain with tax impact summary
// ─────────────────────────────────────────────────────────────
function TradingView({ engine, loadedClient, allocSnapshot, keptHoldings=[], clientDirected=[], additionalCash=0, onGoComparator, onGoAllocator, onDirtyChange=()=>{} }) {
  const { isMobile } = useBreakpoint();
  const [tradingNotes, setTradingNotes] = useState("");
  // Notify parent when advisor has unsaved trading work
  const emitTradingDirty = useCallback(() => onDirtyChange(true), [onDirtyChange]);
  const { resolveName } = usePIIVault();
  const FF = "'Outfit',sans-serif";

  const assets         = useMemo(() => allAssets(engine), [engine]);

  // ── Guard: need both sides ──
  const latestSaved = loadedClient?.savedAllocations?.length
    ? loadedClient.savedAllocations[loadedClient.savedAllocations.length-1]
    : null;
  const effectiveSnapshot = allocSnapshot || latestSaved;
  const hasClient = !!loadedClient;
  const hasAlloc  = !!(effectiveSnapshot?.alloc && Object.keys(effectiveSnapshot.alloc).length > 0);

  if (!hasClient || !hasAlloc) {
    return (
      <div style={{maxWidth:760,margin:"40px auto",padding:"0 24px",fontFamily:FF}}>
        <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:16,padding:"48px 32px",textAlign:"center"}}>
          <div style={{fontSize:32,marginBottom:16}}>🔀</div>
          <div style={{fontSize:20,fontWeight:300,color:"#1E3A5F",marginBottom:8}}>Trading</div>
          <div style={{fontSize:13,color:"#64748B",marginBottom:32,lineHeight:1.7}}>
            Complete the Compare step first to generate a transition plan.
          </div>
          <button onClick={onGoComparator} style={{padding:"10px 22px",background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:FF}}>
            ⇄ Go to Compare
          </button>
        </div>
      </div>
    );
  }

  const clientHoldings = loadedClient.holdings || [];
  const tv             = effectiveSnapshot.totalValue || loadedClient.totalPortfolioValue || 0;

  // ── Build proposed tickers ──
  const proposed = {};
  assets.forEach(a => {
    const targetPct = effectiveSnapshot.alloc[a.id] || 0;
    proposed[a.id] = {
      pct: targetPct,
      dollars: tv * targetPct / 100,
      tickers: effectiveSnapshot.selTickers?.[a.id] || {},
    };
  });

  const proposedTickers = new Set(assets.flatMap(a => Object.keys(proposed[a.id]?.tickers||{})));
  const currentTickerMap = {};
  clientHoldings.forEach(h => { if(h.ticker) currentTickerMap[h.ticker] = h; });

  const keptHoldingIds = new Set(keptHoldings);
  const cdTickerSet = new Set((clientDirected||[]).map(cd=>cd.ticker?.toUpperCase()));
  const toKeep = clientHoldings.filter(h => {
    if (!h.ticker) return false;
    if (cdTickerSet.has(h.ticker?.toUpperCase())) return true; // client directed overrides everything
    if (h.taxExempt) return false; // non-taxable cannot be kept unless client directed
    return keptHoldingIds.has(h.id) || proposedTickers.has(h.ticker);
  });
  const keptTickers = new Set(toKeep.map(h => h.ticker?.toUpperCase()));
  const toSell = clientHoldings.filter(h =>
    h.ticker && !keptTickers.has(h.ticker?.toUpperCase()) && h.marketValue != null && h.marketValue !== 0
  );
  const toBuy = assets.flatMap(a =>
    Object.entries(proposed[a.id]?.tickers||{}).map(([ticker, split]) => {
      const targetDollars = tv * (proposed[a.id].pct/100) * (Number(split)/100);
      const existing = currentTickerMap[ticker];
      const delta = targetDollars - (existing?.marketValue||0);
      return { ticker, assetId:a.id, assetLabel:a.label, assetColor:a.color, targetDollars, existingDollars:existing?.marketValue||0, delta, existing };
    }).filter(r => r.delta > 100)
  );

  // ── Tax impact ──
  const taxableGains  = toSell.filter(h=>!h.taxExempt&&(h.gainLoss||0)>0).reduce((s,h)=>s+(h.gainLoss||0),0);
  const taxableLosses = toSell.filter(h=>!h.taxExempt&&(h.gainLoss||0)<0).reduce((s,h)=>s+(h.gainLoss||0),0);
  const ltcgRate = 0.20, stcgRate = 0.37;
  const ltGains  = toSell.filter(h=>!h.taxExempt&&(h.gainLoss||0)>0&&h.holdingPeriod==="Long-term").reduce((s,h)=>s+(h.gainLoss||0),0);
  const stGains  = toSell.filter(h=>!h.taxExempt&&(h.gainLoss||0)>0&&h.holdingPeriod!=="Long-term").reduce((s,h)=>s+(h.gainLoss||0),0);
  const estTax   = ltGains * ltcgRate + stGains * stcgRate;
  const clientDisplayName = resolveName(loadedClient.name) || "Client";
  const styleColor = engine.implementationStyles[effectiveSnapshot.implStyle]?.color || "#1E3A5F";

  return (
    <div style={{maxWidth:1060,margin:"0 auto",padding:"24px 24px 56px",fontFamily:FF}}>

      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:24,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:24,fontWeight:300,color:"#1E3A5F",letterSpacing:"0.3px"}}>Trading</div>
          <div style={{fontSize:11,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"1.5px",marginTop:3,fontWeight:600}}>
            Transition Plan · {clientDisplayName}
          </div>
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          <span style={{fontSize:11,padding:"4px 12px",background:styleColor+"15",border:`1px solid ${styleColor}33`,borderRadius:8,color:styleColor,fontWeight:700}}>{effectiveSnapshot.implStyle}</span>
          <span style={{fontSize:11,padding:"4px 12px",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:8,color:"#475569",fontWeight:600}}>{effectiveSnapshot.oppPct}/{100-effectiveSnapshot.oppPct} blend</span>
          <button onClick={onGoComparator} style={{fontSize:11,padding:"5px 12px",background:"#fff",border:"1px solid #E2E8F0",borderRadius:7,color:"#475569",cursor:"pointer",fontFamily:FF,fontWeight:600}}>← Compare</button>
        </div>
      </div>

      {/* Tax impact banner */}
      {/* ── Allocation Overview ── */}
      {(()=>{
        // Build kept allocation by asset class
        const keptByAsset = {};
        assets.forEach(a => { keptByAsset[a.id] = { dollars:0, holdings:[] }; });
        toKeep.forEach(h => {
          const assetId = normalizeAssetId(h.assetClass, engine);
          if (!keptByAsset[assetId]) keptByAsset[assetId] = { dollars:0, holdings:[] };
          keptByAsset[assetId].dollars += h.marketValue||0;
          keptByAsset[assetId].holdings.push(h);
        });
        const totalKept = toKeep.reduce((s,h)=>s+(h.marketValue||0),0);
        const sleeves = [
          { key:"opportunistic", label:engine.sleeves.opportunistic.label, color:engine.sleeves.opportunistic.color, assets:engine.sleeves.opportunistic.assets },
          { key:"defensive",     label:engine.sleeves.defensive.label,     color:engine.sleeves.defensive.color,     assets:engine.sleeves.defensive.assets     },
        ];
        return (
          <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:14,overflow:"hidden",marginBottom:20}}>
            <div style={{background:"#F8FAFC",padding:"12px 20px",borderBottom:"1px solid #E2E8F0",display:"flex",alignItems:"center",gap:10}}>
              <span style={{fontSize:13,fontWeight:700,color:"#1E3A5F"}}>Allocation Overview</span>
              <span style={{fontSize:10,color:"#94A3B8"}}>Kept position weight vs target — shows how much room remains to deploy</span>
              {tv>0&&<span style={{marginLeft:"auto",fontSize:11,color:"#64748B"}}>Portfolio: <strong style={{color:"#1E3A5F"}}>{fmt$(tv)}</strong></span>}
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12,minWidth:560}}>
                <thead>
                  <tr style={{background:"#F8FAFC"}}>
                    <th style={{padding:"8px 16px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"left",borderBottom:"1px solid #E2E8F0",width:"20%"}}>Asset Class</th>
                    <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#2563EB",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Target %</th>
                    <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#2563EB",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Target $</th>
                    <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#059669",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Kept %</th>
                    <th style={{padding:"8px 12px",fontSize:9,fontWeight:700,color:"#059669",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Kept $</th>
                    <th style={{padding:"8px 16px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #E2E8F0"}}>Room to Deploy</th>
                  </tr>
                </thead>
                <tbody>
                  {sleeves.map(sl=>(
                    <React.Fragment key={sl.key}>
                      <tr>
                        <td colSpan={6} style={{padding:"5px 16px",background:sl.color+"10",borderBottom:"1px solid #E2E8F0"}}>
                          <span style={{fontSize:9,fontWeight:800,color:sl.color,letterSpacing:"1px",textTransform:"uppercase"}}>{sl.label}</span>
                        </td>
                      </tr>
                      {sl.assets.map((a,ri)=>{
                        const targetPct = proposed[a.id]?.pct || 0;
                        const targetDol = tv * targetPct / 100;
                        const keptDol   = keptByAsset[a.id]?.dollars || 0;
                        const keptPct   = tv > 0 ? keptDol / tv * 100 : 0;
                        const roomDol   = Math.max(0, targetDol - keptDol);
                        const roomPct   = Math.max(0, targetPct - keptPct);
                        const over      = keptPct > targetPct + 0.5;
                        const barWidth  = targetPct > 0 ? Math.min(keptPct / targetPct * 100, 100) : 0;
                        return (
                          <tr key={a.id} style={{background:ri%2===0?"#fff":"#FAFBFC",borderBottom:"1px solid #F4F6F9"}}>
                            <td style={{padding:"9px 16px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:7}}>
                                <div style={{width:7,height:7,borderRadius:"50%",background:a.color,flexShrink:0}}/>
                                <span style={{fontWeight:600,color:"#1E3A5F",fontSize:11}}>{a.label}</span>
                              </div>
                            </td>
                            <td style={{padding:"9px 12px",textAlign:"right",color:"#2563EB",fontWeight:700}}>{targetPct.toFixed(1)}%</td>
                            <td style={{padding:"9px 12px",textAlign:"right",color:"#94A3B8",fontSize:11}}>{fmt$(targetDol)}</td>
                            <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:over?"#D97706":"#059669"}}>{keptPct.toFixed(1)}%</td>
                            <td style={{padding:"9px 12px",textAlign:"right",color:"#94A3B8",fontSize:11}}>{fmt$(keptDol)}</td>
                            <td style={{padding:"9px 16px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:8,justifyContent:"flex-end"}}>
                                <span style={{fontSize:11,fontWeight:600,color:over?"#D97706":roomPct>0?"#1E3A5F":"#CBD5E1",minWidth:44,textAlign:"right"}}>
                                  {over?`+${(keptPct-targetPct).toFixed(1)}% over`:roomPct>0?fmt$(roomDol):"—"}
                                </span>
                                {/* Progress bar: how much of the target is already covered by kept holdings */}
                                <div style={{width:60,height:5,borderRadius:3,background:"#F1F5F9",flexShrink:0,overflow:"hidden"}}>
                                  <div style={{height:"100%",borderRadius:3,background:over?"#D97706":"#059669",width:`${barWidth}%`,transition:"width 0.3s"}}/>
                                </div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{background:"#F8FAFC",borderTop:"2px solid #E2E8F0"}}>
                    <td style={{padding:"9px 16px",fontWeight:700,fontSize:11,color:"#1E3A5F"}}>Total</td>
                    <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:"#2563EB"}}>100%</td>
                    <td style={{padding:"9px 12px",textAlign:"right",color:"#94A3B8",fontSize:11}}>{fmt$(tv)}</td>
                    <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:"#059669"}}>{tv>0?(totalKept/tv*100).toFixed(1):0}%</td>
                    <td style={{padding:"9px 12px",textAlign:"right",color:"#94A3B8",fontSize:11}}>{fmt$(totalKept)}</td>
                    <td style={{padding:"9px 16px",textAlign:"right",fontWeight:700,color:"#1E3A5F"}}>{fmt$(Math.max(0,tv-totalKept))} to deploy</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── Tax impact ── */}
      {(taxableGains>0||taxableLosses<0)&&(
        <div style={{background:"#FFFBEB",border:"1px solid #FDE68A",borderRadius:12,padding:"14px 20px",marginBottom:20,display:"flex",gap:20,flexWrap:"wrap",alignItems:"center"}}>
          <div style={{fontSize:13,fontWeight:700,color:"#92400E"}}>⚠ Estimated Tax Impact (Taxable Holdings)</div>
          <div style={{display:"flex",gap:16,flexWrap:"wrap",marginLeft:"auto"}}>
            {taxableGains>0&&<div style={{textAlign:"right"}}><div style={{fontSize:9,color:"#B45309",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px"}}>Gains to Realize</div><div style={{fontSize:14,fontWeight:700,color:"#DC2626"}}>{fmt$(taxableGains)}</div></div>}
            {taxableLosses<0&&<div style={{textAlign:"right"}}><div style={{fontSize:9,color:"#B45309",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px"}}>Losses to Harvest</div><div style={{fontSize:14,fontWeight:700,color:"#059669"}}>{fmt$(Math.abs(taxableLosses))}</div></div>}
            {estTax>0&&<div style={{textAlign:"right"}}><div style={{fontSize:9,color:"#B45309",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.5px"}}>Est. Tax Liability</div><div style={{fontSize:14,fontWeight:700,color:"#DC2626"}}>{fmt$(estTax)}</div><div style={{fontSize:9,color:"#94A3B8"}}>LT:{(ltcgRate*100).toFixed(0)}% · ST:{(stcgRate*100).toFixed(0)}%</div></div>}
          </div>
        </div>
      )}

      {/* Transition plan — 3 columns */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:14,marginBottom:20}}>

        {/* Sell / Exit */}
        <div style={{background:"#fff",border:"1px solid #FCA5A5",borderRadius:12,overflow:"hidden"}}>
          <div style={{background:"#FFF5F5",padding:"10px 14px",borderBottom:"1px solid #FCA5A5",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:12}}>↓</span>
            <span style={{fontSize:12,fontWeight:700,color:"#DC2626"}}>Sell / Exit</span>
            <span style={{marginLeft:"auto",fontSize:10,color:"#94A3B8"}}>{toSell.length} position{toSell.length!==1?"s":""}</span>
          </div>
          <div style={{padding:"8px 10px",maxHeight:400,overflowY:"auto"}}>
            {toSell.length===0?<div style={{fontSize:11,color:"#CBD5E1",fontStyle:"italic",padding:"8px 4px"}}>No exits needed</div>
            :toSell.map((h,i)=>(
              <div key={i} style={{padding:"7px 6px",borderBottom:i<toSell.length-1?"1px solid #FEF2F2":"none"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontWeight:700,fontSize:12,color:"#DC2626"}}>{h.ticker}</span>
                  {h.taxExempt&&<span style={{fontSize:8,padding:"1px 5px",background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:4,color:"#059669",fontWeight:700}}>🛡</span>}
                  <span style={{marginLeft:"auto",fontSize:11,fontWeight:600,color:"#1E3A5F"}}>{fmt$(h.marketValue)}</span>
                </div>
                <div style={{fontSize:10,color:"#64748B",marginTop:2}}>{h.name||h.assetClass}</div>
                {!h.taxExempt&&h.gainLoss!=null&&(
                  <div style={{fontSize:10,color:(h.gainLoss||0)>=0?"#DC2626":"#059669",marginTop:1}}>
                    {(h.gainLoss||0)>=0?"Gain: ":"Loss: "}{fmt$(Math.abs(h.gainLoss||0))}
                    {h.holdingPeriod&&h.holdingPeriod!=="Unknown"&&<span style={{color:"#94A3B8",marginLeft:4}}>{h.holdingPeriod==="Long-term"?"LT":"ST"}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
          {toSell.length>0&&(
            <div style={{padding:"7px 14px",background:"#FFF5F5",borderTop:"1px solid #FCA5A5",fontSize:11,color:"#DC2626",fontWeight:700}}>
              {fmt$(toSell.reduce((s,h)=>s+(h.marketValue||0),0))} to liquidate
            </div>
          )}
        </div>

        {/* Buy / Add */}
        <div style={{background:"#fff",border:"1px solid #6EE7B7",borderRadius:12,overflow:"hidden"}}>
          <div style={{background:"#ECFDF5",padding:"10px 14px",borderBottom:"1px solid #6EE7B7",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:12}}>↑</span>
            <span style={{fontSize:12,fontWeight:700,color:"#059669"}}>Buy / Add</span>
            <span style={{marginLeft:"auto",fontSize:10,color:"#94A3B8"}}>{toBuy.length} position{toBuy.length!==1?"s":""}</span>
          </div>
          <div style={{padding:"8px 10px",maxHeight:400,overflowY:"auto"}}>
            {toBuy.length===0?<div style={{fontSize:11,color:"#CBD5E1",fontStyle:"italic",padding:"8px 4px"}}>No new buys needed</div>
            :toBuy.map((r,i)=>(
              <div key={i} style={{padding:"7px 6px",borderBottom:i<toBuy.length-1?"1px solid #F0FDF4":"none"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontWeight:700,fontSize:12,color:"#059669"}}>{r.ticker}</span>
                  <span style={{fontSize:9,padding:"1px 5px",background:r.assetColor+"15",border:`1px solid ${r.assetColor}33`,borderRadius:4,color:r.assetColor,fontWeight:600}}>{r.assetLabel}</span>
                  <span style={{marginLeft:"auto",fontSize:11,fontWeight:600,color:"#1E3A5F"}}>{fmt$(r.targetDollars)}</span>
                </div>
                {r.existingDollars>0&&(
                  <div style={{fontSize:10,color:"#94A3B8",marginTop:2}}>Already held: {fmt$(r.existingDollars)} · Add: {fmt$(r.delta)}</div>
                )}
              </div>
            ))}
          </div>
          {toBuy.length>0&&(
            <div style={{padding:"7px 14px",background:"#ECFDF5",borderTop:"1px solid #6EE7B7",fontSize:11,color:"#059669",fontWeight:700}}>
              {fmt$(toBuy.reduce((s,r)=>s+r.delta,0))} to deploy
            </div>
          )}
        </div>

        {/* Keep / Retain */}
        <div style={{background:"#fff",border:"1px solid #BFDBFE",borderRadius:12,overflow:"hidden"}}>
          <div style={{background:"#EFF6FF",padding:"10px 14px",borderBottom:"1px solid #BFDBFE",display:"flex",alignItems:"center",gap:6}}>
            <span style={{fontSize:12}}>→</span>
            <span style={{fontSize:12,fontWeight:700,color:"#2563EB"}}>Keep / Retain</span>
            <span style={{marginLeft:"auto",fontSize:10,color:"#94A3B8"}}>{toKeep.length} position{toKeep.length!==1?"s":""}</span>
          </div>
          <div style={{padding:"8px 10px",maxHeight:400,overflowY:"auto"}}>
            {toKeep.length===0?<div style={{fontSize:11,color:"#CBD5E1",fontStyle:"italic",padding:"8px 4px"}}>No holdings marked to keep</div>
            :toKeep.map((h,i)=>(
              <div key={i} style={{padding:"7px 6px",borderBottom:i<toKeep.length-1?"1px solid #EFF6FF":"none"}}>
                <div style={{display:"flex",alignItems:"center",gap:6}}>
                  <span style={{fontWeight:700,fontSize:12,color:"#2563EB"}}>{h.ticker}</span>
                  {keptHoldingIds.has(h.id)&&<span style={{fontSize:8,padding:"1px 5px",background:"#EFF6FF",border:"1px solid #2563EB",borderRadius:4,color:"#2563EB",fontWeight:700}}>✓ kept</span>}
                  {h.taxExempt&&<span style={{fontSize:8,padding:"1px 5px",background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:4,color:"#059669",fontWeight:700}}>🛡</span>}
                  <span style={{marginLeft:"auto",fontSize:11,fontWeight:600,color:"#1E3A5F"}}>{fmt$(h.marketValue)}</span>
                </div>
                <div style={{fontSize:10,color:"#64748B",marginTop:2}}>{h.name||h.assetClass}</div>
              </div>
            ))}
          </div>
          {toKeep.length>0&&(
            <div style={{padding:"7px 14px",background:"#EFF6FF",borderTop:"1px solid #BFDBFE",fontSize:11,color:"#2563EB",fontWeight:700}}>
              {fmt$(toKeep.reduce((s,h)=>s+(h.marketValue||0),0))} retained
            </div>
          )}
        </div>
      </div>

      {/* ── Transition Strategies ── */}
      {(()=>{
        const FF2 = "'Outfit',sans-serif";
        const analysisResults = loadedClient.analysisResults || {};
        const hasConcentration = !!(analysisResults.concentrationRisk?.topHoldings?.length);
        const hasHarvest       = !!(analysisResults.taxLossHarvesting?.candidates?.length);
        const hasRisk          = !!(analysisResults.riskMetrics?.recommendations?.length);
        const hasAnalysis      = hasConcentration || hasHarvest || hasRisk;
        const currentYear      = new Date().getFullYear();

        // ── Build ordered sell list for Strategy 2 ──
        const buildOrderedSells = () => {
          const sellable = toSell.filter(h => !h.taxExempt); // taxable only
          const exempt   = toSell.filter(h => h.taxExempt);

          if (hasAnalysis) {
            // Analyzer-driven ordering
            const concentrated = hasConcentration
              ? (analysisResults.concentrationRisk.topHoldings||[]).map(h=>h.ticker)
              : [];
            const harvestFirst = hasHarvest
              ? (analysisResults.taxLossHarvesting.candidates||[]).map(h=>h.ticker)
              : [];

            const scored = sellable.map(h => {
              let score = 0;
              const gainPct = h.marketValue > 0 ? (h.gainLoss||0)/h.marketValue : 0;
              // Losses first (harvest) → then low-gain concentrated → then by asset drift
              if (harvestFirst.includes(h.ticker)) score -= 1000; // harvest losses first
              if (concentrated.includes(h.ticker)) score += gainPct * 100; // concentrated but consider gain
              score += gainPct * 50; // otherwise prefer lower gain%
              return { ...h, _score: score };
            });
            return [
              ...scored.sort((a,b) => a._score - b._score),
              ...exempt, // exempt positions at end (no tax impact)
            ];
          } else {
            // Rules-based ordering: fix most over-allocated asset class first,
            // within class sell largest position with lowest gain first
            const assetDrift = {};
            assets.forEach(a => {
              const targetPct = proposed[a.id]?.pct || 0;
              const heldDol   = clientHoldings.filter(h => normalizeAssetId(h.assetClass, engine)===a.id)
                                              .reduce((s,h)=>s+(h.marketValue||0),0);
              const heldPct   = tv > 0 ? heldDol/tv*100 : 0;
              assetDrift[a.id] = heldPct - targetPct; // positive = over-allocated
            });

            const scored = sellable.map(h => {
              const assetId    = normalizeAssetId(h.assetClass, engine);
              const drift      = assetDrift[assetId] || 0;
              const gainPct    = h.marketValue > 0 ? (h.gainLoss||0)/h.marketValue : 0;
              const mv         = h.marketValue || 0;
              // Primary: most over-allocated asset first (negative drift = sell sooner)
              // Secondary: largest position with lowest gain
              return { ...h, _score: -drift * 1000 - mv * 0.01 + gainPct * 500 };
            });
            return [
              ...scored.sort((a,b) => a._score - b._score),
              ...exempt,
            ];
          }
        };

        // ── Compute Strategy 1: full liquidation ──
        const strat1Sells = clientHoldings.filter(h => !h.taxExempt && (h.marketValue||0)>0);
        const strat1Exempt = clientHoldings.filter(h => h.taxExempt && (h.marketValue||0)>0);
        const strat1Gains  = strat1Sells.reduce((s,h)=>s+Math.max(0,h.gainLoss||0),0);
        const strat1Losses = strat1Sells.reduce((s,h)=>s+Math.min(0,h.gainLoss||0),0);
        const strat1LT     = strat1Sells.filter(h=>(h.gainLoss||0)>0&&h.holdingPeriod==="Long-term").reduce((s,h)=>s+(h.gainLoss||0),0);
        const strat1ST     = strat1Sells.filter(h=>(h.gainLoss||0)>0&&h.holdingPeriod!=="Long-term").reduce((s,h)=>s+(h.gainLoss||0),0);
        const strat1Tax    = strat1LT*0.20 + strat1ST*0.37;
        const strat1TotalMV = strat1Sells.reduce((s,h)=>s+(h.marketValue||0),0);

        return (
          <TransitionStrategies
            FF={FF2}
            currentYear={currentYear}
            buildOrderedSells={buildOrderedSells}
            strat1={{sells:strat1Sells, exempt:strat1Exempt, gains:strat1Gains, losses:strat1Losses, tax:strat1Tax, totalMV:strat1TotalMV}}
            toSell={toSell}
            toKeep={toKeep}
            tv={tv}
            proposed={proposed}
            assets={assets}
            engine={engine}
            hasAnalysis={hasAnalysis}
            analysisResults={analysisResults}
            dcaConfig={engine.dcaConfig}
            additionalCash={additionalCash}
            onDirtyChange={emitTradingDirty}
          />
        );
      })()}

      {/* ── Advisor Notes ── */}
      <div style={{background:"#fff",border:"1px solid #E2E8F0",borderRadius:14,overflow:"hidden",marginBottom:16}}>
        <div style={{background:"#F8FAFC",padding:"12px 18px",borderBottom:"1px solid #E2E8F0",display:"flex",alignItems:"center",gap:8}}>
          <span style={{fontSize:13,fontWeight:700,color:"#1E3A5F"}}>Advisor Notes</span>
          <span style={{fontSize:10,color:"#94A3B8"}}>Additional context, instructions, or considerations for this transition plan</span>
          {tradingNotes.trim()&&<span style={{marginLeft:"auto",fontSize:9,padding:"2px 8px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:6,color:"#2563EB",fontWeight:700}}>✎ Notes added</span>}
        </div>
        <div style={{padding:"14px 18px"}}>
          <textarea
            value={tradingNotes}
            onChange={e=>{setTradingNotes(e.target.value);if(e.target.value.trim())emitTradingDirty();}}
            placeholder="e.g. Client has indicated preference for avoiding tech sector during transition. Coordinate timing around Q4 tax year end. Review kept positions at next meeting..."
            rows={5}
            style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:8,padding:"10px 14px",fontSize:12,fontFamily:"'Outfit',sans-serif",outline:"none",resize:"vertical",color:"#334155",lineHeight:1.6,boxSizing:"border-box",background:"#FAFBFC"}}
            onFocus={e=>e.target.style.borderColor="#93C5FD"}
            onBlur={e=>e.target.style.borderColor="#E2E8F0"}
          />
          <div style={{display:"flex",justifyContent:"space-between",marginTop:6}}>
            <span style={{fontSize:10,color:"#94A3B8"}}>{tradingNotes.length} characters</span>
            {tradingNotes.trim()&&(
              <button onClick={()=>setTradingNotes("")}
                style={{background:"none",border:"none",fontSize:10,color:"#94A3B8",cursor:"pointer",fontFamily:"'Outfit',sans-serif",padding:0}}
                onMouseEnter={e=>e.target.style.color="#DC2626"}
                onMouseLeave={e=>e.target.style.color="#94A3B8"}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TRANSITION STRATEGIES — tax-aware transition planning
// Strategy 1: Full liquidation  |  Strategy 2: Gradual transition
// ─────────────────────────────────────────────────────────────
function TransitionStrategies({ FF, currentYear, buildOrderedSells, strat1, toSell, toKeep, tv, proposed, assets, engine, hasAnalysis, analysisResults, dcaConfig={}, additionalCash=0, onDirtyChange=()=>{} }) {
  const { isMobile } = useBreakpoint();
  const [selected, setSelected] = useState(null);
  const selectStrategy = (s) => {
    setSelected(prev => { const next = prev===s?null:s; if(next) onDirtyChange(); return next; });
  };
  const [budgetType,  setBudgetType]  = useState("gains");
  const [budgetRaw,   setBudgetRaw]   = useState("");
  // DCA — embedded within S1/S2, pre-populated from additionalCash
  const [dcaEnabled,  setDcaEnabled]  = useState(additionalCash > 0);
  const [dcaCash,     setDcaCash]     = useState(additionalCash > 0 ? String(Math.round(additionalCash)) : "");
  const [dcaInterval, setDcaInterval] = useState("");
  const [dcaPeriods,  setDcaPeriods]  = useState("");
  // Cash needs state
  const [cashNeedAmt,  setCashNeedAmt]  = useState("");
  const [cashNeedFreq, setCashNeedFreq] = useState("monthly");
  // Keep dcaCash in sync if additionalCash prop updates
  useEffect(() => {
    if (additionalCash > 0) {
      setDcaCash(String(Math.round(additionalCash)));
      setDcaEnabled(true);
    }
  }, [additionalCash]);

  const ltcgRate = 0.20, stcgRate = 0.37;

  // ── Parse budget ──
  const budgetNum = Number(String(budgetRaw).replace(/,/g,"")) || 0;
  // Convert tax budget → gains equivalent if needed
  const gainsEquiv = budgetType === "tax"
    ? (budgetNum / ((ltcgRate + stcgRate) / 2)) // rough blended rate
    : budgetNum;

  // ── Build Strategy 2 year-by-year schedule ──
  const buildSchedule = () => {
    if (!gainsEquiv || gainsEquiv <= 0) return [];
    const orderedSells = buildOrderedSells();
    const schedule = []; // [{ year, sells:[h], gains, tax, cumulativeGains }]
    let remaining = [...orderedSells];
    let year = currentYear;
    let cumulativeGains = 0;
    const maxYears = 20;

    while (remaining.length > 0 && year < currentYear + maxYears) {
      let yearGains = 0;
      const yearSells = [];

      for (let i = 0; i < remaining.length; i++) {
        const h = remaining[i];
        const gain = Math.max(0, h.gainLoss || 0);
        // Check if selling this position would bust the budget
        // Losses are always fine — they offset gains
        if ((h.gainLoss||0) < 0 || yearGains + gain <= gainsEquiv) {
          yearSells.push(h);
          yearGains += gain;
        } else if (yearGains === 0 && yearSells.length === 0) {
          // Position alone exceeds budget — must sell it anyway (can't split a position)
          yearSells.push(h);
          yearGains += gain;
          break;
        }
      }

      if (yearSells.length === 0) break;
      const yearTax = yearSells.reduce((s,h) => {
        if ((h.gainLoss||0) <= 0) return s;
        return s + (h.holdingPeriod==="Long-term" ? (h.gainLoss||0)*ltcgRate : (h.gainLoss||0)*stcgRate);
      }, 0);
      cumulativeGains += yearGains;
      schedule.push({ year, sells:yearSells, gains:yearGains, tax:yearTax, cumulativeGains });
      remaining = remaining.filter(h => !yearSells.includes(h));
      year++;
    }
    return schedule;
  };

  // ── Compute deviation score: sum of absolute differences across all asset classes ──
  const computeDeviation = (alloc) =>
    assets.reduce((s,a) => s + Math.abs((alloc[a.id]||0) - (proposed[a.id]?.pct||0)), 0);

  // ── Deploy cash only into underweight buckets, proportional to gap size ──
  // Returns { [assetId]: pctPointsAdded }
  const deployCash = (heldAlloc, cashToReinvest) => {
    const additions = {};
    assets.forEach(a => { additions[a.id] = 0; });
    if (!cashToReinvest || cashToReinvest <= 0) return additions;

    // Gap = how underweight is each bucket right now (clamped to 0 for over-allocated)
    const gaps = {};
    let totalGap = 0;
    assets.forEach(a => {
      const targetPct = proposed[a.id]?.pct || 0;
      const heldPct   = heldAlloc[a.id] || 0;
      const gap = Math.max(0, targetPct - heldPct);
      gaps[a.id] = gap;
      totalGap += gap;
    });

    if (totalGap <= 0) return additions; // all buckets at or above target — nowhere to deploy

    // Distribute cash proportional to each bucket's gap
    assets.forEach(a => {
      const share  = gaps[a.id] / totalGap;             // fraction of cash going here
      const dollarsHere = cashToReinvest * share;        // dollars into this bucket
      additions[a.id] = (dollarsHere / tv) * 100;       // as portfolio pct points
    });

    return additions;
  };

  // ── Build allocation-over-time chart data for Strategy 2 ──
  // ── Cash needs: annual withdrawal computed from per-period amount × frequency ──
  const CASH_FREQ_MULT = { weekly:52, biweekly:26, monthly:12, quarterly:4, semiannual:2, annual:1 };
  const cashNeedAmtNum = parseFloat(String(cashNeedAmt||"").replace(/,/g,""))||0;
  const annualCashNeed = cashNeedAmtNum * (CASH_FREQ_MULT[cashNeedFreq]||12);

  // ── Build allocation-over-time chart data for Strategy 2 ──
  // Accounts for annual cash withdrawals — reserved in Cash bucket first,
  // remainder deploys to model. Portfolio value shrinks each year by withdrawals.
  const buildChartData = (schedule) => {
    if (!schedule.length) return [];
    const allAssetIds = assets.map(a => a.id);
    const allHoldings = [...toSell, ...toKeep];

    const buildHeldAlloc = (remainingHoldings, modelHeld, runningTv) => {
      const alloc = {};
      allAssetIds.forEach(id => { alloc[id] = 0; });
      if (!runningTv) return alloc;
      remainingHoldings.forEach(h => {
        const id = normalizeAssetId(h.assetClass, engine);
        if (id) alloc[id] = (alloc[id]||0) + (h.marketValue||0)/runningTv*100;
      });
      allAssetIds.forEach(id => { alloc[id] = (alloc[id]||0) + (modelHeld[id]||0); });
      return alloc;
    };

    const currentAlloc = buildHeldAlloc(allHoldings, {}, tv);
    const startDev = computeDeviation(currentAlloc);
    const rows = [{ year:"Now", alloc:{...currentAlloc}, label:"Current", deviation:startDev, overAllocated:[], portfolioValue:tv, withdrawal:0 }];

    let soldSoFar = new Set();
    let modelHeld = {};
    let runningTv = tv; // shrinks each year by cash withdrawals
    allAssetIds.forEach(id => { modelHeld[id] = 0; });
    // Find Cash asset id for reservation
    const cashAssetId = allAssetIds.find(id => id.toLowerCase().includes("cash")) || null;

    schedule.forEach(yr => {
      yr.sells.forEach(h => soldSoFar.add(h.id));

      const remainingHoldings = allHoldings.filter(h => !soldSoFar.has(h.id));
      const remainingMV  = remainingHoldings.reduce((s,h)=>s+(h.marketValue||0),0);
      const rawCashFreed = Math.max(0, runningTv - remainingMV - Object.values(modelHeld).reduce((s,v)=>s+(v/100*runningTv),0));

      // Reserve withdrawal from freed cash — Cash bucket gets it, not redeployed
      const yearWithdrawal = Math.min(annualCashNeed, rawCashFreed);
      const cashForModel   = Math.max(0, rawCashFreed - yearWithdrawal);

      // Portfolio shrinks by the withdrawal (money leaves the account)
      runningTv = Math.max(0, runningTv - yearWithdrawal);

      const heldAlloc = buildHeldAlloc(remainingHoldings, modelHeld, runningTv);

      // Show reserved cash in the Cash bucket
      if (cashAssetId && yearWithdrawal > 0 && runningTv > 0) {
        heldAlloc[cashAssetId] = (heldAlloc[cashAssetId]||0) + (yearWithdrawal / runningTv * 100);
      }

      const additions = deployCash(heldAlloc, cashForModel);
      allAssetIds.forEach(id => { modelHeld[id] = (modelHeld[id]||0) + (additions[id]||0); });

      const alloc = {};
      allAssetIds.forEach(id => { alloc[id] = (heldAlloc[id]||0) + (additions[id]||0); });

      const overAllocated = allAssetIds.filter(id => (alloc[id]||0) > (proposed[id]?.pct||0) + 0.5);
      const deviation = computeDeviation(alloc);
      rows.push({ year:String(yr.year), alloc, label:String(yr.year), deviation, overAllocated, portfolioValue:runningTv, withdrawal:yearWithdrawal });
    });

    const targetAlloc = {};
    allAssetIds.forEach(id => { targetAlloc[id] = proposed[id]?.pct || 0; });
    rows.push({ year:"Target", alloc:targetAlloc, label:"Target", deviation:0, overAllocated:[], portfolioValue:runningTv, withdrawal:0 });
    return rows;
  };

  const schedule  = selected==="s2" && budgetNum > 0 ? buildSchedule() : [];
  const chartData = selected==="s2" && budgetNum > 0 ? buildChartData(schedule) : [];
  const completionYear = schedule.length > 0 ? schedule[schedule.length-1].year : null;
  const totalS2Gains = schedule.reduce((s,yr)=>s+yr.gains,0);
  const totalS2Tax   = schedule.reduce((s,yr)=>s+yr.tax,0);

  // ── DCA helpers ──
  const ALL_INTERVALS = [
    { id:"weekly",     label:"Weekly",      weeksPerPeriod:1   },
    { id:"biweekly",   label:"Bi-weekly",   weeksPerPeriod:2   },
    { id:"monthly",    label:"Monthly",     weeksPerPeriod:4.33},
    { id:"quarterly",  label:"Quarterly",   weeksPerPeriod:13  },
    { id:"semiannual", label:"Semi-annual", weeksPerPeriod:26  },
    { id:"annual",     label:"Annual",      weeksPerPeriod:52  },
  ];

  // Convert admin maxPeriod+maxUnit to weeks for comparison
  const unitToWeeks = { weeks:1, months:4.33, quarters:13, years:52 };
  const dcaMaxWeeks = (dcaConfig.maxPeriod||12) * (unitToWeeks[dcaConfig.maxUnit||"months"]||4.33);

  // Only show intervals admin has allowed
  const allowedIvs = ALL_INTERVALS.filter(iv => (dcaConfig.allowedIntervals||ALL_INTERVALS.map(x=>x.id)).includes(iv.id));

  // Effective interval object
  const activeIv = allowedIvs.find(iv=>iv.id===dcaInterval) || allowedIvs[0];

  // Max periods for the chosen interval given admin cap
  const maxPeriodsForInterval = activeIv ? Math.floor(dcaMaxWeeks / activeIv.weeksPerPeriod) : 1;

  const dcaCashNum   = parseFloat(String(dcaCash).replace(/,/g,""))||0;
  const dcaPeriodsNum = Math.min(parseInt(dcaPeriods)||1, maxPeriodsForInterval);
  const cashPerPeriod = dcaPeriodsNum > 0 ? dcaCashNum / dcaPeriodsNum : 0;

  // Build DCA period schedule: each period deploys cashPerPeriod into underweight buckets
  const buildDCASchedule = () => {
    if (!dcaCashNum || !dcaPeriodsNum || !activeIv) return [];
    const allAssetIds = assets.map(a=>a.id);
    const rows = [];
    let cumulativeAlloc = {};
    allAssetIds.forEach(id => {
      // Start from current portfolio allocation
      const allHoldings = [...toSell, ...toKeep];
      cumulativeAlloc[id] = allHoldings.reduce((s,h) => {
        const hid = normalizeAssetId(h.assetClass, engine);
        return hid===id ? s+(h.marketValue||0)/(tv||1)*100 : s;
      }, 0);
    });

    const startDev = computeDeviation(cumulativeAlloc);
    rows.push({ period:0, label:"Now", cashDeployed:0, cumulativeCash:0, alloc:{...cumulativeAlloc}, deviation:startDev });

    let cumulativeCash = 0;
    for (let p=1; p<=dcaPeriodsNum; p++) {
      // Deploy this period's cash proportional to gap
      const totalPortfolio = tv + cumulativeCash; // grows as cash is added
      const additions = deployCash(cumulativeAlloc, cashPerPeriod);
      const newAlloc = {};
      allAssetIds.forEach(id => {
        // Recalculate pct — portfolio value grows each period
        const prevDollar = (cumulativeAlloc[id]/100) * (tv + cumulativeCash);
        const addedDollar = (additions[id]/100) * (tv||1);
        newAlloc[id] = ((prevDollar + addedDollar) / (tv + cumulativeCash + cashPerPeriod)) * 100;
      });
      cumulativeCash += cashPerPeriod;
      cumulativeAlloc = newAlloc;
      const dev = computeDeviation(cumulativeAlloc);
      // Label: "Wk 1", "Mo 3", "Q 2", etc.
      const prefix = {weekly:"Wk",biweekly:"Bwk",monthly:"Mo",quarterly:"Q",semiannual:"Sa",annual:"Yr"}[activeIv.id]||"P";
      rows.push({ period:p, label:`${prefix} ${p}`, cashDeployed:cashPerPeriod, cumulativeCash, alloc:{...cumulativeAlloc}, deviation:dev });
    }
    // Target row
    const targetAlloc = {};
    allAssetIds.forEach(id => { targetAlloc[id] = proposed[id]?.pct||0; });
    rows.push({ period:dcaPeriodsNum+1, label:"Target", cashDeployed:0, cumulativeCash:dcaCashNum, alloc:targetAlloc, deviation:0 });
    return rows;
  };

  const dcaSchedule = dcaEnabled && dcaCashNum>0 && dcaPeriodsNum>0 && activeIv ? buildDCASchedule() : [];

  // ── Chart colors per asset class ──
  const assetColors = Object.fromEntries(assets.map(a=>[a.id, a.color]));

  const cardHdr = (bg, border) => ({ background:bg, padding:"12px 18px", borderBottom:`1px solid ${border}`, display:"flex", alignItems:"center", gap:8 });

  return (
    <div style={{marginTop:24}}>
      {/* Section header */}
      <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:16}}>
        <div style={{flex:1,height:1,background:"#E2E8F0"}}/>
        <span style={{fontSize:11,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"1.5px",whiteSpace:"nowrap"}}>Transition Strategies</span>
        <div style={{flex:1,height:1,background:"#E2E8F0"}}/>
      </div>

      {/* Annual tax budget input */}
      <div style={{...card, marginBottom:16}}>
        <div style={{...cardHdr("#F8FAFC","#E2E8F0")}}>
          <span style={{fontSize:13,fontWeight:700,color:"#1E3A5F"}}>Annual Tax Budget</span>
          <span style={{fontSize:10,color:"#94A3B8"}}>Used to project the gradual transition timeline</span>
        </div>
        <div style={{padding:"14px 18px",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
          <div style={{display:"flex",background:"#F1F5F9",borderRadius:8,padding:2,gap:2}}>
            {[["gains","Max Gains Realized"],["tax","Max Tax Liability"]].map(([k,l])=>(
              <button key={k} onClick={()=>setBudgetType(k)}
                style={{padding:"6px 14px",borderRadius:6,border:"none",background:budgetType===k?"#fff":"transparent",color:budgetType===k?"#1E3A5F":"#64748B",fontSize:11,fontWeight:budgetType===k?700:500,cursor:"pointer",fontFamily:FF,boxShadow:budgetType===k?"0 1px 4px rgba(0,0,0,0.08)":"none",transition:"all 0.15s"}}>
                {l}
              </button>
            ))}
          </div>
          <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:180}}>
            <span style={{fontSize:13,color:"#475569",fontWeight:600}}>$</span>
            <input value={budgetRaw}
              onChange={e=>{const r=e.target.value.replace(/[^0-9]/g,"");setBudgetRaw(r?Number(r).toLocaleString():"");}}
              placeholder={budgetType==="gains"?"e.g. 250,000":"e.g. 50,000"}
              style={{flex:1,border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 12px",fontSize:13,fontFamily:FF,outline:"none"}}/>
            {budgetNum>0&&(
              <span style={{fontSize:11,color:"#64748B",whiteSpace:"nowrap"}}>
                {budgetType==="tax"
                  ? `≈ ${fmt$(gainsEquiv)} in gains`
                  : `≈ ${fmt$(budgetNum*((ltcgRate+stcgRate)/2))} in taxes`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Cash Needs */}
      <div style={{...card, marginBottom:16}}>
        <div style={{...cardHdr("#F0FDF4","#6EE7B7")}}>
          <span style={{fontSize:13,fontWeight:700,color:"#065F46"}}>Client Cash Needs</span>
          <span style={{fontSize:10,color:"#94A3B8",marginLeft:8}}>Regular withdrawals reserved before model deployment</span>
          {annualCashNeed>0&&<span style={{marginLeft:"auto",fontSize:11,fontWeight:700,color:"#059669"}}>{fmt$(annualCashNeed)}/yr reserved</span>}
        </div>
        <div style={{padding:"14px 18px",display:"flex",gap:12,alignItems:"flex-start",flexWrap:"wrap"}}>
          <div style={{display:"flex",alignItems:"center",gap:8,flex:1,minWidth:140}}>
            <span style={{fontSize:13,color:"#475569",fontWeight:600}}>$</span>
            <input value={cashNeedAmt}
              onChange={e=>{const r=e.target.value.replace(/[^0-9]/g,"");setCashNeedAmt(r?Number(r).toLocaleString():"");}}
              placeholder="e.g. 4,000"
              style={{flex:1,border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 12px",fontSize:13,fontFamily:FF,outline:"none"}}/>
          </div>
          <select value={cashNeedFreq} onChange={e=>setCashNeedFreq(e.target.value)}
            style={{border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 12px",fontSize:13,fontFamily:FF,outline:"none",background:"#fff",cursor:"pointer"}}>
            <option value="weekly">per Week</option>
            <option value="biweekly">per Bi-week</option>
            <option value="monthly">per Month</option>
            <option value="quarterly">per Quarter</option>
            <option value="semiannual">per Half-year</option>
            <option value="annual">per Year</option>
          </select>
          {annualCashNeed>0&&tv>0&&(
            <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
              <span style={{fontSize:11,color:"#64748B"}}>
                <span style={{fontWeight:700,color:"#059669"}}>{fmt$(annualCashNeed)}</span>/yr · {((annualCashNeed/tv)*100).toFixed(1)}% of portfolio
              </span>
              <span style={{fontSize:11,color:"#64748B"}}>
                <span style={{fontWeight:700,color:"#475569"}}>{fmt$(cashNeedAmt ? parseFloat(String(cashNeedAmt).replace(/,/g,"")) : 0)}</span> per {cashNeedFreq.replace("semi","half-")}
              </span>
            </div>
          )}
          {!cashNeedAmt&&(
            <span style={{fontSize:10,color:"#94A3B8",fontStyle:"italic"}}>Leave blank if no regular withdrawals — proceeds will fully redeploy to model</span>
          )}
        </div>
      </div>

      {/* Analysis source badge */}
      {hasAnalysis&&(
        <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
          <span style={{fontSize:10,color:"#475569",fontWeight:600,alignSelf:"center"}}>Sell ordering driven by:</span>
          {analysisResults.concentrationRisk?.topHoldings?.length>0&&<span style={{fontSize:9,padding:"2px 9px",background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8,color:"#92400E",fontWeight:700}}>◉ Concentration Risk</span>}
          {analysisResults.taxLossHarvesting?.candidates?.length>0&&<span style={{fontSize:9,padding:"2px 9px",background:"#ECFDF5",border:"1px solid #6EE7B7",borderRadius:8,color:"#065F46",fontWeight:700}}>◇ Tax-Loss Harvesting</span>}
          {analysisResults.riskMetrics?.recommendations?.length>0&&<span style={{fontSize:9,padding:"2px 9px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:8,color:"#1E40AF",fontWeight:700}}>△ Risk Profile</span>}
        </div>
      )}

      {/* Strategy cards */}
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14,marginBottom:20}}>

        {/* Strategy 1 */}
        <div onClick={()=>selectStrategy("s1")}
          style={{...card,marginBottom:0,cursor:"pointer",border:`2px solid ${selected==="s1"?"#DC2626":"#E2E8F0"}`,transition:"all 0.15s"}}>
          <div style={{...cardHdr(selected==="s1"?"#FFF5F5":"#F8FAFC",selected==="s1"?"#FCA5A5":"#E2E8F0")}}>
            <div style={{width:24,height:24,borderRadius:"50%",background:selected==="s1"?"#DC2626":"#E2E8F0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:selected==="s1"?"#fff":"#94A3B8",flexShrink:0}}>1</div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:selected==="s1"?"#DC2626":"#1E3A5F"}}>Full Liquidation</div>
              <div style={{fontSize:10,color:"#64748B"}}>Sell all positions immediately — fastest path to target</div>
            </div>
            <span style={{marginLeft:"auto",fontSize:11,fontWeight:700,color:selected==="s1"?"#DC2626":"#94A3B8"}}>{selected==="s1"?"▲ Selected":"Select →"}</span>
          </div>
          <div style={{padding:"14px 18px",display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
            <div><div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Positions to Sell</div><div style={{fontSize:18,fontWeight:700,color:"#1E3A5F"}}>{strat1.sells.length}</div><div style={{fontSize:10,color:"#94A3B8"}}>{strat1.exempt.length} tax-exempt excluded</div></div>
            <div><div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Total Proceeds</div><div style={{fontSize:18,fontWeight:700,color:"#1E3A5F"}}>{fmt$(strat1.totalMV)}</div></div>
            <div><div style={{fontSize:9,fontWeight:700,color:"#DC2626",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Gains to Realize</div><div style={{fontSize:16,fontWeight:700,color:"#DC2626"}}>{fmt$(strat1.gains)}</div></div>
            <div><div style={{fontSize:9,fontWeight:700,color:"#DC2626",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Est. Tax Liability</div><div style={{fontSize:16,fontWeight:700,color:"#DC2626"}}>{fmt$(strat1.tax)}</div><div style={{fontSize:9,color:"#94A3B8"}}>LT 20% · ST 37%</div></div>
            {strat1.losses<0&&<div style={{gridColumn:"span 2"}}><div style={{fontSize:9,fontWeight:700,color:"#059669",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Losses to Offset</div><div style={{fontSize:14,fontWeight:700,color:"#059669"}}>{fmt$(Math.abs(strat1.losses))}</div></div>}
            <div style={{gridColumn:"span 2",background:"#FFF5F5",border:"1px solid #FCA5A5",borderRadius:7,padding:"8px 12px"}}>
              <div style={{fontSize:10,color:"#DC2626",fontWeight:600}}>⏱ Timeline: Immediate — completes in {currentYear}</div>
            </div>
          </div>
        </div>

        {/* Strategy 2 */}
        <div onClick={()=>selectStrategy("s2")}
          style={{...card,marginBottom:0,cursor:"pointer",border:`2px solid ${selected==="s2"?"#2563EB":"#E2E8F0"}`,transition:"all 0.15s"}}>
          <div style={{...cardHdr(selected==="s2"?"#EFF6FF":"#F8FAFC",selected==="s2"?"#BFDBFE":"#E2E8F0")}}>
            <div style={{width:24,height:24,borderRadius:"50%",background:selected==="s2"?"#2563EB":"#E2E8F0",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:selected==="s2"?"#fff":"#94A3B8",flexShrink:0}}>2</div>
            <div>
              <div style={{fontSize:13,fontWeight:700,color:selected==="s2"?"#2563EB":"#1E3A5F"}}>Gradual Transition</div>
              <div style={{fontSize:10,color:"#64748B"}}>Respect kept positions · spread sells across years by tax budget</div>
            </div>
            <span style={{marginLeft:"auto",fontSize:11,fontWeight:700,color:selected==="s2"?"#2563EB":"#94A3B8"}}>{selected==="s2"?"▲ Selected":"Select →"}</span>
          </div>
          <div style={{padding:"14px 18px",display:"grid",gridTemplateColumns:isMobile?"1fr":"1fr 1fr",gap:12}}>
            <div><div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Positions to Sell</div><div style={{fontSize:18,fontWeight:700,color:"#1E3A5F"}}>{toSell.length}</div><div style={{fontSize:10,color:"#94A3B8"}}>{toKeep.length} kept / retained</div></div>
            <div><div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Total Gains</div><div style={{fontSize:18,fontWeight:700,color:"#D97706"}}>{fmt$(toSell.filter(h=>!h.taxExempt&&(h.gainLoss||0)>0).reduce((s,h)=>s+(h.gainLoss||0),0))}</div></div>
            {budgetNum>0&&schedule.length>0?(
              <>
                <div><div style={{fontSize:9,fontWeight:700,color:"#2563EB",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Projected Years</div><div style={{fontSize:18,fontWeight:700,color:"#2563EB"}}>{schedule.length}</div></div>
                <div><div style={{fontSize:9,fontWeight:700,color:"#2563EB",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:3}}>Completes</div><div style={{fontSize:18,fontWeight:700,color:"#2563EB"}}>{completionYear}</div></div>
                <div style={{gridColumn:"span 2",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:7,padding:"8px 12px"}}>
                  <div style={{fontSize:10,color:"#2563EB",fontWeight:600}}>⏱ {currentYear} → {completionYear} · {fmt$(totalS2Tax)} total est. tax · ~{fmt$(totalS2Tax/Math.max(1,schedule.length))}/yr</div>
                </div>
              </>
            ):(
              <div style={{gridColumn:"span 2",background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 12px"}}>
                <div style={{fontSize:10,color:"#94A3B8"}}>Set an annual tax budget above to see the projected timeline</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Strategy 2 detail: year-by-year schedule + chart ── */}
      {selected==="s2"&&budgetNum>0&&schedule.length>0&&(
        <div>
          {/* Year-by-year schedule table */}
          <div style={{...card}}>
            <div style={{...cardHdr("#EFF6FF","#BFDBFE")}}>
              <span style={{fontSize:13,fontWeight:700,color:"#1E3A5F"}}>Year-by-Year Schedule</span>
              <span style={{fontSize:10,color:"#64748B",marginLeft:8}}>Sell order: {hasAnalysis?"analyzer-driven":"rules-based"}</span>
              <span style={{marginLeft:"auto",fontSize:10,color:"#94A3B8"}}>{budgetType==="gains"?"Gain":"Tax"} budget: {fmt$(budgetNum)}/yr</span>
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#F8FAFC"}}>
                    {["Year","Positions","Gains Realized","Est. Tax","Cumulative Gains","Sells"].map(h=>(
                      <th key={h} style={{padding:"8px 14px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:h==="Sells"?"left":"right",borderBottom:"1px solid #E2E8F0",whiteSpace:"nowrap",textAlign:h==="Year"?"left":"right"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {schedule.map((yr,i)=>(
                    <tr key={yr.year} style={{background:i%2===0?"#fff":"#FAFBFC",borderBottom:"1px solid #F4F6F9"}}>
                      <td style={{padding:"9px 14px",fontWeight:700,color:"#1E3A5F"}}>{yr.year}</td>
                      <td style={{padding:"9px 14px",textAlign:"right",color:"#475569"}}>{yr.sells.length}</td>
                      <td style={{padding:"9px 14px",textAlign:"right",color:"#D97706",fontWeight:600}}>{fmt$(yr.gains)}</td>
                      <td style={{padding:"9px 14px",textAlign:"right",color:"#DC2626",fontWeight:600}}>{fmt$(yr.tax)}</td>
                      <td style={{padding:"9px 14px",textAlign:"right",color:"#64748B"}}>{fmt$(yr.cumulativeGains)}</td>
                      <td style={{padding:"9px 14px",color:"#475569",fontSize:11}}>{yr.sells.map(h=>h.ticker).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{background:"#F8FAFC",borderTop:"2px solid #E2E8F0"}}>
                    <td style={{padding:"9px 14px",fontWeight:700,color:"#1E3A5F"}}>Total</td>
                    <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:"#1E3A5F"}}>{schedule.reduce((s,yr)=>s+yr.sells.length,0)}</td>
                    <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:"#D97706"}}>{fmt$(totalS2Gains)}</td>
                    <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:"#DC2626"}}>{fmt$(totalS2Tax)}</td>
                    <td colSpan={2}/>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* ── DCA panel — deploy new cash alongside gradual transition ── */}
          {dcaConfig.enabled!==false&&(
            <div style={{...card,background:"#FAFFFE",border:"1px solid #6EE7B7"}}>
              <div style={{padding:"12px 18px",display:"flex",alignItems:"center",gap:10}}>
                <button onClick={()=>setDcaEnabled(d=>!d)}
                  style={{padding:"5px 14px",background:dcaEnabled?"#059669":"#F8FAFC",border:`1.5px solid ${dcaEnabled?"#059669":"#E2E8F0"}`,borderRadius:7,color:dcaEnabled?"#fff":"#64748B",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
                  {dcaEnabled?"✓ DCA On":"+ Add DCA"}
                </button>
                <span style={{fontSize:11,color:"#64748B"}}>Dollar-cost average new cash into underweight buckets alongside this transition</span>
                {additionalCash>0&&!dcaEnabled&&<span style={{fontSize:10,color:"#059669",fontWeight:600}}>💡 {fmt$(additionalCash)} new cash detected</span>}
              </div>
              {dcaEnabled&&(
                <div style={{padding:"0 18px 14px",display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>New Cash ($)</div>
                    <input value={dcaCash} onChange={e=>setDcaCash(e.target.value)} placeholder="e.g. 250,000"
                      style={{width:"100%",border:"1px solid #6EE7B7",borderRadius:7,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box",background:"#F0FDF4"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>Interval</div>
                    <select value={dcaInterval||allowedIvs[0]?.id||""} onChange={e=>setDcaInterval(e.target.value)}
                      style={{width:"100%",border:"1px solid #6EE7B7",borderRadius:7,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",background:"#F0FDF4"}}>
                      {allowedIvs.map(iv=><option key={iv.id} value={iv.id}>{iv.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>Periods <span style={{color:"#CBD5E1"}}>(max {maxPeriodsForInterval})</span></div>
                    <input type="number" min={1} max={maxPeriodsForInterval} value={dcaPeriods}
                      onChange={e=>setDcaPeriods(Math.min(parseInt(e.target.value)||1,maxPeriodsForInterval))}
                      placeholder={`1–${maxPeriodsForInterval}`}
                      style={{width:"100%",border:"1px solid #6EE7B7",borderRadius:7,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box",background:"#F0FDF4"}}/>
                  </div>
                  {dcaCashNum>0&&dcaPeriodsNum>0&&(
                    <div style={{gridColumn:"span 3",padding:"7px 12px",background:"#059669",borderRadius:7,display:"flex",gap:16}}>
                      <span style={{fontSize:11,color:"#fff",fontWeight:700}}>{fmt$(cashPerPeriod)} per {activeIv?.label?.toLowerCase()||"period"}</span>
                      <span style={{fontSize:11,color:"#A7F3D0"}}>{fmt$(dcaCashNum)} total over {dcaPeriodsNum} period{dcaPeriodsNum!==1?"s":""}</span>
                      <span style={{fontSize:10,color:"#6EE7B7",marginLeft:"auto"}}>⚙ Max {dcaConfig.maxPeriod||12} {dcaConfig.maxUnit||"months"}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Allocation-over-time chart */}
          {chartData.length>1&&(
            <div style={{...card}}>
              <div style={{...cardHdr("#F8FAFC","#E2E8F0"),flexWrap:"wrap",gap:8}}>
                <span style={{fontSize:13,fontWeight:700,color:"#1E3A5F"}}>Allocation Over Time</span>
                <span style={{fontSize:10,color:"#64748B"}}>Proceeds redeploy into underweight buckets each year · dashed line = model deviation</span>
                <div style={{marginLeft:"auto",display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{display:"flex",alignItems:"center",gap:4,fontSize:9,color:"#94A3B8"}}>
                    <svg width={16} height={8}><line x1={0} y1={4} x2={16} y2={4} stroke="#DC2626" strokeWidth={1.5} strokeDasharray="3,2"/></svg>
                    Model Deviation
                  </span>
                  {chartData.some(d=>(d.overAllocated||[]).length>0)&&(
                    <span style={{fontSize:9,color:"#D97706",fontWeight:600}}>⚠ Over-allocated buckets shown with hatching</span>
                  )}
                </div>
              </div>
              <div style={{padding:"20px 18px",overflowX:"auto"}}>
                {(()=>{
                  const chartW   = Math.max(540, chartData.length * 90);
                  const chartH   = 280;
                  const leftPad  = 48, rightPad = 48, topPad = 16, botPad = 48;
                  const innerW   = chartW - leftPad - rightPad;
                  const innerH   = chartH - topPad - botPad;
                  const colW     = innerW / Math.max(1, chartData.length - 1);
                  const barW     = Math.min(colW * 0.65, 44);

                  const assetList = assets.filter(a => chartData.some(d => (d.alloc[a.id]||0) > 0.3));

                  // Max deviation for scaling the deviation line (right Y axis)
                  const maxDev = Math.max(...chartData.map(d => d.deviation||0), 1);

                  // Bar x center per column index
                  const barCx = (ci) => ci === 0 ? leftPad + barW/2 : leftPad + ci * colW;

                  return (
                    <svg width={chartW} height={chartH} style={{fontFamily:FF,display:"block"}}>
                      <defs>
                        {/* Hatching pattern for over-allocated segments */}
                        <pattern id="overAlloc" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
                          <line x1={0} y1={0} x2={0} y2={6} stroke="rgba(217,119,6,0.5)" strokeWidth={2}/>
                        </pattern>
                      </defs>

                      {/* Left Y-axis: allocation % */}
                      {[0,25,50,75,100].map(pct=>{
                        const y = topPad + innerH - (pct/100)*innerH;
                        return (
                          <g key={pct}>
                            <line x1={leftPad} y1={y} x2={leftPad+innerW} y2={y} stroke="#F1F5F9" strokeWidth={1}/>
                            <text x={leftPad-6} y={y+4} fontSize={9} fill="#94A3B8" textAnchor="end">{pct}%</text>
                          </g>
                        );
                      })}

                      {/* Right Y-axis: deviation % */}
                      {[0,25,50,75,100].map(pct=>{
                        const devVal = (pct/100) * maxDev;
                        const y = topPad + innerH - (pct/100)*innerH;
                        return (
                          <text key={pct} x={leftPad+innerW+6} y={y+4} fontSize={8} fill="#DC262680" textAnchor="start">
                            {devVal.toFixed(0)}%
                          </text>
                        );
                      })}
                      <text x={leftPad+innerW+6} y={topPad-4} fontSize={8} fill="#DC262680" textAnchor="start">dev</text>

                      {/* Stacked bars */}
                      {chartData.map((point, ci)=>{
                        const cx       = barCx(ci);
                        const bx       = cx - barW/2;
                        const isTarget = point.year==="Target";
                        const isNow    = point.year==="Now";
                        let cumH = 0;
                        return (
                          <g key={point.year}>
                            {assetList.map(a => {
                              const pct     = point.alloc[a.id] || 0;
                              const targetP = proposed[a.id]?.pct || 0;
                              const h       = (pct/100) * innerH;
                              const y       = topPad + innerH - cumH - h;
                              const isOver  = (point.overAllocated||[]).includes(a.id);
                              cumH += h;
                              return (
                                <g key={a.id}>
                                  <rect x={bx} y={y} width={barW} height={Math.max(0,h)}
                                    fill={a.color} opacity={isTarget?0.55:1}
                                    stroke="#fff" strokeWidth={0.5}/>
                                  {/* Hatching over over-allocated portion */}
                                  {isOver&&!isTarget&&(
                                    <rect x={bx} y={y} width={barW} height={Math.max(0,h)}
                                      fill="url(#overAlloc)" opacity={0.7}/>
                                  )}
                                </g>
                              );
                            })}
                            {/* Target dashed outline */}
                            {isTarget&&(
                              <rect x={bx} y={topPad} width={barW} height={innerH}
                                fill="none" stroke="#1E3A5F" strokeWidth={1.5} strokeDasharray="3,2" opacity={0.4}/>
                            )}
                            {/* Deviation label above bar */}
                            {!isNow&&!isTarget&&point.deviation!=null&&(
                              <text x={cx} y={topPad+innerH - (point.deviation/maxDev)*innerH - 5}
                                fontSize={8} fill="#DC2626" textAnchor="middle" fontWeight={600}>
                                {point.deviation.toFixed(0)}%
                              </text>
                            )}
                            {/* X-axis label */}
                            <text x={cx} y={topPad+innerH+14} fontSize={9}
                              fill={isTarget?"#1E3A5F":isNow?"#059669":"#64748B"}
                              textAnchor="middle" fontWeight={isTarget||isNow?700:400}>
                              {point.label}
                            </text>
                          </g>
                        );
                      })}

                      {/* Deviation line — connect year points only (skip Now and Target for cleaner line) */}
                      {(()=>{
                        const linePoints = chartData
                          .map((d,ci) => ({ ci, dev: d.deviation, year: d.year }))
                          .filter(p => p.year !== "Target");
                        if (linePoints.length < 2) return null;
                        const pts = linePoints.map(p => {
                          const cx = barCx(p.ci);
                          const y  = topPad + innerH - ((p.dev||0)/maxDev)*innerH;
                          return `${cx},${y}`;
                        }).join(" ");
                        return (
                          <g>
                            <polyline points={pts} fill="none" stroke="#DC2626" strokeWidth={1.5}
                              strokeDasharray="4,3" opacity={0.75}/>
                            {linePoints.map(p => {
                              const cx = barCx(p.ci);
                              const y  = topPad + innerH - ((p.dev||0)/maxDev)*innerH;
                              return <circle key={p.ci} cx={cx} cy={y} r={3} fill="#DC2626" opacity={0.75}/>;
                            })}
                          </g>
                        );
                      })()}

                      {/* Legend — asset classes */}
                      {assetList.slice(0,8).map((a,i)=>(
                        <g key={a.id} transform={`translate(${leftPad + (i%4)*((innerW)/4)}, ${chartH - (i<4?10:0)})`}>
                          <rect width={8} height={8} y={-8} fill={a.color} rx={1}/>
                          <text x={11} fontSize={8} fill="#64748B">{a.label.length>12?a.label.slice(0,12)+"…":a.label}</text>
                        </g>
                      ))}
                    </svg>
                  );
                })()}
              </div>

              {/* Deviation + cash flow summary row */}
              <div style={{padding:"10px 18px",borderTop:"1px solid #E2E8F0",display:"flex",gap:20,flexWrap:"wrap"}}>
                <div style={{fontSize:11,color:"#64748B"}}>
                  <span style={{fontWeight:700,color:"#1E3A5F"}}>Starting deviation: </span>
                  {chartData[0]?.deviation?.toFixed(1)}%
                </div>
                {chartData.length>2&&(
                  <div style={{fontSize:11,color:"#64748B"}}>
                    <span style={{fontWeight:700,color:"#2563EB"}}>End of transition: </span>
                    {chartData[chartData.length-2]?.deviation?.toFixed(1)}%
                  </div>
                )}
                <div style={{fontSize:11,color:"#64748B"}}>
                  <span style={{fontWeight:700,color:"#059669"}}>Target deviation: </span>
                  0%
                </div>
                {annualCashNeed>0&&(
                  <>
                    <div style={{fontSize:11,color:"#64748B"}}>
                      <span style={{fontWeight:700,color:"#059669"}}>Annual withdrawal: </span>
                      {fmt$(annualCashNeed)}/yr
                    </div>
                    <div style={{fontSize:11,color:"#64748B"}}>
                      <span style={{fontWeight:700,color:"#475569"}}>Portfolio after transition: </span>
                      {fmt$(chartData[chartData.length-2]?.portfolioValue||0)}
                    </div>
                    <div style={{fontSize:11,color:"#D97706",fontWeight:600}}>
                      ⬇ Total withdrawn: {fmt$(chartData.slice(1,-1).reduce((s,d)=>s+(d.withdrawal||0),0))}
                    </div>
                  </>
                )}
                {chartData.some(d=>(d.overAllocated||[]).length>0)&&(
                  <div style={{fontSize:11,color:"#D97706",fontWeight:600}}>
                    ⚠ {chartData.flatMap(d=>d.overAllocated||[]).filter((v,i,a)=>a.indexOf(v)===i).map(id=>assets.find(a=>a.id===id)?.label).filter(Boolean).join(", ")} over-allocated — excess drift persists until kept positions are rebalanced
                  </div>
                )}
              </div>

              {/* Cash flow schedule — shown when cash needs are set */}
              {annualCashNeed>0&&chartData.length>2&&(
                <div style={{borderTop:"1px solid #E2E8F0"}}>
                  <div style={{padding:"10px 18px 6px",fontSize:11,fontWeight:700,color:"#065F46"}}>💰 Cash Flow Schedule</div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:11}}>
                      <thead>
                        <tr style={{background:"#F0FDF4"}}>
                          {["Year","Withdrawal","Remaining Portfolio","Cumulative Withdrawn"].map(h=>(
                            <th key={h} style={{padding:"6px 14px",fontSize:9,fontWeight:700,color:"#065F46",textTransform:"uppercase",textAlign:h==="Year"?"left":"right",borderBottom:"1px solid #6EE7B7",whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {chartData.slice(1,-1).map((row,i)=>{
                          const cumWithdrawn = chartData.slice(1,i+2).reduce((s,d)=>s+(d.withdrawal||0),0);
                          return (
                            <tr key={row.year} style={{background:i%2===0?"#fff":"#F9FFFE",borderBottom:"1px solid #F0FDF4"}}>
                              <td style={{padding:"6px 14px",fontWeight:700,color:"#059669"}}>{row.year}</td>
                              <td style={{padding:"6px 14px",textAlign:"right",color:row.withdrawal>0?"#DC2626":"#94A3B8",fontWeight:600}}>{row.withdrawal>0?`-${fmt$(row.withdrawal)}`:"—"}</td>
                              <td style={{padding:"6px 14px",textAlign:"right",color:"#1E3A5F",fontWeight:600}}>{fmt$(row.portfolioValue)}</td>
                              <td style={{padding:"6px 14px",textAlign:"right",color:"#64748B"}}>{cumWithdrawn>0?fmt$(cumWithdrawn):"—"}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Strategy 1 detail */}
      {selected==="s1"&&(
        <div style={{...card}}>
          <div style={{...cardHdr("#FFF5F5","#FCA5A5")}}>
            <span style={{fontSize:13,fontWeight:700,color:"#DC2626"}}>Full Liquidation — Sell Schedule</span>
            <span style={{fontSize:10,color:"#64748B",marginLeft:8}}>All positions sold immediately in {currentYear}</span>
          </div>
          <div style={{overflowX:"auto"}}>
            <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
              <thead>
                <tr style={{background:"#FFF5F5"}}>
                  {["Ticker","Name","Market Value","Gain / Loss","Holding Period","Est. Tax"].map(h=>(
                    <th key={h} style={{padding:"8px 14px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:"right",borderBottom:"1px solid #FCA5A5",whiteSpace:"nowrap",textAlign:h==="Ticker"||h==="Name"?"left":"right"}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {strat1.sells.map((h,i)=>{
                  const gain = h.gainLoss||0;
                  const tax  = gain>0 ? gain*(h.holdingPeriod==="Long-term"?ltcgRate:stcgRate) : 0;
                  return (
                    <tr key={i} style={{background:i%2===0?"#fff":"#FAFBFC",borderBottom:"1px solid #F4F6F9"}}>
                      <td style={{padding:"8px 14px",fontWeight:700,color:"#DC2626"}}>{h.ticker}</td>
                      <td style={{padding:"8px 14px",color:"#475569",fontSize:11}}>{h.name||"—"}</td>
                      <td style={{padding:"8px 14px",textAlign:"right",fontWeight:600,color:"#1E3A5F"}}>{fmt$(h.marketValue)}</td>
                      <td style={{padding:"8px 14px",textAlign:"right",color:gain>=0?"#DC2626":"#059669",fontWeight:600}}>{gain>=0?"+":""}{fmt$(gain)}</td>
                      <td style={{padding:"8px 14px",textAlign:"right",fontSize:10,color:"#64748B"}}>{h.holdingPeriod||"—"}</td>
                      <td style={{padding:"8px 14px",textAlign:"right",color:tax>0?"#DC2626":"#64748B",fontWeight:600}}>{tax>0?fmt$(tax):"—"}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{background:"#FFF5F5",borderTop:"2px solid #FCA5A5"}}>
                  <td colSpan={2} style={{padding:"9px 14px",fontWeight:700,color:"#DC2626"}}>Total</td>
                  <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:"#1E3A5F"}}>{fmt$(strat1.totalMV)}</td>
                  <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:"#DC2626"}}>{fmt$(strat1.gains)}</td>
                  <td/>
                  <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:"#DC2626"}}>{fmt$(strat1.tax)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* ── DCA panel — deploy new cash alongside liquidation ── */}
          {dcaConfig.enabled!==false&&(
            <div style={{padding:"14px 18px",borderTop:"1px solid #F4F6F9",background:"#FAFFFE"}}>
              <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:dcaEnabled?14:0}}>
                <button onClick={()=>setDcaEnabled(d=>!d)}
                  style={{padding:"5px 14px",background:dcaEnabled?"#059669":"#F8FAFC",border:`1.5px solid ${dcaEnabled?"#059669":"#E2E8F0"}`,borderRadius:7,color:dcaEnabled?"#fff":"#64748B",fontSize:11,fontWeight:700,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
                  {dcaEnabled?"✓ DCA On":"+ Add DCA"}
                </button>
                <span style={{fontSize:11,color:"#64748B"}}>Dollar-cost average new cash into underweight buckets alongside this transition</span>
                {additionalCash>0&&!dcaEnabled&&<span style={{fontSize:10,color:"#059669",fontWeight:600}}>💡 {fmt$(additionalCash)} new cash detected from allocator</span>}
              </div>
              {dcaEnabled&&(
                <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10}}>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>New Cash ($)</div>
                    <input value={dcaCash} onChange={e=>setDcaCash(e.target.value)} placeholder="e.g. 250,000"
                      style={{width:"100%",border:"1px solid #6EE7B7",borderRadius:7,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box",background:"#F0FDF4"}}/>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>Interval</div>
                    <select value={dcaInterval||allowedIvs[0]?.id||""} onChange={e=>setDcaInterval(e.target.value)}
                      style={{width:"100%",border:"1px solid #6EE7B7",borderRadius:7,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",background:"#F0FDF4"}}>
                      {allowedIvs.map(iv=><option key={iv.id} value={iv.id}>{iv.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:4}}>Periods <span style={{color:"#CBD5E1"}}>(max {maxPeriodsForInterval})</span></div>
                    <input type="number" min={1} max={maxPeriodsForInterval} value={dcaPeriods}
                      onChange={e=>setDcaPeriods(Math.min(parseInt(e.target.value)||1,maxPeriodsForInterval))}
                      placeholder={`1–${maxPeriodsForInterval}`}
                      style={{width:"100%",border:"1px solid #6EE7B7",borderRadius:7,padding:"7px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box",background:"#F0FDF4"}}/>
                  </div>
                  {dcaCashNum>0&&dcaPeriodsNum>0&&(
                    <div style={{gridColumn:"span 3",padding:"7px 12px",background:"#059669",borderRadius:7,display:"flex",gap:16}}>
                      <span style={{fontSize:11,color:"#fff",fontWeight:700}}>{fmt$(cashPerPeriod)} per {activeIv?.label?.toLowerCase()||"period"}</span>
                      <span style={{fontSize:11,color:"#A7F3D0"}}>{fmt$(dcaCashNum)} total over {dcaPeriodsNum} period{dcaPeriodsNum!==1?"s":""}</span>
                      <span style={{fontSize:10,color:"#6EE7B7",marginLeft:"auto"}}>⚙ Max {dcaConfig.maxPeriod||12} {dcaConfig.maxUnit||"months"}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Redeployment chart ── */}
          {(()=>{
            const allAssetIds = assets.map(a => a.id);

            // Current allocation (all holdings)
            const allHoldings = [...toSell, ...toKeep];
            const currentAlloc = {};
            allAssetIds.forEach(id => { currentAlloc[id] = 0; });
            allHoldings.forEach(h => {
              const id = normalizeAssetId(h.assetClass, engine);
              if (id && tv > 0) currentAlloc[id] = (currentAlloc[id]||0) + (h.marketValue||0)/tv*100;
            });

            // After liquidation: exempt positions stay, rest becomes cash
            // Cash gets deployed proportionally into underweight buckets
            const exemptAlloc = {};
            allAssetIds.forEach(id => { exemptAlloc[id] = 0; });
            (strat1.exempt||[]).forEach(h => {
              const id = normalizeAssetId(h.assetClass, engine);
              if (id && tv > 0) exemptAlloc[id] = (exemptAlloc[id]||0) + (h.marketValue||0)/tv*100;
            });
            const exemptMV     = (strat1.exempt||[]).reduce((s,h)=>s+(h.marketValue||0),0);
            const cashToReinvest = tv - exemptMV;
            const additions    = deployCash(exemptAlloc, cashToReinvest);
            const postLiqAlloc = {};
            allAssetIds.forEach(id => {
              postLiqAlloc[id] = (exemptAlloc[id]||0) + (additions[id]||0);
            });

            // Target allocation
            const targetAlloc = {};
            allAssetIds.forEach(id => { targetAlloc[id] = proposed[id]?.pct || 0; });

            const startDev  = computeDeviation(currentAlloc);
            const postDev   = computeDeviation(postLiqAlloc);
            const chartRows = [
              { label:"Now",         alloc:currentAlloc, deviation:startDev,  color:"#64748B" },
              { label:"After Liq.",  alloc:postLiqAlloc, deviation:postDev,   color:"#DC2626" },
              { label:"Target",      alloc:targetAlloc,  deviation:0,         color:"#1E3A5F" },
            ];

            const assetList = assets.filter(a => chartRows.some(r => (r.alloc[a.id]||0) > 0.3));
            const chartW = 480, chartH = 200;
            const leftPad = 44, rightPad = 12, topPad = 14, botPad = 36;
            const innerW = chartW - leftPad - rightPad;
            const innerH = chartH - topPad - botPad;
            const colW   = innerW / (chartRows.length - 1);
            const barW   = Math.min(colW * 0.6, 60);

            const overAllocated = allAssetIds.filter(id =>
              (postLiqAlloc[id]||0) > (proposed[id]?.pct||0) + 0.5
            );

            return (
              <div style={{padding:"16px 18px",borderTop:"1px solid #F4F6F9"}}>
                <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12,flexWrap:"wrap"}}>
                  <span style={{fontSize:12,fontWeight:700,color:"#1E3A5F"}}>Proceeds Redeployment into Model</span>
                  <span style={{fontSize:10,color:"#94A3B8"}}>Cash deployed proportionally into underweight buckets · exempt positions held</span>
                </div>
                <div style={{overflowX:"auto"}}>
                  <svg width={chartW} height={chartH} style={{fontFamily:FF,display:"block"}}>
                    <defs>
                      <pattern id="s1Over" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
                        <line x1={0} y1={0} x2={0} y2={6} stroke="rgba(217,119,6,0.5)" strokeWidth={2}/>
                      </pattern>
                    </defs>
                    {/* Y-axis */}
                    {[0,25,50,75,100].map(pct=>{
                      const y = topPad + innerH - (pct/100)*innerH;
                      return (
                        <g key={pct}>
                          <line x1={leftPad} y1={y} x2={leftPad+innerW} y2={y} stroke="#F1F5F9" strokeWidth={1}/>
                          <text x={leftPad-6} y={y+4} fontSize={9} fill="#94A3B8" textAnchor="end">{pct}%</text>
                        </g>
                      );
                    })}
                    {/* Bars */}
                    {chartRows.map((row, ci) => {
                      const cx = leftPad + ci * colW;
                      const bx = ci===0 ? leftPad : cx - barW/2;
                      let cumH = 0;
                      const isTarget = row.label==="Target";
                      return (
                        <g key={row.label}>
                          {assetList.map(a => {
                            const pct  = row.alloc[a.id] || 0;
                            const h    = (pct/100) * innerH;
                            const y    = topPad + innerH - cumH - h;
                            const isOver = overAllocated.includes(a.id) && row.label==="After Liq.";
                            cumH += h;
                            return (
                              <g key={a.id}>
                                <rect x={bx} y={y} width={barW} height={Math.max(0,h)}
                                  fill={a.color} opacity={isTarget?0.55:1}
                                  stroke="#fff" strokeWidth={0.5}/>
                                {isOver&&<rect x={bx} y={y} width={barW} height={Math.max(0,h)} fill="url(#s1Over)" opacity={0.7}/>}
                              </g>
                            );
                          })}
                          {isTarget&&(
                            <rect x={bx} y={topPad} width={barW} height={innerH}
                              fill="none" stroke="#1E3A5F" strokeWidth={1.5} strokeDasharray="3,2" opacity={0.4}/>
                          )}
                          {/* Deviation badge above bar */}
                          {!isTarget&&(
                            <text x={bx+barW/2} y={topPad+innerH-(row.deviation/Math.max(startDev,1))*innerH-5}
                              fontSize={8} fill="#DC2626" textAnchor="middle" fontWeight={600}>
                              {row.deviation.toFixed(1)}% dev
                            </text>
                          )}
                          <text x={bx+barW/2} y={topPad+innerH+14} fontSize={9}
                            fill={row.color} textAnchor="middle" fontWeight={700}>
                            {row.label}
                          </text>
                        </g>
                      );
                    })}
                    {/* Legend */}
                    {assetList.slice(0,8).map((a,i)=>(
                      <g key={a.id} transform={`translate(${leftPad+(i%4)*(innerW/4)},${chartH-(i<4?10:0)})`}>
                        <rect width={8} height={8} y={-8} fill={a.color} rx={1}/>
                        <text x={11} fontSize={8} fill="#64748B">{a.label.length>12?a.label.slice(0,12)+"…":a.label}</text>
                      </g>
                    ))}
                  </svg>
                </div>
                {/* Summary strip */}
                <div style={{display:"flex",gap:20,flexWrap:"wrap",marginTop:10,padding:"8px 0",borderTop:"1px solid #F1F5F9"}}>
                  <div style={{fontSize:11,color:"#64748B"}}><span style={{fontWeight:700,color:"#1E3A5F"}}>Starting deviation: </span>{startDev.toFixed(1)}%</div>
                  <div style={{fontSize:11,color:"#64748B"}}><span style={{fontWeight:700,color:"#DC2626"}}>Post-liquidation: </span>{postDev.toFixed(1)}%</div>
                  <div style={{fontSize:11,color:"#64748B"}}><span style={{fontWeight:700,color:"#059669"}}>Target: </span>0%</div>
                  {(strat1.exempt||[]).length>0&&(
                    <div style={{fontSize:11,color:"#D97706",fontWeight:600}}>
                      ⚠ {(strat1.exempt||[]).length} exempt position{(strat1.exempt||[]).length!==1?"s":""} held — residual deviation from non-taxable accounts
                    </div>
                  )}
                  {overAllocated.length>0&&(
                    <div style={{fontSize:11,color:"#D97706",fontWeight:600}}>
                      ⚠ {overAllocated.map(id=>assets.find(a=>a.id===id)?.label).filter(Boolean).join(", ")} over-allocated due to exempt positions
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── DCA detail: embedded in whichever strategy is active ── */}
      {(selected==="s1"||selected==="s2")&&dcaEnabled&&dcaSchedule.length>2&&(
        <div>
          {/* Period schedule table */}
          <div style={{...card}}>
            <div style={{...cardHdr("#F0FDF4","#6EE7B7")}}>
              <span style={{fontSize:13,fontWeight:700,color:"#065F46"}}>DCA Schedule — {activeIv?.label} Deployment</span>
              <span style={{fontSize:10,color:"#64748B",marginLeft:8}}>{fmt$(cashPerPeriod)} per period · {dcaPeriodsNum} period{dcaPeriodsNum!==1?"s":""} · {fmt$(dcaCashNum)} total</span>
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead>
                  <tr style={{background:"#F8FAFC"}}>
                    {["Period","Cash Deployed","Cumulative Cash","Model Deviation","Asset Class Additions"].map(h=>(
                      <th key={h} style={{padding:"8px 14px",fontSize:9,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",textAlign:h==="Period"||h==="Asset Class Additions"?"left":"right",borderBottom:"1px solid #E2E8F0",whiteSpace:"nowrap"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dcaSchedule.filter(r=>r.period>0&&r.label!=="Target").map((row,i)=>{
                    // Show which asset classes received cash this period
                    const prev  = dcaSchedule[i]; // one row back (period-1)
                    const gains = assets.filter(a=>{
                      const cur  = row.alloc[a.id]||0;
                      const prv  = prev?.alloc?.[a.id]||0;
                      return cur > prv + 0.1;
                    });
                    return (
                      <tr key={row.period} style={{background:i%2===0?"#fff":"#FAFBFC",borderBottom:"1px solid #F4F6F9"}}>
                        <td style={{padding:"8px 14px",fontWeight:700,color:"#059669"}}>{row.label}</td>
                        <td style={{padding:"8px 14px",textAlign:"right",color:"#059669",fontWeight:600}}>{fmt$(row.cashDeployed)}</td>
                        <td style={{padding:"8px 14px",textAlign:"right",color:"#475569"}}>{fmt$(row.cumulativeCash)}</td>
                        <td style={{padding:"8px 14px",textAlign:"right",color:row.deviation<5?"#059669":row.deviation<15?"#D97706":"#DC2626",fontWeight:600}}>{row.deviation.toFixed(1)}%</td>
                        <td style={{padding:"8px 14px",fontSize:10,color:"#64748B"}}>
                          {gains.slice(0,3).map(a=>(
                            <span key={a.id} style={{display:"inline-flex",alignItems:"center",gap:4,marginRight:6}}>
                              <span style={{width:6,height:6,borderRadius:"50%",background:a.color,display:"inline-block"}}/>
                              {a.label}
                            </span>
                          ))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:"#F0FDF4",borderTop:"2px solid #6EE7B7"}}>
                    <td style={{padding:"9px 14px",fontWeight:700,color:"#059669"}}>Total</td>
                    <td colSpan={2} style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:"#059669"}}>{fmt$(dcaCashNum)}</td>
                    <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:"#059669"}}>{dcaSchedule[dcaSchedule.length-2]?.deviation?.toFixed(1)}% final</td>
                    <td/>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Allocation over time chart */}
          {(()=>{
            const rows    = dcaSchedule;
            const assetList = assets.filter(a=>rows.some(r=>(r.alloc[a.id]||0)>0.3));
            const maxDev  = Math.max(...rows.map(r=>r.deviation||0),1);
            const chartW  = Math.max(540, rows.length*70);
            const chartH  = 260;
            const lP=48,rP=48,tP=16,bP=48;
            const iW=chartW-lP-rP, iH=chartH-tP-bP;
            const colW = iW/Math.max(1,rows.length-1);
            const barW = Math.min(colW*0.6,44);
            const cx   = (ci) => ci===0?lP+barW/2:lP+ci*colW;

            return (
              <div style={{...card}}>
                <div style={{...cardHdr("#F0FDF4","#6EE7B7"),flexWrap:"wrap",gap:6}}>
                  <span style={{fontSize:13,fontWeight:700,color:"#065F46"}}>Allocation Over Time — DCA</span>
                  <span style={{fontSize:10,color:"#64748B"}}>Portfolio converges to model as cash is deployed · dashed = deviation</span>
                </div>
                <div style={{padding:"16px 18px",overflowX:"auto"}}>
                  <svg width={chartW} height={chartH} style={{fontFamily:FF,display:"block"}}>
                    <defs>
                      <pattern id="dcaOver" patternUnits="userSpaceOnUse" width={6} height={6} patternTransform="rotate(45)">
                        <line x1={0} y1={0} x2={0} y2={6} stroke="rgba(217,119,6,0.5)" strokeWidth={2}/>
                      </pattern>
                    </defs>
                    {[0,25,50,75,100].map(pct=>{
                      const y=tP+iH-(pct/100)*iH;
                      return (
                        <g key={pct}>
                          <line x1={lP} y1={y} x2={lP+iW} y2={y} stroke="#F1F5F9" strokeWidth={1}/>
                          <text x={lP-6} y={y+4} fontSize={9} fill="#94A3B8" textAnchor="end">{pct}%</text>
                        </g>
                      );
                    })}
                    {/* Right axis — deviation */}
                    {[0,25,50,75,100].map(pct=>{
                      const devVal=(pct/100)*maxDev;
                      const y=tP+iH-(pct/100)*iH;
                      return <text key={pct} x={lP+iW+6} y={y+4} fontSize={8} fill="#DC262680" textAnchor="start">{devVal.toFixed(0)}%</text>;
                    })}
                    {rows.map((row,ci)=>{
                      const bx   = cx(ci)-barW/2;
                      const isT  = row.label==="Target";
                      const isNow= row.label==="Now";
                      let cumH=0;
                      return (
                        <g key={row.label}>
                          {assetList.map(a=>{
                            const pct=(row.alloc[a.id]||0);
                            const h=(pct/100)*iH;
                            const y=tP+iH-cumH-h;
                            cumH+=h;
                            return (
                              <rect key={a.id} x={bx} y={y} width={barW} height={Math.max(0,h)}
                                fill={a.color} opacity={isT?0.55:1} stroke="#fff" strokeWidth={0.5}/>
                            );
                          })}
                          {isT&&<rect x={bx} y={tP} width={barW} height={iH} fill="none" stroke="#1E3A5F" strokeWidth={1.5} strokeDasharray="3,2" opacity={0.4}/>}
                          <text x={cx(ci)} y={tP+iH+14} fontSize={9}
                            fill={isT?"#1E3A5F":isNow?"#64748B":"#059669"}
                            textAnchor="middle" fontWeight={isT||isNow?700:500}>
                            {row.label}
                          </text>
                        </g>
                      );
                    })}
                    {/* Deviation line */}
                    {(()=>{
                      const pts=rows.filter(r=>r.label!=="Target").map((r,ci)=>`${cx(ci)},${tP+iH-((r.deviation||0)/maxDev)*iH}`).join(" ");
                      return pts?(
                        <g>
                          <polyline points={pts} fill="none" stroke="#DC2626" strokeWidth={1.5} strokeDasharray="4,3" opacity={0.75}/>
                          {rows.filter(r=>r.label!=="Target").map((r,ci)=>(
                            <circle key={ci} cx={cx(ci)} cy={tP+iH-((r.deviation||0)/maxDev)*iH} r={3} fill="#DC2626" opacity={0.75}/>
                          ))}
                        </g>
                      ):null;
                    })()}
                    {assetList.slice(0,8).map((a,i)=>(
                      <g key={a.id} transform={`translate(${lP+(i%4)*(iW/4)},${chartH-(i<4?10:0)})`}>
                        <rect width={8} height={8} y={-8} fill={a.color} rx={1}/>
                        <text x={11} fontSize={8} fill="#64748B">{a.label.length>12?a.label.slice(0,12)+"…":a.label}</text>
                      </g>
                    ))}
                  </svg>
                </div>
                {/* Summary */}
                <div style={{padding:"10px 18px",borderTop:"1px solid #E2E8F0",display:"flex",gap:20,flexWrap:"wrap"}}>
                  <div style={{fontSize:11,color:"#64748B"}}><span style={{fontWeight:700,color:"#1E3A5F"}}>Starting deviation: </span>{dcaSchedule[0]?.deviation?.toFixed(1)}%</div>
                  <div style={{fontSize:11,color:"#64748B"}}><span style={{fontWeight:700,color:"#059669"}}>After all periods: </span>{dcaSchedule[dcaSchedule.length-2]?.deviation?.toFixed(1)}%</div>
                  <div style={{fontSize:11,color:"#64748B"}}><span style={{fontWeight:700,color:"#64748B"}}>Total cash deployed: </span>{fmt$(dcaCashNum)}</div>
                  <div style={{fontSize:11,color:"#64748B"}}><span style={{fontWeight:700,color:"#059669"}}>Per {activeIv?.label?.toLowerCase()||"period"}: </span>{fmt$(cashPerPeriod)}</div>
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PLATFORM DASHBOARD — your company's super-admin layer
// Manages firm licenses, feature flags, and isolated firm data
// ─────────────────────────────────────────────────────────────
function PlatformDashboard({ platform, setPlatform, currentUser, onSignOut, defaultEngine, addAudit=()=>{} }) {
  const [tab, setTab]           = useState("firms");
  const [editingFirm, setEditingFirm] = useState(null); // firm id being edited, or "new"
  const [draftFirm, setDraftFirm]     = useState(null);
  const [saved, setSaved]       = useState(false);
  const FF = "'Outfit',sans-serif";

  const firms = platform.firms || [];

  const blankFirm = () => ({
    id:            `firm_${Date.now()}`,
    firmName:      "",
    logoInitials:  "",
    brandColor:    "#1E3A5F",
    tier:          "base",
    features:      { ...BASE_FEATURES },
    maxSeats:      10,
    billingContact:"",
    billingEmail:  "",
    adminEmail:    "",
    contractStart: new Date().toISOString().slice(0,10),
    contractEnd:   new Date(Date.now()+365*24*60*60*1000).toISOString().slice(0,10),
    notes:         "",
    createdAt:     new Date().toISOString().slice(0,10),
    active:        true,
    engine:        JSON.parse(JSON.stringify(defaultEngine)),
  });

  const openNew  = () => { setDraftFirm(blankFirm()); setEditingFirm("new"); };
  const openEdit = (f) => { setDraftFirm(JSON.parse(JSON.stringify(f))); setEditingFirm(f.id); };

  const saveFirm = async () => {
    if (!draftFirm.firmName.trim()) return;
    if (!draftFirm.engine) draftFirm.engine = JSON.parse(JSON.stringify(defaultEngine));
    draftFirm.engine.firmName = draftFirm.firmName;

    if (editingFirm === "new") {
      // Create new firm in Supabase
      const { data: newFirm, error } = await supabase.from("firms")
        .insert({ name: draftFirm.firmName, engine: draftFirm.engine })
        .select().single();
      if (error) { alert("Failed to create firm: " + error.message); return; }
      draftFirm.id = newFirm.id;

      // Send invite to firm admin if email provided
      if (draftFirm.adminEmail?.trim()) {
        const adminEmail = draftFirm.adminEmail.trim().toLowerCase();
        // Record invite in database
        await supabase.from("invites").insert({
          firm_id:    newFirm.id,
          email:      adminEmail,
          role:       "admin",
          invited_by: currentUser?.id,
        });
        // Send proper Supabase invite via server-side API
        await fetch("/api/invite", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email:    adminEmail,
            firmId:   newFirm.id,
            role:     "admin",
            firmName: draftFirm.firmName,
            invitedBy: currentUser?.name || "",
          }),
        });
      }
    } else {
      // Update existing firm
      await supabase.from("firms")
        .update({ name: draftFirm.firmName, engine: draftFirm.engine, updated_at: new Date().toISOString() })
        .eq("id", draftFirm.id);
    }

    setPlatform(prev => {
      const exists = prev.firms.find(f => f.id === draftFirm.id);
      return {
        ...prev,
        firms: exists
          ? prev.firms.map(f => f.id === draftFirm.id ? draftFirm : f)
          : [...prev.firms, draftFirm],
      };
    });
    addAudit("firm_saved", `Firm ${editingFirm==="new"?"created":"updated"}: ${draftFirm.firmName}`);
    setEditingFirm(null); setDraftFirm(null);
    setSaved(true); setTimeout(()=>setSaved(false), 2500);
  };

  const toggleFirmActive = (firmId) => {
    setPlatform(prev => ({
      ...prev,
      firms: prev.firms.map(f => f.id===firmId ? {...f, active:!f.active} : f)
    }));
  };

  const toggleFeature = (firmId, feature) => {
    setPlatform(prev => ({
      ...prev,
      firms: prev.firms.map(f => f.id===firmId
        ? { ...f, features: { ...f.features, [feature]: !f.features[feature] } }
        : f
      )
    }));
    addAudit("feature_toggled", `${feature} toggled for firm ${firmId}`);
  };

  const FEATURE_LABELS = {
    analyzer:   { label:"Analyzer",    icon:"📋", desc:"Statement extraction & portfolio analysis" },
    allocator:  { label:"Allocator",   icon:"📊", desc:"Portfolio allocation tool" },
    comparator: { label:"Compare",     icon:"⇄",  desc:"Current vs proposed comparison" },
    trading:    { label:"Trading",     icon:"🔀", desc:"Transition plan & trade list" },
    adminSuite: { label:"Admin Suite", icon:"⚙️",  desc:"Full engine configuration dashboard" },
  };

  const TIER_COLORS = { base:"#2563EB", premium:"#7C3AED", enterprise:"#059669" };

  return (
    <div style={{minHeight:"100vh",background:"#0F1A2E",fontFamily:FF}}>
      {/* Header */}
      <div style={{background:"linear-gradient(135deg,#0F1A2E,#1E3A5F)",padding:"14px 28px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
        <div>
          <div style={{fontSize:18,fontWeight:700,color:"#fff",letterSpacing:"0.3px"}}>⬡ Platform Console</div>
          <div style={{fontSize:10,color:"#64748B",letterSpacing:"1.5px",textTransform:"uppercase",marginTop:1}}>Wealth Tools · License Management</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          {saved&&<span style={{fontSize:11,color:"#6EE7B7",fontWeight:600}}>✓ Saved</span>}
          <div style={{fontSize:11,color:"#94A3B8"}}>{currentUser?.name}</div>
          <button onClick={onSignOut} style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:7,color:"#94A3B8",padding:"6px 12px",fontSize:11,cursor:"pointer",fontFamily:FF}}>Sign out</button>
        </div>
      </div>

      {/* Firm editor modal */}
      {editingFirm&&draftFirm&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
          <div style={{background:"#fff",borderRadius:16,width:"min(580px,96vw)",maxHeight:"88vh",display:"flex",flexDirection:"column",boxShadow:"0 24px 64px rgba(0,0,0,0.4)",overflow:"hidden"}}>
            <div style={{background:"linear-gradient(135deg,#0F1A2E,#1E3A5F)",padding:"18px 24px",flexShrink:0}}>
              <div style={{fontSize:15,fontWeight:700,color:"#fff"}}>{editingFirm==="new"?"Add New Firm":"Edit Firm License"}</div>
              <div style={{fontSize:10,color:"#93C5FD",marginTop:2}}>{editingFirm==="new"?"Creates a new isolated engine + employee directory":"Updates license and feature access"}</div>
            </div>
            <div style={{padding:"20px 24px",overflowY:"auto",flex:1}}>

              {/* Firm identity */}
              <div style={{display:"grid",gridTemplateColumns:"minmax(0,2fr) minmax(0,1fr) 80px",gap:12,marginBottom:16,flexWrap:"wrap"}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Firm Name</div>
                  <input value={draftFirm.firmName} onChange={e=>setDraftFirm(p=>({...p,firmName:e.target.value}))}
                    placeholder="e.g. Meridian Wealth Advisors"
                    style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Logo Initials</div>
                  <input value={draftFirm.logoInitials} onChange={e=>setDraftFirm(p=>({...p,logoInitials:e.target.value.slice(0,3).toUpperCase()}))}
                    placeholder="MW" maxLength={3}
                    style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box",textTransform:"uppercase"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Brand</div>
                  <input type="color" value={draftFirm.brandColor} onChange={e=>setDraftFirm(p=>({...p,brandColor:e.target.value}))}
                    style={{width:"100%",height:38,border:"1px solid #E2E8F0",borderRadius:7,cursor:"pointer",padding:2}}/>
                </div>
              </div>

              {/* Tier + Seats */}
              <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)",gap:12,marginBottom:16}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>License Tier</div>
                  <select value={draftFirm.tier} onChange={e=>setDraftFirm(p=>({...p,tier:e.target.value}))}
                    style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:13,fontFamily:FF,outline:"none",background:"#fff"}}>
                    {["base","premium","enterprise"].map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Max Advisor Seats</div>
                  <input type="number" min={1} value={draftFirm.maxSeats} onChange={e=>setDraftFirm(p=>({...p,maxSeats:parseInt(e.target.value)||1}))}
                    style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:13,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>

              {/* Feature flags */}
              <div style={{marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>Feature Access</div>
                <div style={{display:"flex",flexDirection:"column",gap:6}}>
                  {Object.entries(FEATURE_LABELS).map(([key,f])=>{
                    const on = draftFirm.features[key];
                    return (
                      <div key={key} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:on?"#F0FDF4":"#F8FAFC",border:`1px solid ${on?"#6EE7B7":"#E2E8F0"}`,borderRadius:8,cursor:"pointer",transition:"all 0.15s"}}
                        onClick={()=>setDraftFirm(p=>({...p,features:{...p.features,[key]:!p.features[key]}}))}>
                        <span style={{fontSize:14}}>{f.icon}</span>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:700,color:on?"#059669":"#64748B"}}>{f.label}</div>
                          <div style={{fontSize:10,color:"#94A3B8"}}>{f.desc}</div>
                        </div>
                        <div style={{width:36,height:20,borderRadius:10,background:on?"#059669":"#CBD5E1",position:"relative",transition:"background 0.2s",flexShrink:0}}>
                          <div style={{width:16,height:16,borderRadius:"50%",background:"#fff",position:"absolute",top:2,left:on?18:2,transition:"left 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.2)"}}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Billing */}
              <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)",gap:12,marginBottom:16}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Billing Contact</div>
                  <input value={draftFirm.billingContact} onChange={e=>setDraftFirm(p=>({...p,billingContact:e.target.value}))}
                    placeholder="Jane Doe" style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Billing Email</div>
                  <input value={draftFirm.billingEmail} onChange={e=>setDraftFirm(p=>({...p,billingEmail:e.target.value}))}
                    placeholder="billing@firm.com" style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>

              {/* Admin invite — only show for new firms */}
              {editingFirm === "new" && (
                <div style={{marginBottom:16,padding:"14px 16px",background:"#EFF6FF",border:"1px solid #BFDBFE",borderRadius:10}}>
                  <div style={{fontSize:11,fontWeight:700,color:"#1E40AF",marginBottom:6}}>✉ Invite Firm Admin</div>
                  <div style={{fontSize:11,color:"#3B82F6",marginBottom:8,lineHeight:1.5}}>
                    Enter the email of the person who will manage this firm. They'll receive an invite to set up their account.
                  </div>
                  <input value={draftFirm.adminEmail||""} onChange={e=>setDraftFirm(p=>({...p,adminEmail:e.target.value}))}
                    type="email" placeholder="admin@newfirm.com"
                    style={{width:"100%",border:"1px solid #BFDBFE",borderRadius:7,padding:"8px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box",background:"#fff"}}/>
                </div>
              )}
              <div style={{display:"grid",gridTemplateColumns:"minmax(0,1fr) minmax(0,1fr)",gap:12,marginBottom:16}}>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Contract Start</div>
                  <input type="date" value={draftFirm.contractStart} onChange={e=>setDraftFirm(p=>({...p,contractStart:e.target.value}))}
                    style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
                </div>
                <div>
                  <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Contract End</div>
                  <input type="date" value={draftFirm.contractEnd} onChange={e=>setDraftFirm(p=>({...p,contractEnd:e.target.value}))}
                    style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:12,fontFamily:FF,outline:"none",boxSizing:"border-box"}}/>
                </div>
              </div>

              {/* Notes */}
              <div>
                <div style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:5}}>Internal Notes</div>
                <textarea value={draftFirm.notes} onChange={e=>setDraftFirm(p=>({...p,notes:e.target.value}))}
                  placeholder="Internal memo..."
                  rows={2} style={{width:"100%",border:"1px solid #E2E8F0",borderRadius:7,padding:"8px 10px",fontSize:12,fontFamily:FF,outline:"none",resize:"vertical",boxSizing:"border-box"}}/>
              </div>
            </div>
            <div style={{padding:"14px 24px",borderTop:"1px solid #E2E8F0",display:"flex",gap:8,flexShrink:0}}>
              <button onClick={()=>{setEditingFirm(null);setDraftFirm(null);}}
                style={{flex:1,padding:"10px",background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:8,color:"#64748B",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:FF}}>Cancel</button>
              <button onClick={saveFirm} disabled={!draftFirm.firmName.trim()}
                style={{flex:2,padding:"10px",background:draftFirm.firmName.trim()?"#1E3A5F":"#E2E8F0",border:"none",borderRadius:8,color:draftFirm.firmName.trim()?"#fff":"#94A3B8",fontSize:13,fontWeight:700,cursor:draftFirm.firmName.trim()?"pointer":"not-allowed",fontFamily:FF}}>
                {editingFirm==="new"?"Add Firm →":"Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tab nav */}
      <div style={{display:"flex",gap:2,padding:"16px 28px 0",borderBottom:"1px solid rgba(255,255,255,0.06)"}}>
        {[["firms","🏢 Firms"],["features","⚡ Features"],["audit","📋 Audit"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)}
            style={{padding:"8px 18px",borderRadius:"8px 8px 0 0",border:"none",background:tab===id?"#1E3A5F":"transparent",color:tab===id?"#fff":"#64748B",fontSize:12,fontWeight:tab===id?700:500,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
            {label}
          </button>
        ))}
        <div style={{marginLeft:"auto",padding:"8px 0"}}>
          <button onClick={openNew}
            style={{padding:"7px 16px",background:"#2563EB",border:"none",borderRadius:8,color:"#fff",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:FF}}>
            + Add Firm
          </button>
        </div>
      </div>

      <div style={{padding:"24px 28px"}}>

        {/* ── Firms tab ── */}
        {tab==="firms"&&(
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            {firms.length===0&&<div style={{color:"#475569",fontSize:13,padding:"40px 0",textAlign:"center"}}>No firms yet. Add one to get started.</div>}
            {firms.map(f=>{
              const seats = (f.engine?.employees||[]).filter(e=>e.role==="advisor").length;
              const daysLeft = f.contractEnd ? Math.ceil((new Date(f.contractEnd)-new Date())/(1000*60*60*24)) : null;
              const expired = daysLeft !== null && daysLeft < 0;
              const expiring = daysLeft !== null && daysLeft >= 0 && daysLeft <= 30;
              return (
                <div key={f.id} style={{background:"#1a2744",border:`1px solid ${f.active?"rgba(255,255,255,0.08)":"rgba(255,255,255,0.03)"}`,borderRadius:12,padding:"16px 20px",opacity:f.active?1:0.55}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:14}}>
                    {/* Logo */}
                    <div style={{width:42,height:42,borderRadius:10,background:f.brandColor,display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,fontWeight:800,color:"#fff",flexShrink:0}}>
                      {f.logoInitials||f.firmName.slice(0,2).toUpperCase()}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                        <span style={{fontSize:14,fontWeight:700,color:"#fff"}}>{f.firmName}</span>
                        <span style={{fontSize:9,padding:"2px 8px",borderRadius:8,background:TIER_COLORS[f.tier]+"20",color:TIER_COLORS[f.tier],border:`1px solid ${TIER_COLORS[f.tier]}33`,fontWeight:700,textTransform:"uppercase"}}>{f.tier}</span>
                        {!f.active&&<span style={{fontSize:9,padding:"2px 8px",borderRadius:8,background:"#DC262620",color:"#DC2626",fontWeight:700}}>INACTIVE</span>}
                        {expired&&<span style={{fontSize:9,padding:"2px 8px",borderRadius:8,background:"#DC262620",color:"#DC2626",fontWeight:700}}>EXPIRED</span>}
                        {expiring&&!expired&&<span style={{fontSize:9,padding:"2px 8px",borderRadius:8,background:"#D9770620",color:"#D97706",fontWeight:700}}>{daysLeft}d left</span>}
                      </div>
                      <div style={{display:"flex",gap:16,marginTop:6,flexWrap:"wrap"}}>
                        <span style={{fontSize:11,color:"#64748B"}}>{seats}/{f.maxSeats} advisor seats</span>
                        {f.billingContact&&<span style={{fontSize:11,color:"#64748B"}}>{f.billingContact}</span>}
                        {f.contractEnd&&<span style={{fontSize:11,color:"#64748B"}}>Until {f.contractEnd}</span>}
                      </div>
                      {/* Feature pills */}
                      <div style={{display:"flex",gap:5,marginTop:8,flexWrap:"wrap"}}>
                        {Object.entries(f.features||{}).map(([key,on])=>(
                          <span key={key} style={{fontSize:9,padding:"1px 7px",borderRadius:6,background:on?"#EFF6FF":"rgba(255,255,255,0.04)",color:on?"#2563EB":"#475569",border:`1px solid ${on?"#BFDBFE":"rgba(255,255,255,0.06)"}`,fontWeight:600}}>
                            {FEATURE_LABELS[key]?.icon} {FEATURE_LABELS[key]?.label}
                          </span>
                        ))}
                      </div>
                      {f.notes&&<div style={{fontSize:10,color:"#475569",marginTop:6,fontStyle:"italic"}}>{f.notes}</div>}
                    </div>
                    <div style={{display:"flex",gap:6,flexShrink:0}}>
                      <button onClick={()=>openEdit(f)} style={{padding:"5px 12px",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:7,color:"#93C5FD",fontSize:11,cursor:"pointer",fontFamily:FF}}>Edit</button>
                      <button onClick={()=>toggleFirmActive(f.id)} style={{padding:"5px 12px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:7,color:f.active?"#94A3B8":"#6EE7B7",fontSize:11,cursor:"pointer",fontFamily:FF}}>
                        {f.active?"Deactivate":"Activate"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Features tab ── */}
        {tab==="features"&&(
          <div>
            <div style={{fontSize:12,color:"#64748B",marginBottom:16}}>Toggle features per firm. Changes take effect immediately on next login.</div>
            <div style={{display:"flex",flexDirection:"column",gap:10}}>
              {firms.map(f=>(
                <div key={f.id} style={{background:"#1a2744",border:"1px solid rgba(255,255,255,0.08)",borderRadius:12,padding:"14px 18px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
                    <div style={{width:30,height:30,borderRadius:7,background:f.brandColor,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",flexShrink:0}}>
                      {f.logoInitials||f.firmName.slice(0,2).toUpperCase()}
                    </div>
                    <span style={{fontSize:13,fontWeight:700,color:"#fff"}}>{f.firmName}</span>
                    <span style={{fontSize:9,padding:"2px 8px",borderRadius:8,background:TIER_COLORS[f.tier]+"20",color:TIER_COLORS[f.tier],fontWeight:700,textTransform:"uppercase"}}>{f.tier}</span>
                  </div>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    {Object.entries(FEATURE_LABELS).map(([key,fl])=>{
                      const on = f.features?.[key];
                      return (
                        <button key={key} onClick={()=>toggleFeature(f.id,key)}
                          style={{display:"flex",alignItems:"center",gap:5,padding:"5px 11px",borderRadius:7,border:`1px solid ${on?"#2563EB33":"rgba(255,255,255,0.08)"}`,background:on?"#EFF6FF10":"transparent",cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
                          <span style={{fontSize:12}}>{fl.icon}</span>
                          <span style={{fontSize:11,fontWeight:600,color:on?"#93C5FD":"#475569"}}>{fl.label}</span>
                          <span style={{width:8,height:8,borderRadius:"50%",background:on?"#2563EB":"#374151",flexShrink:0}}/>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Audit tab ── */}
        {tab==="audit"&&(
          <div>
            <div style={{fontSize:12,color:"#64748B",marginBottom:14}}>Platform-level activity log</div>
            {(platform.auditLog||[]).length===0
              ? <div style={{color:"#475569",fontSize:13,padding:"40px 0",textAlign:"center"}}>No activity recorded yet</div>
              : (
                <div style={{display:"flex",flexDirection:"column",gap:4}}>
                  {[...(platform.auditLog||[])].reverse().slice(0,100).map((e,i)=>(
                    <div key={i} style={{display:"flex",gap:12,alignItems:"center",padding:"8px 14px",background:"#1a2744",border:"1px solid rgba(255,255,255,0.05)",borderRadius:8,fontSize:11}}>
                      <span style={{color:"#475569",whiteSpace:"nowrap"}}>{new Date(e.timestamp).toLocaleString()}</span>
                      <span style={{color:"#64748B",fontWeight:600,whiteSpace:"nowrap"}}>{e.type}</span>
                      <span style={{color:"#94A3B8",flex:1}}>{e.detail}</span>
                      <span style={{color:"#475569",whiteSpace:"nowrap"}}>{e.userName}</span>
                    </div>
                  ))}
                </div>
              )
            }
          </div>
        )}

      </div>
    </div>
  );
}

// ADMIN DASHBOARD
// ─────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────
// AI CONFIG TAB — version-controlled model + prompts
// ─────────────────────────────────────────────────────────────
function AIConfigTab({ engine, setEngine, addAudit, adminUser }) {
  const FF = "'Outfit',sans-serif";
  const cfg = engine.aiConfig || {};
  const [model,           setModel]           = useState(cfg.model           || PA_MODEL);
  const [extractPrompt,   setExtractPrompt]   = useState(cfg.extractPrompt   || EXTRACT_PROMPT);
  const [analysisPrompt,  setAnalysisPrompt]  = useState(cfg.analysisPrompt  || "You are a senior financial advisor analyst. Given these holdings, run the requested analysis and return ONLY valid JSON with ONLY that single key.");
  const [promptVersion,   setPromptVersion]   = useState(cfg.promptVersion   || "1.0.0");
  const [saved,           setSaved]           = useState(false);
  const [activeSection,   setActiveSection]   = useState("model");

  const isDirty = model !== cfg.model || extractPrompt !== cfg.extractPrompt ||
                  analysisPrompt !== cfg.analysisPrompt || promptVersion !== cfg.promptVersion;

  const handleSave = () => {
    setEngine(prev => ({
      ...prev,
      aiConfig: {
        ...prev.aiConfig,
        model,
        extractPrompt,
        analysisPrompt,
        promptVersion,
        lastUpdatedBy: adminUser?.name || "Admin",
        lastUpdatedAt: new Date().toISOString(),
      }
    }));
    addAudit("ai_config_updated", `AI config updated — model: ${model}, prompt version: ${promptVersion}`, adminUser);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const sections = [
    { id:"model",    label:"Model" },
    { id:"extract",  label:"Extraction Prompt" },
    { id:"analysis", label:"Analysis Prompt" },
    { id:"history",  label:"Version History" },
  ];

  const KNOWN_MODELS = [
    "claude-sonnet-4-5",
    "claude-opus-4-5",
    "claude-haiku-4-5",
  ];

  return (
    <div style={{padding:"20px 24px",fontFamily:FF}}>
      {/* Header */}
      <div style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",marginBottom:20,flexWrap:"wrap",gap:12}}>
        <div>
          <div style={{fontSize:16,fontWeight:700,color:"#1E3A5F",marginBottom:4}}>🤖 AI Configuration</div>
          <div style={{fontSize:12,color:"#64748B",lineHeight:1.6,maxWidth:560}}>
            Control exactly which model and prompts power the Portfolio Analyzer. Changes are version-stamped and audit-logged.
            <strong style={{color:"#D97706"}}> Changing prompts affects all future analyses — test carefully before saving.</strong>
          </div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {saved && <span style={{fontSize:12,color:"#059669",fontWeight:700,background:"#D1FAE5",padding:"5px 12px",borderRadius:8,border:"1px solid #6EE7B7"}}>✓ Saved</span>}
          <button onClick={handleSave} disabled={!isDirty}
            style={{padding:"9px 20px",background:isDirty?"#1E3A5F":"#94A3B8",border:"none",borderRadius:9,color:"#fff",fontSize:13,fontWeight:700,cursor:isDirty?"pointer":"not-allowed",fontFamily:FF}}>
            {isDirty ? "💾 Save Changes" : "No Changes"}
          </button>
        </div>
      </div>

      {/* Current state pill */}
      <div style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:10,padding:"10px 16px",marginBottom:20,display:"flex",gap:24,flexWrap:"wrap"}}>
        <div><span style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px"}}>Active Model</span><div style={{fontSize:13,fontWeight:700,color:"#1E3A5F",marginTop:2}}>{cfg.model||PA_MODEL}</div></div>
        <div><span style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px"}}>Prompt Version</span><div style={{fontSize:13,fontWeight:700,color:"#1E3A5F",marginTop:2}}>{cfg.promptVersion||"1.0.0"}</div></div>
        <div><span style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px"}}>Last Updated By</span><div style={{fontSize:13,fontWeight:700,color:"#1E3A5F",marginTop:2}}>{cfg.lastUpdatedBy||"—"}</div></div>
        <div><span style={{fontSize:10,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px"}}>Last Updated</span><div style={{fontSize:13,fontWeight:700,color:"#1E3A5F",marginTop:2}}>{cfg.lastUpdatedAt ? new Date(cfg.lastUpdatedAt).toLocaleDateString() : "—"}</div></div>
      </div>

      {/* Section tabs */}
      <div style={{display:"flex",gap:4,marginBottom:16,borderBottom:"2px solid #F1F5F9",paddingBottom:0}}>
        {sections.map(s => (
          <button key={s.id} onClick={()=>setActiveSection(s.id)}
            style={{padding:"7px 16px",border:"none",borderBottom:activeSection===s.id?"2px solid #2563EB":"2px solid transparent",marginBottom:-2,background:"transparent",color:activeSection===s.id?"#2563EB":"#64748B",fontSize:12,fontWeight:activeSection===s.id?700:500,cursor:"pointer",fontFamily:FF,transition:"all 0.15s"}}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Model section */}
      {activeSection === "model" && (
        <div>
          <div style={{fontSize:13,color:"#64748B",marginBottom:16,lineHeight:1.6}}>
            Select the Claude model used for statement extraction and portfolio analysis. Pinned model strings (like <code style={{background:"#F1F5F9",padding:"1px 5px",borderRadius:4,fontSize:12}}>claude-sonnet-4-5</code>) are stable — Anthropic does not change what a pinned string returns without notice.
          </div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:8}}>Select Model</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {KNOWN_MODELS.map(m => (
                <label key={m} style={{display:"flex",alignItems:"center",gap:10,padding:"10px 14px",border:`1px solid ${model===m?"#2563EB":"#E2E8F0"}`,borderRadius:9,cursor:"pointer",background:model===m?"#EFF6FF":"#fff",transition:"all 0.15s"}}>
                  <input type="radio" checked={model===m} onChange={()=>setModel(m)} style={{accentColor:"#2563EB"}}/>
                  <div>
                    <div style={{fontSize:13,fontWeight:700,color:"#1E3A5F"}}>{m}</div>
                    <div style={{fontSize:11,color:"#64748B"}}>
                      {m.includes("opus") ? "Most capable — best for complex multi-account statements. Slower, higher cost." :
                       m.includes("sonnet") ? "Recommended — strong accuracy, fast, cost-effective. Current default." :
                       "Fastest, lowest cost — suitable for simple statements only."}
                    </div>
                  </div>
                  {m === PA_MODEL && <span style={{marginLeft:"auto",fontSize:10,fontWeight:700,color:"#059669",background:"#D1FAE5",padding:"2px 8px",borderRadius:20}}>default</span>}
                </label>
              ))}
            </div>
            <div style={{marginTop:12}}>
              <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:6}}>Or enter a custom model string</div>
              <input value={model} onChange={e=>setModel(e.target.value)}
                style={{...inp,width:"100%",boxSizing:"border-box",fontSize:12,fontFamily:"monospace"}}
                placeholder="e.g. claude-sonnet-4-5"/>
            </div>
          </div>
        </div>
      )}

      {/* Extraction prompt section */}
      {activeSection === "extract" && (
        <div>
          <div style={{fontSize:13,color:"#64748B",marginBottom:12,lineHeight:1.6}}>
            This prompt is sent with every brokerage statement when an advisor clicks <strong>🤖 AI Review</strong>. It instructs Claude how to extract holdings data. The JSON schema at the end is critical — changing field names will break the analyzer.
          </div>
          <div style={{background:"#FEF3C7",border:"1px solid #FDE68A",borderRadius:8,padding:"8px 12px",marginBottom:12,fontSize:11,color:"#92400E",lineHeight:1.6}}>
            ⚠️ <strong>Do not remove or rename JSON schema fields.</strong> The app parses the response by exact field name. You can safely add instructions above the schema or modify the tone/phrasing.
          </div>
          <textarea value={extractPrompt} onChange={e=>setExtractPrompt(e.target.value)}
            style={{width:"100%",minHeight:320,border:"1px solid #E2E8F0",borderRadius:9,padding:"12px",fontSize:12,fontFamily:"monospace",lineHeight:1.7,outline:"none",boxSizing:"border-box",resize:"vertical",background:"#FAFAFA"}}/>
          <button onClick={()=>setExtractPrompt(EXTRACT_PROMPT)}
            style={{marginTop:8,background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",padding:"5px 14px",cursor:"pointer",fontSize:11,fontFamily:FF}}>
            ↩ Reset to default
          </button>
        </div>
      )}

      {/* Analysis prompt section */}
      {activeSection === "analysis" && (
        <div>
          <div style={{fontSize:13,color:"#64748B",marginBottom:12,lineHeight:1.6}}>
            This is the system instruction sent with every portfolio analysis (Allocation Breakdown, Tax Consequences, etc.). Holdings data and the specific analysis type are appended automatically — only edit the persona and instructions here.
          </div>
          <textarea value={analysisPrompt} onChange={e=>setAnalysisPrompt(e.target.value)}
            style={{width:"100%",minHeight:140,border:"1px solid #E2E8F0",borderRadius:9,padding:"12px",fontSize:12,fontFamily:"monospace",lineHeight:1.7,outline:"none",boxSizing:"border-box",resize:"vertical",background:"#FAFAFA"}}/>
          <button onClick={()=>setAnalysisPrompt("You are a senior financial advisor analyst. Given these holdings, run the requested analysis and return ONLY valid JSON with ONLY that single key.")}
            style={{marginTop:8,background:"none",border:"1px solid #E2E8F0",borderRadius:7,color:"#64748B",padding:"5px 14px",cursor:"pointer",fontSize:11,fontFamily:FF}}>
            ↩ Reset to default
          </button>
        </div>
      )}

      {/* Version history section */}
      {activeSection === "history" && (
        <div>
          <div style={{fontSize:13,color:"#64748B",marginBottom:16,lineHeight:1.6}}>
            Every save is recorded in the Audit Log. Use the version field below to tag your config with a semantic version so you can track changes over time.
          </div>
          <div style={{marginBottom:16}}>
            <div style={{fontSize:11,fontWeight:700,color:"#94A3B8",textTransform:"uppercase",letterSpacing:"0.5px",marginBottom:6}}>Prompt Version Tag</div>
            <input value={promptVersion} onChange={e=>setPromptVersion(e.target.value)}
              placeholder="e.g. 1.2.0"
              style={{...inp,width:180,fontSize:13,fontFamily:"monospace"}}/>
            <div style={{fontSize:11,color:"#94A3B8",marginTop:6}}>Use semantic versioning (major.minor.patch). Increment major for schema changes, minor for instruction changes, patch for typo fixes.</div>
          </div>
          <div style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:10,padding:"14px 16px"}}>
            <div style={{fontSize:12,fontWeight:700,color:"#1E3A5F",marginBottom:8}}>Current Config Snapshot</div>
            <div style={{display:"grid",gridTemplateColumns:"140px 1fr",gap:"6px 12px",fontSize:12}}>
              <span style={{color:"#94A3B8",fontWeight:600}}>Model:</span><span style={{fontFamily:"monospace",color:"#1E3A5F"}}>{cfg.model||PA_MODEL}</span>
              <span style={{color:"#94A3B8",fontWeight:600}}>Version:</span><span style={{fontFamily:"monospace",color:"#1E3A5F"}}>{cfg.promptVersion||"1.0.0"}</span>
              <span style={{color:"#94A3B8",fontWeight:600}}>Last updated:</span><span style={{color:"#1E3A5F"}}>{cfg.lastUpdatedAt ? new Date(cfg.lastUpdatedAt).toLocaleString() : "Never — using defaults"}</span>
              <span style={{color:"#94A3B8",fontWeight:600}}>Updated by:</span><span style={{color:"#1E3A5F"}}>{cfg.lastUpdatedBy||"—"}</span>
            </div>
            <div style={{marginTop:12,fontSize:11,color:"#94A3B8"}}>Full change history is in Admin → 🔒 Audit Log, filtered by type <code style={{background:"#F1F5F9",padding:"1px 5px",borderRadius:4}}>ai_config_updated</code>.</div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminDashboard({ engine, setEngine, logs, onClose, adminUser, addAudit=()=>{}, fullPage=false, onSwitchToAdvisor=null, sbClients=[], loadClients=()=>{} }) {
  const [tab,   setTab]   = useState("analytics");
  const [saved, setSaved] = useState(false);
  const { isMobile }      = useBreakpoint();

  const update = (path, value) => {
    setEngine(prev => {
      // Targeted nested update — no deep clone needed
      const keys = path.split(".");
      if (keys.length === 1) return { ...prev, [keys[0]]: value };
      if (keys.length === 2) return { ...prev, [keys[0]]: { ...prev[keys[0]], [keys[1]]: value } };
      if (keys.length === 3) return { ...prev, [keys[0]]: { ...prev[keys[0]], [keys[1]]: { ...prev[keys[0]][keys[1]], [keys[2]]: value } } };
      // Fallback for deeper paths (rare)
      const next = { ...prev };
      let obj = next;
      for (let i = 0; i < keys.length-1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length-1]] = value;
      return next;
    });
  };

  const tabs = [
    { id:"analytics", label:"Analytics" },
    { id:"clients",   label:"Clients" },
    { id:"models",    label:"📐 Models" },
    { id:"styles",    label:"Impl. Styles" },
    { id:"sleeves",   label:"Sleeve Weights" },
    { id:"assets",    label:"Asset Classes" },
    { id:"tickers",   label:"Tickers" },
    { id:"employees", label:"Employees" },
    { id:"approved",  label:"Approved Holdings" },
    { id:"benchmarks",label:"Benchmarks" },
    { id:"allocator", label:"📐 Allocator" },
    { id:"trading",   label:"📈 Trading Rules" },
    { id:"aiconfig",  label:"🤖 AI Config" },
    { id:"audit",     label:"🔒 Audit Log" },
    { id:"help",      label:"📖 Help & Docs" },
  ];

  const shell = fullPage
    ? { display:"flex", flexDirection:"column", minHeight:"100vh", background:"#fff" }
    : { position:"fixed", inset:0, background:"rgba(15,23,42,0.6)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" };

  const inner = fullPage
    ? { display:"flex", flexDirection:"column", flex:1, overflow:"hidden", minHeight:"100vh" }
    : { background:"#fff", borderRadius:isMobile?0:16, width:isMobile?"100vw":"min(980px,96vw)", height:isMobile?"100dvh":undefined, maxHeight:isMobile?"100dvh":"90vh", display:"flex", flexDirection:"column", boxShadow:"0 24px 64px rgba(0,0,0,0.18)", overflow:"hidden" };

  return (
    <div style={shell}>
      <div style={inner}>

        <div style={{background:"#1E3A5F",padding:isMobile?"12px 16px":"14px 24px",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:16}}>
            <div>
              <div style={{fontFamily:"'Outfit',sans-serif",fontSize:isMobile?17:20,fontWeight:700,color:"#fff"}}>Engine Configuration</div>
              <div style={{fontSize:11,color:"#93C5FD",letterSpacing:"1px",textTransform:"uppercase",marginTop:1}}>Investment Committee · Admin</div>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:10}}>
            {adminUser&&(
              <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(255,255,255,0.1)",borderRadius:20,padding:"4px 10px"}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:"#7C3AED",display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,fontWeight:800,color:"#fff",flexShrink:0}}>
                  {adminUser.name.split(" ").map(n=>n[0]).join("")}
                </div>
                {!isMobile&&<span style={{fontSize:11,color:"#fff",fontWeight:600}}>{adminUser.name}</span>}
              </div>
            )}
            <button onClick={()=>{setSaved(true);setTimeout(()=>setSaved(false),2000);}}
              style={{background:saved?"#22C55E":"#2563EB",border:"none",borderRadius:8,color:"#fff",padding:"7px 14px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",transition:"all 0.3s"}}>{saved?"✓ Saved":"Save"}</button>
            {onSwitchToAdvisor && fullPage && (
              <button onClick={onSwitchToAdvisor}
                style={{background:"rgba(255,255,255,0.08)",border:"1px solid rgba(255,255,255,0.2)",borderRadius:8,color:"#93C5FD",padding:"7px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
                📋 Advisor View
              </button>
            )}
            <button onClick={onClose}
              style={{background:"rgba(255,255,255,0.1)",border:"none",borderRadius:8,color:"#93C5FD",padding:"7px 12px",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
              {fullPage?"Sign out":"×"}
            </button>
          </div>
        </div>

        <div style={{display:"flex",borderBottom:"1px solid #E2E8F0",padding:"0 16px",background:"#F8FAFC",overflowX:"auto",flexShrink:0}}>
          {tabs.map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:"11px 14px",border:"none",borderBottom:tab===t.id?"3px solid #2563EB":"3px solid transparent",background:"transparent",color:tab===t.id?"#1E3A5F":"#94A3B8",fontWeight:tab===t.id?700:500,fontSize:12,cursor:"pointer",fontFamily:"'Outfit',sans-serif",marginBottom:-1,whiteSpace:"nowrap"}}>
              {t.label}{t.id==="analytics"&&logs.length>0?<span style={{marginLeft:5,background:"#2563EB",color:"#fff",borderRadius:10,padding:"0px 6px",fontSize:10}}>{logs.length}</span>:null}
            </button>
          ))}
        </div>

        <div style={{overflowY:"auto",padding:isMobile?"14px":"22px",flex:1}}>
          {tab==="analytics"  && <AnalyticsTab engine={engine} logs={logs}/>}
          {tab==="models"     && <ModelsTab engine={engine} setEngine={setEngine} addAudit={addAudit}/>}
          {tab==="styles"     && <ImplementationStylesTab engine={engine} setEngine={setEngine} addAudit={addAudit}/>}
          {tab==="sleeves"    && <SleeveEditor engine={engine} setEngine={setEngine} addAudit={addAudit}/>}
          {tab==="assets"     && <AssetClassEditor engine={engine} setEngine={setEngine}/>}
          {tab==="tickers"    && <TickerCSVManager engine={engine} setEngine={setEngine} addAudit={addAudit}/>}
          {tab==="allocator"  && <AllocatorSettingsTab engine={engine} setEngine={setEngine}/>}
          {tab==="trading"   && <TradingRulesTab engine={engine} setEngine={setEngine}/>}
          {tab==="aiconfig"  && <AIConfigTab engine={engine} setEngine={setEngine} addAudit={addAudit} adminUser={adminUser}/>}
          {tab==="audit"      && <AuditLogTab engine={engine} setEngine={setEngine}/>}
          {tab==="help"       && <AdminHelpTab/>}
          {tab==="clients"    && <ClientsTab engine={engine} setEngine={setEngine} sbClients={sbClients} loadClients={loadClients} currentUser={adminUser}/>}
          {tab==="approved"   && <ApprovedHoldingsTab engine={engine} setEngine={setEngine} addAudit={addAudit}/>}

          {tab==="employees"&&<EmployeesTab engine={engine} setEngine={setEngine} firmId={adminUser?.firm_id || currentUser?.firm_id} adminUser={adminUser}/>}

          {tab==="benchmarks"&&(
            <div>
              <div style={{fontSize:13,color:"#64748B",marginBottom:16}}>Edit benchmark allocations used in the comparison chart. Weights don't need to sum to 100.</div>
              {Object.entries(engine.benchmarks).map(([bk,bv])=>(
                <div key={bk} style={{background:"#F8FAFC",border:"1px solid #E2E8F0",borderRadius:10,padding:"12px 14px",marginBottom:12}}>
                  <div style={{fontFamily:"'Outfit',sans-serif",fontSize:15,fontWeight:700,color:"#1E3A5F",marginBottom:10}}>{bk}</div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(150px,1fr))",gap:8}}>
                    {allAssets(engine).map(a=>(
                      <div key={a.id} style={{display:"flex",alignItems:"center",gap:6,background:"#fff",border:"1px solid #E2E8F0",borderRadius:7,padding:"6px 10px"}}>
                        <div style={{width:7,height:7,borderRadius:"50%",background:a.color,flexShrink:0}}/>
                        <div style={{flex:1,fontSize:11,color:"#64748B",lineHeight:1.2}}>{a.label}</div>
                        <input type="number" min={0} max={100} value={bv[a.id]||0} onChange={e=>update(`benchmarks.${bk}.${a.id}`,Number(e.target.value))}
                          style={{width:44,padding:"3px 5px",border:"1px solid #E2E8F0",borderRadius:5,fontSize:12,fontWeight:700,textAlign:"right",color:"#1E3A5F",background:"#F8FAFC",outline:"none",fontFamily:"'Outfit',sans-serif"}}/>
                        <span style={{fontSize:10,color:"#94A3B8"}}>%</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// PASSWORD MODAL (kept for potential future use but no longer shown)
// ─────────────────────────────────────────────────────────────
function PasswordModal({ engine, onSuccess, onClose }) {
  const [pw, setPw] = useState(""); const [err, setErr] = useState(false);
  const submit = () => { if(pw===engine.adminPassword){onSuccess();}else{setErr(true);setPw("");setTimeout(()=>setErr(false),1500);} };
  return (
    <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.55)",zIndex:999,display:"flex",alignItems:"center",justifyContent:"center",backdropFilter:"blur(3px)",padding:16}}>
      <div style={{background:"#fff",borderRadius:14,padding:"28px 24px",width:"min(340px,92vw)",boxShadow:"0 16px 48px rgba(0,0,0,0.15)"}}>
        <div style={{fontFamily:"'Outfit',sans-serif",fontSize:20,fontWeight:700,color:"#1E3A5F",marginBottom:6}}>Admin Access</div>
        <div style={{fontSize:13,color:"#64748B",marginBottom:18}}>Enter the investment committee password.</div>
        <input type="password" value={pw} onChange={e=>{setPw(e.target.value);setErr(false);}} onKeyDown={e=>e.key==="Enter"&&submit()} placeholder="Password" autoFocus style={{...inp,marginBottom:10,borderColor:err?"#FCA5A5":"#E2E8F0",background:err?"#FFF5F5":"#fff"}}/>
        {err&&<div style={{fontSize:12,color:"#DC2626",marginBottom:10}}>Incorrect password.</div>}
        <div style={{display:"flex",gap:10}}>
          <button onClick={submit} style={{flex:1,background:"#1E3A5F",border:"none",borderRadius:8,color:"#fff",padding:10,fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Unlock</button>
          <button onClick={onClose} style={{flex:1,background:"#F1F5F9",border:"none",borderRadius:8,color:"#64748B",padding:10,fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ROOT APP
// ─────────────────────────────────────────────────────────────
export default function App() {
  // ── Platform state — firms with isolated engines ──
  const [platform, setPlatform] = useState(() => {
    const p = JSON.parse(JSON.stringify(DEFAULT_PLATFORM));
    p.firms = p.firms.map(f => ({ ...f, engine: f.engine || JSON.parse(JSON.stringify(DEFAULT_ENGINE)) }));
    return p;
  });
  const [activeFirmId, setActiveFirmId] = useState(DEFAULT_PLATFORM.firms[0]?.id || null);
  // Keep a ref so setEngine always has the latest firmId without needing useCallback to re-bind
  const activeFirmIdRef = useRef(activeFirmId);
  useEffect(() => { activeFirmIdRef.current = activeFirmId; }, [activeFirmId]);

  const activeFirm = useMemo(
    () => platform.firms.find(f => f.id === activeFirmId) || platform.firms[0],
    [platform, activeFirmId]
  );
  const engine = useMemo(() => activeFirm?.engine || DEFAULT_ENGINE, [activeFirm]);
  const setEngine = useCallback((updater) => {
    setPlatform(prev => {
      const currentEngine = prev.firms[0]?.engine || DEFAULT_ENGINE;
      const newEngine = typeof updater === "function" ? updater(currentEngine) : updater;

      // Get firm ID from session user metadata — most reliable source
      supabase.auth.getSession().then(({ data: { session } }) => {
        const firmId = session?.user?.user_metadata?.firm_id;
        if (firmId) {
          supabase.from("firms")
            .update({ engine: newEngine, updated_at: new Date().toISOString() })
            .eq("id", firmId)
            .then(({ error }) => {
              if (error) console.warn("Engine save failed:", error.message, "firmId:", firmId);
            });
        }
      });

      return {
        ...prev,
        firms: prev.firms.map((f, idx) => idx === 0 ? { ...f, engine: newEngine } : f)
      };
    });
  }, []);
  // appMode: "portal" | "advisor" | "admin" | "platform"
  const [appMode,       setAppMode]       = useState("portal");
  const [needsPassword, setNeedsPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [sbClients,   setSbClients]   = useState([]);

  const loadClients = async (firmId) => {
    if (!firmId) return;
    invalidateClientsCache();
    const clients = await sbGetClients(firmId);
    setSbClients(clients);
  };

  useEffect(() => {
    // Check if this is an invite link (Supabase adds #access_token to URL)
    const hash = window.location.hash;
    const isInviteFlow = hash.includes("type=invite") || hash.includes("type=signup") ||
      (hash.includes("access_token") && hash.includes("type="));

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const meta = session.user.user_metadata || {};
        let profile = null;
        if (meta.role) {
          profile = {
            id:      session.user.id,
            email:   session.user.email,
            name:    meta.name || session.user.email,
            role:    meta.role,
            firm_id: meta.firm_id || null,
          };
        } else {
          const { data: profileData } = await supabase
            .from("profiles").select("*").eq("id", session.user.id).single();
          profile = profileData;
        }

        if (profile) {
          setCurrentUser({ ...profile, email: session.user.email });

          // If this is an invite link, show password setup
          if (isInviteFlow && meta.firm_id) {
            // Mark invite accepted and create profile
            await supabase.from("invites").update({ accepted: true })
              .eq("email", session.user.email).eq("accepted", false);
            const { data: ep } = await supabase.from("profiles").select("id").eq("id", session.user.id).single();
            if (!ep) {
              await supabase.from("profiles").insert({
                id: session.user.id, firm_id: meta.firm_id,
                name: meta.name || session.user.email.split("@")[0],
                email: session.user.email, role: meta.role,
              });
            }
            setNeedsPassword(true);
            setAuthLoading(false);
            return;
          }

          if (profile.role === "platform") {
            // Load all firms for platform admin
            const { data: allFirms } = await supabase.from("firms").select("id, name, engine").order("created_at");
            if (allFirms && allFirms.length > 0) {
              setPlatform(prev => ({
                ...prev,
                firms: allFirms.map(f => ({
                  id:       f.id,
                  firmName: f.name,
                  name:     f.name,
                  engine:   { ...DEFAULT_ENGINE, ...(f.engine||{}) },
                  active:   true,
                  tier:     "base",
                  features: { ...BASE_FEATURES },
                  maxSeats: 10,
                }))
              }));
            }
            setAppMode("platform");
          } else {
            // Load firm engine for advisor/admin
            if (profile.firm_id) {
              const { data: firmData } = await supabase.from("firms").select("id, name, engine").eq("id", profile.firm_id).single();
              if (firmData) {
                const loadedEngine = (firmData.engine && Object.keys(firmData.engine).length > 0)
                  ? { ...DEFAULT_ENGINE, ...firmData.engine }
                  : DEFAULT_ENGINE;
                if (!firmData.engine || Object.keys(firmData.engine).length === 0) {
                  await supabase.from("firms").update({ engine: DEFAULT_ENGINE }).eq("id", profile.firm_id);
                }
                setPlatform(prev => ({
                  ...prev,
                  firms: prev.firms.map(f =>
                    f.id === prev.firms[0].id
                      ? { ...f, id: firmData.id, name: firmData.name, engine: loadedEngine }
                      : f
                  )
                }));
                setActiveFirmId(firmData.id);
                activeFirmIdRef.current = firmData.id;
              }
              await loadClients(profile.firm_id);
            }
            setAppMode(profile.role === "admin" ? "admin" : "advisor");
          }
        }
      }
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === "SIGNED_OUT") {
        setAppMode("portal");
        setCurrentUser(null);
        setSbClients([]);
        invalidateClientsCache();
      }
    });
    return () => subscription.unsubscribe();
  }, []);
  // advisorView: "allocator" | "analyzer"
  const [advisorView, setAdvisorView] = useState("analyzer");
  // PRODUCTION: replace with real auth — auto-logs in first advisor for demo
  const [currentUser, setCurrentUser] = useState(() => DEFAULT_ENGINE.employees.find(e => e.role === "advisor") || DEFAULT_ENGINE.employees[0]);
  const [clientName,  setClientName]  = useState("");
  const [totalValue,  setTotalValue]  = useState("");
  const [logs,        setLogs]        = useState([]);
  // Loaded client from analyzer → allocator
  const [loadedClient,   setLoadedClient]   = useState(null);  // full client object
  const [keptHoldings,   setKeptHoldings]   = useState([]);    // array of holding ids
  const [clientDirected, setClientDirected] = useState([]);   // [{ticker, reason}] — advisor overrides
  const [additionalCash,  setAdditionalCash]  = useState("");   // new cash being added alongside transition
  const [showAdvisorHelp, setShowAdvisorHelp] = useState(false);
  // Allocation snapshot for comparator — set whenever allocator state changes
  const [allocSnapshot,  setAllocSnapshot]  = useState(null);
  // Analyzer dirty state — true when the analyzer has unsaved work in progress
  const [analyzerDirty,  setAnalyzerDirty]  = useState(false);
  // Allocator dirty — true when snapshot exists but hasn't been saved since last change
  const [allocatorDirty, setAllocatorDirty] = useState(false);
  // Trading dirty — true when advisor has active strategy selection or notes
  const [tradingDirty,   setTradingDirty]   = useState(false);
  // Nav guard modal
  const [navGuard,       setNavGuard]       = useState(null); // null | target view string
  // { implStyle, oppPct, alloc:{[assetId]:pct}, selTickers:{[assetId]:{[ticker]:split}},
  //   totalValue, loadedClientId, activeSwaps }
  const { isMobile, isTablet } = useBreakpoint();

  // PRODUCTION: session events should stream to your analytics backend, not stay in memory refs
  const sessionStartRef  = useRef(null);
  const sessionEventsRef = useRef([]);
  const currentStateRef  = useRef({});

  const sessionAPI = {
    track: useCallback((type, data) => {
      sessionEventsRef.current.push({ type, data, t: Date.now() });
    }, []),
    setState: useCallback((state) => {
      currentStateRef.current = state;
    }, []),
  };

  // ── Central audit logger ──
  // PRODUCTION: audit events must write to an immutable server-side log — client should never be the source of truth
  const addAudit = useCallback((type, detail, user) => {
    const actor = user || currentUser;
    setEngine(prev => ({
      ...prev,
      auditLog: [...(prev.auditLog||[]), {
        id:         `al_${Date.now()}_${Math.random().toString(36).slice(2,6)}`,
        timestamp:  new Date().toISOString(),
        type,                          // login | logout | client_saved | client_deleted | alloc_exported | engine_changed | ticker_imported | approved_imported | sleeve_imported
        detail,                        // free-text description
        userId:     actor?.id   || "",
        userName:   actor?.name || "",
        userRole:   actor?.role || "",
      }],
    }));
  }, [currentUser]);

  // PRODUCTION: access code and credentials must be verified server-side, never in client JS
  const handlePlatformLogin = async (pu) => {
    setCurrentUser({ ...pu, role:"platform" });
    // Load all firms from Supabase
    const { data: allFirms } = await supabase.from("firms").select("id, name, engine, updated_at").order("created_at");
    if (allFirms && allFirms.length > 0) {
      setPlatform(prev => ({
        ...prev,
        firms: allFirms.map(f => ({
          id:       f.id,
          firmName: f.name,
          name:     f.name,
          engine:   { ...DEFAULT_ENGINE, ...(f.engine||{}) },
          active:   true,
          tier:     "base",
          features: { ...BASE_FEATURES },
          maxSeats: 10,
        }))
      }));
    }
    setAppMode("platform");
  };

  const handleFirmSelect = (firmId) => {
    setActiveFirmId(firmId);
  };

  const addPlatformAudit = (type, detail) => {
    setPlatform(prev => ({
      ...prev,
      auditLog: [...(prev.auditLog||[]), {
        id: `pal_${Date.now()}`, timestamp: new Date().toISOString(),
        type, detail, userId: currentUser?.id||"", userName: currentUser?.name||"",
      }]
    }));
  };

  // PRODUCTION: replace client-side credential matching with server-side JWT/session auth
  const handleAdvisorLogin = async (emp) => {
    setCurrentUser(emp);
    if (emp.role === "platform") { setAppMode("platform"); return; }
    if (emp.firm_id) {
      const { data: firmData } = await supabase.from("firms").select("id, name, engine").eq("id", emp.firm_id).single();
      if (firmData) {
        const loadedEngine = (firmData.engine && Object.keys(firmData.engine).length > 0)
          ? { ...DEFAULT_ENGINE, ...firmData.engine }
          : DEFAULT_ENGINE;
        setPlatform(prev => ({
          ...prev,
          firms: prev.firms.map(f =>
            f.id === prev.firms[0].id
              ? { ...f, id: firmData.id, name: firmData.name, engine: loadedEngine }
              : f
          )
        }));
        setActiveFirmId(firmData.id);
        activeFirmIdRef.current = firmData.id;
      }
    }
    setAppMode("advisor");
    setAdvisorView("analyzer");
    setAnalyzerDirty(false);
    sessionStartRef.current  = Date.now();
    sessionEventsRef.current = [];
    currentStateRef.current  = {};
    if (emp.firm_id) loadClients(emp.firm_id);
    setEngine(prev => ({...prev, auditLog:[...(prev.auditLog||[]), {id:`al_${Date.now()}`,timestamp:new Date().toISOString(),type:"login",detail:`Advisor login`,userId:emp.id,userName:emp.name,userRole:emp.role}]}));
  };

  const handleAdminLogin = async (emp) => {
    setCurrentUser(emp);
    if (emp.role === "platform") { setAppMode("platform"); return; }
    if (emp.firm_id) {
      const { data: firmData } = await supabase.from("firms").select("id, name, engine").eq("id", emp.firm_id).single();
      if (firmData) {
        const loadedEngine = (firmData.engine && Object.keys(firmData.engine).length > 0)
          ? { ...DEFAULT_ENGINE, ...firmData.engine }
          : DEFAULT_ENGINE;
        setPlatform(prev => ({
          ...prev,
          firms: prev.firms.map(f =>
            f.id === prev.firms[0].id
              ? { ...f, id: firmData.id, name: firmData.name, engine: loadedEngine }
              : f
          )
        }));
        setActiveFirmId(firmData.id);
        activeFirmIdRef.current = firmData.id;
      }
    }
    setAppMode("admin");
    if (emp.firm_id) loadClients(emp.firm_id);
    setEngine(prev => ({...prev, auditLog:[...(prev.auditLog||[]), {id:`al_${Date.now()}`,timestamp:new Date().toISOString(),type:"login",detail:`Admin login`,userId:emp.id,userName:emp.name,userRole:emp.role}]}));
  };

  const handleSignOut = async () => {
    if (currentUser) {
      setEngine(prev => ({...prev, auditLog:[...(prev.auditLog||[]), {id:`al_${Date.now()}`,timestamp:new Date().toISOString(),type:"logout",detail:`Sign out`,userId:currentUser.id,userName:currentUser.name,userRole:currentUser.role}]}));
    }
    await supabase.auth.signOut();
    setCurrentUser(null);
    setSbClients([]);
    invalidateClientsCache();
    setAppMode("portal");
    setClientName("");
    setTotalValue("");
    setLoadedClient(null);
    setKeptHoldings([]);
    setClientDirected([]);
    setAllocSnapshot(null);
    setAllocatorDirty(false);
  };

  // ── Save current allocation snapshot to the client record (or create minimal record) ──
  const { tokenizePII: allocTokenizePII } = usePIIVault();
  const [allocSaveState, setAllocSaveState] = useState(null); // null | "saving" | "saved" | "created"

  const handleSaveAllocation = async (andNavigate=false) => {
    if (!allocSnapshot) return;
    setAllocSaveState("saving");
    const snap = {
      id:         `alloc_${Date.now()}`,
      savedAt:    new Date().toISOString(),
      advisorId:  currentUser.id,
      implStyle:  allocSnapshot.implStyle,
      oppPct:     allocSnapshot.oppPct,
      alloc:      allocSnapshot.alloc,
      selTickers: allocSnapshot.selTickers,
      totalValue: allocSnapshot.totalValue,
    };

    if (loadedClient) {
      // Append allocation to existing client and save to Supabase
      const updatedClient = { ...loadedClient, savedAllocations: [...(loadedClient.savedAllocations||[]), snap] };
      sbSaveClient(updatedClient, currentUser?.firm_id, currentUser?.id).then(() => loadClients(currentUser?.firm_id));
      setLoadedClient(updatedClient);
      addAudit("client_saved", `Allocation saved to client record — ${snap.implStyle} ${snap.oppPct}/${100-snap.oppPct}`);
      setAllocSaveState("saved");
      setAllocatorDirty(false);
      setTradingDirty(false);
    } else {
      // Create a minimal client record, tokenizing the name properly via vault
      const { nameToken } = await allocTokenizePII(clientName || "Unnamed Client", null);
      const newClient = {
        id:                  `client_${Date.now()}`,
        name:                nameToken,
        accountNumber:       null,
        accountType:         "Brokerage",
        totalPortfolioValue: allocSnapshot.totalValue || 0,
        cashBalance:         0,
        holdings:            [],
        analysisResults:     {},
        advisorId:           currentUser.id,
        advisorName:         currentUser.name,
        savedAt:             new Date().toISOString(),
        savedAllocations:    [snap],
        sharedWith:          [],
      };
      sbSaveClient(newClient, currentUser?.firm_id, currentUser?.id).then(() => loadClients(currentUser?.firm_id));
      setLoadedClient(newClient);
      addAudit("client_saved", `New client created with allocation — ${snap.implStyle} ${snap.oppPct}/${100-snap.oppPct}`);
      setAllocSaveState("created");
      setAllocatorDirty(false);
      setTradingDirty(false);
    }
    if (andNavigate) {
      setTimeout(() => { setAllocSaveState(null); setAdvisorView("comparator"); }, 300);
    } else {
      setTimeout(() => setAllocSaveState(null), 3000);
    }
  };

  const handleExport = () => {
    const events   = sessionEventsRef.current;
    const state    = currentStateRef.current;
    const duration = Math.round((Date.now() - (sessionStartRef.current||Date.now())) / 1000);
    setLogs(prev => [...prev, {
      timestamp:    new Date().toISOString(),
      advisorId:    currentUser?.id,
      advisorName:  currentUser?.name,
      finalStyle:   state.implStyle || "—",
      finalOppPct:  state.oppPct ?? 60,
      tickerSwaps:  events.filter(e=>e.type==="tickerSwap").length,
      blendChanges: events.filter(e=>e.type==="blendChange").length,
      styleChanges: events.filter(e=>e.type==="styleChange").length,
      durationSecs: duration,
    }]);
    sessionStartRef.current  = Date.now();
    sessionEventsRef.current = [];
    addAudit("alloc_exported", `Allocation exported — ${state.implStyle||"—"} style, ${state.oppPct??60}/${100-(state.oppPct??60)} blend${state.clientName ? `, client: ${state.clientName}` : ""}`);
    // PRODUCTION: generate PDF server-side (Puppeteer/WeasyPrint) and return a download link instead of window.print()
    window.print();
  };

  // Called when advisor clicks "Load in Allocator" from analyzer
  const handleLoadClient = (client) => {
    setLoadedClient(client);
    setKeptHoldings([]);
    setClientDirected([]);
    setClientName(client.name || "");
    const pv = client.totalPortfolioValue;
    setTotalValue(pv ? String(Math.round(pv)) : "");
    // PRODUCTION: server-side JWT auth — never navigate based on client-side state
  };

  const handleToggleKeep = useCallback((holdingId) => {
    setKeptHoldings(prev => prev.includes(holdingId) ? prev.filter(id=>id!==holdingId) : [...prev, holdingId]);
  }, []);

  // ── Set Password (new invited users) ──
  if (needsPassword && currentUser) {
    return <SetPasswordScreen currentUser={currentUser} onComplete={async (password) => {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) { alert("Error setting password: " + error.message); return; }
      setNeedsPassword(false);
      // Load their firm and proceed
      if (currentUser.firm_id) {
        const { data: firmData } = await supabase.from("firms").select("id, name, engine").eq("id", currentUser.firm_id).single();
        if (firmData) {
          const loadedEngine = (firmData.engine && Object.keys(firmData.engine).length > 0) ? { ...DEFAULT_ENGINE, ...firmData.engine } : DEFAULT_ENGINE;
          setPlatform(prev => ({ ...prev, firms: prev.firms.map(f => f.id === prev.firms[0].id ? { ...f, id: firmData.id, name: firmData.name, engine: loadedEngine } : f) }));
          setActiveFirmId(firmData.id);
          activeFirmIdRef.current = firmData.id;
        }
        await loadClients(currentUser.firm_id);
      }
      setAppMode(currentUser.role === "admin" ? "admin" : "advisor");
    }}/>;
  }

  // ── Portal ──
  if (appMode === "portal") {
    return (
      <EngineCtx.Provider value={engine}>
        <PortalScreen
          engine={engine}
          platform={platform}
          activeFirmId={activeFirmId}
          onFirmSelect={handleFirmSelect}
          onAdvisorLogin={handleAdvisorLogin}
          onAdminLogin={handleAdminLogin}
          onPlatformLogin={handlePlatformLogin}
        />
        <style>{`*{box-sizing:border-box;}body{margin:0;overflow-x:hidden;}-webkit-text-size-adjust:100%;`}</style>
      </EngineCtx.Provider>
    );
  }

  // ── Platform ──
  // PRODUCTION: feature flags must also be enforced server-side — never trust client-declared feature access
  if (authLoading) {
    return (
      <div style={{minHeight:"100vh",display:"flex",alignItems:"center",justifyContent:"center",background:"#F4F7FC",fontFamily:"'Outfit',sans-serif"}}>
        <div style={{textAlign:"center"}}>
          <div style={{width:40,height:40,border:"3px solid #E2E8F0",borderTopColor:"#1E3A5F",borderRadius:"50%",margin:"0 auto 16px",animation:"spin 0.8s linear infinite"}}/>
          <div style={{fontSize:14,color:"#64748B"}}>Loading…</div>
          <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
      </div>
    );
  }

  if (appMode === "platform") {
    return (
      <PIIVaultProvider engine={engine} setEngine={setEngine} currentUser={currentUser}>
      <EngineCtx.Provider value={engine}>
        <PlatformDashboard
          platform={platform}
          setPlatform={setPlatform}
          currentUser={currentUser}
          onSignOut={handleSignOut}
          defaultEngine={DEFAULT_ENGINE}
          addAudit={addPlatformAudit}
        />
      </EngineCtx.Provider>
      </PIIVaultProvider>
    );
  }

  // ── Admin ──
  if (appMode === "admin") {
    return (
      <PIIVaultProvider engine={engine} setEngine={setEngine} currentUser={currentUser}>
      <EngineCtx.Provider value={engine}>
        <div style={{minHeight:"100vh",background:"#F4F7FC",fontFamily:"'Outfit',sans-serif"}}>
          <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet"/>
          <AdminDashboard engine={engine} setEngine={setEngine} logs={logs} adminUser={currentUser} onClose={handleSignOut} addAudit={addAudit} fullPage={true} onSwitchToAdvisor={()=>setAppMode("advisor")} sbClients={sbClients} loadClients={loadClients}/>
          <style>{`*{box-sizing:border-box;}body{margin:0;overflow-x:hidden;}-webkit-text-size-adjust:100%;input:focus,select:focus{border-color:#93C5FD!important;box-shadow:0 0 0 3px #BFDBFE44;}input[type=number]::-webkit-inner-spin-button{opacity:0.4;}`}</style>
        </div>
      </EngineCtx.Provider>
      </PIIVaultProvider>
    );
  }

  // ── Advisor ──
  const navigateTo = (view) => {
    if (view === advisorView) return;
    if (advisorView === "analyzer" && analyzerDirty) {
      setNavGuard({ target: view, source: "analyzer" });
      return;
    }
    if (advisorView === "allocator" && allocatorDirty && allocSnapshot) {
      setNavGuard({ target: view, source: "allocator" });
      return;
    }
    if (advisorView === "trading" && tradingDirty) {
      setNavGuard({ target: view, source: "trading" });
      return;
    }
    setAdvisorView(view);
  };

  const navBtn = (view, label) => (
    <button onClick={()=>navigateTo(view)}
      style={{padding:isMobile?"5px 10px":"6px 14px",borderRadius:7,border:"none",background:advisorView===view?"rgba(255,255,255,0.18)":"transparent",color:advisorView===view?"#fff":"#93C5FD",fontSize:isMobile?11:12,fontWeight:advisorView===view?700:500,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap",transition:"all 0.15s"}}>
      {label}
    </button>
  );

  return (
    <PIIVaultProvider engine={engine} setEngine={setEngine} currentUser={currentUser}>
    <EngineCtx.Provider value={engine}>
      <SessionCtx.Provider value={sessionAPI}>
        <div style={{minHeight:"100vh",background:"#F4F7FC",fontFamily:"'Outfit',sans-serif",color:"#1E293B",transition:"background 0.3s"}}>
          <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" rel="stylesheet"/>
          {typeof document!=="undefined"&&!document.querySelector("meta[name=viewport]")&&(()=>{const m=document.createElement("meta");m.name="viewport";m.content="width=device-width,initial-scale=1";document.head.appendChild(m);return null;})()}

          {/* Advisor Header */}
          <div style={{background:"#1E3A5F",padding:isMobile?"0 12px":"0 24px",display:"flex",alignItems:"center",justifyContent:"space-between",height:isMobile?52:58,gap:8,flexShrink:0,position:"sticky",top:0,zIndex:50}}>
            {/* Left: firm name */}
            <div style={{fontFamily:"'Outfit',sans-serif",fontSize:isMobile?16:20,fontWeight:700,color:"#fff",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",flexShrink:1,minWidth:0}}>
              {engine.firmName}
            </div>

            {/* Center: nav tabs */}
            <div style={{display:"flex",alignItems:"center",gap:4,background:"rgba(255,255,255,0.06)",borderRadius:9,padding:"3px 4px",flexShrink:0}}>
              {navBtn("analyzer","📋 Analyzer")}
              {navBtn("allocator","📊 Allocator")}
              {navBtn("comparator","⇄ Compare")}
              {navBtn("trading","🔀 Trading")}
              {navBtn("clients","👤 Clients")}
            </div>

            {/* Right: user + help + export */}
            <div style={{display:"flex",alignItems:"center",gap:6,flexShrink:0}}>
              <div style={{display:"flex",alignItems:"center",gap:6,background:"rgba(255,255,255,0.1)",borderRadius:20,padding:"4px 10px"}}>
                <div style={{width:22,height:22,borderRadius:"50%",background:"#2563EB",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,fontWeight:800,color:"#fff",flexShrink:0}}>
                  {currentUser.name.split(" ").map(n=>n[0]).join("")}
                </div>
                {!isMobile&&<span style={{fontSize:12,color:"#fff",fontWeight:600}}>{currentUser.name}</span>}
                {currentUser?.role === "admin" && (
                  <button onClick={()=>setAppMode("admin")} style={{background:"none",border:"1px solid rgba(255,255,255,0.25)",borderRadius:6,color:"#93C5FD",cursor:"pointer",fontSize:11,padding:"3px 8px",marginRight:4}}>⚙ Admin</button>
                )}
                <button onClick={handleSignOut} style={{background:"none",border:"none",color:"#93C5FD",cursor:"pointer",fontSize:11,padding:0}}>{isMobile?"✕":"Sign out"}</button>
              </div>
              <button onClick={()=>setShowAdvisorHelp(true)}
                style={{width:30,height:30,borderRadius:"50%",border:"1px solid rgba(255,255,255,0.25)",background:"rgba(255,255,255,0.08)",color:"#93C5FD",fontSize:13,fontWeight:800,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Outfit',sans-serif",flexShrink:0}}
                title="Help & Walkthrough">
                ?
              </button>
              {advisorView==="allocator"&&(
                <button onClick={handleExport} style={{background:"#2563EB",border:"none",borderRadius:7,color:"#fff",padding:isMobile?"6px 10px":"7px 14px",fontSize:isMobile?11:12,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif",whiteSpace:"nowrap"}}>
                  {isMobile?"PDF":"Export PDF"}
                </button>
              )}
            </div>
          </div>

          {/* ── Clients view ── */}
          {advisorView==="clients"&&(
            <AdvisorClientsView
              engine={engine}
              setEngine={setEngine}
              currentUser={currentUser}
              addAudit={addAudit}
              sbClients={sbClients}
              loadClients={loadClients}
              onLoadClient={(c)=>{ handleLoadClient(c); setAdvisorView("allocator"); }}
            />
          )}

          {/* ── Portfolio Analyzer view ── */}
          {advisorView==="analyzer"&&(
            <PortfolioAnalyzer
              currentUser={currentUser}
              engine={engine}
              setEngine={setEngine}
              addAudit={addAudit}
              sbClients={sbClients}
              loadClients={loadClients}
              onLoadClient={handleLoadClient}
              onDirtyChange={setAnalyzerDirty}
            />
          )}

          {/* ── Allocator view ── */}
          {advisorView==="allocator"&&(
            <div style={{maxWidth:1060,margin:"0 auto",padding:isMobile?"14px 12px 48px":isTablet?"18px 18px 56px":"24px 32px 56px"}}>

              {/* ── Client selector ── */}
              <ClientSelector
                engine={engine}
                currentUser={currentUser}
                sbClients={sbClients}
                loadedClient={loadedClient}
                clientName={clientName}
                totalValue={totalValue}
                keptHoldings={keptHoldings}
                clientDirected={clientDirected}
                onClientDirectedChange={setClientDirected}
                additionalCash={additionalCash}
                onAdditionalCashChange={setAdditionalCash}
                onLoadClient={handleLoadClient}
                onNewClient={()=>{setLoadedClient(null);setKeptHoldings([]);setClientDirected([]);setAdditionalCash("");setClientName("");setTotalValue("");}}
                onClearClient={()=>{setLoadedClient(null);setKeptHoldings([]);setClientDirected([]);setAdditionalCash("");}}
                onToggleKeep={handleToggleKeep}
                onClientNameChange={setClientName}
                onTotalValueChange={setTotalValue}
                isMobile={isMobile}
              />

              <AllocatorView clientName={clientName} totalValue={totalValue} keptHoldings={keptHoldings} loadedClient={loadedClient}
                onSnapshotChange={snap=>{ setAllocSnapshot(snap); setAllocatorDirty(true); }}/>

              {/* ── Floating save + compare buttons ── */}
              {allocSnapshot&&(
                <div style={{position:"fixed",bottom:28,right:28,zIndex:100,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
                  {allocSaveState==="saved"&&<div style={{background:"#059669",color:"#fff",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,boxShadow:"0 4px 12px rgba(0,0,0,0.15)"}}>✓ Saved</div>}
                  {allocSaveState==="created"&&<div style={{background:"#7C3AED",color:"#fff",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,boxShadow:"0 4px 12px rgba(0,0,0,0.15)"}}>✓ Client Created</div>}
                  <div style={{display:"flex",gap:8}}>
                    <button onClick={()=>handleSaveAllocation(false)}
                      disabled={allocSaveState==="saving"}
                      style={{display:"flex",alignItems:"center",gap:7,padding:"11px 18px",background:"#1E3A5F",border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:allocSaveState==="saving"?"not-allowed":"pointer",fontFamily:"'Outfit',sans-serif",boxShadow:"0 4px 16px rgba(30,58,95,0.35)",transition:"all 0.2s",whiteSpace:"nowrap"}}
                      onMouseEnter={e=>{if(allocSaveState!=="saving"){e.currentTarget.style.background="#2563EB";e.currentTarget.style.transform="translateY(-1px)";}}}
                      onMouseLeave={e=>{e.currentTarget.style.background="#1E3A5F";e.currentTarget.style.transform="translateY(0)";}}>
                      <span>💾</span>
                      {allocSaveState==="saving"?"Saving…":loadedClient?"Save Allocation":"Save to Client"}
                    </button>
                    <button onClick={()=>handleSaveAllocation(true)}
                      disabled={allocSaveState==="saving"}
                      style={{display:"flex",alignItems:"center",gap:7,padding:"11px 18px",background:"#7C3AED",border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:allocSaveState==="saving"?"not-allowed":"pointer",fontFamily:"'Outfit',sans-serif",boxShadow:"0 4px 16px rgba(124,58,237,0.35)",transition:"all 0.2s",whiteSpace:"nowrap"}}
                      onMouseEnter={e=>{if(allocSaveState!=="saving"){e.currentTarget.style.background="#6D28D9";e.currentTarget.style.transform="translateY(-1px)";}}}
                      onMouseLeave={e=>{e.currentTarget.style.background="#7C3AED";e.currentTarget.style.transform="translateY(0)";}}>
                      <span>⇄</span>
                      {isMobile?"Compare":"Save & Compare"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Comparator view ── */}
          {advisorView==="comparator"&&(
            <PortfolioComparator
              engine={engine}
              loadedClient={loadedClient}
              allocSnapshot={allocSnapshot}
              keptHoldings={keptHoldings}
              clientDirected={clientDirected}
              onGoAllocator={()=>setAdvisorView("allocator")}
              onGoAnalyzer={()=>setAdvisorView("analyzer")}
            />
          )}

          {advisorView==="trading"&&(
            <div style={{position:"relative"}}>
              <TradingView
                engine={engine}
                loadedClient={loadedClient}
                allocSnapshot={allocSnapshot}
                keptHoldings={keptHoldings}
                clientDirected={clientDirected}
                additionalCash={Number(String(additionalCash||"0").replace(/,/g,""))||0}
                onGoComparator={()=>setAdvisorView("comparator")}
                onGoAllocator={()=>setAdvisorView("allocator")}
                onDirtyChange={setTradingDirty}
              />
              {/* ── Floating save button (trading page) ── */}
              {allocSnapshot&&(
                <div style={{position:"fixed",bottom:28,right:28,zIndex:100,display:"flex",flexDirection:"column",alignItems:"flex-end",gap:8}}>
                  {allocSaveState==="saved"&&<div style={{background:"#059669",color:"#fff",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,boxShadow:"0 4px 12px rgba(0,0,0,0.15)"}}>✓ Saved</div>}
                  {allocSaveState==="created"&&<div style={{background:"#7C3AED",color:"#fff",borderRadius:8,padding:"6px 14px",fontSize:12,fontWeight:700,boxShadow:"0 4px 12px rgba(0,0,0,0.15)"}}>✓ Client Created</div>}
                  <button onClick={()=>handleSaveAllocation(false)}
                    disabled={allocSaveState==="saving"}
                    style={{display:"flex",alignItems:"center",gap:7,padding:"11px 18px",background:"#1E3A5F",border:"none",borderRadius:10,color:"#fff",fontSize:13,fontWeight:700,cursor:allocSaveState==="saving"?"not-allowed":"pointer",fontFamily:"'Outfit',sans-serif",boxShadow:"0 4px 16px rgba(30,58,95,0.35)",transition:"all 0.2s",whiteSpace:"nowrap"}}
                    onMouseEnter={e=>{if(allocSaveState!=="saving"){e.currentTarget.style.background="#2563EB";e.currentTarget.style.transform="translateY(-1px)";}}}
                    onMouseLeave={e=>{e.currentTarget.style.background="#1E3A5F";e.currentTarget.style.transform="translateY(0)";}}>
                    <span>💾</span>
                    {allocSaveState==="saving"?"Saving…":loadedClient?"Save Allocation":"Save to Client"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Nav guard modal ── */}
          {navGuard&&(
            <div style={{position:"fixed",inset:0,background:"rgba(15,23,42,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:16,backdropFilter:"blur(4px)"}}>
              <div style={{background:"#fff",borderRadius:14,width:"min(440px,96vw)",boxShadow:"0 20px 60px rgba(0,0,0,0.2)",overflow:"hidden",fontFamily:"'Outfit',sans-serif"}}>
                <div style={{background:navGuard.source==="allocator"?"linear-gradient(135deg,#1E3A5F,#2563EB)":navGuard.source==="trading"?"linear-gradient(135deg,#059669,#065F46)":"linear-gradient(135deg,#D97706,#B45309)",padding:"16px 20px"}}>
                  <div style={{fontSize:15,fontWeight:700,color:"#fff"}}>
                    {navGuard.source==="allocator"?"⚠ Unsaved Allocation":navGuard.source==="trading"?"⚠ Unsaved Trading Plan":"⚠ Unsaved Analysis"}
                  </div>
                  <div style={{fontSize:11,color:navGuard.source==="allocator"?"#BFDBFE":navGuard.source==="trading"?"#A7F3D0":"#FDE68A",marginTop:3}}>
                    You have work in progress that hasn't been saved
                  </div>
                </div>
                <div style={{padding:"20px"}}>
                  <p style={{fontSize:13,color:"#475569",lineHeight:1.6,margin:"0 0 20px"}}>
                    {navGuard.source==="allocator"
                      ? "Leaving the Allocator without saving will not lose your allocation — it stays active this session. However the 💾 Save and ⇄ Compare buttons will let you attach it to the client record before leaving."
                      : navGuard.source==="trading"
                      ? "Leaving the Trading page will discard your strategy selection, cash needs, DCA settings, and any notes you've added. Save the allocation first to preserve your work."
                      : "Leaving the Analyzer will discard your current holdings and any analysis results that haven't been saved to a client record. This cannot be undone."
                    }
                  </p>
                  <div style={{display:"flex",gap:10}}>
                    <button onClick={()=>setNavGuard(null)}
                      style={{flex:1,padding:"10px",background:"#F1F5F9",border:"1px solid #E2E8F0",borderRadius:8,color:"#475569",fontSize:13,fontWeight:600,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                      ← Stay & Save
                    </button>
                    <button onClick={()=>{
                      const target = navGuard.target;
                      const src    = navGuard.source;
                      setNavGuard(null);
                      if (src==="analyzer") setAnalyzerDirty(false);
                      if (src==="allocator") setAllocatorDirty(false);
                      if (src==="trading")  setTradingDirty(false);
                      setAdvisorView(target);
                    }}
                      style={{flex:1,padding:"10px",background:"#DC2626",border:"none",borderRadius:8,color:"#fff",fontSize:13,fontWeight:700,cursor:"pointer",fontFamily:"'Outfit',sans-serif"}}>
                      Leave Anyway
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {showAdvisorHelp && <AdvisorHelpModal onClose={()=>setShowAdvisorHelp(false)}/>}
          <style>{`
            @media print{button{display:none!important;}}
            *{box-sizing:border-box;}
            body{margin:0;overflow-x:hidden;}
            input:focus,select:focus{border-color:#93C5FD!important;box-shadow:0 0 0 3px #BFDBFE44;}
            input[type=number]::-webkit-inner-spin-button{opacity:0.4;}
          `}</style>
        </div>
      </SessionCtx.Provider>
    </EngineCtx.Provider>
    </PIIVaultProvider>
  );
}
